import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ScatterChart, Scatter, Cell,
  PieChart, Pie, Legend, ReferenceLine, ComposedChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from "recharts";
import { supabase } from "./supabase";
import { T, GRAPH, EI_COLOR, css, sliderFill } from "./styles/theme";
import { injectStyles } from "./styles/inject";
import { HI, navIcon, KakaoIcon, GoogleIcon, NaverIcon } from "./components/icons";
import { Card, Pill, NavyNum, SectionTitle, Divider, Spinner, ChartTip } from "./components/ui";
import { QUANT_ITEMS, QUAL_ITEMS, SUBJECTS, SUBJECT_CONFIG, ERR_CODES, ERR_LABELS, GRADES } from "./utils/constants";
import { calcGrade, gradeInfo, calcEI } from "./utils/grade";
import { ROSTER2, ROSTER2_NAVER_DATES, ROSTER2_MORNING_DATES, ROSTER2_NIGHT_DATES, ROSTER2_DAY_KO, roster2FmtKey, roster2Fmt, isLateByDeadline, INIT_ATTENDANCE2, MIN_ATTENDANCE_MINUTES, isValidAttendance, attendanceThreshold, cafeArticleUrl } from "./utils/roster2";
import { draftKey, restoreDraft, clearDraft, useDraftBackup } from "./utils/draft";

// ══════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════
// FONT INJECT — Sandoll Gothic Neo + Noto Sans KR fallback
// ══════════════════════════════════════════════════════
injectStyles();


// ══════════════════════════════════════════════════════
// RESPONSIVE
// ══════════════════════════════════════════════════════
const useMobile = () => {
  const [m, setM] = useState(window.innerWidth < 768);
  useEffect(() => { const h=()=>setM(window.innerWidth<768); window.addEventListener("resize",h); return()=>window.removeEventListener("resize",h); },[]);
  return m;
};

// ══════════════════════════════════════════════════════
// BASE COMPONENTS
// ══════════════════════════════════════════════════════
// 생년월일 입력 공통 컴포넌트 (년/월/일 세 드롭다운) — T 선언 이후에 위치해야 함
// showGrade=true 이면 학년 태그 표시 (학생용)
const BirthInput = ({year, month, day, onYear, onMonth, onDay, showGrade=false}) => {
  const grade = showGrade ? calcGrade(year ? Number(year) : null, month ? Number(month) : null) : null;
  const selStyle = {padding:"9px 6px",borderRadius:8,border:`1px solid ${T.border}`,fontSize:14,fontFamily:"'Noto Sans KR',sans-serif",background:"#fff",cursor:"pointer"};
  const curYear = new Date().getFullYear();
  return (
    <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
      <select value={year||""} onChange={e=>onYear(e.target.value)} style={{...selStyle,flex:"1 1 90px"}}>
        <option value="">년도</option>
        {Array.from({length:curYear-1950+1},(_,i)=>curYear-i).map(y=><option key={y} value={y}>{y}년</option>)}
      </select>
      <select value={month||""} onChange={e=>onMonth(e.target.value)} style={{...selStyle,flex:"1 1 70px"}}>
        <option value="">월</option>
        {Array.from({length:12},(_,i)=><option key={i+1} value={i+1}>{i+1}월</option>)}
      </select>
      <select value={day||""} onChange={e=>onDay(e.target.value)} style={{...selStyle,flex:"1 1 70px"}}>
        <option value="">일</option>
        {Array.from({length:31},(_,i)=><option key={i+1} value={i+1}>{i+1}일</option>)}
      </select>
      {grade && <span style={{fontSize:12,color:T.muted,whiteSpace:"nowrap"}}>({grade})</span>}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// LOGO COMPONENT
// "project 20HA" — 20 얇게, 나머지 볼드, 산돌고딕체
// ══════════════════════════════════════════════════════
const Logo = ({size="md", dark=false, onClick=null, headerMode=false}) => {
  const c = dark ? T.white : T.navy;
  const s = size==="lg" ? {project:12,num:44,meta:44} : size==="md" ? {project:10,num:24,meta:24} : {project:8,num:18,meta:18};
  if(headerMode) {
    // 헤더: META - X 만 표시
    return (
      <div onClick={onClick} style={{display:"inline-flex",alignItems:"baseline",gap:0,cursor:onClick?"pointer":"default",lineHeight:1}}>
        <span style={{fontSize:s.meta,fontWeight:100,color:c,letterSpacing:"-0.02em",lineHeight:1}}>META </span>
        <span style={{fontSize:s.meta,fontWeight:300,color:c,letterSpacing:"-0.02em",lineHeight:1}}>– </span>
        <span style={{fontSize:s.meta,fontWeight:700,color:c,letterSpacing:"-0.02em",lineHeight:1}}>X</span>
      </div>
    );
  }
  return (
    <div className="logo-20ha" onClick={onClick} style={{display:"inline-flex",flexDirection:"column",lineHeight:1,gap:3,cursor:onClick?"pointer":"default"}}>
      <div style={{display:"flex",alignItems:"baseline",gap:4}}>
        <span style={{fontSize:s.project,fontWeight:700,color:dark?T.white:T.navy,letterSpacing:"0.04em"}}>Project</span>
        <span style={{fontSize:s.project,fontWeight:400,color:dark?"rgba(255,255,255,0.8)":"rgba(25,29,84,0.6)",letterSpacing:"0.04em"}}>20</span>
        <span style={{fontSize:s.project,fontWeight:700,color:dark?T.white:T.navy,letterSpacing:"0.04em"}}>HA</span>
      </div>
      <div style={{display:"flex",alignItems:"baseline",gap:0}}>
        <span style={{fontSize:s.num,fontWeight:100,color:c,letterSpacing:"-0.02em",lineHeight:1}}>META </span>
        <span style={{fontSize:s.num,fontWeight:300,color:c,letterSpacing:"-0.02em",lineHeight:1}}>– </span>
        <span style={{fontSize:s.num,fontWeight:700,color:c,letterSpacing:"-0.02em",lineHeight:1}}>X</span>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// SOCIAL LOGIN BUTTONS
// ══════════════════════════════════════════════════════
const SocialBtn = ({icon,label,color,bg,border,onClick,loading}) => (
  <button onClick={onClick} disabled={loading}
    style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"12px 16px",borderRadius:10,border:`1px solid ${border||T.border}`,background:bg||T.white,color:color||T.text,fontSize:14,fontWeight:700,cursor:"pointer",transition:"all 0.15s",marginBottom:8,opacity:loading?0.7:1}}>
    {loading ? <Spinner size={18}/> : <span style={{fontSize:20,lineHeight:1}}>{icon}</span>}
    <span>{label}</span>
  </button>
);

// 카카오 로고 SVG
// ══════════════════════════════════════════════════════
// PASSWORD RESET SCREEN
// ══════════════════════════════════════════════════════
const PasswordResetScreen = ({ onDone }) => {
  const [newPw, setNewPw]   = useState("");
  const [newPwC, setNewPwC] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone]     = useState(false);
  const [err, setErr]       = useState("");
  const isMobile = useMobile();

  const handleReset = async () => {
    const pwRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/;
    if(newPw !== newPwC){ setErr("비밀번호가 일치하지 않습니다."); return; }
    if(!pwRegex.test(newPw)){ setErr("영문 대소문자, 특수문자 포함 8자 이상이어야 합니다."); return; }
    setSaving(true); setErr("");
    const { error } = await supabase.auth.updateUser({ password: newPw });
    setSaving(false);
    if(error){ setErr("변경 실패: " + error.message); return; }
    setDone(true);
    setTimeout(onDone, 2000);
  };

  return (
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Noto Sans KR',sans-serif",padding:20}}>
      <Card style={{width:"100%",maxWidth:400,padding:"40px 36px"}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <Logo size="md"/>
        </div>
        <div style={{fontSize:20,fontWeight:800,color:T.navy,marginBottom:6}}>새 비밀번호 설정</div>
        <div style={{fontSize:13,color:T.muted,marginBottom:20}}>사용할 새 비밀번호를 입력해주세요.</div>
        {done ? (
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}>{HI.check(48,"#16A34A")}</div>
            <div style={{fontSize:15,fontWeight:700,color:T.navy}}>비밀번호가 변경됐어요!</div>
            <div style={{fontSize:13,color:T.muted,marginTop:6}}>잠시 후 로그인 화면으로 이동합니다...</div>
          </div>
        ) : (
          <form onSubmit={e=>{e.preventDefault();handleReset();}} autoComplete="new-password">
            <div style={{display:"grid",gap:12,marginBottom:12}}>
              <div>
                <label style={css.label}>새 비밀번호 <span style={{fontWeight:400,color:T.muted}}>(대소문자+특수문자 8자↑)</span></label>
                <input name="new-password" type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="••••••••" autoComplete="new-password" style={css.input}/>
              </div>
              <div>
                <label style={css.label}>새 비밀번호 확인</label>
                <input name="new-password-confirm" type="password" value={newPwC} onChange={e=>setNewPwC(e.target.value)} placeholder="••••••••" autoComplete="new-password" style={css.input}/>
              </div>
            </div>
            {err && <div style={{background:"#FEE2E2",border:"1px solid #FECACA",borderRadius:8,padding:"10px 14px",fontSize:13,color:T.danger,marginBottom:12}}>{err}</div>}
            <button type="submit" disabled={saving} style={{...css.btnPrimary,width:"100%",padding:"13px 0",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              {saving?<><Spinner size={18} color="#fff"/>변경 중...</>:"비밀번호 변경"}
            </button>
          </form>
        )}
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// AUTH SCREEN — 로그인 + 소셜 + 데모 버튼
// ══════════════════════════════════════════════════════
const AuthScreen = ({ onLogin, onSignupModeChange }) => {
  const [loading, setLoading] = useState({});
  const [error, setError]     = useState("");
  const [email, setEmail]     = useState(()=>localStorage.getItem("20ha_saved_email")||"");
  const [pw, setPw]           = useState("");
  const [rememberMe, setRememberMe] = useState(()=>!!localStorage.getItem("20ha_saved_email"));
  const [mode, setMode]       = useState("login"); // "login"|"signup"
  const SIGNUP_DRAFT_KEY = draftKey("signup");
  const _restoredSignup = useMemo(() => restoreDraft(SIGNUP_DRAFT_KEY) || {}, []);
  const [suRole, setSuRole]   = useState(_restoredSignup.suRole || "student");
  const [suName, setSuName]   = useState(_restoredSignup.suName || "");
  const [suBirthYear, setSuBirthYear]   = useState(_restoredSignup.suBirthYear || "");
  const [suBirthMonth, setSuBirthMonth] = useState(_restoredSignup.suBirthMonth || "");
  const [suBirthDay, setSuBirthDay]     = useState(_restoredSignup.suBirthDay || "");
  const [suEmail, setSuEmail] = useState(_restoredSignup.suEmail || "");
  const [suPw, setSuPw]       = useState(""); // 보안: 비번 백업 안 함
  const [suPwC, setSuPwC]     = useState("");
  const [suTarget, setSuTarget] = useState(85);
  // 비번 제외 회원가입 입력 백업 (debounce 500ms)
  useDraftBackup(SIGNUP_DRAFT_KEY, { suRole, suName, suBirthYear, suBirthMonth, suBirthDay, suEmail });
  // 가입 모드 변화를 App에 전달 → OTP verify로 생긴 임시 SIGNED_IN을 무시하기 위함
  useEffect(() => { onSignupModeChange?.(mode === "signup"); }, [mode, onSignupModeChange]);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [ageAgreed, setAgeAgreed] = useState(false);
  const [signupDone, setSignupDone] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [fpEmail, setFpEmail] = useState("");
  const [fpSent, setFpSent] = useState(false);
  const isMobile = useMobile();

  const setLoad = (k,v) => setLoading(p=>({...p,[k]:v}));

  const handleLogin = async (overrideEmail, overridePw) => {
    const e = overrideEmail || email;
    const p = overridePw   || pw;
    if(!e||!p){ setError("이메일과 비밀번호를 입력하세요."); return; }
    if(mode==="login"&&p.length<8){ setError("비밀번호를 확인해주세요."); return; }
    setLoad("email",true); setError("");
    const {data,error:err} = await supabase.auth.signInWithPassword({email:e, password:p});
    setLoad("email",false);
    if(err){ setError(translateSupabaseError(err.message)); return; }
    if(rememberMe) localStorage.setItem("20ha_saved_email", e);
    else localStorage.removeItem("20ha_saved_email");
    // Chrome 비밀번호 저장 프롬프트 트리거
    if(window.PasswordCredential) {
      try {
        const cred = new window.PasswordCredential({ id: e, password: p });
        await navigator.credentials.store(cred);
      } catch(_) {}
    }
    onLogin(data.session);
  };

  const handleForgotPassword = async () => {
    if(!fpEmail){ setError("이메일을 입력해주세요."); return; }
    if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fpEmail)){ setError("유효하지 않은 이메일 형식입니다."); return; }
    setLoad("fp", true); setError("");
    // 1) 가입된 이메일인지 사전 체크 (Supabase는 보안상 항상 success를 반환하므로 RPC로 별도 확인)
    const { data: exists, error: chkErr } = await supabase.rpc("auth_email_exists", { p_email: fpEmail });
    if(chkErr){
      setLoad("fp", false);
      setError("확인 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
      return;
    }
    if(!exists){
      setLoad("fp", false);
      setError("가입되지 않은 이메일입니다. 이메일을 확인하거나 회원가입을 진행해주세요.");
      return;
    }
    // 2) 실제 재설정 메일 발송
    const { error:err } = await supabase.auth.resetPasswordForEmail(fpEmail, {
      redirectTo: "https://meta-x.ai.kr"
    });
    setLoad("fp", false);
    if(err){ setError(translateSupabaseError(err.message)); return; }
    setFpSent(true);
  };

  const handleSendOtp = async () => {
    setError("");
    if(!suEmail){ setError("이메일을 입력해주세요."); return; }
    if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(suEmail)){ setError("유효하지 않은 이메일 형식입니다."); return; }
    setLoad("sendOtp",true);
    const tempPw = `Tmp${Date.now()}`;
    let { data: signUpData, error:err } = await supabase.auth.signUp({ email:suEmail, password:tempPw });
    // 케이스 1: 명시적 "already registered" 에러
    // 케이스 2: identities=[] → 이미 인증된 이메일 (확인된 기존 계정)
    // 케이스 3: identities 있지만 created_at이 과거 → OTP 보냈지만 인증 안 한 기존 계정
    //           → cleanup 후 재생성해야 새 OTP가 확실히 발송됨
    const createdAgo = signUpData?.user?.created_at
      ? Date.now() - new Date(signUpData.user.created_at).getTime()
      : 0;
    const isExisting =
      (err && (err.message.toLowerCase().includes("already registered") || err.message.toLowerCase().includes("already been registered"))) ||
      (!err && signUpData?.user?.identities?.length === 0) ||
      (!err && signUpData?.user?.identities?.length > 0 && createdAgo > 10000);
    if(isExisting) {
      err = null;
      const { data: cleaned } = await supabase.rpc("cleanup_incomplete_signup", { p_email: suEmail });
      if(cleaned) {
        const retry = await supabase.auth.signUp({ email:suEmail, password:tempPw });
        err = retry.error;
      } else {
        err = { message: "already registered" };
      }
    }
    setLoad("sendOtp",false);
    if(err){ setError(translateSupabaseError(err.message)); return; }
    setOtpSent(true); setOtpVerified(false); setOtpCode("");
  };

  const handleVerifyOtp = async () => {
    if(otpCode.length !== 6){ setError("6자리 코드를 입력해주세요."); return; }
    setLoad("otp",true); setError("");
    const {error:err} = await supabase.auth.verifyOtp({ email:suEmail, token:otpCode, type:"signup" });
    setLoad("otp",false);
    if(err){
      const m = err.message?.toLowerCase() || "";
      if(m.includes("expired") || m.includes("otp_expired") || m.includes("token has expired")){
        setError("인증 코드가 만료되었습니다. 인증 메일을 다시 요청해주세요.");
        setOtpSent(false); setOtpCode(""); // 인증 메일 보내기 버튼으로 돌아가기
      } else {
        setError("인증 코드가 올바르지 않습니다. 다시 확인해주세요.");
      }
      return;
    }
    setOtpSent(false); setOtpVerified(true);
  };

  const handleSignup = async () => {
    setError("");
    if(!otpVerified){ setError("이메일 인증을 먼저 완료해주세요."); return; }
    if(!termsAgreed||!privacyAgreed||!ageAgreed){ setError("필수 동의 항목을 모두 체크해 주세요."); return; }
    if(!suName||!suPw){ setError("모든 항목을 입력해 주세요."); return; }
    if(suName.trim()!==suName||suName.includes(" ")){ setError("이름에 공백을 포함할 수 없습니다."); return; }
    if(suName.trim().length<2){ setError("이름은 2자 이상 입력해주세요."); return; }
    const pwRegex=/^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/;
    if(suPw!==suPwC){ setError("비밀번호가 일치하지 않습니다."); return; }
    if(!pwRegex.test(suPw)){ setError("비밀번호는 영문 대소문자, 특수문자를 포함한 8자 이상이어야 합니다."); return; }
    setLoad("signup",true);
    const {data,error:err} = await supabase.auth.updateUser({
      password:suPw, data:{name:suName, grade:suRole==="student"?calcGrade(Number(suBirthYear),suBirthMonth?Number(suBirthMonth):null):"", target_ei:85, role:suRole}
    });
    if(err){
      // 세션 유지 — 사용자가 바로 재시도 가능
      setLoad("signup",false);
      setError(translateSupabaseError(err.message));
      return;
    }
    if(data?.user){
      const { error:profErr } = await supabase.from("profiles").upsert({
        id:data.user.id, name:suName,
        grade:suRole==="student"?calcGrade(Number(suBirthYear),null):"",
        birth_year:suBirthYear?Number(suBirthYear):null,
        birth_month:suBirthMonth?Number(suBirthMonth):null,
        birth_day:suBirthDay?Number(suBirthDay):null,
        target_ei:85, role:suRole, approval_status:"pending"
      });
      if(profErr){
        // 좀비 계정 방지: auth.users는 비번까지 설정됐는데 profile 없음
        // cleanup으로 정리 후 사용자에게 재시도 안내 (같은 이메일로 다시 시도 가능)
        try {
          await supabase.auth.signOut();
          await supabase.rpc("cleanup_incomplete_signup", { p_email: suEmail });
        } catch (_) {}
        setLoad("signup",false);
        setError("프로필 저장 중 오류가 발생했습니다. 잠시 후 같은 이메일로 다시 시도해주세요.");
        // OTP 다시 받아야 하므로 인증 상태 초기화
        setOtpVerified(false); setOtpSent(false); setOtpCode("");
        return;
      }
      // 학생 회원가입 시 20HA 2기 명단(cert_students)과 이름 매칭되면 자동 연동
      // 결과를 받아 사용자에게 상태 안내 (ambiguous=동명이인은 관리자 수동 연동)
      let linkMsg = "";
      if(suRole === "student"){
        try {
          const { data: linkRes } = await supabase.rpc("try_link_2ki_on_signup", { p_profile_id: data.user.id, p_name: suName.trim() });
          if(linkRes && !linkRes.linked && linkRes.reason === "ambiguous"){
            linkMsg = `\n\n※ '${suName}' 동명이인이 ${linkRes.match_count}명 발견되어 자동 연동되지 않았습니다.\n관리자가 수동으로 연동해드릴게요.`;
          }
          // no_match는 안내 안 함 — 2기 아닌 일반 학생일 수 있음
        } catch (e) { /* RPC 실패는 가입 자체에 영향 없음 (관리자 수동으로 연동 가능) */ }
      }
      // 향후 setSignupDone 후 화면에서 노출
      if(linkMsg) window.__signupLinkMsg = linkMsg;
    }
    // signOut 실패해도 가입은 완료 — approval_status=pending이 진입 차단
    try { await supabase.auth.signOut(); } catch (_) {}
    setLoad("signup",false);
    clearDraft(SIGNUP_DRAFT_KEY);
    setSignupDone(true);
  };

  const translateSupabaseError = (msg) => {
  if(!msg) return "오류가 발생했습니다.";
  const m = msg.toLowerCase();
  if(m.includes("already registered")||m.includes("already been registered")||m.includes("user already")) return "이미 가입된 이메일입니다.";
  if(m.includes("invalid login")||m.includes("invalid credentials")||m.includes("invalid email or password")) return "이메일 또는 비밀번호가 올바르지 않습니다.";
  if(m.includes("email not confirmed")) return "이메일 인증이 완료되지 않았습니다.";
  if(m.includes("too many requests")||m.includes("too many")) return "요청이 너무 많습니다. 잠시 후 다시 시도해주세요.";
  if(m.includes("user not found")) return "등록되지 않은 이메일입니다.";
  if(m.includes("signup is disabled")||m.includes("signups not allowed")) return "현재 신규 가입이 비활성화되어 있습니다.";
  if(m.includes("password should be")) return "비밀번호는 영문 대소문자, 특수문자를 포함한 8자 이상이어야 합니다.";
  if(m.includes("unable to validate email")) return "유효하지 않은 이메일 형식입니다.";
  if(m.includes("email address") && m.includes("invalid")) return "유효하지 않은 이메일 형식입니다.";
  if(m.includes("network")) return "네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.";
  if(m.includes("token has expired")||m.includes("otp_expired")) return "인증 코드가 만료되었습니다. 인증 메일을 다시 요청해주세요.";
  if(m.includes("expired")) return "세션이 만료되었습니다. 다시 시도해주세요.";
  // 영어 메시지 그대로 노출 방지 - 알 수 없는 오류
  if(/[a-zA-Z]{4,}/.test(msg) && !/[가-힣]/.test(msg)) return "오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
  return msg;
};
const handleSocial = async (provider) => {
    setLoad(provider,true); setError("");
    const {error:err} = await supabase.auth.signInWithOAuth({
      provider, options:{ redirectTo: window.location.origin }
    });
    if(err){ setError(translateSupabaseError(err.message)); setLoad(provider,false); }
  };

  const brandPanel = (
    <div style={{flex:1,background:T.grad,display:"flex",flexDirection:"column",justifyContent:"center",padding:"60px 64px",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:-80,right:-80,width:400,height:400,background:"rgba(255,255,255,0.04)",borderRadius:"50%",pointerEvents:"none"}}/>
      <div style={{position:"absolute",bottom:-120,left:-60,width:500,height:500,background:"rgba(246,139,30,0.1)",borderRadius:"50%",pointerEvents:"none"}}/>
      <div style={{position:"relative"}}>
        <Logo size="lg" dark />
        <div style={{height:1,background:"rgba(255,255,255,0.15)",margin:"28px 0"}}/>
        <div style={{fontSize:15,color:"rgba(255,255,255,0.6)",lineHeight:1.85,maxWidth:340,marginBottom:40}}>
          보이지 않던 공부의 과정을 데이터로 읽어내다.<br/>Meta-X, 성장의 흐름을 기록하는 학습 시스템
        </div>
        {[
          {icon:"clipboard",text:"학습데이터 기록 — 공부 시간, 수행 여부, 이해도 등 학습 과정을 데이터로 기록합니다."},
          {icon:"chart",    text:"학습 상태 시각화 — 아이의 학습 흐름과 현재 상태를 한눈에 확인할 수 있습니다."},
          {icon:"search",   text:"학습 패턴 분석 — 반복적으로 막히는 지점과 공부 습관을 데이터로 분석합니다."},
          {icon:"check",    text:"자기점검 시스템 — 학생이 자신의 학습을 스스로 점검하고 조정하도록 돕습니다."},
        ].map(f=>(
          <div key={f.text} style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
            <div style={{width:34,height:34,background:"rgba(255,255,255,0.1)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {HI[f.icon]?.(18,"rgba(255,255,255,0.85)")}
            </div>
            <span style={{fontSize:14,color:"rgba(255,255,255,0.7)"}}>{f.text}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // 가입 완료 화면
  if(signupDone) return (
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",fontFamily:"'Noto Sans KR',sans-serif"}}>
      {!isMobile && brandPanel}
      <div style={{width:isMobile?"100%":480,background:T.surface,borderLeft:isMobile?"none":`1px solid ${T.border}`,display:"flex",flexDirection:"column",justifyContent:"center",padding:"48px 44px"}}>
        <div style={{textAlign:"center"}}>
          <div style={{marginBottom:16,display:"flex",justifyContent:"center"}}>{HI.check(52,"#16A34A")}</div>
          <div style={{fontSize:20,fontWeight:800,color:T.navy,marginBottom:8}}>가입 완료!</div>
          {typeof window !== "undefined" && window.__signupLinkMsg && (
            <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#92400E",marginBottom:16,whiteSpace:"pre-line",textAlign:"left"}}>
              {window.__signupLinkMsg.trim()}
            </div>
          )}
          <div style={{fontSize:14,color:T.textMid,lineHeight:1.8,marginBottom:24}}>
            관리자 승인 후 로그인하실 수 있습니다.<br/>담당 선생님께 승인을 요청해 주세요.
          </div>
          <button onClick={()=>{setSignupDone(false);setMode("login");}} style={{...css.btnPrimary,padding:"12px 32px"}}>로그인 화면으로</button>
        </div>
      </div>
    </div>
  );

  const loginForm = (
    <div>
      <div style={{fontSize:22,fontWeight:900,color:T.navy,marginBottom:4}}>
        {mode==="login"?"로그인":mode==="forgot"?"비밀번호 찾기":"회원가입"}
      </div>
      <div style={{fontSize:13,color:T.muted,marginBottom:20}}>
        {mode==="login"?"계정으로 로그인하세요.":mode==="forgot"?"가입 시 사용한 이메일을 입력해주세요.":"계정을 만들어 학습을 시작하세요."}
      </div>

      {mode==="login" && (
        <form onSubmit={e=>{e.preventDefault();handleLogin();}} autoComplete="on">
          <div style={{display:"flex",alignItems:"center",gap:10,margin:"14px 0"}}>
            <div style={{flex:1,height:1,background:T.border}}/>
            <span style={{fontSize:12,color:T.muted,fontWeight:600}}>이메일로 로그인</span>
            <div style={{flex:1,height:1,background:T.border}}/>
          </div>

          <div style={{marginBottom:12}}>
            <label style={css.label}>이메일</label>
            <input name="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="example@email.com" autoComplete="email" style={css.input}/>
          </div>
          <div style={{marginBottom:12}}>
            <label style={css.label}>비밀번호</label>
            <input name="password" type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="••••••••" autoComplete="current-password" style={css.input}/>
          </div>
          <div onClick={()=>setRememberMe(v=>!v)} style={{display:"flex",alignItems:"center",gap:8,marginBottom:14,cursor:"pointer"}}>
            <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${rememberMe?T.navy:T.borderStrong}`,background:rememberMe?T.navy:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
              {rememberMe&&<span style={{color:T.white,fontSize:11,lineHeight:1,fontWeight:800}}>✓</span>}
            </div>
            <span style={{fontSize:13,color:T.textMid,userSelect:"none"}}>자동 로그인</span>
          </div>

          {error && <div style={{background:"#FEE2E2",border:"1px solid #FECACA",borderRadius:8,padding:"10px 14px",fontSize:13,color:T.danger,marginBottom:12}}>{error}</div>}

          <button type="submit" disabled={loading.email} style={{...css.btnPrimary,width:"100%",padding:"13px 0",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            {loading.email?<><Spinner size={18} color="#fff"/>로그인 중...</>:"로그인 →"}
          </button>

          <div style={{textAlign:"center",marginTop:10,fontSize:12}}>
            <span onClick={()=>{setMode("forgot");setError("");setFpSent(false);setFpEmail("");}} style={{color:T.muted,cursor:"pointer",textDecoration:"underline"}}>비밀번호를 잊으셨나요?</span>
          </div>
          <div style={{textAlign:"center",marginTop:10,fontSize:13,color:T.muted}}>
            계정이 없으신가요? <span onClick={()=>{setMode("signup");setError("");}} style={{color:T.orange,fontWeight:700,cursor:"pointer"}}>이메일로 가입</span>
          </div>
        </form>
      )}

      {mode==="forgot" && (<>
        {fpSent ? (
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:40,marginBottom:14}}>📧</div>
            <div style={{fontSize:15,fontWeight:700,color:T.navy,marginBottom:8}}>이메일을 보냈어요!</div>
            <div style={{fontSize:13,color:T.textMid,lineHeight:1.8,marginBottom:20}}>
              <strong>{fpEmail}</strong>로 비밀번호 재설정 링크를 보냈어요.<br/>
              이메일을 확인하고 링크를 클릭해 주세요. (스팸함도 확인!)
            </div>
            <button onClick={()=>{setMode("login");setFpSent(false);setError("");}} style={{...css.btnGhost,padding:"10px 28px"}}>로그인으로 돌아가기</button>
          </div>
        ) : (<>
          <div style={{marginBottom:12}}>
            <label style={css.label}>이메일</label>
            <input value={fpEmail} onChange={e=>setFpEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleForgotPassword()} placeholder="가입 시 사용한 이메일" style={css.input}/>
          </div>
          {error && <div style={{background:"#FEE2E2",border:"1px solid #FECACA",borderRadius:8,padding:"10px 14px",fontSize:13,color:T.danger,marginBottom:12}}>{error}</div>}
          <button onClick={handleForgotPassword} disabled={loading.fp} style={{...css.btnPrimary,width:"100%",padding:"13px 0",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            {loading.fp?<><Spinner size={18} color="#fff"/>전송 중...</>:"재설정 이메일 보내기"}
          </button>
          <div style={{textAlign:"center",marginTop:14,fontSize:13,color:T.muted}}>
            <span onClick={()=>{setMode("login");setError("");}} style={{color:T.navy,fontWeight:700,cursor:"pointer"}}>← 로그인으로 돌아가기</span>
          </div>
        </>)}
      </>)}

      {mode==="signup" && (<>
        {/* 역할 선택 */}
        <div style={{display:"flex",gap:8,marginBottom:16,background:T.surfaceAlt,borderRadius:10,padding:4}}>
          {[{v:"student",icon:"cap",label:"학생"},{v:"parent",icon:"users",label:"학부모"}].map(({v,icon,label})=>(
            <button key={v} onClick={()=>setSuRole(v)}
              style={{flex:1,padding:"9px 0",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:700,
                background:suRole===v?T.navy:"transparent",color:suRole===v?T.white:T.muted,transition:"all 0.15s",
                display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
              {navIcon(icon,13,suRole===v?T.white:T.muted)} {label}
            </button>
          ))}
        </div>
        <div style={{display:"grid",gap:12,marginBottom:12}}>
          <div><label style={css.label}>이름 <span style={{color:"#e53e3e"}}>*</span></label><input value={suName} onChange={e=>setSuName(e.target.value)} placeholder="홍길동" required style={css.input}/></div>
          <div>
            <label style={css.label}>생년월일 <span style={{color:"#e53e3e"}}>*</span></label>
            <BirthInput year={suBirthYear} month={suBirthMonth} day={suBirthDay}
              onYear={setSuBirthYear} onMonth={setSuBirthMonth} onDay={setSuBirthDay}
              showGrade={suRole==="student"}/>
          </div>

          <div>
            <label style={css.label}>이메일</label>
            <div style={{display:"flex",gap:8}}>
              <input value={suEmail} onChange={e=>{setSuEmail(e.target.value);setOtpSent(false);setOtpVerified(false);setOtpCode("");}} placeholder="example@email.com" disabled={otpVerified} style={{...css.input,flex:1,opacity:otpVerified?0.6:1}}/>
              {otpVerified
                ? <span style={{flexShrink:0,color:"#16A34A",fontWeight:700,fontSize:13,padding:"0 8px"}}>✓ 인증완료</span>
                : <button onClick={handleSendOtp} disabled={loading.sendOtp||otpSent} style={{...css.btnPrimary,padding:"0 14px",fontSize:12,fontWeight:700,whiteSpace:"nowrap",flexShrink:0,opacity:otpSent?0.5:1}}>
                    {loading.sendOtp?<Spinner size={14} color="#fff"/>:otpSent?"재전송":"인증메일 보내기"}
                  </button>
              }
            </div>
          </div>
          {otpSent&&(
            <div style={{background:"#F0F8FF",border:`1px solid #BFDBFE`,borderRadius:10,padding:"12px 14px"}}>
              <div style={{fontSize:12,color:"#1D4ED8",marginBottom:8,fontWeight:700}}>📧 {suEmail}으로 전송된 6자리 코드를 입력해주세요.</div>
              <div style={{display:"flex",gap:8}}>
                <input value={otpCode} onChange={e=>setOtpCode(e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="000000" maxLength={6}
                  style={{...css.input,flex:1,fontSize:20,fontWeight:900,textAlign:"center",letterSpacing:8,padding:"10px 14px"}}/>
                <button onClick={handleVerifyOtp} disabled={loading.otp||otpCode.length!==6}
                  style={{...css.btnPrimary,padding:"0 16px",fontSize:13,fontWeight:700,flexShrink:0,opacity:otpCode.length!==6?0.5:1}}>
                  {loading.otp?<Spinner size={14} color="#fff"/>:"확인"}
                </button>
              </div>
              <button onClick={async()=>{setOtpCode("");setError("");await handleSendOtp();}}
                disabled={loading.sendOtp||loading.otp}
                style={{background:"none",border:"none",color:"#6B7280",fontSize:11,cursor:(loading.sendOtp||loading.otp)?"default":"pointer",marginTop:6,padding:0,opacity:(loading.sendOtp||loading.otp)?0.5:1}}>
                {loading.sendOtp ? "전송 중..." : "코드 재전송"}
              </button>
            </div>
          )}
          <div><label style={css.label}>비밀번호 <span style={{fontWeight:400,color:T.muted}}>(대소문자+특수문자 포함 8자↑)</span></label><input type="password" value={suPw} onChange={e=>setSuPw(e.target.value)} placeholder="••••••••" style={css.input}/></div>
          <div><label style={css.label}>비밀번호 확인</label><input type="password" value={suPwC} onChange={e=>setSuPwC(e.target.value)} placeholder="••••••••" style={css.input}/></div>
        </div>
        <div style={{background:T.orangePale,border:`1px solid ${T.orange}30`,borderRadius:10,padding:"10px 14px",fontSize:12,color:T.textMid,marginBottom:12}}>
          ⚠️ 가입 후 <strong>관리자 승인</strong>이 완료되어야 로그인할 수 있습니다.
        </div>
        {/* 서비스 이용약관 동의 */}
        <div style={{border:`1px solid ${T.border}`,borderRadius:10,marginBottom:10}}>
          <div style={{height:100,overflowY:"auto",padding:"12px 14px",fontSize:11,lineHeight:1.7,color:"#555",background:"#FAFAFA",borderRadius:"10px 10px 0 0"}}>
            <div style={{fontWeight:700,color:T.navy,marginBottom:6,fontSize:12}}>서비스 이용약관</div>
            <p><strong>제1조 (목적)</strong><br/>본 약관은 (주)아이작교육그룹(이하 "회사")이 제공하는 META-X 서비스(이하 "서비스") 이용에 관한 조건을 정함을 목적으로 합니다.</p>
            <p style={{marginTop:6}}><strong>제2조 (서비스 이용)</strong><br/>서비스는 학습 데이터 기록 및 분석 기능을 제공합니다. 서비스 이용은 회원 가입 및 관리자 승인 후 가능합니다.</p>
            <p style={{marginTop:6}}><strong>제3조 (회원의 의무)</strong><br/>회원은 타인의 정보를 도용하거나 서비스를 악용해서는 안 됩니다. 정직한 학습 데이터 입력이 권장됩니다.</p>
            <p style={{marginTop:6}}><strong>제4조 (서비스 변경 및 중단)</strong><br/>회사는 운영상 필요에 따라 서비스 내용을 변경하거나 중단할 수 있으며, 이 경우 사전 공지합니다.</p>
            <p style={{marginTop:6}}><strong>제5조 (면책)</strong><br/>회사는 회원의 귀책사유로 인한 서비스 이용 장애에 대해 책임지지 않습니다.</p>
            <p style={{marginTop:6,color:"#888"}}>문의: (주)아이작교육그룹 &nbsp;|&nbsp; ☎ 02-1522-5316</p>
          </div>
          <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:"pointer",borderTop:`1px solid ${T.border}`}}>
            <input type="checkbox" checked={termsAgreed} onChange={e=>setTermsAgreed(e.target.checked)}
              style={{width:16,height:16,accentColor:T.navy,cursor:"pointer",flexShrink:0}}/>
            <span style={{fontSize:13,color:T.text,fontWeight:600}}>(필수) 서비스 이용약관에 동의합니다.</span>
          </label>
        </div>

        {/* 개인정보 처리방침 동의 */}
        <div style={{border:`1px solid ${T.border}`,borderRadius:10,marginBottom:10}}>
          <div style={{height:120,overflowY:"auto",padding:"12px 14px",fontSize:11,lineHeight:1.7,color:"#555",background:"#FAFAFA",borderRadius:"10px 10px 0 0"}}>
            <div style={{fontWeight:700,color:T.navy,marginBottom:6,fontSize:12}}>개인정보 처리방침</div>
            <p><strong>(주)아이작교육그룹</strong>(이하 "회사")은 META-X 서비스(이하 "서비스") 운영을 위해 아래와 같이 개인정보를 수집·이용합니다.</p>
            <p style={{marginTop:6}}><strong>1. 수집 항목</strong><br/>이름, 이메일 주소, 학년, 학습 기록 데이터</p>
            <p style={{marginTop:6}}><strong>2. 수집·이용 목적</strong><br/>회원 식별 및 가입 관리, 학습 데이터 분석·제공, 서비스 운영 및 고객 지원</p>
            <p style={{marginTop:6}}><strong>3. 보유 및 이용 기간</strong><br/>서비스 탈퇴 시까지. 단, 법령에 따라 일정 기간 보존이 필요한 경우 해당 기간까지 보유.</p>
            <p style={{marginTop:6}}><strong>4. 개인정보 제3자 제공</strong><br/>회사는 이용자의 개인정보를 원칙적으로 제3자에게 제공하지 않습니다.</p>
            <p style={{marginTop:6}}><strong>5. 동의 거부 권리</strong><br/>위 개인정보 수집·이용에 동의하지 않을 수 있으나, 미동의 시 서비스 이용이 제한됩니다.</p>
            <p style={{marginTop:6,color:"#888"}}>문의: (주)아이작교육그룹 &nbsp;|&nbsp; ☎ 02-1522-5316</p>
          </div>
          <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:"pointer",borderTop:`1px solid ${T.border}`}}>
            <input type="checkbox" checked={privacyAgreed} onChange={e=>setPrivacyAgreed(e.target.checked)}
              style={{width:16,height:16,accentColor:T.navy,cursor:"pointer",flexShrink:0}}/>
            <span style={{fontSize:13,color:T.text,fontWeight:600}}>(필수) 개인정보 처리방침에 동의합니다.</span>
          </label>
        </div>
        {/* 나이 확인 */}
        <label style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:12,cursor:"pointer"}}>
          <input type="checkbox" checked={ageAgreed} onChange={e=>setAgeAgreed(e.target.checked)}
            style={{width:16,height:16,accentColor:T.navy,cursor:"pointer",flexShrink:0,marginTop:2}}/>
          <span style={{fontSize:12,color:T.textMid,lineHeight:1.6}}>
            (필수) 본인은 <strong>만 14세 이상</strong>이거나, 만 14세 미만인 경우 <strong>보호자(부모님)의 동의</strong>를 받았습니다.
          </span>
        </label>
        {error && <div style={{background:"#FEE2E2",border:"1px solid #FECACA",borderRadius:8,padding:"10px 14px",fontSize:13,color:T.danger,marginBottom:12}}>{error}</div>}
        <button onClick={handleSignup} disabled={loading.signup} style={{...css.btnOrange,width:"100%",padding:"13px 0",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",gap:8,opacity:(!otpVerified||!privacyAgreed||!termsAgreed||!ageAgreed)?0.4:1}}>
          {loading.signup?<><Spinner size={18} color="#fff"/>가입 중...</>:"가입하기 →"}
        </button>
        <div style={{textAlign:"center",marginTop:14,fontSize:13,color:T.muted}}>
          이미 계정이 있으신가요? <span onClick={()=>{setMode("login");setError("");}} style={{color:T.navy,fontWeight:700,cursor:"pointer"}}>로그인</span>
        </div>
      </>)}
    </div>
  );

  if(isMobile) return (
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:"'Noto Sans KR',sans-serif",display:"flex",flexDirection:"column"}}>
      <div style={{background:T.grad,padding:"48px 28px 36px",textAlign:"center"}}>
        <Logo size="lg" dark/>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",marginTop:14}}>성장의 흐름을 기록하는 학습 시스템</div>
      </div>
      <div style={{flex:1,padding:"24px 20px"}}><Card>{loginForm}</Card></div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",fontFamily:"'Noto Sans KR',sans-serif"}}>
      {brandPanel}
      <div style={{width:480,background:T.surface,borderLeft:`1px solid ${T.border}`,display:"flex",flexDirection:"column",justifyContent:"center",padding:"48px 44px",overflowY:"auto"}}>
        {loginForm}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// PROFILE SETUP SCREEN (소셜 로그인 최초 가입)
// ══════════════════════════════════════════════════════
const ProfileSetupScreen = ({user, onComplete}) => {
  const [name, setName]           = useState(user.user_metadata?.full_name||user.user_metadata?.name||"");
  const [birthYear, setBirthYear]   = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay]     = useState("");
  const [target, setTarget]     = useState(85);
  const [saving, setSaving]     = useState(false);
  const [done, setDone]         = useState(false);
  const [error, setError]       = useState("");

  const save = async () => {
    if(!name){ setError("이름을 입력해 주세요."); return; }
    if(!birthYear){ setError("생년월일을 입력해 주세요."); return; }
    setSaving(true);
    const by = Number(birthYear), bm = birthMonth?Number(birthMonth):null, bd = birthDay?Number(birthDay):null;
    await supabase.from("profiles").upsert({
      id:user.id, name,
      grade: calcGrade(by, bm),
      birth_year: by, birth_month: bm, birth_day: bd,
      target_ei:85, role:"student", approval_status:"pending"
    });
    setSaving(false);
    setDone(true);
  };

  if(done) return (
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:"'Noto Sans KR',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <Card style={{width:"100%",maxWidth:400,textAlign:"center",padding:"40px 32px"}}>
        <div style={{fontSize:48,marginBottom:16}}>⏳</div>
        <Logo size="md"/>
        <div style={{height:1,background:T.border,margin:"20px 0"}}/>
        <div style={{fontSize:18,fontWeight:800,color:T.navy,marginBottom:8}}>승인 대기 중</div>
        <div style={{fontSize:14,color:T.textMid,lineHeight:1.8,marginBottom:24}}>
          가입 신청이 완료됐습니다.<br/>관리자 승인 후 로그인하실 수 있습니다.
        </div>
        <div style={{background:T.orangePale,border:`1px solid ${T.orange}30`,borderRadius:12,padding:"12px 16px",fontSize:13,color:T.textMid,marginBottom:24}}>
          📞 승인 관련 문의는 담당 선생님께 연락해 주세요.
        </div>
        <button onClick={onComplete} style={{...css.btnGhost,padding:"10px 28px"}}>로그인 화면으로</button>
      </Card>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:"'Noto Sans KR',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <Card style={{width:"100%",maxWidth:420}}>
        <Logo size="md"/>
        <div style={{height:1,background:T.border,margin:"16px 0"}}/>
        <div style={{fontSize:18,fontWeight:800,color:T.navy,marginBottom:4}}>프로필 설정</div>
        <div style={{fontSize:13,color:T.muted,marginBottom:20}}>소셜 가입 완료! 아래 정보를 입력해 주세요.</div>
        <div style={{display:"grid",gap:14,marginBottom:16}}>
          <div><label style={css.label}>이름</label><input value={name} onChange={e=>setName(e.target.value)} placeholder="홍길동" style={css.input}/></div>
          <div>
            <label style={css.label}>생년월일 <span style={{color:"#e53e3e"}}>*</span></label>
            <BirthInput year={birthYear} month={birthMonth} day={birthDay}
              onYear={setBirthYear} onMonth={setBirthMonth} onDay={setBirthDay}
              showGrade={true}/>
          </div>

        </div>
        {error && <div style={{background:"#FEE2E2",border:"1px solid #FECACA",borderRadius:8,padding:"10px 14px",fontSize:13,color:T.danger,marginBottom:12}}>{error}</div>}
        <div style={{background:T.orangePale,border:`1px solid ${T.orange}30`,borderRadius:10,padding:"10px 14px",fontSize:12,color:T.textMid,marginBottom:16}}>
          ⚠️ 저장 후 관리자 승인이 완료되어야 로그인할 수 있습니다.
        </div>
        <button onClick={save} disabled={saving} style={{...css.btnPrimary,width:"100%",padding:"13px 0",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          {saving?<><Spinner size={18} color="#fff"/>저장 중...</>:"저장하고 승인 대기 →"}
        </button>
        <button onClick={async()=>{ try { window.__intentionalLogout = true; } catch(_){}; await supabase.auth.signOut(); window.location.reload(); }}
          style={{...css.btnGhost,width:"100%",marginTop:10,padding:"10px 0",textAlign:"center"}}>
          ← 로그인 화면으로 돌아가기
        </button>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// COMPLETE PROFILE SCREEN (가입 미완료 사용자 - 이름/비밀번호 입력)
// ══════════════════════════════════════════════════════
const CompleteProfileScreen = ({ session, onDone }) => {
  const [name, setName]           = useState("");
  const [role, setRole]           = useState("student");
  const [birthYear, setBirthYear]   = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay]     = useState("");
  const [pw, setPw]             = useState("");
  const [pwC, setPwC]           = useState("");
  const [saving, setSaving]     = useState(false);
  const [done, setDone]         = useState(false);
  const [error, setError]       = useState("");

  const save = async () => {
    setError("");
    if(!name.trim() || name.trim().length < 2){ setError("이름을 2자 이상 입력해주세요."); return; }
    if(!birthYear){ setError("생년월일을 입력해주세요."); return; }
    const pwRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/;
    if(!pwRegex.test(pw)){ setError("비밀번호는 영문 대소문자+특수문자 8자 이상이어야 합니다."); return; }
    if(pw !== pwC){ setError("비밀번호가 일치하지 않습니다."); return; }
    setSaving(true);
    const uid = session?.user?.id;
    const by = Number(birthYear), bm = birthMonth?Number(birthMonth):null, bd = birthDay?Number(birthDay):null;
    const grd = role==="student" ? calcGrade(by, bm) : "";
    const { error: ue } = await supabase.auth.updateUser({ password: pw, data: { name, role, grade: grd } });
    if(!ue && uid) {
      await supabase.from("profiles").upsert({ id: uid, name, grade: grd, birth_year: by, birth_month: bm, birth_day: bd, role, approval_status:"pending", target_ei:85, is_active:true });
    }
    setSaving(false);
    if(ue){ setError("오류가 발생했습니다. 다시 시도해주세요."); return; }
    setDone(true);
  };

  if(done) return (
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:"'Noto Sans KR',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <Card style={{width:"100%",maxWidth:400,textAlign:"center",padding:"40px 32px"}}>
        <div style={{fontSize:48,marginBottom:16}}>⏳</div>
        <Logo size="md"/>
        <div style={{height:1,background:T.border,margin:"20px 0"}}/>
        <div style={{fontSize:18,fontWeight:800,color:T.navy,marginBottom:8}}>승인 대기 중</div>
        <div style={{fontSize:14,color:T.textMid,lineHeight:1.8,marginBottom:24}}>가입 신청이 완료됐습니다.<br/>관리자 승인 후 로그인하실 수 있습니다.</div>
        <button onClick={onDone} style={{...css.btnGhost,padding:"10px 28px"}}>로그인 화면으로</button>
      </Card>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:T.bg,fontFamily:"'Noto Sans KR',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <Card style={{width:"100%",maxWidth:420}}>
        <Logo size="md"/>
        <div style={{height:1,background:T.border,margin:"16px 0"}}/>
        <div style={{fontSize:18,fontWeight:800,color:T.navy,marginBottom:4}}>가입 정보 입력</div>
        <div style={{fontSize:13,color:T.muted,marginBottom:20}}>이메일 인증이 완료됐습니다. 아래 정보를 입력해 가입을 완료해주세요.</div>
        <div style={{display:"grid",gap:14,marginBottom:16}}>
          <div><label style={css.label}>이름 <span style={{color:"#e53e3e"}}>*</span></label><input value={name} onChange={e=>setName(e.target.value)} placeholder="홍길동" style={css.input}/></div>
          <div>
            <label style={css.label}>역할</label>
            <div style={{display:"flex",gap:8}}>
              {[["student","학생"],["parent","학부모"]].map(([v,l])=>(
                <button key={v} onClick={()=>setRole(v)} style={{...( role===v?css.btnPrimary:css.btnGhost),flex:1,padding:"10px 0",fontSize:13}}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <label style={css.label}>생년월일 <span style={{color:"#e53e3e"}}>*</span></label>
            <BirthInput year={birthYear} month={birthMonth} day={birthDay}
              onYear={setBirthYear} onMonth={setBirthMonth} onDay={setBirthDay}
              showGrade={role==="student"}/>
          </div>
          <div><label style={css.label}>비밀번호 <span style={{fontWeight:400,color:T.muted,fontSize:11}}>(대소문자+특수문자 8자↑)</span></label><input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="••••••••" style={css.input}/></div>
          <div><label style={css.label}>비밀번호 확인</label><input type="password" value={pwC} onChange={e=>setPwC(e.target.value)} placeholder="••••••••" style={css.input}/></div>
        </div>
        {error&&<div style={{background:"#FEE2E2",border:"1px solid #FECACA",borderRadius:8,padding:"10px 14px",fontSize:13,color:T.danger,marginBottom:12}}>{error}</div>}
        <button onClick={save} disabled={saving} style={{...css.btnOrange,width:"100%",padding:"13px 0",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          {saving?<><Spinner size={18} color="#fff"/>저장 중...</>:"가입 완료 →"}
        </button>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// DATA INPUT FORM
// ══════════════════════════════════════════════════════
const DataInputForm = ({uid, onSave, onCancel}) => {
  const [step, setStep]   = useState(0);
  const [err, setErr]     = useState("");
  const [saving, setSaving] = useState(false);
  const [saveWarnings, setSaveWarnings] = useState([]);
  const isMobile = useMobile();
  const DRAFT_KEY = draftKey("datainput", uid);
  const [form, setForm]   = useState(() => {
    const restored = restoreDraft(DRAFT_KEY);
    if (restored) return restored;
    return {
      date:new Date().toISOString().slice(0,10), subject:"수학", bookLevel:1,
      startTime:"", endTime:"", breakTime:0, questionCount:20,
      qBasic:0, qMid:0, qAdv:0,
      tBasic:0, tMid:0, tAdv:0,
      quant:Object.fromEntries(QUANT_ITEMS.map(k=>[k,80])),
      qual:Object.fromEntries(QUAL_ITEMS.map(k=>[k,3])),
      quantEnabled:{}, qualEnabled:{},
      coinFilter:{cc:0,ci:0,ic:0,ii:0},
      errorAnalysis:{Q1:0,Q2:0,Q3:0,M1:0,M2:0,M3:0},
    };
  });
  useDraftBackup(DRAFT_KEY, form);
  const subjectCfg = SUBJECT_CONFIG[form.subject] || SUBJECT_CONFIG["수학"];
  const handleSubjectChange = s => setForm(f=>({...f, subject:s, qBasic:0, qMid:0, qAdv:0, quantEnabled:{}, qualEnabled:{}}));
  const toMin = t=>{if(!t)return 0;const[h,m]=t.split(":").map(Number);return h*60+m;};
  const netTime = Math.max(0,toMin(form.endTime)-toMin(form.startTime)-Number(form.breakTime));
  const breakRatio = Number(form.breakTime)/Math.max(1,toMin(form.endTime)-toMin(form.startTime))*100;

  // 단계별 시간 자동 분배: q값 바뀌면 균등, netTime만 바뀌면 비율 유지
  const prevQRef = useRef({qBasic:0,qMid:0,qAdv:0});
  useEffect(()=>{
    if(!subjectCfg.qLevels) return;
    const hasB=form.qBasic>0, hasM=form.qMid>0, hasA=form.qAdv>0;
    const count=[hasB,hasM,hasA].filter(Boolean).length;
    const qChanged=prevQRef.current.qBasic!==form.qBasic||prevQRef.current.qMid!==form.qMid||prevQRef.current.qAdv!==form.qAdv;
    prevQRef.current={qBasic:form.qBasic,qMid:form.qMid,qAdv:form.qAdv};
    if(count===0||netTime===0){setForm(f=>({...f,tBasic:0,tMid:0,tAdv:0}));return;}
    const distrib=(nt)=>{
      const each=Math.floor(nt/count), rem=nt%count;
      let i=0; return [hasB,hasM,hasA].map(a=>a?each+(i++<rem?1:0):0);
    };
    if(qChanged){
      const v=distrib(netTime);
      setForm(f=>({...f,tBasic:v[0],tMid:v[1],tAdv:v[2]}));
    } else {
      setForm(f=>{
        const sum=(f.tBasic||0)+(f.tMid||0)+(f.tAdv||0);
        if(sum===netTime) return f;
        if(sum===0){const v=distrib(netTime);return{...f,tBasic:v[0],tMid:v[1],tAdv:v[2]};}
        const b=hasB?Math.round((f.tBasic||0)/sum*netTime):0;
        const m=hasM?Math.round((f.tMid||0)/sum*netTime):0;
        const a=hasA?netTime-b-m:0;
        return {...f,tBasic:b,tMid:m,tAdv:Math.max(0,a)};
      });
    }
  },[form.qBasic,form.qMid,form.qAdv,netTime]);
  const metrics = (()=>{
    const aqKeys=subjectCfg.quant.filter(k=>form.quantEnabled[k]);
    const alKeys=subjectCfg.qual.filter(k=>form.qualEnabled[k]);
    const qT=aqKeys.reduce((s,k)=>s+Number(form.quant[k]||0),0);
    const qlT=alKeys.reduce((s,k)=>s+((form.qual[k]||0)/5*100),0);
    const denom=(aqKeys.length*100*1.0+alKeys.length*100*0.5)/100;
    const strategyScore=denom>0?(qT*1.0+qlT*0.5)/denom:0;
    const qB=form.qBasic||0, qM=form.qMid||0, qA=form.qAdv||0;
    // 단계별 시간 - 직접 입력값 사용 (수학), 비수학은 전체 시간을 basic으로
    const bTime=subjectCfg.qLevels?(form.tBasic||0):netTime;
    const mTime=subjectCfg.qLevels?(form.tMid||0):0;
    const aTime=subjectCfg.qLevels?(form.tAdv||0):0;
    // 단계별 문항당 소요시간(초)으로 효율성 계산
    const spB=qB>0?bTime*60/qB:0, spM=qM>0?mTime*60/qM:0, spA=qA>0?aTime*60/qA:0;
    let efficiencyIndex;
    if(subjectCfg.qLevels){
      // 수학: 기본60초, 응용90초, 심화150초 기준
      const effB=qB>0?Math.min(100,Math.max(0,100-(spB/60)*30)):null;
      const effM=qM>0?Math.min(100,Math.max(0,100-(spM/90)*35)):null;
      const effA=qA>0?Math.min(100,Math.max(0,100-(spA/150)*40)):null;
      const effVals=[effB,effM,effA].filter(v=>v!==null);
      efficiencyIndex=effVals.length>0?effVals.reduce((s,v)=>s+v,0)/effVals.length:50;
    } else {
      // 비수학: 전체 문항 기준 90초/문항
      const totalQ=qB; const sp=totalQ>0?netTime*60/totalQ:0;
      efficiencyIndex=totalQ>0?Math.min(100,Math.max(0,100-(sp/90)*30)):50;
    }
    const{cc,ci,ic,ii}=form.coinFilter; const tot=cc+ci+ic+ii;
    const metacognitionAccuracy=tot>0?((cc+ii)/tot)*100:0;
    return{strategyScore:+strategyScore.toFixed(1),efficiencyIndex:+efficiencyIndex.toFixed(1),metacognitionAccuracy:+metacognitionAccuracy.toFixed(1),engramIndex:calcEI({strategyScore,efficiencyIndex,metacognitionAccuracy}),bTime,mTime,aTime};
  })();

  const doSave = async()=>{
    setSaveWarnings([]); setSaving(true); setErr("");
    const{error}=await supabase.from("learning_logs").insert({
      uid, date:form.date, subject:form.subject, book_level:form.bookLevel,
      start_time:form.startTime, end_time:form.endTime, break_time:form.breakTime, net_time:netTime,
      question_count:(form.qBasic||0)+(form.qMid||0)+(form.qAdv||0)||form.questionCount,
      q_basic:form.qBasic||0, q_mid:form.qMid||0, q_adv:form.qAdv||0,
      t_basic:Math.round(metrics.bTime), t_mid:Math.round(metrics.mTime), t_adv:Math.round(metrics.aTime),
      strategy_score:metrics.strategyScore, efficiency_index:metrics.efficiencyIndex,
      metacognition_accuracy:metrics.metacognitionAccuracy, engram_index:metrics.engramIndex,
      coin_cc:form.coinFilter.cc, coin_ci:form.coinFilter.ci,
      coin_ic:form.coinFilter.ic, coin_ii:form.coinFilter.ii,
      err_q1:form.errorAnalysis.Q1, err_q2:form.errorAnalysis.Q2, err_q3:form.errorAnalysis.Q3,
      err_m1:form.errorAnalysis.M1, err_m2:form.errorAnalysis.M2, err_m3:form.errorAnalysis.M3,
      quant_data:form.quant, qual_data:form.qual,
    });
    setSaving(false);
    if(error){setErr("저장 오류: "+error.message);return;}
    clearDraft(DRAFT_KEY);
    onSave();
  };

  const save = async()=>{
    if(!form.date){setErr("⚠️ 날짜를 입력해주세요.");return;}
    if(!form.subject){setErr("⚠️ 과목을 선택해주세요.");return;}
    if(!form.startTime||!form.endTime){setErr("⚠️ 시작 시간과 종료 시간을 입력해주세요.");return;}
    if(toMin(form.endTime)<=toMin(form.startTime)){setErr("⚠️ 종료 시간이 시작 시간보다 빠릅니다.");return;}
    if(breakRatio>50){setErr("⚠️ 쉬는 시간이 전체 학습 시간의 50%를 초과했습니다.");return;}
    const totalQ=(form.qBasic||0)+(form.qMid||0)+(form.qAdv||0);
    const coinSum=Object.values(form.coinFilter).reduce((s,v)=>s+v,0);
    // 문제 풀이가 있는 경우에만 CO-IN 입력 필수
    if(totalQ>0&&coinSum===0){setErr("⚠️ CO-IN Filter를 입력해주세요.");return;}
    // 문항 수 불일치 경고 검사 (문제 풀이가 있는 경우에만)
    const warns=[];
    const{cc,ci,ic,ii}=form.coinFilter; const coinTotal=cc+ci+ic+ii;
    if(totalQ>0&&coinTotal!==totalQ) warns.push(`CO-IN 합계(${coinTotal}문항)가 총 문항 수(${totalQ}문항)와 다릅니다.`);
    if(subjectCfg.showQM&&totalQ>0){
      const qmTotal=Object.values(form.errorAnalysis).reduce((s,v)=>s+v,0);
      const wrongCount=ci+ii;
      if(qmTotal!==wrongCount) warns.push(`QM 오답 합계(${qmTotal}문항)가 오답 수 [C-I], [I-I] 합계(${wrongCount}문항)와 다릅니다.`);
    }
    if(warns.length>0){setSaveWarnings(warns);return;}
    await doSave();
  };

  const STEPS=["① 기본 정보","② 메타인지"];
  return(
    <div style={{maxWidth:820,margin:"0 auto"}}>
      {/* 진행 인디케이터 제거 — 단일 페이지 레이아웃 */}

      {err&&<div style={{background:"#FEE2E2",border:"1px solid #FECACA",borderRadius:10,padding:"10px 16px",color:T.danger,fontSize:13,marginBottom:12}}>{err}</div>}

      {(step===0||true)&&(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {/* 카드1: 날짜 · 과목 */}
          <Card>
            <div style={{fontSize:12,fontWeight:700,color:T.muted,marginBottom:12,letterSpacing:"0.06em",display:"flex",alignItems:"center",gap:5}}>{HI.calendar(12,T.muted)} 날짜 · 과목</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div>
                <label style={css.label}>날짜</label>
                <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} style={css.input}/>
              </div>
              <div>
                <label style={css.label}>과목</label>
                <select value={form.subject} onChange={e=>handleSubjectChange(e.target.value)} style={css.select}>
                  {SUBJECTS.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </Card>
          {/* 카드2: 문항 수 */}
          <Card>
            <div style={{fontSize:12,fontWeight:700,color:T.muted,marginBottom:12,letterSpacing:"0.06em"}}>📚 문제집으로 공부한 문항 수</div>
            {subjectCfg.qLevels?(
              <>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                  {[{label:"기본(개념, 연산) 문제",sub:"예시: A step/ 1단계",key:"qBasic",color:T.navy},{label:"응용 문제",sub:"예시: B step/ 2단계",key:"qMid",color:T.orange},{label:"심화 문제",sub:"예시: C step/ 3단계",key:"qAdv",color:"#7C3AED"}].map(({label,sub,key,color})=>(
                    <div key={key}>
                      <label style={{...css.label,color}}>{label}</label>
                      <div style={{fontSize:10,color:T.muted,marginBottom:4}}>{sub}</div>
                      <input type="number" min={0} max={200} value={form[key]||0}
                        onChange={e=>setForm(f=>({...f,[key]:Number(e.target.value)}))}
                        style={{...css.input,borderColor:color+"60",textAlign:"center",fontWeight:700,color}}/>
                    </div>
                  ))}
                </div>
                <div style={{marginTop:10,padding:"8px 12px",background:T.surfaceAlt,borderRadius:8,fontSize:12,color:T.muted}}>
                  총 문항: <strong style={{color:T.navy}}>{(form.qBasic||0)+(form.qMid||0)+(form.qAdv||0)}문항</strong>
                </div>
              </>
            ):(
              <div style={{maxWidth:200}}>
                <label style={css.label}>총 문항 수</label>
                <input type="number" min={0} max={500} value={form.qBasic||0}
                  onChange={e=>setForm(f=>({...f,qBasic:Number(e.target.value),qMid:0,qAdv:0}))}
                  style={{...css.input,textAlign:"center",fontWeight:700,color:T.navy}}/>
              </div>
            )}
          </Card>
          {/* 카드3: 시간 */}
          <Card>
            <div style={{fontSize:12,fontWeight:700,color:T.muted,marginBottom:12,letterSpacing:"0.06em"}}>⏱ 학습 시간</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
              <div>
                <label style={css.label}>시작 시간</label>
                <input type="time" value={form.startTime} onChange={e=>setForm(f=>({...f,startTime:e.target.value}))} style={css.input}/>
              </div>
              <div>
                <label style={css.label}>종료 시간</label>
                <input type="time" value={form.endTime} onChange={e=>setForm(f=>({...f,endTime:e.target.value}))} style={css.input}/>
              </div>
            </div>
            <label style={css.label}>쉬는 시간: <strong style={{color:T.orange}}>{form.breakTime}분</strong> · 순수 풀이: <strong style={{color:T.navy}}>{netTime}분</strong></label>
            <input type="range" min={0} max={60} value={form.breakTime} onChange={e=>setForm(f=>({...f,breakTime:Number(e.target.value)}))} style={sliderFill(form.breakTime,0,60,T.orange)}/>
            {breakRatio>50&&<div style={{fontSize:12,color:T.danger,marginTop:4}}>⚠️ 쉬는 시간 50% 초과</div>}

            {/* 단계별 풀이 시간 - 수학+문항 있을 때 표시. 드래그 bar + 직접 입력 연동 */}
            {subjectCfg.qLevels&&(form.qBasic>0||form.qMid>0||form.qAdv>0)&&netTime>0&&(()=>{
              const hasB=form.qBasic>0, hasM=form.qMid>0, hasA=form.qAdv>0;
              const activeCount=[hasB,hasM,hasA].filter(Boolean).length;
              const tB=form.tBasic||0, tM=form.tMid||0, tA=form.tAdv||0;
              const allocated=tB+tM+tA;
              // bar 비율 (netTime 기준)
              const pB=netTime>0?Math.min(100,tB/netTime*100):0;
              const pM=netTime>0?Math.min(100-pB,tM/netTime*100):0;
              const pA=netTime>0?Math.min(100-pB-pM,tA/netTime*100):0;
              // 핸들 위치 (%)
              const h1pct=activeCount===2?(hasB?pB:pM):pB;
              const h2pct=pB+pM;
              // 드래그: 1분 단위 스냅, 합계=netTime 유지
              const barRef={current:null};
              const onBarDrag=(e,ref)=>{
                if(!ref.current)return;
                const rect=ref.current.getBoundingClientRect();
                const getMins=(clientX)=>Math.round(Math.max(0,Math.min(netTime,(clientX-rect.left)/rect.width*netTime)));
                const move=(ev)=>{
                  const clientX=ev.touches?ev.touches[0].clientX:ev.clientX;
                  const mins=getMins(clientX);
                  if(activeCount===2){
                    if(hasB&&hasM){const b=Math.max(1,Math.min(netTime-1,mins));setForm(f=>({...f,tBasic:b,tMid:netTime-b}));}
                    else if(hasB&&hasA){const b=Math.max(1,Math.min(netTime-1,mins));setForm(f=>({...f,tBasic:b,tAdv:netTime-b}));}
                    else{const m=Math.max(1,Math.min(netTime-1,mins));setForm(f=>({...f,tMid:m,tAdv:netTime-m}));}
                  } else {
                    setForm(f=>{
                      const h1m=f.tBasic, h2m=f.tBasic+f.tMid;
                      if(Math.abs(mins-h1m)<=Math.abs(mins-h2m)){
                        const b=Math.max(1,Math.min(h2m-1,mins));return{...f,tBasic:b,tMid:h2m-b};
                      } else {
                        const h2=Math.max(h1m+1,Math.min(netTime-1,mins));return{...f,tMid:h2-h1m,tAdv:netTime-h2};
                      }
                    });
                  }
                };
                const up=()=>{window.removeEventListener("mousemove",move);window.removeEventListener("mouseup",up);window.removeEventListener("touchmove",move);window.removeEventListener("touchend",up);};
                window.addEventListener("mousemove",move);window.addEventListener("mouseup",up);
                window.addEventListener("touchmove",move,{passive:false});window.addEventListener("touchend",up);
                e.preventDefault();
              };
              return(
                <div style={{marginTop:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.muted,marginBottom:8}}>단계별 풀이 시간
                    <span style={{fontWeight:400,marginLeft:6,fontSize:11}}>경계선을 드래그하여 조절 (1분 단위)</span>
                  </div>
                  {/* 드래그 bar */}
                  <div ref={el=>barRef.current=el}
                    style={{position:"relative",height:36,borderRadius:8,overflow:"hidden",display:"flex",cursor:"ew-resize",userSelect:"none",background:T.border}}
                    onMouseDown={e=>onBarDrag(e,barRef)} onTouchStart={e=>onBarDrag(e,barRef)}
                  >
                    {hasB&&pB>0&&<div style={{width:`${pB}%`,background:T.navy,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                      {pB>=12&&<span style={{fontSize:10,fontWeight:700,color:"#fff",whiteSpace:"nowrap"}}>기본 {tB}분</span>}
                    </div>}
                    {hasM&&pM>0&&<div style={{width:`${pM}%`,background:T.orange,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                      {pM>=12&&<span style={{fontSize:10,fontWeight:700,color:"#fff",whiteSpace:"nowrap"}}>응용 {tM}분</span>}
                    </div>}
                    {hasA&&pA>0&&<div style={{width:`${pA}%`,background:"#7C3AED",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                      {pA>=12&&<span style={{fontSize:10,fontWeight:700,color:"#fff",whiteSpace:"nowrap"}}>심화 {tA}분</span>}
                    </div>}
                    {activeCount>=2&&<div style={{position:"absolute",left:`${h1pct}%`,top:0,bottom:0,width:3,background:"rgba(255,255,255,0.9)",transform:"translateX(-50%)",boxShadow:"0 0 4px rgba(0,0,0,0.4)",borderRadius:2}}/>}
                    {activeCount===3&&<div style={{position:"absolute",left:`${h2pct}%`,top:0,bottom:0,width:3,background:"rgba(255,255,255,0.9)",transform:"translateX(-50%)",boxShadow:"0 0 4px rgba(0,0,0,0.4)",borderRadius:2}}/>}
                  </div>
                </div>
              );
            })()}
          </Card>
        </div>
      )}

      {(step===1||true)&&(
        <div style={{display:"flex",flexDirection:"column",gap:14,marginTop:12}}>
          <Card>
            <div style={{fontSize:13,fontWeight:700,color:T.navy,marginBottom:14,display:"flex",alignItems:"center",gap:5}}>{HI.chart(13,T.navy)} CO-IN Filter</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[{key:"cc",label:"C-C",desc:"맞을 것 같았고 실제로 맞음",color:GRAPH.ccColor},{key:"ci",label:"C-I",desc:"맞을 것 같았으나 틀림",color:GRAPH.ciColor},{key:"ic",label:"I-C",desc:"틀릴 것 같았으나 맞음",color:GRAPH.icColor},{key:"ii",label:"I-I",desc:"틀릴 것 같았고 실제로 틀림",color:GRAPH.iiColor}].map(({key,label,desc,color})=>(
                <div key={key} style={{background:color+"0e",border:`1px solid ${color}30`,borderRadius:12,padding:"12px 14px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                    <div><div style={{fontSize:14,fontWeight:800,color}}>{label}</div><div style={{fontSize:10,color:T.muted,marginTop:2,lineHeight:1.4}}>{desc}</div></div>
                    <div style={{whiteSpace:"nowrap",flexShrink:0,marginLeft:6,lineHeight:1}}>
                      <span style={{fontSize:24,fontWeight:900,color,fontFamily:"'DM Mono',monospace"}}>{form.coinFilter[key]}</span><span style={{fontSize:11,color,opacity:0.7}}>문항</span>
                    </div>
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:6}}>
                    {[0,1,2,3,4,5,6,7,8,9,10].map(n=>(
                      <div key={n} onClick={()=>setForm(f=>({...f,coinFilter:{...f.coinFilter,[key]:n}}))}
                        style={{width:28,height:28,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,cursor:"pointer",fontWeight:700,
                          background:form.coinFilter[key]===n?color:T.surfaceAlt,color:form.coinFilter[key]===n?T.white:T.muted,
                          border:`1px solid ${form.coinFilter[key]===n?color:T.border}`}}>{n}</div>
                    ))}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <input type="number" min={0} max={999} placeholder="직접 입력"
                      onFocus={e=>{ if(form.coinFilter[key]<=10) e.target.value=""; }}
                      onChange={e=>{ const v=e.target.value; if(v===""||v==="-") return; const n=parseInt(v,10); if(!isNaN(n)&&n>=0) setForm(f=>({...f,coinFilter:{...f.coinFilter,[key]:n}})); }}
                      style={{width:"100%",padding:"5px 8px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:12,color:T.text,background:T.white,outline:"none"}}/>
                    <span style={{fontSize:11,color:T.muted,whiteSpace:"nowrap"}}>문항</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          {subjectCfg.showQM&&(<Card>
            <div style={{fontSize:13,fontWeight:700,color:T.navy,marginBottom:14}}>❌ QM 오답분석</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
              {ERR_CODES.map(code=>{
                const ec=code[0]==="Q"?T.danger:T.orange;
                return(
                <div key={code}>
                  <div style={{fontSize:11,textAlign:"center",marginBottom:6,color:ec,fontWeight:700}}>
                    {code}<br/><span style={{color:T.muted,fontWeight:400,fontSize:9}}>{ERR_LABELS[code]}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"center",gap:3,flexWrap:"wrap",marginBottom:4}}>
                    {[0,1,2,3,4,5].map(n=>(
                      <div key={n} onClick={()=>setForm(f=>({...f,errorAnalysis:{...f.errorAnalysis,[code]:n}}))}
                        style={{width:28,height:28,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:12,fontWeight:700,
                          background:form.errorAnalysis[code]===n?ec:T.surfaceAlt,
                          color:form.errorAnalysis[code]===n?T.white:T.muted,
                          border:`1px solid ${form.errorAnalysis[code]===n?ec:T.border}`}}>{n}</div>
                    ))}
                  </div>
                  <input type="number" min={0} max={99} placeholder="직접 입력"
                    onFocus={e=>{ if(form.errorAnalysis[code]<=5) e.target.value=""; }}
                    onChange={e=>{ const v=e.target.value; if(v===""||v==="-") return; const n=parseInt(v,10); if(!isNaN(n)&&n>=0) setForm(f=>({...f,errorAnalysis:{...f.errorAnalysis,[code]:n}})); }}
                    style={{width:"100%",padding:"4px 6px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:11,color:T.text,background:T.white,outline:"none",textAlign:"center"}}/>
                </div>
              );})}
            </div>
          </Card>)}
        </div>
      )}

      {saveWarnings.length>0&&(
        <div style={{background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:12,padding:"14px 16px",marginTop:16}}>
          <div style={{fontSize:13,fontWeight:700,color:T.orange,marginBottom:8}}>⚠️ 문항 수 불일치</div>
          {saveWarnings.map((w,i)=><div key={i} style={{fontSize:12,color:T.text,marginBottom:4}}>• {w}</div>)}
          <div style={{fontSize:12,color:T.muted,marginTop:8,marginBottom:12}}>이대로 입력하시겠습니까?</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={doSave} disabled={saving} style={{...css.btnOrange,flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,fontSize:13}}>
              {saving?<><Spinner size={14} color="#fff"/>저장 중...</>:"이대로 저장"}
            </button>
            <button onClick={()=>setSaveWarnings([])} style={{...css.btnGhost,flex:1,fontSize:13}}>다시 확인</button>
          </div>
        </div>
      )}
      <div style={{fontSize:11,color:T.muted,textAlign:"center",marginTop:16,marginBottom:6}}>
        ⚠️ 저장된 데이터는 수정할 수 없습니다. 입력 내용을 다시 한번 확인해 주세요.
      </div>
      {/* 하단 CTA 배너 */}
      <div style={{borderRadius:12, background:"linear-gradient(135deg,#191D54,#3D4499)", padding:"16px 20px", color:"#fff", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12}}>
        <div>
          <div style={{fontSize:13, fontWeight:700, marginBottom:2, display:"flex", alignItems:"center", gap:5}}>{HI.chart(13,"rgba(255,255,255,0.9)")} 진도를 업데이트할 준비가 됐나요?</div>
          <div style={{fontSize:11, opacity:0.7}}>입력 데이터는 매주 학습 인사이트 생성에 사용됩니다.</div>
        </div>
        <div style={{display:"flex", gap:8, flexShrink:0}}>
          <button onClick={onCancel} style={{background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:8, padding:"9px 16px", color:"#fff", fontSize:13, cursor:"pointer", fontWeight:600}}>취소</button>
          <button onClick={save} disabled={saving||saveWarnings.length>0} style={{background:"#F68B1E", border:"none", borderRadius:8, padding:"9px 20px", color:"#fff", fontSize:13, cursor:"pointer", fontWeight:800, display:"flex", alignItems:"center", gap:6, opacity:saveWarnings.length>0?0.4:1}}>
            {saving?<><Spinner size={14} color="#fff"/>저장 중...</>:"💾 저장"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// STUDENT DASHBOARD
// ══════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════
// KPI CARD WITH TOOLTIP
// ══════════════════════════════════════════════════════
const KpiCard = ({label,desc,kpi,value,unit="",color,sub,subColor,isMobile}) => {
  const [show,setShow] = useState(false);
  const [pos,setPos]   = useState({x:0,y:0});
  return (
    <Card style={{padding:"16px 18px",position:"relative"}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:2}}>
        <div style={{fontSize:11,color:T.navy,fontWeight:700,lineHeight:1.4,flex:1}}>{label}</div>
        {kpi&&(
          <div
            onMouseEnter={e=>{const r=e.currentTarget.getBoundingClientRect();setPos({x:r.left,y:r.bottom+6});setShow(true);}}
            onMouseLeave={()=>setShow(false)}
            style={{width:15,height:15,borderRadius:"50%",background:T.muted+"25",display:"flex",alignItems:"center",justifyContent:"center",cursor:"help",flexShrink:0,marginLeft:4}}>
            <span style={{fontSize:9,color:T.muted,fontWeight:800}}>?</span>
          </div>
        )}
      </div>
      <div style={{fontSize:10,color:T.muted,marginBottom:6,lineHeight:1.4}}>{desc}</div>
      <NavyNum value={value} unit={unit} size={isMobile?20:24} color={color}/>
      {sub&&<div style={{fontSize:11,color:subColor,marginTop:5,fontWeight:600}}>{sub}</div>}
      {show&&kpi&&(
        <div style={{position:"fixed",...(pos.x+276>window.innerWidth?{right:8,left:"auto"}:{left:pos.x}),top:pos.y,zIndex:9999,pointerEvents:"none",
          background:T.navy,color:T.white,borderRadius:10,padding:"12px 16px",fontSize:12,
          width:260,lineHeight:1.8,boxShadow:"0 4px 20px rgba(0,0,0,0.25)"}}>
          {kpi.split("\n").map((line,i)=>
            line===""
              ? <div key={i} style={{height:6}}/>
              : <div key={i}>{line}</div>
          )}
        </div>
      )}
    </Card>
  );
};

// ══════════════════════════════════════════════════════
// LEARNING CALENDAR
// ══════════════════════════════════════════════════════
const LearningCalendar = ({logs}) => {
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed

  const logDates = new Set(logs.map(l => l.date));
  const logByDate = logs.reduce((acc, l) => {
    if(!acc[l.date]) acc[l.date] = [];
    acc[l.date].push(l);
    return acc;
  }, {});

  const firstDay = new Date(year, month, 1).getDay(); // 0=일
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const todayStr = today.toISOString().slice(0,10);
  const WEEK = ["일","월","화","수","목","금","토"];
  const MONTHS = ["1월","2월","3월","4월","5월","6월","7월","8월","9월","10월","11월","12월"];

  const [tooltip, setTooltip] = useState(null);

  const cells = [];
  for(let i=0; i<firstDay; i++) cells.push(null);
  for(let d=1; d<=daysInMonth; d++) cells.push(d);

  return (
    <Card style={{marginBottom:12}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:T.navy,display:"flex",alignItems:"center",gap:5}}>{HI.calendar(14,T.navy)} 학습 달력</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>색칠된 날 = 학습 기록이 있는 날이에요</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
          <button onClick={()=>{if(month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1);}}
            style={{background:"none",border:`1px solid ${T.border}`,borderRadius:6,width:28,height:28,cursor:"pointer",fontSize:14,color:T.navy,flexShrink:0}}>‹</button>
          <span style={{fontSize:13,fontWeight:700,color:T.navy,whiteSpace:"nowrap",textAlign:"center"}}>{year}년 {MONTHS[month]}</span>
          <button onClick={()=>{if(month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1);}}
            style={{background:"none",border:`1px solid ${T.border}`,borderRadius:6,width:28,height:28,cursor:"pointer",fontSize:14,color:T.navy}}>›</button>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:4}}>
        {WEEK.map((w,i) => (
          <div key={w} style={{textAlign:"center",fontSize:11,fontWeight:700,padding:"4px 0",
            color:i===0?"#E8394A":i===6?"#2563EB":T.muted}}>{w}</div>
        ))}
        {cells.map((d, i) => {
          if(!d) return <div key={`e${i}`}/>;
          const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
          const hasLog = logDates.has(dateStr);
          const dayLogs = logByDate[dateStr] || [];
          const isToday = dateStr === todayStr;
          const ei = hasLog ? (dayLogs.reduce((s,l)=>s+(l.engram_index||0),0)/dayLogs.length) : 0;
          const dotColor = ei>=81?GRAPH.ccColor:ei>=66?T.navy:ei>=51?T.orange:GRAPH.ciColor;
          return (
            <div key={d}
              onMouseEnter={(e)=>{ if(hasLog){ const r=e.currentTarget.getBoundingClientRect(); setTooltip({d,dateStr,dayLogs,ei,x:r.left+r.width/2,y:r.top}); }}}
              onMouseLeave={()=>setTooltip(null)}
              style={{
                minHeight:30,
                borderRadius:6,
                display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                cursor:hasLog?"pointer":"default",
                background:hasLog?dotColor+"18":isToday?T.navy+"0a":T.surfaceAlt,
                border:`1px solid ${isToday?T.navy:hasLog?dotColor+"40":T.border}`,
                transition:"all 0.15s",
              }}>
              <span style={{fontSize:11,fontWeight:isToday?800:hasLog?700:400,
                color:isToday?T.navy:hasLog?dotColor:T.muted}}>{d}</span>
              {hasLog && <div style={{width:4,height:4,borderRadius:"50%",background:dotColor,marginTop:1}}/>}
            </div>
          );
        })}
      </div>
      {tooltip && (
        <div style={{position:"fixed",
          left:tooltip.x+80>window.innerWidth?"auto":tooltip.x,
          right:tooltip.x+80>window.innerWidth?8:"auto",
          top:tooltip.y-8,
          transform:tooltip.x+80>window.innerWidth?"translateY(-100%)":"translate(-50%,-100%)",
          background:T.navy,color:T.white,borderRadius:10,padding:"10px 14px",fontSize:12,
          zIndex:9999,pointerEvents:"none",boxShadow:"0 4px 16px rgba(0,0,0,0.2)",minWidth:140,whiteSpace:"nowrap"}}>
          <strong>{tooltip.dateStr}</strong> — {tooltip.dayLogs.length}건 학습
          {tooltip.dayLogs.map((l,i)=>(
            <div key={i} style={{marginTop:4,color:"rgba(255,255,255,0.8)"}}>
              {l.subject} · EI <strong style={{color:T.orange}}>{(l.engram_index||0).toFixed(1)}</strong>
            </div>
          ))}
          <div style={{position:"absolute",bottom:-6,left:"50%",transform:"translateX(-50%)",
            width:12,height:12,background:T.navy,clipPath:"polygon(0 0,100% 0,50% 100%)"}}/>
        </div>
      )}
      <div style={{display:"flex",gap:12,marginTop:8,flexWrap:"wrap"}}>
        {[{c:GRAPH.ccColor,l:"A등급 이상 (81~100)"},{c:T.navy,l:"B등급 (66~80)"},{c:T.orange,l:"C등급 (51~65)"},{c:GRAPH.ciColor,l:"D등급 (~50)"}].map(({c,l})=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.muted}}>
            <div style={{width:10,height:10,borderRadius:3,background:c+"40",border:`1px solid ${c}`}}/>
            {l}
          </div>
        ))}
      </div>
    </Card>
  );
};

// ══════════════════════════════════════════════════════
// AI 학습 조언 (Claude API)
// ══════════════════════════════════════════════════════
const AIAdvice = ({logs, profile}) => {
  const getCacheKey = () => {
    if(!logs||logs.length===0) return "";
    const sorted = [...logs].sort((a,b)=>b.date.localeCompare(a.date));
    return `${sorted[0]?.id}_${logs.length}`;
  };

  const [advice, setAdvice]     = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [lastFetch, setLastFetch] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [loadingCache, setLoadingCache] = useState(true);

  // 마운트 시 DB에서 캐시 조회
  useEffect(()=>{
    const checkCache = async () => {
      if(!profile?.id || !logs?.length){ setLoadingCache(false); return; }
      const cacheKey = getCacheKey();
      const { data } = await supabase
        .from("ai_advice_logs")
        .select("response, created_at")
        .eq("uid", profile.id)
        .eq("cache_key", cacheKey)
        .order("created_at", {ascending:false})
        .limit(1)
        .single();
      if(data){
        setAdvice(data.response);
        setLastFetch(new Date(data.created_at).toLocaleString("ko-KR",{month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"}));
        setIsCached(true);
      }
      setLoadingCache(false);
    };
    checkCache();
  },[logs?.length, profile?.id]);

  const getAdvice = async (forceRefresh=false) => {
    if(logs.length === 0){ setError("학습 기록이 없어요. 먼저 데이터를 입력해 주세요!"); return; }
    // 캐시 있고 강제 갱신 아니면 스킵
    if(isCached && !forceRefresh){ setError(""); return; }
    setLoading(true); setError(""); setAdvice("");

    // ── 데이터 준비
    const all    = [...logs].sort((a,b)=>a.date.localeCompare(b.date));
    // 최근 7일 기준, 7건 미만이면 최근 7건 fallback
    const sevenDaysAgo = new Date(Date.now() - 7*24*60*60*1000);
    const recent7d = all.filter(l => new Date(l.date) >= sevenDaysAgo);
    const recent   = recent7d.length >= 7 ? recent7d : all.slice(-7);
    const recentLabel = recent7d.length >= 7
      ? `최근 7일 (${recent[0].date}~${recent[recent.length-1].date})`
      : `최근 7건 (${recent[0].date}~${recent[recent.length-1].date})`;
    const latest = all[all.length-1];

    // EI 통계
    const avgEI  = (recent.reduce((s,l)=>s+(l.engram_index||0),0)/recent.length).toFixed(1);
    const allAvgEI = (all.reduce((s,l)=>s+(l.engram_index||0),0)/all.length).toFixed(1);
    const eiTrend = all.length>=2 ? (latest.engram_index - all[Math.max(0,all.length-8)].engram_index).toFixed(1) : null;

    // Co-In 메타인지
    const coinTot = recent.reduce((s,l)=>s+(l.coin_cc||0)+(l.coin_ci||0)+(l.coin_ic||0)+(l.coin_ii||0),0);
    const coinCC  = recent.reduce((s,l)=>s+(l.coin_cc||0),0);
    const coinCI  = recent.reduce((s,l)=>s+(l.coin_ci||0),0);
    const coinIC  = recent.reduce((s,l)=>s+(l.coin_ic||0),0);
    const coinII  = recent.reduce((s,l)=>s+(l.coin_ii||0),0);
    const ciRate  = coinTot>0 ? (coinCI/coinTot*100).toFixed(1) : 0;
    const metaAcc = coinTot>0 ? ((coinCC+coinII)/coinTot*100).toFixed(1) : 0;

    // 오답 유형 합산
    const errTot = {Q1:0,Q2:0,Q3:0,M1:0,M2:0,M3:0};
    recent.forEach(l=>{ errTot.Q1+=(l.err_q1||0);errTot.Q2+=(l.err_q2||0);errTot.Q3+=(l.err_q3||0);errTot.M1+=(l.err_m1||0);errTot.M2+=(l.err_m2||0);errTot.M3+=(l.err_m3||0); });
    const topErr = Object.entries(errTot).sort((a,b)=>b[1]-a[1]).filter(([,v])=>v>0).slice(0,3).map(([k,v])=>`${k}(${v}회)`).join(", ");

    // 과목별 Co-In + 평균 EI
    const subjectMap = {};
    recent.forEach(l=>{
      if(!subjectMap[l.subject]) subjectMap[l.subject]={tot:0,ci:0,ic:0,eis:[]};
      const tot=(l.coin_cc||0)+(l.coin_ci||0)+(l.coin_ic||0)+(l.coin_ii||0);
      subjectMap[l.subject].tot+=tot;
      subjectMap[l.subject].ci+=(l.coin_ci||0);
      subjectMap[l.subject].ic+=(l.coin_ic||0);
      subjectMap[l.subject].eis.push(l.engram_index||0);
    });
    const subjectCoIn = Object.entries(subjectMap).map(([s,d])=>{
      const avgEIs=(d.eis.reduce((a,b)=>a+b,0)/d.eis.length).toFixed(1);
      const ciPct=d.tot>0?(d.ci/d.tot*100).toFixed(1):0;
      const icPct=d.tot>0?(d.ic/d.tot*100).toFixed(1):0;
      return `  ${s}: C-I ${ciPct}%(${d.tot}문항 중 ${d.ci}회) / I-C ${icPct}%(${d.ic}회) / 평균EI ${avgEIs}점`;
    }).join("\n");

    // 학습 시간 패턴
    const avgNetTime = (recent.reduce((s,l)=>s+(l.net_time||0),0)/recent.length).toFixed(0);
    const avgQCount  = (recent.reduce((s,l)=>s+(l.question_count||0),0)/recent.length).toFixed(0);

    // 전략 지수
    const avgStrategy = (recent.reduce((s,l)=>s+(l.strategy_score||0),0)/recent.length).toFixed(1);
    const avgEfficiency = (recent.reduce((s,l)=>s+(l.efficiency_index||0),0)/recent.length).toFixed(1);

    const prompt = `당신은 20HA META-X 학습 프로그램의 코치 AI입니다.
[용어 정의]
EI(엔그램 지수): 학습 각인도 종합 점수(100점 만점)
Co-In(메타인지): C-C 정확한 자기예측 / C-I 과잉확신 / I-C 과소평가 / I-I 정직한 오답 인식
오답 유형(수학 전용) — Q(지식 결여): Q1 개념미숙, Q2 추론실패, Q3 지식공백 / M(실행 오류): M1 계산실수, M2 문제오독, M3 단순실수

아래 학습 데이터를 바탕으로 학생에게 직접 전달하는 코칭 메시지를 작성해주세요.
조건:
- 반드시 존댓말 사용 (~해요, ~세요)
- 2~3문장
- 이름 언급 금지
- 수치 나열이나 칭찬 위주 금지 — 개선 포인트와 오늘 실천할 구체적 행동 중심으로
- 가장 시급한 문제 1가지만 콕 집어서

[학습 데이터] (${recentLabel})
EI: 평균 ${avgEI}점 / 목표 ${profile?.target_ei}점 / 추세 ${eiTrend!==null?(Number(eiTrend)>=0?"+":"")+eiTrend+"점":"유지"}
전략수행: ${avgStrategy}점 / 효율성: ${avgEfficiency}점
학습패턴: 평균 ${avgNetTime}분 / ${avgQCount}문항/회
오답Top3: ${topErr||"없음"}

메타인지 Co-In 분포 (총 ${coinTot}문항):
  C-C ${coinTot>0?(coinCC/coinTot*100).toFixed(1):0}%(${coinCC}회) / C-I ${ciRate}%(${coinCI}회) / I-C ${coinTot>0?(coinIC/coinTot*100).toFixed(1):0}%(${coinIC}회) / I-I ${coinTot>0?(coinII/coinTot*100).toFixed(1):0}%(${coinII}회)

과목별 Co-In:
${subjectCoIn}`;

    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      if(!apiKey){ setError("REACT_APP_GEMINI_API_KEY 환경변수를 Vercel에 추가해 주세요."); setLoading(false); return; }
      // gemini-2.5-flash-lite: 2026-06-01 현재 이 API key의 무료 등급이 유일하게 호출 가능한 모델
      //   (2.0-flash, 2.0-flash-lite는 free_tier_requests limit=0; 2.5-flash는 high demand 잦음;
      //    1.5-flash는 deprecated 404). 변경 시 반드시 무료 등급 호출 가능 여부 사전 확인.
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${apiKey}`,
        { method:"POST", headers:{"Content-Type":"application/json"},
          body:JSON.stringify({contents:[{parts:[{text:prompt}]}],generationConfig:{temperature:0.7}}) }
      );
      const data = await res.json();
      if(data.error){
        const msg = data.error.message || "";
        // 일시 capacity 초과 메시지는 한국어 안내로 친절화
        if(/high demand|overloaded|UNAVAILABLE|try again/i.test(msg) || res.status === 503){
          setError("AI 서버가 일시적으로 혼잡합니다. 잠시 후 다시 시도해 주세요.");
        } else {
          setError("AI 오류: " + msg);
        }
        setLoading(false); return;
      }
      const text = data.candidates?.[0]?.content?.parts?.[0]?.text || "조언을 가져올 수 없었어요.";
      const now  = new Date();
      const time = now.toLocaleString("ko-KR",{month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
      const cacheKey = getCacheKey();
      const snapshot = { avgEI, allAvgEI, metaAcc, ciRate, avgStrategy, avgEfficiency, topErr, subjectCoIn, avgNetTime, avgQCount, recentLabel };
      await supabase.from("ai_advice_logs").insert({ uid:profile.id, prompt, response:text, data_snapshot:snapshot, cache_key:cacheKey });
      setAdvice(text);
      setLastFetch(time);
      setIsCached(true);
    } catch(e) {
      setError("네트워크 오류가 발생했어요. 잠시 후 다시 시도해 주세요.");
    }
    setLoading(false);
  };

  return (
    <Card style={{marginBottom:12,background:"linear-gradient(135deg,#F7F8FC,#EEF1FF)"}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:12}}>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:T.navy}}>🤖 AI 학습 코치</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>{isCached ? "새 학습 기록 추가 시 새로운 코칭을 받을 수 있습니다." : "최근 7일 학습 데이터 기반 AI 맞춤 피드백"}</div>
        </div>
        {!isCached &&
          <button onClick={()=>getAdvice(false)} disabled={loading||loadingCache}
            style={{padding:"7px 14px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,
              background:T.navy,color:T.white,display:"flex",alignItems:"center",gap:6,flexShrink:0,opacity:(loading||loadingCache)?0.7:1}}>
            {loading ? <><Spinner size={13} color="#fff"/>분석 중...</> : "✨ 조언 받기"}
          </button>
        }
      </div>
      {error && (
        <div style={{background:"#FEE2E2",border:"1px solid #FECACA",borderRadius:8,padding:"10px 14px",fontSize:13,color:T.danger}}>{error}</div>
      )}
      {advice && (
        <div style={{background:T.white,borderRadius:10,padding:"14px 16px",border:`1px solid ${T.border}`}}>
          <div style={{fontSize:13,color:T.text,lineHeight:1.85,whiteSpace:"pre-wrap"}}>{advice}</div>
          {lastFetch && <div style={{fontSize:11,color:T.muted,marginTop:8,textAlign:"right"}}>{lastFetch}</div>}
        </div>
      )}
      {!advice && !error && !loading && (
        <div style={{textAlign:"center",padding:"20px 0",color:T.muted,fontSize:13}}>
          버튼을 누르면 나만의 맞춤 조언을 드려요! 🎯
        </div>
      )}
    </Card>
  );
};

const StudentDashboard = ({logs, profile, isAdminView=false}) => {
  const [subjectFilter, setSubjectFilter] = useState("전체");
  const isMobile = useMobile();
  const normLogs = useMemo(() => logs.map(l=>({
    ...l, engramIndex:l.engram_index, strategyScore:l.strategy_score,
    efficiencyIndex:l.efficiency_index, metacognitionAccuracy:l.metacognition_accuracy,
    coinFilter:{cc:l.coin_cc,ci:l.coin_ci,ic:l.coin_ic,ii:l.coin_ii},
    errorAnalysis:{Q1:l.err_q1,Q2:l.err_q2,Q3:l.err_q3,M1:l.err_m1,M2:l.err_m2,M3:l.err_m3},
    subject:l.subject, bookLevel:l.book_level, netTime:l.net_time, questionCount:l.question_count,
    qBasic:l.q_basic||0, qMid:l.q_mid||0, qAdv:l.q_adv||0,
  })), [logs]);
  const allSubjects=["전체",...new Set(normLogs.map(l=>l.subject))];
  const filtered=subjectFilter==="전체"?normLogs:normLogs.filter(l=>l.subject===subjectFilter);
  const ninetyDaysAgo=new Date(); ninetyDaysAgo.setDate(ninetyDaysAgo.getDate()-90);
  const ninetyStr=ninetyDaysAgo.toISOString().slice(0,10);
  const sorted=[...filtered].filter(l=>l.date>=ninetyStr).sort((a,b)=>a.date.localeCompare(b.date));
  const _dateEI={};sorted.forEach(l=>{if(!_dateEI[l.date])_dateEI[l.date]=[];_dateEI[l.date].push(l.engramIndex);});
  const dailyAvg=Object.entries(_dateEI).sort((a,b)=>a[0].localeCompare(b[0])).map(([date,eis])=>({date,engramIndex:+(eis.reduce((s,v)=>s+v,0)/eis.length).toFixed(1)}));
  const maData=dailyAvg.map((d,i)=>{const w=dailyAvg.slice(Math.max(0,i-6),i+1);return{...d,movingAvg:+(w.reduce((s,v)=>s+v.engramIndex,0)/w.length).toFixed(1)};});
  const latest=dailyAvg[dailyAvg.length-1],prev=dailyAvg[dailyAvg.length-2];
  const delta=latest&&prev?+(latest.engramIndex-prev.engramIndex).toFixed(1):null;
  const todayStr=new Date().toISOString().slice(0,10);
  const latestIsToday=latest?.date===todayStr;
  const coinT=filtered.reduce((acc,l)=>{const cf=l.coinFilter||{};Object.entries(cf).forEach(([k,v])=>{acc[k]=(acc[k]||0)+v;});return acc;},{cc:0,ci:0,ic:0,ii:0});
  const coinTot=Object.values(coinT).reduce((s,v)=>s+v,0);
  const mAcc=coinTot>0?(((coinT.cc+coinT.ii)/coinTot)*100).toFixed(1):0;
  const errT=filtered.reduce((acc,l)=>{const ea=l.errorAnalysis||{};Object.entries(ea).forEach(([k,v])=>{acc[k]=(acc[k]||0)+v;});return acc;},{});
  const totalErr=Object.values(errT).reduce((s,v)=>s+v,0);
  const errPie=ERR_CODES.map(c=>({name:c,value:errT[c]||0,label:ERR_LABELS[c]})).filter(d=>d.value>0);
  const ERR_C={Q1:GRAPH.errQ[0],Q2:GRAPH.errQ[1],Q3:GRAPH.errQ[2],M1:GRAPH.errM[0],M2:GRAPH.errM[1],M3:GRAPH.errM[2]};
  const COIN_C={"C-C":GRAPH.ccColor,"C-I":GRAPH.ciColor,"I-C":GRAPH.icColor,"I-I":GRAPH.iiColor};
  const coinScatter=normLogs.flatMap(l=>{const d=l.coinFilter||{};return[...Array(d.cc||0).fill(0).map(()=>({x:0.5+Math.random()*0.9,y:0.5+Math.random()*0.9,type:"C-C"})),...Array(d.ci||0).fill(0).map(()=>({x:0.5+Math.random()*0.9,y:-0.3-Math.random()*0.9,type:"C-I"})),...Array(d.ic||0).fill(0).map(()=>({x:-0.3-Math.random()*0.9,y:0.5+Math.random()*0.9,type:"I-C"})),...Array(d.ii||0).fill(0).map(()=>({x:-0.5-Math.random()*0.9,y:-0.5-Math.random()*0.9,type:"I-I"}))];});
  const targetEI=profile?.target_ei||85;

  if(normLogs.length===0) return(
    <div style={{textAlign:"center",padding:"80px 20px"}}>
      <div style={{fontSize:48,marginBottom:16}}>📚</div>
      <div style={{fontSize:18,fontWeight:700,color:T.navy,marginBottom:8}}>아직 학습 기록이 없습니다</div>
      <div style={{fontSize:14,color:T.muted}}>상단의 <strong style={{color:T.orange}}>+ 학습 입력</strong> 버튼으로 첫 데이터를 입력해 보세요.</div>
    </div>
  );

  return(
    <div>
      {!isAdminView && <AIAdvice logs={logs} profile={profile}/>}
      <LearningCalendar logs={logs}/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:16}}>
        {[
          {label:latestIsToday?"오늘 EI 점수":(latest?`최근 EI (${latest.date.slice(5).replace('-','/')})`:  "최근 EI 점수"),desc:"최근 학습이 뇌에 얼마나 효과적으로 각인되었는지를 나타내는 종합 점수",kpi:"S(93~) A(81~) B(66~) C(51~) D(~50)\n\n세 가지 역량(전략 수행·풀이 효율·메타인지)을\n종합해 산출하는 학습 각인도 지수입니다.\n점수가 높을수록 오래 기억에 남는 학습을 했다는 의미예요.",value:latest?.engramIndex??"—",color:latest?EI_COLOR(latest.engramIndex):T.navy,sub:delta!==null?`${delta>=0?"▲":"▼"} ${Math.abs(delta)} 전 기록 대비`:null,subColor:delta>=0?T.success:T.danger},
          (()=>{
            const curEI = latest?.engramIndex??null;
            const curG = curEI!=null?gradeInfo(curEI):null;
            const nextG = curG?GRADE_NEXT[curG.g]:null;
            const nextMin = nextG?GRADE_MIN[nextG]:null;
            const diff = curEI!=null&&nextMin!=null?(nextMin-curEI).toFixed(1):null;
            const atTop = curG?.g==="S";
            return {
              label:"다음 등급까지",
              desc:"현재 등급에서 다음 등급 최솟값까지 남은 점수",
              kpi:"현재 최근 EI 기준으로 다음 등급까지 얼마나 남았는지 보여줍니다.\n목표 설정과 무관한 절대적 지표입니다.",
              value: atTop?"MAX":diff!=null?`+${diff}`:"—",
              unit: atTop?"":diff!=null?"점":"",
              color: atTop?"#16A34A":curG?curG.c:T.orange,
              sub: curG&&!atTop?`현재 ${curG.g}등급 → ${nextG}등급`:curG?"S등급 달성!":null,
              subColor: atTop?"#16A34A":T.muted,
            };
          })(),
          {label:"메타인지 지수",desc:"내 예측과 실제 결과가 일치한 비율",kpi:"문제를 풀기 전 맞출지 틀릴지 예측했을 때\n그 예측이 얼마나 정확했는지를 나타냅니다.\n\n높을수록 자신의 실력을 정확히 파악하고 있다는 뜻이에요.\n낮으면 과신하거나 과소평가하는 경향이 있을 수 있어요.",value:mAcc,unit:"%",color:"#7C3AED"},
          {label:"누적 학습일",desc:"지금까지 기록된 총 학습 세션 수",kpi:"데이터를 입력한 날의 총 횟수입니다.\n꾸준히 쌓일수록 분석의 정확도가 높아져요.",value:normLogs.length,unit:"일",color:T.navyMid},
        ].map(({label,desc="",kpi,value,unit="",color,sub,subColor})=>(
          <KpiCard key={label} label={label} desc={desc} kpi={kpi} value={value} unit={unit} color={color} sub={sub} subColor={subColor} isMobile={isMobile}/>
        ))}
      </div>
      <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
        {allSubjects.map(s=>(
          <button key={s} onClick={()=>setSubjectFilter(s)} style={{padding:"5px 14px",borderRadius:20,border:`1px solid ${subjectFilter===s?T.navy:T.border}`,cursor:"pointer",fontSize:12,fontWeight:700,background:subjectFilter===s?T.navy:T.white,color:subjectFilter===s?T.white:T.muted}}>{s}</button>
        ))}
      </div>
      <Card style={{marginBottom:12}}>
        <SectionTitle sub="일별 EI(막대) · 7일 이동평균(선) · 목표선(점선)" tooltip="엔그램 지수(EI)는 학습이 뇌에 얼마나 효과적으로 각인되었는지를 수치화한 종합 지표입니다.\n\n이동평균선이 목표선(점선)에 가까울수록\n목표에 근접한 안정적 성장을 의미합니다.">📈 엔그램 지수(EI) 추이</SectionTitle>
        <ResponsiveContainer width="100%" height={isMobile?180:210}>
          <ComposedChart data={maData} margin={{top:4,right:8,bottom:4,left:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey="date" tick={{fill:T.muted,fontSize:10}} tickFormatter={d=>d.slice(5)}/>
            <YAxis domain={[0,100]} tick={{fill:T.muted,fontSize:10}} width={28}/>
            <Tooltip content={<ChartTip/>}/>
            <ReferenceLine y={targetEI} stroke={T.orange} strokeDasharray="5 5" label={{value:`목표 ${targetEI}`,fill:T.orange,fontSize:11,position:"insideTopRight",offset:4}}/>
            <Bar dataKey="engramIndex" name="일일 EI" fill={T.navy+"28"} stroke={T.navy+"60"} radius={[4,4,0,0]}/>
            <Line type="monotone" dataKey="movingAvg" name="7일 평균" stroke={T.orange} strokeWidth={2.5} dot={false}/>
          </ComposedChart>
        </ResponsiveContainer>
      </Card>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:12,marginBottom:12}}>
        <Card>
          <SectionTitle sub="C-C: 정확한 자기예측 · C-I: 과잉확신 · I-C: 과소평가 · I-I: 정직한 오답 인식" tooltip="Co-In(Confidence-Incorrect) 메타인지 필터입니다.\n\nC-C: 맞을 것 같았고 실제로 맞음 → 이상적\nC-I: 맞을 것 같았으나 틀림 → 과잉확신 경보(30% 초과 시 Red Flag)\nI-C: 틀릴 것 같았으나 맞음 → 과소평가\nI-I: 틀릴 것 같았고 실제로 틀림 → 정직한 인식\n\nC-C 비율이 높을수록 메타인지 정확도 우수">🧠 내 예측 vs 실제 결과</SectionTitle>
          <ResponsiveContainer width="100%" height={200}>
            <RadarChart data={[
              {label:"C-C 정확한 예측",value:coinT.cc||0,full:Math.max(coinT.cc||0,coinT.ci||0,coinT.ic||0,coinT.ii||0,1)},
              {label:"C-I 과잉확신",value:coinT.ci||0,full:Math.max(coinT.cc||0,coinT.ci||0,coinT.ic||0,coinT.ii||0,1)},
              {label:"I-I 정직한 인식",value:coinT.ii||0,full:Math.max(coinT.cc||0,coinT.ci||0,coinT.ic||0,coinT.ii||0,1)},
              {label:"I-C 과소평가",value:coinT.ic||0,full:Math.max(coinT.cc||0,coinT.ci||0,coinT.ic||0,coinT.ii||0,1)},
            ]} margin={{top:10,right:24,bottom:10,left:24}}>
              <PolarGrid stroke={T.border}/>
              <PolarAngleAxis dataKey="label" tick={{fontSize:10,fill:T.muted,fontWeight:600}}/>
              <Radar name="횟수" dataKey="value" stroke={T.navy} fill={T.navy} fillOpacity={0.25} strokeWidth={2}/>
              <Tooltip content={<ChartTip/>}/>
            </RadarChart>
          </ResponsiveContainer>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",justifyContent:"center",marginBottom:12}}>
            {[["C-C",GRAPH.ccColor,coinT.cc||0],["C-I",GRAPH.ciColor,coinT.ci||0],["I-C",T.orange,coinT.ic||0],["I-I",T.navyMid,coinT.ii||0]].map(([k,c,v])=>(
              <span key={k} style={{fontSize:11,color:c,fontWeight:700}}>● {k}: {v}회</span>
            ))}
          </div>
          {/* COIN 백분율 띠그래프 */}
          {(()=>{
            const tot=(coinT.cc||0)+(coinT.ci||0)+(coinT.ic||0)+(coinT.ii||0);
            if(tot===0) return null;
            const bars=[
              {k:"C-C",v:coinT.cc||0,color:GRAPH.ccColor},
              {k:"C-I",v:coinT.ci||0,color:GRAPH.ciColor},
              {k:"I-C",v:coinT.ic||0,color:T.orange},
              {k:"I-I",v:coinT.ii||0,color:"#9CA3AF"},
            ];
            return(
              <div style={{marginBottom:14}}>
                <div style={{height:18,borderRadius:9,overflow:"hidden",display:"flex",gap:2}}>
                  {bars.map(({k,v,color})=>{
                    const p=v/tot*100;
                    if(p<0.5) return null;
                    return(
                      <div key={k} style={{flex:`0 0 ${p}%`,background:color,borderRadius:0,transition:"flex 0.6s ease",position:"relative"}}
                        title={`${k}: ${v}회 (${p.toFixed(1)}%)`}/>
                    );
                  })}
                </div>
                <div style={{display:"flex",gap:10,marginTop:6,flexWrap:"wrap",justifyContent:"center"}}>
                  {bars.map(({k,v,color})=>{
                    const p=+(v/tot*100).toFixed(0);
                    return(
                      <div key={k} style={{display:"flex",alignItems:"center",gap:3,fontSize:10}}>
                        <div style={{width:8,height:8,borderRadius:2,background:color,flexShrink:0}}/>
                        <span style={{color:T.muted}}>{k} <strong style={{color}}>{p}%</strong></span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {/* C-I / I-C 위험 지표 강조 */}
          {(coinT.ci||0)+(coinT.ic||0)>0&&(()=>{
            const tot=Object.values(coinT).reduce((s,v)=>s+v,0)||1;
            const ciR=+((coinT.ci||0)/tot*100).toFixed(0);
            const icR=+((coinT.ic||0)/tot*100).toFixed(0);
            const ciDanger=ciR>30, ciWarn=ciR>15;
            const icDanger=icR>30, icWarn=icR>15;
            const anyDanger=ciDanger||icDanger;
            const anyWarn=ciWarn||icWarn;
            const borderColor=anyDanger?"#FECACA":anyWarn?"#FED7AA":"#BBF7D0";
            const bgColor=anyDanger?"#FFF5F5":anyWarn?"#FFFBEB":"#F0FDF4";
            const titleColor=anyDanger?T.danger:anyWarn?T.orange:T.success;
            const titleIcon=anyDanger?"warn":anyWarn?"warn":"check";
            const titleText=anyDanger?"메타인지 위험 지표":anyWarn?"메타인지 주의 지표":"메타인지 양호";
            return(
              <div style={{background:bgColor,border:`1px solid ${borderColor}`,borderRadius:10,padding:"10px 14px"}}>
                <div style={{fontSize:11,fontWeight:800,color:titleColor,marginBottom:8,display:"flex",alignItems:"center",gap:4}}>
                  {HI[titleIcon]?.(12,titleColor)} {titleText}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  <div>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:11,color:GRAPH.ciColor,fontWeight:700}}>C-I 과잉확신</span>
                      <span style={{fontSize:11,fontWeight:800,color:ciDanger?T.danger:ciWarn?T.orange:T.success}}>
                        {ciR}% {ciDanger?"🔴 위험":ciWarn?"🟡 주의":"🟢 양호"}
                      </span>
                    </div>
                    <div style={{height:6,background:T.surfaceAlt,borderRadius:3}}>
                      <div style={{height:"100%",width:`${Math.min(ciR,100)}%`,background:GRAPH.ciColor,borderRadius:3,transition:"width 0.5s"}}/>
                    </div>
                  </div>
                  <div>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:11,color:GRAPH.icColor,fontWeight:700}}>I-C 과소평가</span>
                      <span style={{fontSize:11,fontWeight:800,color:icDanger?T.danger:icWarn?T.orange:T.success}}>
                        {icR}% {icDanger?"🔴 위험":icWarn?"🟡 주의":"🟢 양호"}
                      </span>
                    </div>
                    <div style={{height:6,background:T.surfaceAlt,borderRadius:3}}>
                      <div style={{height:"100%",width:`${Math.min(icR,100)}%`,background:GRAPH.icColor,borderRadius:3,transition:"width 0.5s"}}/>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </Card>
        <Card>
          <SectionTitle sub="Q1 개념미숙 · Q2 추론실패 · Q3 지식공백 · M1 계산실수 · M2 문제오독 · M3 단순실수" tooltip="오답 유형 6분류 체계입니다.\n\n[Q - 지식 결여]\nQ1: 해당 개념을 아예 모름 → 개념 학습 필요\nQ2: 알지만 문제에 적용 실패 → 응용 훈련 필요\nQ3: 배우지 않은 범위 → 커리큘럼 확인\n\n[M - 실행 오류]\nM1: 계산·풀이 과정 실수 → 검산 습관\nM2: 문제 조건을 잘못 읽음 → 정독 훈련\nM3: 알면서도 틀림 → 집중력 관리">❌ 왜 틀렸을까? 오답 분석</SectionTitle>
          {totalErr>0 ? (<>
            {/* 막대그래프: Q1~M3 */}
            <ResponsiveContainer width="100%" height={163}>
              <BarChart data={ERR_CODES.map(c=>({name:c,value:errT[c]||0}))} margin={{top:8,right:16,bottom:4,left:4}}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false}/>
                <XAxis dataKey="name" tick={{fontSize:10,fill:T.muted,fontWeight:700}}/>
                <YAxis tick={{fontSize:10,fill:T.muted}} width={24} allowDecimals={false}/>
                <Tooltip content={<ChartTip/>}/>
                <Bar dataKey="value" radius={[4,4,0,0]}>
                  {ERR_CODES.map(c=><Cell key={c} fill={ERR_C[c]}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* 원그래프: Q전체 vs M전체 */}
            <div style={{display:"flex",alignItems:"center",gap:0,marginTop:10,paddingLeft:8}}>
              <ResponsiveContainer width="50%" height={110}>
                <PieChart>
                  <Pie data={[
                    {name:"몰라서 틀림",value:(errT.Q1||0)+(errT.Q2||0)+(errT.Q3||0),fill:GRAPH.errQ[0]},
                    {name:"실수로 틀림",value:(errT.M1||0)+(errT.M2||0)+(errT.M3||0),fill:GRAPH.errM[0]},
                  ].filter(d=>d.value>0)} cx="55%" cy="55%" innerRadius={28} outerRadius={42} paddingAngle={4} dataKey="value">
                    {[GRAPH.errQ[0],GRAPH.errM[0]].map((c,i)=><Cell key={i} fill={c}/>)}
                  </Pie>
                  <Tooltip content={<ChartTip/>}/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:6}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <div style={{width:10,height:10,borderRadius:3,background:GRAPH.errQ[0]}}/>
                  <span style={{fontSize:11,color:T.textMid}}>몰라서 틀림: <strong>{(errT.Q1||0)+(errT.Q2||0)+(errT.Q3||0)}회</strong></span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <div style={{width:10,height:10,borderRadius:3,background:GRAPH.errM[0]}}/>
                  <span style={{fontSize:11,color:T.textMid}}>실수로 틀림: <strong>{(errT.M1||0)+(errT.M2||0)+(errT.M3||0)}회</strong></span>
                </div>
              </div>
            </div>
          </>) : <div style={{height:190,display:"flex",alignItems:"center",justifyContent:"center",color:T.muted,fontSize:13}}>오답 데이터 없음</div>}
        </Card>
      </div>
      <Card style={{marginBottom:12}}>
        <SectionTitle sub="문항 1개를 푸는 데 걸린 평균 시간(초)입니다. 그래프가 우하향할수록 같은 시간에 더 많은 문제를 처리하게 된다는 뜻으로, 풀이 효율이 향상된 것입니다. 문제집 단계별 난이도가 가중치로 보정됩니다." tooltip="문항 1개를 푸는 데 평균적으로 얼마나 걸렸는지 보여줍니다.\n\n숫자가 낮아질수록(우하향) 같은 시간에 더 많은\n문제를 처리할 수 있게 된다는 뜻이에요.\n\n문제집 난이도에 따라 보정된 값을 사용합니다.">⚡ 문제 풀이 속도</SectionTitle>
        {(()=>{
          const isMathView = subjectFilter==="수학" || subjectFilter==="전체";
          const allDates=[...new Set(sorted.map(l=>l.date.slice(5)))].sort();
          const speedData=allDates.map(d=>{
            const dayLogs=sorted.filter(l=>l.date.slice(5)===d);
            const calcLvl=(key)=>{const ls=dayLogs.filter(l=>l[key]>0);const tot=ls.reduce((s,l)=>s+l[key],0);return ls.length>0&&tot>0?+(ls.reduce((s,l)=>s+l.netTime*60,0)/tot).toFixed(1):null;};
            return{date:d, basic:calcLvl('qBasic'), mid:calcLvl('qMid'), adv:calcLvl('qAdv')};
          });
          return(
            <ResponsiveContainer width="100%" height={170}>
              <LineChart data={speedData} margin={{top:4,right:8,bottom:4,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
                <XAxis dataKey="date" tick={{fill:T.muted,fontSize:10}}/>
                <YAxis tick={{fill:T.muted,fontSize:10}} unit="초" width={48}/>
                <Tooltip content={<ChartTip/>}/>
                <Legend formatter={v=><span style={{fontSize:11,color:T.textMid}}>{v}</span>}/>
                <Line type="monotone" dataKey="basic" name={isMathView?"기본":"전체"} stroke={T.navy} strokeWidth={2} dot={{r:2}} connectNulls/>
                {isMathView&&<Line type="monotone" dataKey="mid" name="응용" stroke={T.orange} strokeWidth={2} dot={{r:2}} connectNulls/>}
                {isMathView&&<Line type="monotone" dataKey="adv" name="심화" stroke="#7C3AED" strokeWidth={2} dot={{r:2}} connectNulls/>}
              </LineChart>
            </ResponsiveContainer>
          );
        })()}
      </Card>
      <Card>
        <SectionTitle sub="전략수행: 20HA 학습 전략(워밍업·채점·복습 등)을 얼마나 충실히 실천했는가 / 효율성: 순공부 시간 대비 문항 처리 속도 / 메타인지: 내 실력을 얼마나 정확히 예측하는가(Co-In 기반)" tooltip="EI를 구성하는 3가지 핵심 역량을 독립적으로 추적합니다.\n\n전략수행: 학습 전략을 얼마나 잘 실천했는가\n효율성: 시간 대비 얼마나 많은 문제를 풀었는가\n메타인지: 자신의 실력을 얼마나 정확히 파악하는가\n\n각 지수를 따로 보면 어느 영역이 부족한지 알 수 있어요.">📊 3가지 핵심 지표 변화</SectionTitle>
        <ResponsiveContainer width="100%" height={170}>
          <LineChart data={sorted} margin={{top:4,right:8,bottom:4,left:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey="date" tick={{fill:T.muted,fontSize:10}} tickFormatter={d=>d.slice(5)}/>
            <YAxis domain={[0,100]} tick={{fill:T.muted,fontSize:10}} width={28}/>
            <Tooltip content={<ChartTip/>}/>
            <Line type="monotone" dataKey="strategyScore" name="전략수행" stroke={T.navy} strokeWidth={2} dot={false}/>
            <Line type="monotone" dataKey="efficiencyIndex" name="효율성" stroke={T.orange} strokeWidth={2} dot={false}/>
            <Line type="monotone" dataKey="metacognitionAccuracy" name="메타인지" stroke="#7C3AED" strokeWidth={2} dot={false}/>
            <Legend formatter={v=><span style={{fontSize:11,color:T.textMid}}>{v}</span>}/>
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// PARENT DASHBOARD
// ══════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════
// PARENT HOME — 자녀 아이디 관리 (추가/삭제만)
// ══════════════════════════════════════════════════════
const ParentHomeView = ({children, parentId, onChildrenUpdate}) => {
  const [addEmail, setAddEmail] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const isMobile = useMobile();

  const handleAdd = async () => {
    if(!addEmail){ setMsg({type:"err",text:"이메일을 입력해주세요."}); return; }
    setAddLoading(true); setMsg(null);
    const lowerEmail = addEmail.trim().toLowerCase();
    const { data, error } = await supabase.rpc("find_student_by_email", { student_email: lowerEmail });
    if(error || !data || data.length === 0) {
      // 가입 여부 확인 — 가입은 됐지만 미승인인지, 아예 미가입인지 구분 안내
      const { data: exists } = await supabase.rpc("auth_email_exists", { p_email: lowerEmail });
      const refined = exists
        ? "학생 계정은 있지만 관리자 승인 대기 중입니다. 승인 후 다시 시도해주세요."
        : "해당 이메일로 가입된 학생을 찾을 수 없습니다. 자녀가 먼저 회원가입을 진행해야 합니다.";
      setMsg({type:"err",text: refined});
      setAddLoading(false); return;
    }
    const student = data[0];
    if(children.some(c => c.profile?.id === student.id)) {
      setMsg({type:"err",text:"이미 연결된 학생입니다."}); setAddLoading(false); return;
    }
    const { error: insErr } = await supabase.from("parent_students").insert({ parent_id: parentId, student_id: student.id });
    if(insErr && insErr.code !== '23505'){ setMsg({type:"err",text:"연결 중 오류가 발생했습니다."}); setAddLoading(false); return; }
    const { data: cl } = await supabase.rpc("get_child_logs", { child_id: student.id });
    onChildrenUpdate([...children, { profile: {...student, role:"student"}, logs: cl || [] }], student.id);
    setAddEmail("");
    setMsg({type:"ok",text:`${student.name}(${student.grade}) 학생이 연결됐어요. 좌측 메뉴에 추가됐습니다.`});
    setAddLoading(false);
  };

  const handleDelete = async (childId, childName) => {
    if(!window.confirm(`${childName} 학생 연결을 해제할까요?`)) return;
    const { error } = await supabase.from("parent_students").delete().eq("parent_id", parentId).eq("student_id", childId);
    if(error){ alert("삭제 중 오류가 발생했습니다."); return; }
    onChildrenUpdate(children.filter(c => c.profile.id !== childId), null);
    setMsg({type:"ok",text:`${childName} 학생 연결이 해제되었습니다.`});
  };

  return (
    <div style={{display:"grid",gap:14}}>
      <div style={{fontSize:18,fontWeight:800,color:T.navy}}>👥 자녀 아이디 관리</div>

      {/* 자녀 추가 */}
      <Card style={{padding:"18px 20px"}}>
        <div style={{fontSize:13,fontWeight:700,color:T.navy,marginBottom:10}}>자녀 이메일로 연결</div>
        <div style={{fontSize:11,color:T.muted,marginBottom:12,lineHeight:1.5}}>
          자녀가 회원가입 + 관리자 승인 완료된 상태여야 연결할 수 있어요.
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
          <input value={addEmail} onChange={e=>{setAddEmail(e.target.value);setMsg(null);}}
            onKeyDown={e=>e.key==="Enter"&&handleAdd()}
            placeholder="example@email.com"
            style={{...css.input,margin:0,flex:"1 1 220px",fontSize:13,padding:"10px 14px"}}/>
          <button onClick={handleAdd} disabled={addLoading}
            style={{...css.btnPrimary,padding:"10px 18px",fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:5}}>
            {addLoading?<Spinner size={14} color="#fff"/>:"+ 추가"}
          </button>
        </div>
        {msg && (
          <div style={{
            marginTop:12,padding:"10px 14px",borderRadius:8,fontSize:12,
            background:msg.type==="ok"?"#F0FDF4":"#FEF2F2",
            border:`1px solid ${msg.type==="ok"?"#BBF7D0":"#FECACA"}`,
            color:msg.type==="ok"?"#166534":T.danger,
          }}>
            {msg.type==="ok"?"✅ ":"⚠️ "}{msg.text}
          </div>
        )}
      </Card>

      {/* 연결된 자녀 목록 */}
      <Card style={{padding:"18px 20px"}}>
        <div style={{fontSize:13,fontWeight:700,color:T.navy,marginBottom:12}}>
          연결된 자녀 <span style={{fontSize:12,color:T.muted,fontWeight:400,marginLeft:4}}>({children.length}명)</span>
        </div>
        {children.length === 0 ? (
          <div style={{textAlign:"center",padding:"32px 16px",color:T.muted,fontSize:13}}>
            연결된 자녀가 없어요. 위에서 자녀 이메일을 입력해 연결해보세요.
          </div>
        ) : (
          <div style={{display:"grid",gap:8}}>
            {children.map(c=>(
              <div key={c.profile.id} style={{
                display:"flex",alignItems:"center",gap:10,padding:"12px 14px",
                borderRadius:10,border:`1px solid ${T.border}`,background:T.surface,
              }}>
                <div style={{width:36,height:36,borderRadius:"50%",background:T.navy+"15",
                  display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {HI.cap(18,T.navy)}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:700,color:T.navy}}>
                    {c.profile.name}
                    <span style={{fontSize:11,fontWeight:400,color:T.muted,marginLeft:6}}>
                      ({calcGrade(c.profile.birth_year, c.profile.birth_month) || c.profile.grade || "-"})
                    </span>
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {c.profile.email}
                  </div>
                </div>
                <button onClick={()=>handleDelete(c.profile.id, c.profile.name)}
                  style={{...css.btnOutline,padding:"6px 12px",fontSize:11,color:T.danger,borderColor:T.danger}}>
                  삭제
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const ParentDashboard = ({children, selChildId, setSelChildId, parentId, onChildrenUpdate}) => {
  const [addEmail, setAddEmail]   = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [addMsg, setAddMsg]       = useState(null); // {type:"ok"|"err", text}
  const [childView, setChildView] = useState("dashboard"); // "dashboard" | "history"
  const isMobile = useMobile();

  const selChild = children.find(c => c.profile?.id === selChildId);

  const handleAddChild = async () => {
    if(!addEmail){ setAddMsg({type:"err", text:"이메일을 입력해주세요."}); return; }
    setAddLoading(true); setAddMsg(null);
    // 이메일로 학생 검색
    const lowerEmail = addEmail.trim().toLowerCase();
    const { data, error } = await supabase.rpc("find_student_by_email", { student_email: lowerEmail });
    if(error || !data || data.length === 0) {
      // 가입 여부 확인 — 가입은 됐지만 미승인인지, 아예 미가입인지 구분
      const { data: exists } = await supabase.rpc("auth_email_exists", { p_email: lowerEmail });
      const refined = exists
        ? "학생 계정은 있지만 관리자 승인 대기 중입니다. 승인 후 다시 시도해주세요."
        : "해당 이메일로 가입된 학생을 찾을 수 없습니다. 자녀가 먼저 회원가입을 진행해야 합니다.";
      setAddMsg({type:"err", text: refined}); setAddLoading(false); return;
    }
    const student = data[0];
    // 이미 연결된 경우
    if(children.some(c => c.profile?.id === student.id)) {
      setAddMsg({type:"err", text:"이미 연결된 학생입니다."}); setAddLoading(false); return;
    }
    const { error: insErr } = await supabase.from("parent_students").insert({ parent_id: parentId, student_id: student.id });
    if(insErr && insErr.code !== '23505'){ setAddMsg({type:"err", text:"연결 중 오류가 발생했습니다."}); setAddLoading(false); return; }
    // 새 자녀 데이터 로드
    const { data: cl } = await supabase.rpc("get_child_logs", { child_id: student.id });
    onChildrenUpdate([...children, { profile: {...student, role:"student"}, logs: cl || [] }], student.id);
    setAddEmail("");
    setAddMsg({type:"ok", text:`${student.name}(${student.grade}) 학생이 연결됐어요!`});
    setAddLoading(false);
  };

  return (
    <div>
      {/* 자녀 탭 */}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,flexWrap:"wrap"}}>
        {children.map(c => (
          <button key={c.profile.id} onClick={()=>setSelChildId(c.profile.id)}
            style={{padding:"8px 16px",borderRadius:20,border:`2px solid ${selChildId===c.profile.id?T.navy:T.border}`,
              background:selChildId===c.profile.id?T.navy:"transparent",
              color:selChildId===c.profile.id?T.white:T.textMid,cursor:"pointer",
              fontSize:13,fontWeight:700,transition:"all 0.15s"}}>
            <span style={{display:"flex",alignItems:"center",gap:5}}>
              {HI.cap(13,selChildId===c.profile.id?T.white:T.textMid)}
              {c.profile.name} <span style={{opacity:0.7,fontWeight:400}}>({calcGrade(c.profile.birth_year, c.profile.birth_month) || c.profile.grade})</span>
            </span>
          </button>
        ))}
        {/* 자녀 추가 */}
        <div style={{display:"flex",gap:6,alignItems:"center",flex:isMobile?"1 1 100%":"initial"}}>
          <input value={addEmail} onChange={e=>{setAddEmail(e.target.value);setAddMsg(null);}}
            onKeyDown={e=>e.key==="Enter"&&handleAddChild()}
            placeholder="자녀 이메일 입력" style={{...css.input,margin:0,width:isMobile?"100%":200,fontSize:12,padding:"8px 12px"}}/>
          <button onClick={handleAddChild} disabled={addLoading}
            style={{...css.btnPrimary,padding:"8px 14px",fontSize:12,fontWeight:700,flexShrink:0,display:"flex",alignItems:"center",gap:4}}>
            {addLoading?<Spinner size={13} color="#fff"/>:"+ 추가"}
          </button>
        </div>
      </div>
      {addMsg && (
        <div style={{background:addMsg.type==="ok"?"#F0FDF4":"#FEF2F2",border:`1px solid ${addMsg.type==="ok"?"#BBF7D0":"#FECACA"}`,
          borderRadius:8,padding:"10px 14px",fontSize:13,color:addMsg.type==="ok"?"#166534":T.danger,marginBottom:12}}>
          {addMsg.type==="ok"?"✅ ":"⚠️ "}{addMsg.text}
        </div>
      )}

      {children.length === 0 ? (
        <Card style={{textAlign:"center",padding:"48px 24px"}}>
          <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}>{HI.users(44,T.muted)}</div>
          <div style={{fontSize:16,fontWeight:700,color:T.navy,marginBottom:8}}>연결된 자녀가 없어요</div>
          <div style={{fontSize:13,color:T.muted}}>위에서 자녀의 이메일을 입력해 연결해보세요.</div>
        </Card>
      ) : selChild ? (<>
        {/* 자녀 뷰 탭 */}
        <div style={{display:"flex",gap:2,marginBottom:16,background:T.surfaceAlt,borderRadius:10,padding:4,width:"fit-content"}}>
          {[{v:"dashboard",icon:"cap",label:"메타인지 현황"},{v:"history",icon:"calendar",label:"학습 기록"}].map(({v,icon,label})=>(
            <button key={v} onClick={()=>setChildView(v)}
              style={{padding:"7px 16px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:700,
                background:childView===v?T.navy:"transparent",color:childView===v?T.white:T.muted,transition:"all 0.15s",
                display:"flex",alignItems:"center",gap:5}}>
              {navIcon(icon,13,childView===v?T.white:T.muted)} {label}
            </button>
          ))}
        </div>
        {childView==="dashboard"
          ? <StudentDashboard logs={selChild.logs} profile={selChild.profile} isAdminView={true}/>
          : <LogHistory logs={selChild.logs} onDelete={null} isAdmin={false} allProfiles={[selChild.profile]}/>
        }
      </>) : null}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// ADMIN DASHBOARD (진단 + 회원 관리)
// ── 2기 명단 상수 (렌더마다 재생성 방지) ──────────────
// 한글 조사 헬퍼: 받침 유무 자동 판별
const _hasJong = (s) => {
  const c = s.charCodeAt(s.length-1);
  if (c < 0xAC00 || c > 0xD7A3) return false;
  return ((c - 0xAC00) % 28) !== 0;
};
const J_eul = (s) => `${s}${_hasJong(s) ? "을" : "를"}`;   // 을/를
const J_i   = (s) => `${s}${_hasJong(s) ? "이" : "가"}`;   // 이/가
const J_eun = (s) => `${s}${_hasJong(s) ? "은" : "는"}`;   // 은/는
const J_eu_ro = (s) => `${s}${_hasJong(s) ? "으로" : "로"}`; // 으로/로


// ══════════════════════════════════════════════════════
// 학생: 20HA 인증 현황 탭
// ══════════════════════════════════════════════════════
const StudentCertView = ({profile, viewerMode="self"}) => {
  // viewerMode: "self" = 학생 본인 / "parent" = 학부모가 자녀 보기
  const [peerGrades, setPeerGrades] = useState({});
  const [myCafeCerts, setMyCafeCerts] = useState([]);
  const [classCertStats, setClassCertStats] = useState({}); // {name: cert_count}
  const [classSessionAvgs, setClassSessionAvgs] = useState({}); // {sessionNum: avg_score}
  const [myAttLogs, setMyAttLogs] = useState([]);            // [{session_date, session_type}]
  const [classAttStats, setClassAttStats] = useState({});    // {name: {morning, night}}
  // 출석 셀 hover tooltip — { x, y, lines: [{label, color}, ...], status }
  const [attTip, setAttTip] = useState(null);
  const isMobile = useMobile();
  useEffect(() => {
    // 학년 정보: RLS 우회용 SECURITY DEFINER RPC 사용 (학생/학부모도 다른 학생 학년 조회 가능)
    supabase.rpc("get_student_grades").then(({data}) => {
      if(data && data.length>0) {
        const map = {};
        data.forEach(p => { map[p.name] = calcGrade(p.birth_year, p.birth_month) || p.grade || ""; });
        setPeerGrades(map);
      }
    });
    // 카페 인증 글 (본인 또는 자녀)
    const cafeCertReq = viewerMode === "parent"
      ? supabase.rpc("get_child_cafe_certs", {p_child_id: profile.id})
      : supabase.rpc("get_my_cafe_certs");
    cafeCertReq.then(({data}) => {
      setMyCafeCerts(data || []);
    });
    // 클래스 전체 카페 인증 카운트 (랭킹/평균용)
    supabase.rpc("get_class_cert_stats").then(({data}) => {
      const map = {};
      (data||[]).forEach(r => { map[r.student_name] = r.cert_count; });
      setClassCertStats(map);
    });
    // 회차별 클래스 평균 점수 (그래프용)
    supabase.rpc("get_class_cert_session_avgs").then(({data}) => {
      const map = {};
      (data||[]).forEach(r => { map[r.session_num] = Number(r.avg_score); });
      setClassSessionAvgs(map);
    });
    // 출석 (본인 또는 자녀)
    const attReq = viewerMode === "parent"
      ? supabase.rpc("get_child_attendance_logs", {p_child_id: profile.id})
      : supabase.rpc("get_my_attendance_logs");
    attReq.then(({data}) => {
      setMyAttLogs(data || []);
    });
    // 클래스 전체 출석 카운트 (랭킹/평균용)
    supabase.rpc("get_class_attendance_stats").then(({data}) => {
      const map = {};
      (data||[]).forEach(r => { map[r.student_name] = {morning: r.morning_count, night: r.night_count}; });
      setClassAttStats(map);
    });
  }, []);

  const fk = (dt) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
  const myIdx = ROSTER2.findIndex(s => s.name === profile.name);

  const naverTotal   = ROSTER2_NAVER_DATES.length;
  const morningTotal = ROSTER2_MORNING_DATES.length;
  const nightTotal   = ROSTER2_NIGHT_DATES.length;
  const grandTotal   = naverTotal + morningTotal + nightTotal;

  // 본인의 카페 인증 회차 set (cert.posted_at → ROSTER2_NAVER_DATES의 회차 idx)
  const kstStr_ = (ts) => {
    const k = new Date(new Date(ts).getTime() + 9*3600*1000);
    return `${k.getUTCFullYear()}-${String(k.getUTCMonth()+1).padStart(2,'0')}-${String(k.getUTCDate()).padStart(2,'0')}`;
  };
  // cert의 회차(1-based) 배열: 관리자 수동 회차(session_override) 우선, 없으면 작성일 자동 매칭.
  // 관리자가 회차를 바꾸면 학생 화면에도 그대로 반영되도록 override를 최우선으로 본다.
  const certSessionNums = (c) => {
    if (c.session_override && c.session_override.length > 0) return c.session_override.map(Number);
    const postStr = kstStr_(c.posted_at);
    for (let i = ROSTER2_NAVER_DATES.length - 1; i >= 0; i--) {
      if (postStr >= fk(ROSTER2_NAVER_DATES[i])) return [i + 1];
    }
    return [];
  };
  const myCertSessions = new Set();
  myCafeCerts.forEach(c => {
    certSessionNums(c).forEach(n => {
      const idx = n - 1;
      if (idx >= 0 && idx < ROSTER2_NAVER_DATES.length) myCertSessions.add(idx);
    });
  });

  // 본인 출석 — 임계값(M=15, N=60) 통과해야 출석 인정. participation_minutes=null은 면제(인정)
  // myAttSet: 출석 인정 (카운트 대상) / myAttUnderSet: 참석은 했으나 시간 미달 / myAttMinutes: 분 표시용
  const myAttSet      = new Set();
  const myAttUnderSet = new Set();
  const myAttPreSet   = new Set(); // 사전결석 신청(결석이지만 신청 기록)
  const myAttPreExcessSet = new Set(); // 사전결석 한도 초과 → 미인정
  const myAttMinutes  = new Map();
  const myPreByType   = { M: [], N: [] };
  myAttLogs.forEach(l => {
    const key = `${l.session_type}-${l.session_date}`;
    if (l.pre_absence) { myAttPreSet.add(key); if (myPreByType[l.session_type]) myPreByType[l.session_type].push(l.session_date); return; }
    myAttMinutes.set(key, l.participation_minutes ?? null);
    if (isValidAttendance(l.session_type, l.participation_minutes, l.session_date)) myAttSet.add(key);
    else myAttUnderSet.add(key);
  });
  // 사전결석 한도(모닝 3·나잇 5): 회차 빠른 순 N개만 출석 인정, 초과분 미인정
  const MY_PRE_CAP = { M: 3, N: 5 };
  Object.keys(myPreByType).forEach(t => {
    const cap = MY_PRE_CAP[t] ?? 0;
    myPreByType[t].sort();
    myPreByType[t].forEach((d, i) => { if (i < cap) myAttSet.add(`${t}-${d}`); else myAttPreExcessSet.add(`${t}-${d}`); });
  });

  // 시간 기준 — allStats / mentor / mastery 모두 공통 사용 (TDZ 회피 위해 위로 이동)
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // 전체 통계: 모두 DB 기반
  // 본인 카운트는 "오늘까지" 일자만 (미래 회차에 미리 들어간 출석 row — 예: 6/3 일정 변경 사전 처리 —
  // 화면에 미리 ✓로 표시되지 않도록. 6/3 당일이 되면 자연스레 카운트됨)
  const isTodayOrPast = (dt) => dt <= todayMidnight;
  const allStats = ROSTER2.map((s) => {
    let morning, night;
    if (s.name === profile.name) {
      morning = ROSTER2_MORNING_DATES.filter(dt => isTodayOrPast(dt) && myAttSet.has(`M-${fk(dt)}`)).length;
      night   = ROSTER2_NIGHT_DATES.filter(dt => isTodayOrPast(dt) && myAttSet.has(`N-${fk(dt)}`)).length;
    } else {
      morning = classAttStats[s.name]?.morning || 0;
      night   = classAttStats[s.name]?.night   || 0;
    }
    // N(카페): 본인은 오늘까지 회차만, 그 외 학생은 classCertStats
    const n = s.name === profile.name
      ? [...myCertSessions].filter(i => isTodayOrPast(ROSTER2_NAVER_DATES[i])).length
      : (classCertStats[s.name] || 0);
    return {name: s.name, naver: n, morning, night, total: n + morning + night};
  });
  const myStat = myIdx >= 0 ? allStats[myIdx] : {naver:0, morning:0, night:0, total:0};
  const ranked = [...allStats].sort((a, b) => b.total - a.total);
  const myRank = myIdx >= 0 ? ranked.findIndex(s => s.name === profile.name) + 1 : null;
  const top10  = ranked.slice(0, 10);

  // 클래스 평균
  const avgNaver   = Math.round(allStats.reduce((s,x)=>s+x.naver,0)   / allStats.length);
  const avgMorning = Math.round(allStats.reduce((s,x)=>s+x.morning,0) / allStats.length);
  const avgNight   = Math.round(allStats.reduce((s,x)=>s+x.night,0)   / allStats.length);
  const avgTotal   = avgNaver + avgMorning + avgNight;

  // today / todayMidnight은 위에서 정의됨 (TDZ 회피)
  const weekStart = new Date(today); weekStart.setDate(today.getDate() - today.getDay());
  const thisWeek = Array.from({length:7}, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate()+i); return d; });

  // 주차별 달성률 (8주, 시작: 2026-05-17 일요일)
  const WEEK_START = new Date(2026, 4, 17);
  const NAVER_WEEK_START = new Date(2026, 4, 20); // 카페 인증 주 시작 (수)
  const currentWeekIdx = Math.min(7, Math.max(0, Math.floor((today - WEEK_START) / 604800000)));
  const weeklyData = Array.from({length:8}, (_, w) => {
    const allDates = [
      ...ROSTER2_NAVER_DATES.filter(dt => Math.floor((dt-NAVER_WEEK_START)/604800000)===w).map(dt=>({dt,type:"N"})),
      ...ROSTER2_MORNING_DATES.filter(dt => Math.floor((dt-WEEK_START)/604800000)===w).map(dt=>({dt,type:"M"})),
      ...ROSTER2_NIGHT_DATES.filter(dt => Math.floor((dt-WEEK_START)/604800000)===w).map(dt=>({dt,type:"나"})),
    ];
    const possible = allDates.length;
    // 전부 DB 기반 (카페=myCertSessions, 모닝/나잇=myAttSet)
    const done = allDates.filter(({dt,type}) => {
      if (type === "N") {
        const sessionIdx = ROSTER2_NAVER_DATES.findIndex(d => fk(d) === fk(dt));
        return sessionIdx >= 0 && myCertSessions.has(sessionIdx);
      }
      // type "M" = 모닝, type "나" = 나잇 → DB는 "N"
      const dbType = type === "나" ? "N" : "M";
      return myAttSet.has(`${dbType}-${fk(dt)}`);
    }).length;
    return {w:w+1, possible, done, pct: possible>0 ? Math.round(done/possible*100) : 0, isCurrent: w===currentWeekIdx};
  });

  // 목표까지 남은 횟수 (목표 80%)
  const TARGET_PCT = 80;
  const targetCount = Math.ceil(grandTotal * TARGET_PCT / 100);
  const remaining = Math.max(0, targetCount - myStat.total);

  const pct = (v, t) => t > 0 ? Math.round(v / t * 100) : 0;

  // 오늘 각 카테고리 상태 계산
  const todayKey = fk(today);
  const getActivityStatus = (dates, attendanceKey) => {
    const isScheduled = dates.some(dt => fk(dt) === todayKey);
    if (isScheduled) {
      let done;
      if (attendanceKey === "N") {
        // 카페 인증 — 회차 단위
        const sessionIdx = ROSTER2_NAVER_DATES.findIndex(dt => fk(dt) === todayKey);
        done = sessionIdx >= 0 && myCertSessions.has(sessionIdx);
      } else {
        // 모닝/나잇 — attendance_logs
        const dbType = attendanceKey === "나" ? "N" : "M";
        done = myAttSet.has(`${dbType}-${todayKey}`);
      }
      return done ? "완료" : "진행중";
    }
    const hasFuture = dates.some(dt => dt > today);
    return hasFuture ? "예정" : "종료";
  };
  const statusNaver   = getActivityStatus(ROSTER2_NAVER_DATES,   "N");
  const statusMorning = getActivityStatus(ROSTER2_MORNING_DATES, "M");
  const statusNight   = getActivityStatus(ROSTER2_NIGHT_DATES,   "나");

  const CATS = [
    {key:"morning", label:"미라클모닝",  icon:"sun",   iconBg:"#FFF0E6", v:myStat.morning, t:morningTotal, color:"#EA580C"},
    {key:"naver",   label:"카페 인증",   icon:"naver", iconBg:"#E8F5E9", v:myStat.naver,   t:naverTotal,   color:"#03C75A"},
    {key:"night",   label:"미라클나이트",icon:"moon",  iconBg:"#EEF0FF", v:myStat.night,   t:nightTotal,   color:"#6366F1"},
  ];

  // ── 멘토 인사이트 메시지 풀 (진행 회차 기반)
  // floor + cap 100 보수화 — pct()가 round 사용이면 99.5→100 함정 발생, 여기선 자체 계산
  const totalPct   = grandTotal > 0 ? Math.min(100, Math.floor(myStat.total / grandTotal * 100)) : 0;
  const weeksLeft  = Math.max(0, 7 - currentWeekIdx);
  const thisWeekPct = weeklyData[currentWeekIdx]?.pct ?? 0;

  // 오늘까지 진행된 회차 (오늘은 진행 중이라 제외 → 어제까지)
  const pastCount = (dates) => dates.filter(d => d < todayMidnight).length;
  const morningPast = pastCount(ROSTER2_MORNING_DATES);
  const naverPast   = pastCount(ROSTER2_NAVER_DATES);
  const nightPast   = pastCount(ROSTER2_NIGHT_DATES);
  const grandPast   = morningPast + naverPast + nightPast;

  // 진행 회차 한정 출석 수 — 미래 회차 자동 출석(예: 6/3 일정 변경 사전 처리)으로 done > past 되는 케이스 방지
  // 멘토/마스터리 메시지는 "어제까지 회차에 대한 출석"만 분모/분자로 사용해야 정확
  const morningDoneInPast = ROSTER2_MORNING_DATES.filter(dt => dt < todayMidnight && myAttSet.has(`M-${fk(dt)}`)).length;
  const naverDoneInPast   = [...myCertSessions].filter(i => i < naverPast).length;
  const nightDoneInPast   = ROSTER2_NIGHT_DATES.filter(dt => dt < todayMidnight && myAttSet.has(`N-${fk(dt)}`)).length;
  const totalDoneInPast   = morningDoneInPast + naverDoneInPast + nightDoneInPast;

  // 진행 회차 대비 달성률 (cap 100, floor로 보수화 — 99.5%가 100%로 반올림되는 함정 회피)
  const safePct = (done, past) => past > 0 ? Math.min(100, Math.floor(done / past * 100)) : 0;
  const totalPctPast = safePct(totalDoneInPast, grandPast);

  // 활동별 진행 회차 기반 달성률 — done은 doneInPast만 (미래 출석 미반영)
  const actPctsPast = [
    {label:"미라클모닝",   p: safePct(morningDoneInPast, morningPast), past: morningPast, done: morningDoneInPast, remaining: Math.max(0, morningTotal - myStat.morning), total: morningTotal},
    {label:"카페 인증",    p: safePct(naverDoneInPast,   naverPast),   past: naverPast,   done: naverDoneInPast,   remaining: Math.max(0, naverTotal   - myStat.naver),   total: naverTotal},
    {label:"미라클나이트", p: safePct(nightDoneInPast,   nightPast),   past: nightPast,   done: nightDoneInPast,   remaining: Math.max(0, nightTotal   - myStat.night),   total: nightTotal},
  ];
  const validActs = actPctsPast.filter(a => a.past > 0);
  const weakest   = validActs.length ? [...validActs].sort((a,b)=>a.p-b.p)[0] : null;
  const strongest = validActs.length ? [...validActs].sort((a,b)=>b.p-a.p)[0] : null;

  const mentorPool = [
    // 순위 — 동률 케이스 보수화 (1위 두 명일 때 "유일 1위" 표현 회피)
    ...(myRank===1 && ranked.length > 1 && ranked[1].total < myStat.total ? ["현재 클래스 1위예요. 이 페이스를 유지해보세요."] : []),
    ...(myRank===1 && ranked.length > 1 && ranked[1].total === myStat.total ? ["현재 공동 1위예요. 페이스를 잘 유지하고 있어요."] : []),
    ...(myRank===2                                    ? [`1위와 ${Math.max(0, ranked[0].total-myStat.total)}회 차이예요. 천천히 따라잡아 봐요.`] : []),
    ...(myRank===3                                    ? ["TOP 3 자리예요. 페이스 유지가 가장 중요해요."] : []),
    ...(myRank>3 && myRank<=Math.ceil(ROSTER2.length*.2) ? [`상위 20% 안에 있어요. 한 단계씩 천천히 올라가 봐요.`] : []),
    // 약점 활동 (진행 회차 기반, past>0인 것만) — 실제 미수행 회차가 있을 때만
    ...(weakest && weakest.p < 50 && (weakest.past - weakest.done) > 0 ? [`${weakest.label} 진행 ${weakest.past}회 중 ${weakest.done}회 (${weakest.p}%) — 다음 일정부터 조금씩 챙겨봐요.`] : []),
    ...(weakest && weakest.p >= 50 && weakest.p < 80 && (weakest.past - weakest.done) > 0 ? [`${J_eul(weakest.label)} ${weakest.past - weakest.done}회 더 챙기면 80%까지 갑니다. (현재 ${weakest.p}%)`] : []),
    // 강점 활동 — "100% 완벽" 조건: floor 후 정확히 100 + 정확 비교(done === past, past > 0)
    ...(strongest && strongest.past > 0 && strongest.done === strongest.past
        ? [`${strongest.label}은(는) 진행된 ${strongest.past}회를 모두 채웠어요. 이 페이스를 유지해보세요.`]
        : []),
    ...(strongest && strongest.p >= 80 && strongest.p < 100 && strongest.done < strongest.past
        ? [`${J_i(strongest.label)} ${strongest.past}회 중 ${strongest.done}회 (${strongest.p}%) — 루틴이 잘 잡혀있어요.`]
        : []),
    // 진행 대비 전체 달성률
    ...(grandPast===0                                 ? ["프로젝트가 막 시작했어요. 첫 일정부터 차근차근 채워봐요."] : []),
    ...(grandPast>0 && totalPctPast>=90 && totalDoneInPast < grandPast ? [`진행된 ${grandPast}회 중 ${totalDoneInPast}회 (${totalPctPast}%)를 채우고 있어요. 잘 따라가는 중이에요.`] : []),
    ...(grandPast>0 && totalDoneInPast === grandPast  ? [`진행된 ${grandPast}회를 모두 채웠어요. 이 페이스를 유지해보세요.`] : []),
    ...(grandPast>0 && totalPctPast>=70 && totalPctPast<90 ? [`진행된 일정의 ${totalPctPast}%를 채우고 있어요. 페이스가 안정적이에요.`] : []),
    ...(grandPast>0 && totalPctPast>=40 && totalPctPast<70 ? [`현재 ${totalPctPast}%예요. 한 회차씩 조금씩 채워가 봐요.`] : []),
    ...(grandPast>0 && totalPctPast>0 && totalPctPast<40    ? [`지금까지 ${totalDoneInPast}회를 채웠어요. 다음 한 회차가 루틴을 만들어요.`] : []),
    ...(grandPast>0 && totalPctPast===0               ? ["아직 첫 인증을 기다리고 있어요. 오늘 딱 하나만 해볼까요?"] : []),
    // 주차
    ...(currentWeekIdx<=1                             ? ["프로젝트 초반이에요. 지금 만드는 루틴이 8주를 결정해요."] : []),
    ...(currentWeekIdx>=2 && currentWeekIdx<=4        ? [`${currentWeekIdx+1}주차, 페이스가 잡힐 시기예요. 꾸준함이 답이에요.`] : []),
    ...(currentWeekIdx>=5 && weeksLeft>0              ? ["마지막 스퍼트 구간이에요. 지금 만드는 성과가 최종 결과예요."] : []),
    ...(weeksLeft===0                                 ? ["마지막 주예요. 오늘 하는 인증 하나하나가 기록이에요."] : []),
    // 이번 주 (활동 일정이 이번 주에 있을 때만)
    ...(thisWeekPct>=80 && weeklyData[currentWeekIdx]?.possible>0 ? [`이번 주 ${thisWeekPct}%! 이번 주 정말 잘하고 있어요. 🌟`] : []),
    // 클래스 비교
    ...(myStat.total>avgTotal*1.5 && avgTotal>0       ? ["클래스 평균의 1.5배 이상을 달성 중이에요. 탁월한 성과예요! ✨"] : []),
    ...(myStat.total>avgTotal && myStat.total<=avgTotal*1.5 && avgTotal>0 ? [`클래스 평균보다 ${myStat.total-avgTotal}회 앞서 있어요.`] : []),
    ...(myStat.total<avgTotal && avgTotal>0           ? [`평균까지 ${avgTotal-myStat.total}회예요. 오늘 조금 더 채워볼까요?`] : []),
    ...(myStat.total===avgTotal && avgTotal>0         ? ["클래스 평균 딱 그 자리에 있어요. 한 발 더 내딛어볼까요?"] : []),
  ];
  const mentorMsg = mentorPool.length > 0
    ? mentorPool[(myStat.total + myRank + currentWeekIdx) % mentorPool.length]
    : "꾸준히 인증하는 것 자체가 이미 대단한 일이에요.";

  // ── 마스터리 도전 메시지 풀 (행동 가이드 + 동기부여, 진행 회차 기반)
  const remainingTo100 = Math.max(0, grandTotal - myStat.total);
  const remainingTo80  = Math.max(0, Math.ceil(grandTotal * 0.8) - myStat.total);
  // 약점 활동의 진행 회차 중 놓친 횟수
  const weakestMissed = weakest ? Math.max(0, weakest.past - weakest.done) : 0;

  const masteryPool = [
    // 🏆 100% 완주
    ...(totalPct >= 100 ? [
      "🏆 100% 완주! 8주를 완벽하게 채웠어요.",
      "흠 잡을 데 없는 8주, 자기 통제력의 정점이에요. 🏅",
    ] : []),

    // 🎯 80~99% (목표 달성, 100% 챌린지)
    ...(totalPct >= 80 && totalPct < 100 ? [
      `🎯 목표 80% 달성! 100%까지 ${remainingTo100}회 — 완주에 도전해봐요.`,
      ...(weakest && weakest.p < 100
        ? [`${J_eul(weakest.label)} 마저 챙기면 100% — 마지막 한 발이에요. (현재 ${weakest.p}%)`]
        : []),
    ] : []),

    // 60~80% (목표 직전)
    ...(totalPct >= 60 && totalPct < 80 ? [
      `목표 80%까지 ${remainingTo80}회 — 손 닿을 거리예요.`,
      ...(weakest ? [`${J_eul(weakest.label)} 조금만 더 챙기면 목표 달성이에요. (현재 ${weakest.p}%)`] : []),
    ] : []),

    // 30~60% (페이스 안정 + 약점 보강)
    ...(totalPct >= 30 && totalPct < 60 ? [
      ...(weakest && weakest.p < 60 && weakestMissed > 0
        ? [`${weakest.label} ${weakest.p}% — 진행된 ${weakest.past}회 중 ${weakestMissed}회를 놓쳤어요. 다음부터 챙겨봐요.`]
        : []),
      `현재 진행된 일정의 ${totalPctPast}% 달성 — 페이스 잘 잡고 있어요. 마지막까지 꾸준히!`,
      ...(weakest && weakest.p >= 60 ? [`전체적으로 균형이 잡혀있어요. 이 페이스만 유지하면 충분해요.`] : []),
    ] : []),

    // 10~30% (따라잡기)
    ...(totalPct >= 10 && totalPct < 30 ? [
      ...(weakest && weakest.p === 0
        ? [`${weakest.label} 0회 — 다음 ${weakest.label} 일정부터 챙겨봐요. 가장 빠르게 따라잡을 수 있어요.`]
        : []),
      ...(weakest && weakest.p > 0 && weakest.p < 60
        ? [`${J_eun(weakest.label)} 가장 늦었어요. (${weakest.p}%) 여기에 집중하면 페이스가 살아나요.`]
        : []),
      `지금부터 매 회차를 챙기면 충분히 따라잡을 수 있어요.`,
    ] : []),

    // 1~10% (시작 직후)
    ...(totalPct > 0 && totalPct < 10 ? [
      "시작했어요. 작은 한 걸음이 8주를 만들어요.",
      ...(weakest && weakest.past > 0
        ? [`다음 ${weakest.label} 일정부터 챙겨봐요. 가장 빠르게 따라잡을 수 있어요.`]
        : []),
      ...(weakestMissed === 0 && weakest
        ? [`지금까지 챙긴 회차는 모두 완수! 다음 일정도 놓치지 말고 챙겨봐요.`]
        : []),
    ] : []),

    // 0회 (시작 전)
    ...(myStat.total === 0 && grandPast > 0 ? [
      "첫 인증이 가장 어려워요. 오늘 일정부터 하나만 도전!",
      "한 회차씩 쌓이는 게 결국 8주 결과예요. 지금 시작해볼까요?",
    ] : []),

    // 프로젝트 시작 전
    ...(grandPast === 0 ? [
      "프로젝트가 막 시작했어요. 첫 회차부터 차근차근 채워봐요.",
    ] : []),

    // 마지막 주 미완
    ...(weeksLeft === 0 && totalPct < 100 ? [
      `마지막 주, 100%까지 ${remainingTo100}회 — 끝까지 달려요!`,
    ] : []),
  ];
  const masteryMsg = masteryPool.length > 0
    ? masteryPool[(myStat.total + currentWeekIdx) % masteryPool.length]
    : "한 회차씩 차근차근 챙기면 8주가 채워져요.";

  return (
    <div style={{display:"grid", gap:12}}>

      {/* ① 히어로 카드 — Stitch 스타일 */}
      <div style={{borderRadius:20, background:"linear-gradient(135deg,#0D1340 0%,#141B5C 50%,#1E2878 100%)", padding:"28px 32px", color:"#fff", position:"relative", overflow:"hidden"}}>
        {/* 배경 장식 */}
        <div style={{position:"absolute",top:-60,right:-60,width:240,height:240,borderRadius:"50%",background:"rgba(255,255,255,0.03)",pointerEvents:"none"}}/>
        <div style={{position:"absolute",bottom:-80,right:"30%",width:300,height:300,borderRadius:"50%",background:"rgba(28,40,120,0.5)",pointerEvents:"none"}}/>

        <div style={{position:"relative"}}>
          {/* Academic Progress 배지 */}
          <div style={{display:"inline-block",background:"#fc9024",borderRadius:20,padding:"4px 14px",fontSize:11,fontWeight:700,color:"#fff",marginBottom:14,letterSpacing:"0.02em"}}>
            20HA 2기 진행 중
          </div>

          {/* 제목 + 순위 */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
            <div style={{flex:1}}>
              <div style={{fontSize:22,fontWeight:900,color:"#ffffff",lineHeight:1.3,marginBottom:10}}>
                나의 20HA 인증 현황
              </div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.75,maxWidth:480}}>
                8주 동안 미라클모닝·카페 인증·미라클나이트를 실천하는 자기계발 챌린지.<br/>
                매일의 작은 실천이 쌓여 진짜 변화를 만들어요.
              </div>
            </div>
            {/* 순위 */}
            <div style={{marginLeft:24,flexShrink:0,textAlign:"center",background:"rgba(255,255,255,0.08)",borderRadius:14,padding:"12px 20px",border:"1px solid rgba(255,255,255,0.18)"}}>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.7)",fontWeight:600,letterSpacing:"0.06em",marginBottom:6}}>클래스 순위</div>
              <div style={{display:"flex",alignItems:"baseline",gap:3,justifyContent:"center"}}>
                <span style={{fontSize:36,fontWeight:900,color:"#fc9024",lineHeight:1}}>{myRank ?? "—"}</span>
                <span style={{fontSize:14,color:"rgba(255,255,255,0.75)"}}>위</span>
              </div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.55)",marginTop:4}}>/ {ROSTER2.length}명 중</div>
            </div>
          </div>

          {/* 진행 바 + % */}
          <div style={{display:"flex",alignItems:"center",gap:16,marginTop:18}}>
            <div style={{flex:1,position:"relative"}}>
              <div style={{height:8,background:"rgba(255,255,255,0.15)",borderRadius:4,position:"relative"}}>
                {/* 진행 채우기 (왼쪽부터) */}
                <div style={{height:"100%",width:`${totalPct}%`,background:"linear-gradient(90deg,#fc9024,#ffb77e)",borderRadius:4,transition:"width 1s ease"}}/>
                {/* 80% 목표 마커 — 강조 */}
                <div style={{position:"absolute",left:"80%",top:-4,bottom:-4,width:3,background:"#fff",zIndex:2,boxShadow:"0 0 8px rgba(255,255,255,0.7)",borderRadius:2}}/>
              </div>
              {/* 라벨: 0% / 🎯 목표 80% (80% 위치) / 100% */}
              <div style={{position:"relative",marginTop:8,height:14,fontSize:10,color:"rgba(255,255,255,0.5)"}}>
                <span style={{position:"absolute",left:0}}>0%</span>
                <span style={{position:"absolute",left:"80%",transform:"translateX(-50%)",color:"#fff",fontWeight:800,fontSize:11,whiteSpace:"nowrap"}}>🎯 목표 80%</span>
                <span style={{position:"absolute",right:0}}>100%</span>
              </div>
            </div>
            <div style={{flexShrink:0,textAlign:"right"}}>
              <div style={{fontSize:22,fontWeight:900,color:"#fff",lineHeight:1}}>{totalPct}%</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.6)",marginTop:4}}>{myStat.total} / {grandTotal}회</div>
            </div>
          </div>
        </div>
      </div>

      {/* ② 항목별 3칸 — Stitch 스타일 카드 */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8}}>
        {CATS.map(({label, icon, iconBg, v, t, color}) => (
          <div key={label} style={{borderRadius:16, background:"#FFFFFF", border:"1px solid #E2E6F3",
            padding:"20px 14px 16px", boxShadow:"0 2px 8px rgba(25,29,84,0.06)",
            display:"flex", flexDirection:"column", alignItems:"center", gap:10, textAlign:"center"}}>
            {/* 아이콘 원형 */}
            <div style={{width:60, height:60, borderRadius:"50%", background:iconBg,
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
              {icon === "naver" ? (
                <div style={{width:30, height:30, background:"#03C75A", borderRadius:6,
                  display:"flex", alignItems:"center", justifyContent:"center"}}>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
                    <path d="M16.273 12.845L7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727z"/>
                  </svg>
                </div>
              ) : (
                HI[icon]?.(28, color) || <span style={{fontSize:26}}>{icon}</span>
              )}
            </div>
            {/* 제목 */}
            <div style={{fontSize:14, color:"#8B91C0", fontWeight:500, letterSpacing:"0.02em"}}>{label}</div>
            {/* 진행 바 */}
            <div style={{width:"100%", padding:"0 6px"}}>
              <div style={{height:8, background:`${color}22`, borderRadius:6, overflow:"hidden"}}>
                <div style={{height:"100%", width:`${pct(v,t)}%`, background:color, borderRadius:6, transition:"width 0.8s ease"}}/>
              </div>
            </div>
            {/* 횟수 */}
            <div style={{fontSize:15, fontWeight:700, color:"#191D54"}}>{v} / {t}회</div>
          </div>
        ))}
      </div>

      {/* ③ 8주 전체 일정 */}
      {(()=>{
        const myGrade = calcGrade(profile.birth_year, profile.birth_month) || profile.grade || "";
        // 학년 → 학교급 그룹 변환 ("초N"→"초등부", "중N"→"중등부", "고N"→"고등부")
        const gradeGroup = (g) => {
          if (!g) return "";
          if (g.startsWith("초")) return "초등부";
          if (g.startsWith("중")) return "중등부";
          if (g.startsWith("고")) return "고등부";
          return "";
        };
        const myGroup = gradeGroup(myGrade);
        const sameGroupRanked = myGroup && Object.keys(peerGrades).length>0
          ? [...allStats].filter(s=>gradeGroup(peerGrades[s.name]||"")===myGroup).sort((a,b)=>b.total-a.total).slice(0,3)
          : [];
        const meInTop3 = ranked.slice(0,3).some(s => s.name === profile.name);
        const meInGroupTop3 = sameGroupRanked.some(s => s.name === profile.name);
        const ACTIVITIES = [
          {label:"미라클모닝", dates:ROSTER2_MORNING_DATES, type:"M",   color:"#EA580C"},
          {label:"카페 인증",  dates:ROSTER2_NAVER_DATES,   type:"N",   color:"#03C75A"},
          {label:"미라클나이트",dates:ROSTER2_NIGHT_DATES,   type:"나",  color:"#6366F1"},
        ];
        const RankRow = ({s, rank, isMe, highlightFirst}) => {
          const medalColors=["#F59E0B","#94A3B8","#B87333"];
          const goldHL = rank===0 && highlightFirst;
          return(
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:9,
              background:isMe?"#EEF2FF":goldHL?"#FFFBEB":"transparent",
              border:`1px solid ${isMe?"#C7D2FE":goldHL?"#FDE68A":"transparent"}`}}>
              <span style={{width:24,height:24,borderRadius:6,background:medalColors[rank]||"#CBD5E1",
                display:"inline-flex",alignItems:"center",justifyContent:"center",
                fontSize:11,fontWeight:900,color:"#fff",flexShrink:0}}>{rank+1}</span>
              <div style={{flex:1,fontSize:13,fontWeight:isMe?800:500,color:T.navy,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {s.name}{isMe&&<span style={{fontSize:9,color:"#4F46E5",marginLeft:3}}>(나)</span>}
              </div>
              <div style={{fontSize:12,fontWeight:800,color:isMe?"#4F46E5":goldHL?T.orange:T.navy,flexShrink:0}}>{pct(s.total,grandTotal)}%</div>
            </div>
          );
        };
        return(<>
          <Card style={{padding:"16px 18px"}}>
            <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
              {HI.calendar(15,T.navy)} 8주 전체 일정
            </div>
            {/* 주차 × 활동 히트맵 그리드 */}
            {(()=>{
              const cell = isMobile ? 18 : 22;
              const labelW = isMobile ? 66 : 80;
              // 주차별 시작 날짜 계산
              const weekStarts = Array.from({length:8}, (_,i) => new Date(WEEK_START.getTime()+i*7*86400000));
              return(
                <div style={{position:"relative"}}>
                  <div style={{overflowX:"auto", WebkitOverflowScrolling:"touch"}}>
                    <table style={{borderCollapse:"separate",borderSpacing:`${isMobile?2:3}px 0`,minWidth: labelW + 8*(cell+3)}}>
                      <thead>
                        {/* 월 표시 행 */}
                        <tr>
                          <td style={{width:labelW}}/>
                          {weekStarts.map((ws,i)=>{
                            const prevWs = i>0 ? weekStarts[i-1] : null;
                            const showMonth = i===0 || ws.getMonth() !== prevWs.getMonth();
                            return(
                              <td key={i} style={{textAlign:"center",fontSize:9,color:T.muted,fontWeight:600,
                                paddingTop:4,paddingBottom:2,whiteSpace:"nowrap",
                                background: currentWeekIdx===i ? "rgba(246,139,30,0.07)" : "transparent",
                                borderRadius: currentWeekIdx===i ? "8px 8px 0 0" : 0}}>
                                {showMonth ? `${ws.getMonth()+1}월` : ""}
                              </td>
                            );
                          })}
                        </tr>
                        {/* 주차 헤더 */}
                        <tr>
                          <td style={{width:labelW,paddingBottom:8}}/>
                          {Array.from({length:8},(_,i)=>(
                            <td key={i} style={{textAlign:"center",paddingBottom:8,paddingTop:0,
                              background: currentWeekIdx===i ? "rgba(246,139,30,0.07)" : "transparent"}}>
                              <span style={{
                                color: currentWeekIdx===i ? T.orange : T.muted,
                                fontWeight: currentWeekIdx===i ? 800 : 600,
                                fontSize: isMobile ? 9 : 10,
                              }}>{i+1}주</span>
                            </td>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ACTIVITIES.map(({label,dates,type,color}, actIdx)=>{
                          const isLast = actIdx === ACTIVITIES.length - 1;
                          return(
                          <tr key={label}>
                            <td style={{fontSize:isMobile?10:11,fontWeight:700,color,paddingRight:6,whiteSpace:"nowrap",verticalAlign:"middle",
                              paddingTop: actIdx===0?2:4, paddingBottom: isLast?6:4}}>
                              ● {label}
                            </td>
                            {weekStarts.map((wStart,wIdx)=>{
                              // 카페 인증(N)은 5/20 수요일 기준으로 주 분할 (수~화), 모닝/나잇은 일요일 기준
                              const actualWStart = type==="N"
                                ? new Date(NAVER_WEEK_START.getTime()+wIdx*7*86400000)
                                : wStart;
                              const wEnd=new Date(actualWStart.getTime()+7*86400000);
                              const wDates=[...dates].filter(dt=>dt>=actualWStart&&dt<wEnd).sort((a,b)=>a-b);
                              const isCurrentWeek = wIdx===currentWeekIdx;
                              return(
                                <td key={wIdx} style={{verticalAlign:"middle",
                                  paddingTop: actIdx===0?2:4, paddingBottom: isLast?6:4,
                                  paddingLeft:2, paddingRight:2,
                                  background: isCurrentWeek ? "rgba(246,139,30,0.07)" : "transparent",
                                  borderRadius: isCurrentWeek && isLast ? "0 0 8px 8px" : 0}}>
                                  <div style={{display:"flex",flexWrap:"wrap",gap:2,justifyContent:"center"}}>
                                    {wDates.length===0
                                      ? <div style={{width:cell,height:cell,borderRadius:4,background:T.surfaceAlt,border:`1px solid ${T.border}`}}/>
                                      : wDates.map((dt,i)=>{
                                          const dk=fk(dt);
                                          let done, underMin = false, mins = null;
                                          if (type === "N") {
                                            const sessionIdx = ROSTER2_NAVER_DATES.findIndex(d => fk(d) === dk);
                                            done = sessionIdx >= 0 && myCertSessions.has(sessionIdx);
                                          } else {
                                            const dbType = type === "나" ? "N" : "M";
                                            done    = myAttSet.has(`${dbType}-${dk}`);
                                            underMin= myAttUnderSet.has(`${dbType}-${dk}`);
                                            mins    = myAttMinutes.get(`${dbType}-${dk}`);
                                          }
                                          const isPast=dt<todayMidnight;
                                          const missed = isPast && !done && !underMin;
                                          const minThr = type === "나" ? MIN_ATTENDANCE_MINUTES.N : type === "M" ? MIN_ATTENDANCE_MINUTES.M : null;
                                          const status = done ? "완료" : underMin ? "참여시간미달" : missed ? "미완료" : "예정";
                                          const tipTypeLabel = type === "M" ? "미라클모닝" : type === "나" ? "미라클나이트" : "카페 인증";
                                          const tipLines = [
                                            { label: `${dt.getMonth()+1}/${dt.getDate()} · ${tipTypeLabel}`, color: color },
                                            ...(type !== "N" ? [{ label: `상태: ${status}`, color: done ? "#16A34A" : underMin ? "#9A3412" : missed ? "#B91C1C" : T.muted }] : []),
                                            ...(mins != null ? [{ label: `참여 시간: ${mins}분`, color: T.navy }] : []),
                                            ...(underMin && minThr ? [{ label: `(인정 기준 ${minThr}분 미만)`, color: "#9A3412" }] : []),
                                          ];
                                          const showTip = (e) => setAttTip({ x: e.clientX, y: e.clientY, lines: tipLines });
                                          const moveTip = (e) => setAttTip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
                                          const hideTip = () => setAttTip(null);
                                          return(
                                            <div key={i}
                                              onMouseEnter={showTip} onMouseMove={moveTip} onMouseLeave={hideTip}
                                              style={{
                                                width:cell,height:cell,borderRadius:4,
                                                background:done?color:underMin?"#FED7AA":missed?"transparent":"#F0F2FA",
                                                border:`1px solid ${done?color:underMin?"#FB923C":missed?`${color}35`:"#E2E6F3"}`,
                                                display:"flex",alignItems:"center",justifyContent:"center",
                                                flexShrink:0, position:"relative", overflow:"hidden", cursor:"pointer"
                                              }}>
                                              {done && (
                                                <span style={{fontSize:isMobile?7:8,fontWeight:700,color:"#fff"}}>{dt.getDate()}</span>
                                              )}
                                              {underMin && (
                                                <span style={{fontSize:isMobile?7:8,fontWeight:700,color:"#9A3412"}}>{dt.getDate()}</span>
                                              )}
                                              {missed && (<>
                                                {/* 사선 */}
                                                <div style={{position:"absolute",top:"50%",left:"-5%",width:"110%",height:"1.5px",
                                                  background:`${color}60`,transform:"rotate(-45deg)",transformOrigin:"center"}}/>
                                                <span style={{fontSize:isMobile?7:8,fontWeight:400,color:`${color}70`,zIndex:1}}>{dt.getDate()}</span>
                                              </>)}
                                              {!done && !missed && !underMin && (
                                                <span style={{fontSize:isMobile?7:8,fontWeight:700,color:"#C8CEED"}}>{dt.getDate()}</span>
                                              )}
                                            </div>
                                          );
                                        })
                                    }
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* 모바일 스크롤 힌트 페이드 */}
                  {isMobile && (
                    <div style={{position:"absolute",top:0,right:0,bottom:0,width:24,background:"linear-gradient(to right,transparent,#fff)",pointerEvents:"none"}}/>
                  )}
                </div>
              );
            })()}
            {/* 범례 */}
            <div style={{display:"flex",gap:14,marginTop:12,flexWrap:"wrap",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:T.muted}}>
                <div style={{width:12,height:12,borderRadius:3,background:"#03C75A"}}/> 완료
              </div>
              <div style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:T.muted}}>
                <div style={{width:12,height:12,borderRadius:3,border:"1px solid #C8CEED",position:"relative",overflow:"hidden"}}>
                  <div style={{position:"absolute",top:"50%",left:"-5%",width:"110%",height:"1.5px",background:"#94A3B8",transform:"rotate(-45deg)",transformOrigin:"center"}}/>
                </div> 미완료
              </div>
              <div style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:T.muted}}>
                <div style={{width:12,height:12,borderRadius:3,background:"#FED7AA",border:"1px solid #FB923C"}}/> 참여시간미달
              </div>
              <div style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:T.muted}}>
                <div style={{width:12,height:12,borderRadius:3,background:"#F0F2FA",border:"1px solid #E2E6F3"}}/> 예정
              </div>
              <div style={{marginLeft:"auto",fontSize:10,color:T.muted,opacity:0.6}}>
                ⚠️ 인증 데이터는 실시간으로 반영되지 않을 수 있습니다.
              </div>
            </div>
          </Card>

          {/* ③-2 카페 인증 현황 (회차별 점수) */}
          <Card style={{padding:"16px 18px"}}>
            <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:14,display:"flex",alignItems:"center",gap:6}}>
              📝 나의 카페 인증 현황
              <span style={{fontSize:10,fontWeight:400,color:T.muted}}>· 회차별 점수 / 채점 상태</span>
            </div>
            {(()=>{
              // 회차별로 본인 cert 매칭 (지각도 해당 회차에 포함).
              // 관리자 수동 회차(session_override)를 반영하도록 certSessionNums 사용.
              const sessionMap = {};
              ROSTER2_NAVER_DATES.forEach((dt, idx) => {
                sessionMap[idx] = {date: dt, cert: null, isLate: false};
              });
              myCafeCerts.forEach(c => {
                certSessionNums(c).forEach(n => {
                  const idx = n - 1;
                  if (idx < 0 || idx >= ROSTER2_NAVER_DATES.length) return;
                  if (!sessionMap[idx].cert) {
                    sessionMap[idx].cert = c;
                    sessionMap[idx].isLate = isLateByDeadline(c.posted_at, ROSTER2_NAVER_DATES[idx]);
                  }
                });
              });

              const cols = isMobile ? 3 : 5;
              return (
                <div style={{display:"grid",gridTemplateColumns:`repeat(${cols},1fr)`,gap:8}}>
                  {ROSTER2_NAVER_DATES.map((dt, idx) => {
                    const sm = sessionMap[idx];
                    const cert = sm.cert;
                    const isLate = sm.isLate;
                    const hasScore = cert && cert.completeness_score !== null && cert.completeness_score !== undefined;
                    const submitted = !!cert;
                    const total = hasScore ? Number(cert.completeness_score) : null;
                    const isLow = total !== null && total < 80;
                    const dateLabel = `${dt.getMonth()+1}/${dt.getDate()}(${ROSTER2_DAY_KO[dt.getDay()]})`;
                    return (
                      <div key={idx} style={{
                        padding:"10px 8px",
                        borderRadius:10,
                        border:`1px solid ${isLow?"#FECACA":hasScore?"#86EFAC":submitted?"#FCD34D":"#E2E6F3"}`,
                        background:isLow?"#FEF2F2":hasScore?"#F0FDF4":submitted?"#FFFBEB":"#F8FAFC",
                        textAlign:"center",
                      }}>
                        {/* 1행: 회차 + 날짜 */}
                        <div style={{display:"flex",justifyContent:"center",alignItems:"baseline",gap:4,marginBottom:6}}>
                          <span style={{fontSize:11,fontWeight:800,color:T.navy}}>{idx+1}회</span>
                          <span style={{fontSize:10,color:T.muted}}>{dateLabel}</span>
                        </div>
                        {/* 2행: 상태/점수 (3분할 + 종합) */}
                        {hasScore ? (
                          <div>
                            <div style={{fontSize:9,color:T.muted,lineHeight:1.5,marginBottom:4}}>
                              <div>제출 {cert.submit_score ?? "—"} / 미션 {cert.mission_score ?? "—"}</div>
                              <div>충실도 {cert.fidelity_score ?? "—"}</div>
                            </div>
                            <div style={{fontSize:18,fontWeight:900,color:isLow?"#DC2626":"#16A34A",lineHeight:1}}>
                              {total}<span style={{fontSize:10,fontWeight:600,marginLeft:2}}>점</span>
                            </div>
                            {isLate && <div style={{fontSize:9,color:"#B45309",marginTop:3,fontWeight:600}}>⚠️ 지각</div>}
                          </div>
                        ) : submitted ? (
                          <div>
                            <div style={{fontSize:11,fontWeight:800,color:"#B45309"}}>제출 완료</div>
                            <div style={{fontSize:9,color:T.muted,marginTop:2}}>채점 중...</div>
                            {isLate && <div style={{fontSize:9,color:"#B45309",marginTop:2,fontWeight:600}}>⚠️ 지각</div>}
                          </div>
                        ) : (
                          <div style={{fontSize:11,color:T.muted,fontWeight:600}}>미제출</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {/* 점수 가이드라인 */}
            <div style={{marginTop:14,padding:"12px 14px",borderRadius:8,background:"#F8FAFC",border:`1px solid ${T.border}`}}>
              <div style={{fontSize:11,fontWeight:800,color:T.navy,marginBottom:8}}>📌 점수 가이드라인 (총 100점)</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3, 1fr)",gap:8,fontSize:10,color:T.muted,lineHeight:1.5}}>
                <div>
                  <span style={{fontWeight:700,color:T.navy}}>① 제출 (50점)</span><br/>
                  인증 주기(수/일) 내 제출 — 제출 50점 / 미제출 0점
                </div>
                <div>
                  <span style={{fontWeight:700,color:T.navy}}>② 필수 미션 (30점)</span><br/>
                  주차별 필수 미션 — 모두 포함 30 / 일부 포함 15
                </div>
                <div>
                  <span style={{fontWeight:700,color:T.navy}}>③ 내용 충실도 (20점)</span><br/>
                  학습 과정·결과의 구체성과 성실도 — 매우우수 20 / 우수 15 / 보통 10 / 미흡 5
                </div>
              </div>
            </div>
          </Card>

          {/* ③-3 카페 인증 점수 그래프 */}
          <Card style={{padding:"16px 18px"}}>
            {(()=>{
              const kstStr = ts => {
                const k = new Date(new Date(ts).getTime() + 9*3600*1000);
                return `${k.getUTCFullYear()}-${String(k.getUTCMonth()+1).padStart(2,'0')}-${String(k.getUTCDate()).padStart(2,'0')}`;
              };
              // 회차별 본인 점수 매핑 (지각 포함, 다중 글은 최고점)
              const myBySession = {};
              myCafeCerts.forEach(c => {
                const postStr = kstStr(c.posted_at);
                let sn = null;
                for (let i = ROSTER2_NAVER_DATES.length - 1; i >= 0; i--) {
                  if (postStr >= fk(ROSTER2_NAVER_DATES[i])) { sn = i+1; break; }
                }
                if (!sn) return;
                const sc = c.completeness_score;
                if (sc === null || sc === undefined) return;
                if (myBySession[sn] === undefined || Number(sc) > myBySession[sn]) {
                  myBySession[sn] = Number(sc);
                }
              });
              const myScores = Object.values(myBySession);
              const myAvg = myScores.length ? myScores.reduce((a,b)=>a+b,0)/myScores.length : null;
              const myHigh = myScores.length ? Math.max(...myScores) : null;
              const myLow  = myScores.length ? Math.min(...myScores) : null;
              const safeCount = myScores.filter(s => s>=80).length;
              const classAvgAll = Object.values(classSessionAvgs);
              const classAvgOverall = classAvgAll.length ? classAvgAll.reduce((a,b)=>a+b,0)/classAvgAll.length : null;
              const avgDiff = (myAvg!==null && classAvgOverall!==null) ? Math.round((myAvg - classAvgOverall) * 10) / 10 : null;
              const chartData = ROSTER2_NAVER_DATES.map((dt, idx) => {
                const sn = idx+1;
                return {
                  name: `${sn}회`,
                  date: `${dt.getMonth()+1}/${dt.getDate()}`,
                  myScore: myBySession[sn] ?? null,
                  classAvg: classSessionAvgs[sn] ?? null,
                };
              });
              const ChartTooltip = ({active, payload, label}) => {
                if (!active || !payload || !payload.length) return null;
                const me = payload.find(p=>p.dataKey==="myScore")?.value;
                const cls = payload.find(p=>p.dataKey==="classAvg")?.value;
                const d = chartData.find(x=>x.name===label);
                return (
                  <div style={{background:T.white,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 12px",fontSize:11,boxShadow:"0 4px 12px rgba(0,0,0,0.08)"}}>
                    <div style={{fontWeight:800,color:T.navy,marginBottom:4}}>{label} {d&&<span style={{color:T.muted,fontWeight:500}}>({d.date})</span>}</div>
                    <div style={{color:"#16A34A",fontWeight:700}}>내 점수: {me!==null&&me!==undefined?`${me}점`:"미제출"}</div>
                    <div style={{color:T.muted}}>전체 평균: {cls!==null&&cls!==undefined?`${cls}점`:"-"}</div>
                  </div>
                );
              };

              return (
              <>
                <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:12,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                  📊 카페 인증 점수 그래프
                  <span style={{fontSize:10,fontWeight:400,color:T.muted}}>· 회차별 내 점수 vs 전체 평균</span>
                </div>

                {/* 요약 미니 카드 5개 */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(5, 1fr)",gap:8,marginBottom:14}}>
                  <div style={{padding:"8px 10px",borderRadius:8,background:"#F0FDF4",border:"1px solid #BBF7D0",textAlign:"center"}}>
                    <div style={{fontSize:10,color:"#15803D",fontWeight:700}}>📈 내 누적 평균</div>
                    <div style={{fontSize:16,fontWeight:900,color:"#16A34A",marginTop:2}}>
                      {myAvg!==null ? `${Math.round(myAvg*10)/10}점` : "—"}
                    </div>
                  </div>
                  <div style={{padding:"8px 10px",borderRadius:8,background:"#EFF6FF",border:"1px solid #BFDBFE",textAlign:"center"}}>
                    <div style={{fontSize:10,color:"#1D4ED8",fontWeight:700}}>🎯 80점 이상</div>
                    <div style={{fontSize:16,fontWeight:900,color:"#2563EB",marginTop:2}}>
                      {myScores.length>0 ? `${safeCount}/${myScores.length}회` : "—"}
                    </div>
                  </div>
                  <div style={{padding:"8px 10px",borderRadius:8,background:"#FEF3C7",border:"1px solid #FDE68A",textAlign:"center"}}>
                    <div style={{fontSize:10,color:"#92400E",fontWeight:700}}>⭐ 내 최고/최저 점수</div>
                    <div style={{fontSize:13,fontWeight:900,color:"#B45309",marginTop:2}}>
                      {myHigh!==null ? `${myHigh}/${myLow}점` : "—"}
                    </div>
                  </div>
                  <div style={{padding:"8px 10px",borderRadius:8,background:"#F8FAFC",border:`1px solid ${T.border}`,textAlign:"center"}}>
                    <div style={{fontSize:10,color:T.muted,fontWeight:700}}>👥 전체 누적 평균</div>
                    <div style={{fontSize:16,fontWeight:900,color:T.navy,marginTop:2}}>
                      {classAvgOverall!==null ? `${Math.round(classAvgOverall*10)/10}점` : "—"}
                    </div>
                  </div>
                  <div style={{padding:"8px 10px",borderRadius:8,
                    background: avgDiff===null?"#F8FAFC": avgDiff>=0?"#F0FDF4":"#FEE2E2",
                    border:`1px solid ${avgDiff===null?T.border: avgDiff>=0?"#BBF7D0":"#FECACA"}`,textAlign:"center"}}>
                    <div style={{fontSize:10,color:avgDiff===null?T.muted: avgDiff>=0?"#15803D":"#991B1B",fontWeight:700}}>📊 전체 평균 대비</div>
                    <div style={{fontSize:16,fontWeight:900,color:avgDiff===null?T.muted: avgDiff>=0?"#16A34A":"#DC2626",marginTop:2}}>
                      {avgDiff===null ? "—" : `${avgDiff>=0?"+":""}${avgDiff}점`}
                    </div>
                  </div>
                </div>

                {/* 그래프 */}
                <ResponsiveContainer width="100%" height={isMobile?240:280}>
                  <ComposedChart data={chartData} margin={{top:20,right:84,left:-8,bottom:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB"/>
                    <XAxis dataKey="name" tick={{fontSize:10,fill:T.muted}} interval={0}/>
                    <YAxis domain={[0,100]} tick={{fontSize:10,fill:T.muted}} ticks={[0,20,40,60,80,100]}/>
                    <Tooltip content={<ChartTooltip/>}/>
                    {(()=>{
                      // 두 기준선 라벨이 겹치지 않도록 dy 자동 분리
                      // myAvg가 80 위면 myAvg 위쪽·80 아래쪽 / myAvg가 80 아래면 반대
                      const myAvgVal = myAvg;
                      const aboveOrEq = myAvgVal !== null && myAvgVal >= 80;
                      const safeDy   = aboveOrEq ? 12 : -4;   // 80 라벨
                      const myAvgDy  = aboveOrEq ? -4 : 12;   // 내 평균 라벨
                      return (
                        <>
                          <ReferenceLine y={80} stroke="#DC2626" strokeDasharray="4 4" strokeWidth={1.5}
                            label={{value:"안전선(80)", position:"right", fontSize:10, fill:"#DC2626", dy:safeDy, dx:6}}/>
                          {myAvgVal!==null && (
                            <ReferenceLine y={myAvgVal} stroke="#2563EB" strokeDasharray="6 3" strokeWidth={1.5}
                              label={{value:`내 누적 평균(${Math.round(myAvgVal*10)/10})`, position:"right", fontSize:10, fill:"#2563EB", dy:myAvgDy, dx:6}}/>
                          )}
                        </>
                      );
                    })()}
                    <Bar dataKey="myScore" name="내 점수" radius={[6,6,0,0]} barSize={isMobile?10:14}>
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={entry.myScore===null ? "#E5E7EB" : entry.myScore < 80 ? "#DC2626" : "#16A34A"}/>
                      ))}
                    </Bar>
                    <Line type="monotone" dataKey="classAvg" name="전체 평균" stroke="#94A3B8" strokeWidth={2}
                      dot={{r:3,fill:"#94A3B8",strokeWidth:0}} activeDot={{r:5}} connectNulls={false}/>
                  </ComposedChart>
                </ResponsiveContainer>
                <div style={{display:"flex",justifyContent:"center",gap:14,fontSize:10,color:T.muted,marginTop:6,flexWrap:"wrap"}}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                    <span style={{display:"inline-block",width:10,height:10,background:"#16A34A",borderRadius:2}}/>내 점수 (80 이상)
                  </span>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                    <span style={{display:"inline-block",width:10,height:10,background:"#DC2626",borderRadius:2}}/>내 점수 (80 미만)
                  </span>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                    <span style={{display:"inline-block",width:14,height:2,background:"#94A3B8"}}/>전체 평균 (회차별)
                  </span>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                    <span style={{display:"inline-block",width:14,borderTop:"2px dashed #2563EB"}}/>내 누적 평균
                  </span>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                    <span style={{display:"inline-block",width:14,borderTop:"2px dashed #DC2626"}}/>80점 안전선
                  </span>
                </div>
              </>);
            })()}
          </Card>

          {/* ④ 랭킹 2열 */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Card style={{padding:"14px 16px"}}>
              <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:10,display:"flex",alignItems:"center",gap:5}}>{HI.trophy(14,T.navy)} 전체 BEST 3</div>
              <div style={{display:"grid",gap:5}}>
                {ranked.slice(0,3).map((s,rank)=>(
                  <RankRow key={rank} s={s} rank={rank} isMe={s.name===profile.name} highlightFirst={meInTop3}/>
                ))}
              </div>
            </Card>
            <Card style={{padding:"14px 16px"}}>
              <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:10,display:"flex",alignItems:"center",gap:5}}>
                {HI.cap(14,T.navy)} {myGroup ? `${myGroup} BEST 3` : "BEST 3"}
              </div>
              {sameGroupRanked.length===0 ? (
                <div style={{fontSize:11,color:T.muted,textAlign:"center",padding:"16px 0"}}>
                  {Object.keys(peerGrades).length===0?"학년 정보 로딩 중...":"같은 그룹 데이터 없음"}
                </div>
              ) : (
                <div style={{display:"grid",gap:5}}>
                  {sameGroupRanked.map((s,rank)=>(
                    <RankRow key={rank} s={s} rank={rank} isMe={s.name===profile.name} highlightFirst={meInGroupTop3}/>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>);
      })()}

      {/* ⑤ 멘토 인사이트 + 마스터리 도전 — 2열 */}
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8}}>
        <div style={{borderRadius:14, background:"#1E2255", padding:"16px 18px", color:"#fff"}}>
          <div style={{fontSize:13, fontWeight:700, marginBottom:6, display:"flex", alignItems:"center", gap:5}}>
            {HI.bell(13,"rgba(255,255,255,0.9)")} 멘토 인사이트
          </div>
          <div style={{fontSize:12, opacity:0.8, lineHeight:1.8}}>{mentorMsg}</div>
        </div>
        <div style={{borderRadius:14, background:"linear-gradient(135deg,#F68B1E,#FFA94D)", padding:"16px 18px", color:"#fff"}}>
          <div style={{fontSize:13, fontWeight:700, marginBottom:6, display:"flex", alignItems:"center", gap:5}}>
            {HI.trophy(13,"rgba(255,255,255,0.9)")} 마스터리 도전!
          </div>
          <div style={{fontSize:12, opacity:0.9, lineHeight:1.8, marginBottom:6}}>{masteryMsg}</div>
          <div style={{fontSize:11, opacity:0.7}}>전체 {grandTotal}회 중 {myStat.total}회 완료</div>
        </div>
      </div>

      {/* 출석 셀 hover tooltip — 즉시 표시 + ChartTip 스타일 */}
      {attTip && (
        <div style={{
          position:"fixed",
          left: Math.min(window.innerWidth - 240, attTip.x + 14),
          top:  Math.max(8, attTip.y - 12),
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 11,
          boxShadow: "0 4px 14px rgba(25,29,84,0.18)",
          zIndex: 10000,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}>
          {attTip.lines.map((l,i) => (
            <div key={i} style={{color: l.color, fontWeight: i===0?700:600, fontSize: i===0?11:10.5, marginBottom: i===attTip.lines.length-1?0:3}}>
              {l.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
const AdminDashboard = ({allLogs, allProfiles, onRefresh, defaultTab="users", defaultRoster2Tab="overview"}) => {
  const [adminTab, setAdminTab] = useState(defaultTab);
  useEffect(() => { setAdminTab(defaultTab); }, [defaultTab]);
  const [roster2Tab, setRoster2Tab] = useState(defaultRoster2Tab); // "overview" | "scoresheet" | "grading" | "attendance"
  useEffect(() => { setRoster2Tab(defaultRoster2Tab); }, [defaultRoster2Tab]);
  const [rosterSearch, setRosterSearch] = useState("");
  const [rosterSort, setRosterSort] = useState({by:"name",dir:"asc"});
  const [attendance2, setAttendance2] = useState({}); // attendance_logs에서 로드 (key: "${roster2Idx}-${type}-${YYYY-MM-DD}")
  const [sel, setSel]           = useState("전체");
  const [editStudent, setEditStudent] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [filterStatus, setFilterStatus] = useState("all"); // "all"|"pending"|"approved"|"rejected"
  const [filterRole, setFilterRole]   = useState("all"); // "all"|"student"|"parent"
  const [detailStudent, setDetailStudent] = useState(null);
  // 관리자가 학생 화면을 read-only로 보기 위한 state. profile 객체가 들어있으면 풀스크린 오버레이 표시
  // 학생 사이드바와 동일하게 cert / dashboard / history 3개 탭 지원 (학부모 viewerMode="parent" 컴포넌트 재사용)
  const [viewAsStudent, setViewAsStudent] = useState(null);
  // 출석 셀 hover tooltip (관리자 그리드용) — { x, y, lines: [...] }
  const [gridTip, setGridTip] = useState(null);
  const [viewAsTab, setViewAsTab] = useState("cert"); // "cert" | "dashboard" | "history"
  const [viewAsLogs, setViewAsLogs] = useState([]);
  const [viewAsLoading, setViewAsLoading] = useState(false);
  useEffect(() => {
    if (!viewAsStudent) { setViewAsLogs([]); return; }
    setViewAsTab("cert"); setViewAsLoading(true);
    supabase.rpc("get_child_logs", { child_id: viewAsStudent.id }).then(({data, error}) => {
      setViewAsLogs(data || []);
      setViewAsLoading(false);
    }, () => { setViewAsLoading(false); });
  }, [viewAsStudent]);

  // 기수 데이터 (회원 관리·2기 명단 공용)
  const ROSTER2_NAMES = new Set(["강예나","김가흔","김은채","김태준","박재현","손연재","윤준원","최지유","배정윤","심수윤","한설아","강가인","권민유","권순혁","최유주","김도현","김시원","김시윤","김아란","김준범","김지우","김호진","나지성","문지유","박지우","서소윤","서지우","송민건","양소윤","오수연","우정훈","윤서준","이유빈","이홍윤","임다은","정유진","박선율","한채린","오수빈","남희수","김가인","양은정","테스트학생","한유찬","문성민"]);
  // 다중 기수 지원
  // - 2기 첫 회차일(2026-05-20 KST) 이전 가입자만 "20HA 1기" 자동 부여 (기존 운영 호환)
  // - 그 이후 가입자는 자동 1기 미부여 (신규 가입자가 1기로 잘못 표시되는 문제 방지)
  // - ROSTER2(2기) 이름 매칭 시 "20HA 2기" 추가 (가입일 무관)
  const COHORT1_CUTOFF = new Date(2026, 4, 20).getTime(); // 2026-05-20 00:00 로컬
  const getCohorts = (profile) => {
    if (profile.role !== "student") return [];
    const tags = [];
    const created = profile.created_at ? new Date(profile.created_at).getTime() : null;
    if (created !== null && created < COHORT1_CUTOFF) tags.push("20HA 1기");
    if (ROSTER2_NAMES.has(profile.name)) tags.push("20HA 2기");
    return tags;
  };
  // 회원 관리 검색·페이지 state
  const [memberSearch, setMemberSearch] = useState("");
  const [memberPageSize, setMemberPageSize] = useState(20);
  const [memberPage, setMemberPage] = useState(1);
  const [dashColFilter, setDashColFilter] = useState({});
  const [dashFilterOpen, setDashFilterOpen] = useState(null);
  const [parentLinks, setParentLinks] = useState({}); // {parent_id: [{student_id, name, grade}]}
  const [certData, setCertData] = useState([]); // get_cert_status 결과
  const [certLoading, setCertLoading] = useState(false);
  const [certWeekOffset, setCertWeekOffset] = useState(0); // 0=이번주, -1=지난주 ...
  const [certNickEdit, setCertNickEdit] = useState({}); // {profile_id: editing_value}
  const [invalidCerts, setInvalidCerts] = useState([]);
  const [sessionViewIdx, setSessionViewIdx] = useState(0); // 회차별 확인: 선택 회차 인덱스 (0-based)
  const [certSessionModal, setCertSessionModal] = useState(null); // {id, postTitle, posted_at, sessions: number[]}
  const [certScoreModal, setCertScoreModal] = useState(null); // {id, postTitle, submit, mission, fidelity}
  const [certScoreSaving, setCertScoreSaving] = useState(false);
  const [certSessionSaving, setCertSessionSaving] = useState(false);
  // 전체현황 그리드 셀 수동 편집 모달
  const [gridCertModal, setGridCertModal] = useState(null);  // {certId, studentId, studentName, sessionNum, sessionLabel, postedAt, submit, mission, fidelity}
  const [gridCertSaving, setGridCertSaving] = useState(false);
  const [attEditModal, setAttEditModal] = useState(null);    // {studentId, studentName, sessionDate, sessionType, sessionLabel, present, preAbsence}
  const [attEditSaving, setAttEditSaving] = useState(false);
  const [lastCrawledAt, setLastCrawledAt] = useState(null);
  const [certRecordsPage, setCertRecordsPage] = useState(1);
  const CERT_RECORDS_PAGE_SIZE = 30;
  const [certSearch, setCertSearch] = useState("");
  const [certSessionFilter, setCertSessionFilter] = useState("all"); // "all" | number
  const [certScoreFilter, setCertScoreFilter] = useState("all"); // "all" | "scored" | "unscored" | "low"
  const [certTimeFilter, setCertTimeFilter] = useState("all"); // "all" | "ontime" | "late"
  const [certRecords, setCertRecords] = useState([]);
  const [certRecordsLoading, setCertRecordsLoading] = useState(false);
  const [certChecked, setCertChecked] = useState(() => new Set()); // 일괄 채점용 선택된 글 id
  const [bulkScoring, setBulkScoring] = useState(false);
  const [certStatusFilter, setCertStatusFilter] = useState("all");
  const [certEditRecord, setCertEditRecord] = useState(null); // 편집 중인 행 {id,...}
  const [crawlRunning, setCrawlRunning] = useState(false);
  const [certStudents, setCertStudents] = useState([]);
  const [attendanceCerts, setAttendanceCerts] = useState([]);
  const [attendanceFrom, setAttendanceFrom] = useState(()=>{
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
  });
  const [attendanceTo, setAttendanceTo] = useState(()=>{
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  });
  const [filterName, setFilterName] = useState("");
  const [filterGrade, setFilterGrade] = useState("");
  const [filterDates, setFilterDates] = useState("all"); // all | week | hasData
  const [rosterEditRow, setRosterEditRow] = useState(null);
  const [rosterAddMode, setRosterAddMode] = useState(false);
  const [assignPopup, setAssignPopup] = useState(null); // {studentId, date, certs[]}
  const isMobile = useMobile();

  // 학부모-자녀 연결 로드
  useEffect(()=>{
    const parents = allProfiles.filter(p=>p.role==="parent");
    if(parents.length===0) return;
    supabase.from("parent_students").select("parent_id, student_id").then(({data})=>{
      if(!data) return;
      const map = {};
      data.forEach(({parent_id, student_id})=>{
        if(!map[parent_id]) map[parent_id]=[];
        const sp = allProfiles.find(p=>p.id===student_id);
        if(sp) map[parent_id].push(sp);
      });
      setParentLinks(map);
    });
  },[allProfiles]);

  // 인증 현황 로드
  const loadCertData = async (weekOffset=0) => {
    setCertLoading(true);
    // 해당 주의 월~일 범위 계산 (KST 기준)
    const now = new Date();
    const dow = now.getDay(); // 0=일, 1=월 ...
    const monOffset = dow === 0 ? -6 : 1 - dow; // 이번 주 월요일까지 offset
    const mon = new Date(now); mon.setDate(now.getDate() + monOffset + weekOffset * 7);
    mon.setHours(0,0,0,0);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6); sun.setHours(23,59,59,999);
    const {data} = await supabase.rpc("get_cert_status", {
      p_from: mon.toISOString(), p_to: sun.toISOString()
    });
    setCertData(data || []);
    setCertLoading(false);
  };

  useEffect(()=>{
    if(adminTab!=="roster2") return;
    loadCertStudents();
    loadCertRecords("all");
    // 카페 인증 현황: ROSTER2_NAVER_DATES 전체 기간 로드
    const certFrom = new Date(ROSTER2_NAVER_DATES[0]); certFrom.setHours(0,0,0,0);
    const certTo = new Date(ROSTER2_NAVER_DATES[ROSTER2_NAVER_DATES.length-1]); certTo.setHours(23,59,59,999);
    loadAttendanceCerts(certFrom, certTo);
    // 모닝/나잇 출석 로그 로드 → 전체 활동 기간(5/17~)
    loadAttendance2();
  },[adminTab]);

  // 모닝/나잇 출석을 attendance2 맵으로 로드 (저장 후 갱신에도 재사용)
  const loadAttendance2 = async () => {
    const allDates = [...ROSTER2_MORNING_DATES, ...ROSTER2_NIGHT_DATES];
    const minMs = Math.min(...allDates.map(d=>d.getTime()));
    const maxMs = Math.max(...allDates.map(d=>d.getTime()));
    const f = new Date(minMs); const t = new Date(maxMs);
    const fromStr = `${f.getFullYear()}-${String(f.getMonth()+1).padStart(2,'0')}-${String(f.getDate()).padStart(2,'0')}`;
    const toStr   = `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`;
    // ⚠️ PostgREST는 한 번에 최대 1000행만 반환(나머지는 조용히 잘림). 출석이 1000건을
    //    넘으면 최신 회차가 화면에서 사라진다. → 1000건씩 페이지네이션으로 전부 로드.
    const PAGE = 1000;
    const all = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await supabase
        .rpc("get_attendance_logs", { p_from: fromStr, p_to: toStr })
        .range(from, from + PAGE - 1);
      if (error) break;
      const batch = data || [];
      all.push(...batch);
      if (batch.length < PAGE) break;
    }
    const map = {};
    all.forEach(r => {
      const idx = ROSTER2.findIndex(s => s.name === r.student_name);
      if(idx >= 0){
        // session_type: "M" 또는 "N"(나잇) → roster2 키는 "M" 또는 "나"
        const rosterType = r.session_type === "N" ? "나" : "M";
        // 사전결석 신청 = 출석 인정(카운트 O). 한도(모닝3·나잇5) 초과분 제외는 Phase2.
        const valid = r.pre_absence ? true : isValidAttendance(r.session_type, r.participation_minutes, r.session_date);
        map[`${idx}-${rosterType}-${r.session_date}`] = {
          valid,
          minutes: r.participation_minutes,
          preAbs: !!r.pre_absence,
          preAbsAt: r.pre_absence_at || null,
        };
      }
    });
    // 사전결석 한도(모닝 3·나잇 5): 학생·타입별 회차 빠른 순 N개만 출석 인정, 초과분은
    // 기록은 유지하되 카운트에서 제외(valid=false, preAbsExcess 표시). — 사장님 방침
    const PRE_CAP = { "M": 3, "나": 5 };
    const preGroups = {};
    Object.keys(map).forEach(k => {
      if (!map[k].preAbs) return;
      const parts = k.split("-");                 // [idx, type, YYYY, MM, DD]
      const g = `${parts[0]}-${parts[1]}`;
      const date = parts.slice(2).join("-");
      (preGroups[g] = preGroups[g] || []).push({ date, key: k });
    });
    Object.entries(preGroups).forEach(([g, arr]) => {
      const type = g.split("-")[1];
      const cap = PRE_CAP[type] ?? 0;
      arr.sort((a, b) => a.date.localeCompare(b.date));
      arr.forEach((it, i) => {
        if (i >= cap) { map[it.key].valid = false; map[it.key].preAbsExcess = true; }
      });
    });
    setAttendance2(map);
  };

  const saveNickname = async (profileId, nickname) => {
    await supabase.rpc("update_naver_nickname", {p_profile_id: profileId, p_nickname: nickname.trim()||null});
    setCertNickEdit(p=>({...p,[profileId]:undefined}));
    loadCertData(certWeekOffset);
    onRefresh();
  };

  const loadCertRecords = async (status="all") => {
    setCertRecordsLoading(true);
    const {data} = await supabase.rpc("get_cert_records", {
      p_status: status==="all" ? null : status,
      p_limit: 500,
    });
    const records = data||[];
    setCertRecords(records);
    // 최근 크롤링 시간
    if(records.length > 0) {
      const maxAt = records.reduce((m,r)=> (r.crawled_at||"") > m ? (r.crawled_at||"") : m, "");
      if(maxAt) setLastCrawledAt(maxAt);
    }
    setCertRecordsLoading(false);
  };

  const updateCertSessions = async (certId, sessions) => {
    if (certSessionSaving) return;
    setCertSessionSaving(true);
    try {
      const arr = (sessions||[]).map(Number).filter(n=>n>=1 && n<=ROSTER2_NAVER_DATES.length);
      arr.sort((a,b)=>a-b);
      const dedup = [...new Set(arr)];
      await supabase.rpc("update_cert_sessions", {
        p_cert_id: certId,
        p_sessions: dedup.length ? dedup : null,
      });
      const patch = { session_override: dedup.length ? dedup : null };
      setCertRecords(prev => prev.map(r => r.id===certId ? {...r, ...patch} : r));
      setAttendanceCerts(prev => prev.map(c => c.id===certId ? {...c, ...patch} : c));
    } finally {
      setCertSessionSaving(false);
    }
  };

  const updateCertScore = async (certId, parts) => {
    setCertScoreSaving(true);
    const toNum = v => v==="" || v==null ? null : Number(v);
    const submit   = toNum(parts.submit);
    const mission  = toNum(parts.mission);
    const fidelity = toNum(parts.fidelity);
    const total = (submit===null && mission===null && fidelity===null)
      ? null
      : (submit||0) + (mission||0) + (fidelity||0);
    await supabase.rpc("update_cert_scores", {
      p_cert_id: certId,
      p_submit: submit,
      p_mission: mission,
      p_fidelity: fidelity,
    });
    setCertScoreSaving(false);
    const patch = {
      submit_score: submit, mission_score: mission, fidelity_score: fidelity,
      completeness_score: total, compliance_score: null,
    };
    setCertRecords(prev => prev.map(r => r.id===certId ? {...r, ...patch} : r));
    setAttendanceCerts(prev => prev.map(c => c.id===certId ? {...c, ...patch} : c));
  };

  // ── 전체현황 그리드 셀 수동 편집 저장 ──
  // 출석 셀: 출석/결석/사전결석
  const saveGridAttendance = async () => {
    if (!attEditModal || attEditSaving) return;
    setAttEditSaving(true);
    try {
      await supabase.rpc("admin_set_attendance", {
        p_student_id: attEditModal.studentId,
        p_session_date: attEditModal.sessionDate,
        p_session_type: attEditModal.sessionType,       // 'M' | 'N'
        p_present: attEditModal.present,
        p_pre_absence: !attEditModal.present && !!attEditModal.preAbsence,
        p_pre_absence_at: null,                         // 관리자 수동 = 유효 간주(전날규칙 미검증)
      });
      await loadAttendance2();
      setAttEditModal(null);
    } finally {
      setAttEditSaving(false);
    }
  };
  // 카페 인증 셀: 기존 인증 점수 수정 or 없으면 수동 인증 생성
  const saveGridCert = async () => {
    if (!gridCertModal || gridCertSaving) return;
    const toNum = v => v==="" || v==null ? null : Number(v);
    const submit = toNum(gridCertModal.submit), mission = toNum(gridCertModal.mission), fidelity = toNum(gridCertModal.fidelity);
    setGridCertSaving(true);
    try {
      if (gridCertModal.certId) {
        await updateCertScore(gridCertModal.certId, {
          submit: gridCertModal.submit, mission: gridCertModal.mission, fidelity: gridCertModal.fidelity,
        });
      } else {
        await supabase.rpc("admin_create_manual_cert", {
          p_student_id: gridCertModal.studentId,
          p_session_num: gridCertModal.sessionNum,
          p_posted_at: gridCertModal.postedAt,
          p_submit: submit, p_mission: mission, p_fidelity: fidelity,
        });
      }
      // 그리드 + 채점표 갱신
      const certFrom = new Date(ROSTER2_NAVER_DATES[0]); certFrom.setHours(0,0,0,0);
      const certTo = new Date(ROSTER2_NAVER_DATES[ROSTER2_NAVER_DATES.length-1]); certTo.setHours(23,59,59,999);
      await loadAttendanceCerts(certFrom, certTo);
      loadCertRecords(certStatusFilter);
      setGridCertModal(null);
    } finally {
      setGridCertSaving(false);
    }
  };

  // 체크한 글들을 한 번에 만점(제출50/미션30/충실도20 = 종합 100점) 처리
  const bulkFullScore = async () => {
    const ids = [...certChecked];
    if (ids.length === 0 || bulkScoring) return;
    if (!window.confirm(`체크한 ${ids.length}건을 만점(제출 50 / 미션 30 / 충실도 20 = 종합 100점)으로 처리할까요?`)) return;
    setBulkScoring(true);
    try {
      await Promise.all(ids.map(id =>
        supabase.rpc("update_cert_scores", { p_cert_id: id, p_submit: 50, p_mission: 30, p_fidelity: 20 })
      ));
      const idSet = new Set(ids);
      const patch = { submit_score: 50, mission_score: 30, fidelity_score: 20, completeness_score: 100, compliance_score: null };
      setCertRecords(prev => prev.map(r => idSet.has(r.id) ? {...r, ...patch} : r));
      setAttendanceCerts(prev => prev.map(c => idSet.has(c.id) ? {...c, ...patch} : c));
      setCertChecked(new Set());
    } finally {
      setBulkScoring(false);
    }
  };

  const updateCertRecord = async (id, updates) => {
    await supabase.rpc("update_cert_record", {
      p_id: id,
      p_parsed_name: updates.parsed_name||null,
      p_parsed_grade: updates.parsed_grade||null,
      p_parsed_code: updates.parsed_code||null,
      p_title_match_status: updates.title_match_status||null,
      p_is_valid_format: updates.is_valid_format??null,
    });
    if(updates.assigned_student_id) {
      await supabase.rpc("assign_cert_to_student", {p_cert_id: id, p_student_id: updates.assigned_student_id});
    }
    setCertEditRecord(null);
    loadCertRecords(certStatusFilter);
  };

  const triggerCrawl = async () => {
    const token = (process.env.REACT_APP_GITHUB_TOKEN||"").replace(/[\s\\n]+$/g,"").trim();
    if(!token){ alert("REACT_APP_GITHUB_TOKEN 환경변수가 설정되지 않았습니다."); return; }
    setCrawlRunning(true);
    try {
      const r = await fetch(
        "https://api.github.com/repos/MinDaehyeon/20ha-meta-x/actions/workflows/cafe_crawler.yml/dispatches",
        { method:"POST",
          headers:{"Authorization":`Bearer ${token}`,"Accept":"application/vnd.github+json","Content-Type":"application/json"},
          body: JSON.stringify({ref: ["meta-x.ai.kr","20ha-meta-x.vercel.app"].includes(window.location.hostname) ? "main" : "dev"}) }
      );
      if(r.status===204) alert("크롤링 시작됐습니다!\n약 1분 후 새로고침 하면 데이터가 업데이트됩니다.");
      else alert("실행 실패: " + r.status);
    } catch(e){ alert("오류: "+e.message); }
    setCrawlRunning(false);
  };

  const loadCertStudents = async () => {
    const {data} = await supabase.rpc("get_cert_students");
    setCertStudents(data||[]);
  };

  const loadAttendanceCerts = async (from, to) => {
    const {data} = await supabase.rpc("get_attendance_certs", {
      p_from: from.toISOString(), p_to: to.toISOString()
    });
    setAttendanceCerts(data||[]);
  };

  const upsertCertStudent = async (row) => {
    await supabase.rpc("upsert_cert_student", {
      p_id: row.id||null,
      p_name: row.name,
      p_phone: row.phone||null,
      p_grade: row.grade||null,
      p_naver_nicknames: row.naver_nicknames||[],
    });
    setRosterEditRow(null);
    loadCertStudents();
  };

  const deleteCertStudent = async (id) => {
    if(!window.confirm("명단에서 삭제할까요?")) return;
    await supabase.rpc("delete_cert_student", {p_id: id});
    loadCertStudents();
  };

  const assignCert = async (certId, studentId) => {
    // 배정 대상 인증글의 닉네임 파악
    const cert = attendanceCerts.find(c=>c.id===certId);
    const nick = cert?.naver_nickname;

    // 1) 직접 배정
    await supabase.rpc("assign_cert_to_student", {p_cert_id: certId, p_student_id: studentId||null});

    // 2) 같은 닉네임의 미배정(assigned_student_id=null) 인증글 자동 배정
    if(studentId && nick) {
      const sameNick = attendanceCerts.filter(c =>
        c.id !== certId && c.naver_nickname === nick && !c.assigned_student_id
      );
      await Promise.all(sameNick.map(c =>
        supabase.rpc("assign_cert_to_student", {p_cert_id: c.id, p_student_id: studentId})
      ));
      if(sameNick.length > 0) alert(`✅ "${nick}" 닉네임의 미배정 인증글 ${sameNick.length}건도 자동 배정했습니다.`);
    }

    setAssignPopup(null);
    loadAttendanceCerts(attendanceFrom, attendanceTo);
  };

  const testUids = new Set(allProfiles.filter(p=>p.is_test).map(p=>p.id));
  const normLogs = allLogs.filter(l=>!testUids.has(l.uid)).map(l=>({
    ...l, engramIndex:l.engram_index, strategyScore:l.strategy_score,
    efficiencyIndex:l.efficiency_index, metacognitionAccuracy:l.metacognition_accuracy,
    coinFilter:{cc:l.coin_cc,ci:l.coin_ci,ic:l.coin_ic,ii:l.coin_ii},
    subject:l.subject, bookLevel:l.book_level, netTime:l.net_time, questionCount:l.question_count,
    qBasic:l.q_basic||0, qMid:l.q_mid||0, qAdv:l.q_adv||0,
  }));

  const students = allProfiles.filter(p=>p.role==="student"||p.role==="parent");
  const pendingCount = students.filter(s=>s.approval_status==="pending").length;

  const setApproval = async (id, status) => {
    const {error:ae} = await supabase.rpc("admin_update_profile", {
      p_id: id, p_name: null, p_grade: null, p_target_ei: null, p_approval_status: status,
    });
    if(ae) console.error("approval error:", ae.message);
    onRefresh();
  };

  const updateStudent = async () => {
    if(!editStudent) return;
    setSaving(true);
    const {error:ue} = await supabase.rpc("admin_update_profile", {
      p_id: editStudent.id,
      p_name: editStudent.name,
      p_grade: editStudent.grade || "",
      p_target_ei: editStudent.target_ei,
      p_approval_status: editStudent.approval_status,
    });
    // birth_year는 직접 업데이트 (RPC 파라미터 외)
    if(!ue) {
      await supabase.from("profiles").update({birth_year: editStudent.birth_year||null, birth_month: editStudent.birth_month||null, birth_day: editStudent.birth_day||null}).eq("id", editStudent.id);
    }
    if(ue) console.error("update error:", ue.message);
    setSaving(false); setEditStudent(null); onRefresh();
  };

  const roleFiltered = filterRole==="all" ? students : students.filter(s=>s.role===filterRole);
  const statusFiltered = filterStatus==="all" ? roleFiltered : roleFiltered.filter(s=>s.approval_status===filterStatus);
  const searchFiltered = !memberSearch.trim() ? statusFiltered
    : statusFiltered.filter(s=>(s.name||"").includes(memberSearch)||(s.email||"").includes(memberSearch));
  const totalPages = Math.max(1, Math.ceil(searchFiltered.length / memberPageSize));
  const safePage = Math.min(memberPage, totalPages);
  const filteredStudents = searchFiltered.slice((safePage-1)*memberPageSize, safePage*memberPageSize);

  // Dashboard data
  const filtered=sel==="전체"?normLogs:normLogs.filter(l=>l.uid===sel);
  const ninetyDaysAgo=new Date(); ninetyDaysAgo.setDate(ninetyDaysAgo.getDate()-90);
  const ninetyStr=ninetyDaysAgo.toISOString().slice(0,10);
  const sorted=[...filtered].filter(l=>l.date>=ninetyStr).sort((a,b)=>a.date.localeCompare(b.date));
  const _dateEI2={};sorted.forEach(l=>{if(!_dateEI2[l.date])_dateEI2[l.date]=[];_dateEI2[l.date].push(l.engramIndex);});
  const dailyAvg2=Object.entries(_dateEI2).sort((a,b)=>a[0].localeCompare(b[0])).map(([date,eis])=>({date,engramIndex:+(eis.reduce((s,v)=>s+v,0)/eis.length).toFixed(1)}));
  const maData=dailyAvg2.map((d,i)=>{const w=dailyAvg2.slice(Math.max(0,i-6),i+1);return{...d,movingAvg:+(w.reduce((s,v)=>s+v.engramIndex,0)/w.length).toFixed(1)};});
  const avgEI=filtered.length>0?(filtered.reduce((s,l)=>s+l.engramIndex,0)/filtered.length).toFixed(1):0;
  const coinT=filtered.reduce((acc,l)=>{const cf=l.coinFilter||{};Object.entries(cf).forEach(([k,v])=>{acc[k]=(acc[k]||0)+v;});return acc;},{cc:0,ci:0,ic:0,ii:0});
  const coinTot=Object.values(coinT).reduce((s,v)=>s+v,0);
  const ciRate=coinTot>0?coinT.ci/coinTot:0;
  const advLogs=filtered.filter(l=>(l.qAdv||0)>0);
  const avgSec3=advLogs.length>0?advLogs.reduce((s,l)=>s+(l.qAdv>0?l.netTime*60/l.qAdv:0),0)/advLogs.length:0;
  const feedbacks=[];
  if(ciRate>0.3) feedbacks.push({type:"warn",msg:"과잉확신(C-I) 비중이 높습니다. 백지목차 테스트 강도를 높이세요.",icon:"warn"});
  if(advLogs.length>0&&avgSec3>180) feedbacks.push({type:"alert",msg:`심화 문항 풀이 평균 ${avgSec3.toFixed(0)}초/문항 — 유형별 심화 학습 세션을 추가하세요.`,icon:"warn"});
  if(feedbacks.length===0) feedbacks.push({type:"ok",msg:"현재 데이터에서 주요 위험 신호가 감지되지 않았습니다.",icon:"check"});
  const byStudent=students.filter(s=>s.approval_status==="approved"&&s.role!=="parent"&&!s.is_test).map(s=>{
    const sl=normLogs.filter(l=>l.uid===s.id);
    const now=new Date(), d7=new Date(now-7*86400000), d30=new Date(now-30*86400000);
    const sl7=sl.filter(l=>new Date(l.date)>=d7);
    const sl30=sl.filter(l=>new Date(l.date)>=d30);
    const avgAll=sl.length>0?(sl.reduce((a,l)=>a+l.engramIndex,0)/sl.length):null;
    const avg7=sl7.length>0?(sl7.reduce((a,l)=>a+l.engramIndex,0)/sl7.length):null;
    const avg30=sl30.length>0?(sl30.reduce((a,l)=>a+l.engramIndex,0)/sl30.length):null;
    const latest=sl.length>0?[...sl].sort((a,b)=>b.date.localeCompare(a.date))[0]:null;
    const ct=sl.reduce((acc,l)=>{const cf=l.coinFilter||{};Object.entries(cf).forEach(([k,v])=>{acc[k]=(acc[k]||0)+v;});return acc;},{cc:0,ci:0,ic:0,ii:0});
    const tot=Object.values(ct).reduce((s,v)=>s+v,0);
    const ciRatePct=tot>0?(ct.ci/tot*100):null;
    return{...s,avgAll,avg7,avg30,logCount:sl.length,latestEI:latest?.engramIndex,redFlag:tot>0&&ct.ci/tot>0.3,ciRatePct};
  });
  const validEIs=byStudent.filter(s=>s.avgAll!==null);
  const classAvgEI=validEIs.length>0?(validEIs.reduce((a,s)=>a+s.avgAll,0)/validEIs.length).toFixed(1):"—";
  const GRADE_C={S:"#16A34A",A:"#2563EB",B:"#111827",C:"#F97316",D:"#DC2626"};
  const ciGrp=pct=>pct<10?"S":pct<20?"A":pct<30?"B":pct<40?"C":"D";
  const filteredByDash=byStudent.filter(s=>Object.entries(dashColFilter).every(([col,grp])=>{
    if(!grp) return true;
    if(col==="week7") return s.avg7!==null&&gradeInfo(s.avg7).g===grp;
    if(col==="month30") return s.avg30!==null&&gradeInfo(s.avg30).g===grp;
    if(col==="allTime") return s.avgAll!==null&&gradeInfo(s.avgAll).g===grp;
    if(col==="ci") return s.ciRatePct!==null&&ciGrp(s.ciRatePct)===grp;
    return true;
  }));
  const bySubject=SUBJECTS.reduce((acc,s)=>{const sl=filtered.filter(l=>l.subject===s);if(sl.length>0)acc[s]=(sl.reduce((a,l)=>a+l.engramIndex,0)/sl.length).toFixed(1);return acc;},{});

  const statusBadge = (status) => {
    const map = {pending:{label:"승인 대기",color:T.warn,bg:"#FEF3C7"}, approved:{label:"승인됨",color:T.success,bg:"#F0FDF4"}, rejected:{label:"거절됨",color:T.danger,bg:"#FEE2E2"}};
    const s = map[status] || map.pending;
    return <span style={{fontSize:11,fontWeight:700,color:s.color,background:s.bg,padding:"3px 10px",borderRadius:20,border:`1px solid ${s.color}30`}}>{s.label}</span>;
  };

  return(
    <div>
      {/* ── 회원 관리 탭 ── */}
      {adminTab==="users"&&(
        <div>
          {/* 편집 모달 */}
          {editStudent&&(
            <div style={{position:"fixed",inset:0,background:"rgba(25,29,84,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
              <Card style={{width:"100%",maxWidth:420}}>
                <div style={{fontSize:18,fontWeight:800,color:T.navy,marginBottom:20}}>학생 정보 수정</div>
                <div style={{display:"grid",gap:12}}>
                  {[{label:"이름",key:"name",type:"text"},{label:"목표 EI",key:"target_ei",type:"num",min:50,max:100}].map(({label,key,type,min,max})=>(
                    <div key={key}>
                      <label style={css.label}>{label}</label>
                      <input type={type==="num"?"number":type} min={min} max={max} value={editStudent[key]||""} onChange={e=>setEditStudent(s=>({...s,[key]:type==="num"?Number(e.target.value):e.target.value}))} style={css.input}/>
                    </div>
                  ))}
                  <div>
                    <label style={css.label}>생년월일</label>
                    <BirthInput
                      year={editStudent.birth_year||""} month={editStudent.birth_month||""} day={editStudent.birth_day||""}
                      onYear={v=>setEditStudent(s=>({...s,birth_year:Number(v),grade:calcGrade(Number(v),s.birth_month||null)}))}
                      onMonth={v=>setEditStudent(s=>({...s,birth_month:Number(v),grade:calcGrade(s.birth_year||null,Number(v))}))}
                      onDay={v=>setEditStudent(s=>({...s,birth_day:Number(v)}))}
                      showGrade={editStudent.role==="student"}/>
                  </div>
                  <div>
                    <label style={css.label}>승인 상태</label>
                    <select value={editStudent.approval_status} onChange={e=>setEditStudent(s=>({...s,approval_status:e.target.value}))} style={css.select}>
                      <option value="pending">승인 대기</option>
                      <option value="approved">승인됨</option>
                      <option value="rejected">거절됨</option>
                    </select>
                  </div>

                </div>
                <div style={{display:"flex",gap:10,marginTop:20}}>
                  <button onClick={()=>setEditStudent(null)} style={{...css.btnGhost,flex:1}}>취소</button>
                  <button onClick={updateStudent} disabled={saving} style={{...css.btnPrimary,flex:2,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                    {saving?<><Spinner size={16} color="#fff"/>저장 중...</>:"저장"}
                  </button>
                </div>
              </Card>
            </div>
          )}

          {/* 역할 필터 */}
          <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
            {[
              {k:"all", l:`전체 (${students.length})`},
              {k:"student", l:`학생 (${students.filter(s=>s.role==="student").length})`},
              {k:"parent", l:`학부모 (${students.filter(s=>s.role==="parent").length})`},
            ].map(({k,l})=>(
              <button key={k} onClick={()=>setFilterRole(k)}
                style={{padding:"6px 14px",borderRadius:20,border:`1px solid ${filterRole===k?T.navy:T.border}`,cursor:"pointer",fontSize:12,fontWeight:700,
                  background:filterRole===k?T.navy:T.white,color:filterRole===k?T.white:T.muted}}>{l}</button>
            ))}
          </div>
          {/* 상태 필터 */}
          <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
            {[{k:"all",l:"전체"},{k:"pending",l:`대기 (${roleFiltered.filter(s=>s.approval_status==="pending").length})`},{k:"approved",l:"승인됨"},{k:"rejected",l:"거절됨"}].map(({k,l})=>(
              <button key={k} onClick={()=>setFilterStatus(k)}
                style={{padding:"6px 14px",borderRadius:20,border:`1px solid ${filterStatus===k?T.orange:T.border}`,cursor:"pointer",fontSize:12,fontWeight:700,
                  background:filterStatus===k?T.orange:T.white,color:filterStatus===k?T.white:T.muted}}>{l}</button>
            ))}
          </div>

          {/* 검색 + 페이지 크기 */}
          <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
            <input
              type="text" placeholder="이름·이메일 검색"
              value={memberSearch}
              onChange={e=>{setMemberSearch(e.target.value);setMemberPage(1);}}
              style={{...css.input,flex:1,minWidth:160,maxWidth:280,padding:"7px 12px",fontSize:13}}
            />
            <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:"auto"}}>
              <span style={{fontSize:12,color:T.muted,whiteSpace:"nowrap"}}>
                총 {searchFiltered.length}명
              </span>
              <select value={memberPageSize} onChange={e=>{setMemberPageSize(Number(e.target.value));setMemberPage(1);}}
                style={{...css.select,width:"auto",padding:"6px 10px",fontSize:12}}>
                {[10,20,50,100].map(n=><option key={n} value={n}>{n}명씩</option>)}
              </select>
            </div>
          </div>

          {/* 대기 중 알림 */}
          {pendingCount>0&&filterStatus!=="approved"&&filterStatus!=="rejected"&&(
            <div style={{background:T.orangePale,border:`1px solid ${T.orange}50`,borderRadius:12,padding:"12px 16px",marginBottom:14,fontSize:13,color:T.navy,display:"flex",alignItems:"center",gap:10}}>
              <span style={{display:"flex"}}>{HI.bell(20,T.orange)}</span>
              <span><strong style={{color:T.orange}}>{pendingCount}명</strong>이 승인을 기다리고 있습니다.</span>
            </div>
          )}

          <Card>
            {filteredStudents.length===0?(
              <div style={{textAlign:"center",padding:"40px 0",color:T.muted,fontSize:13}}>해당 상태의 회원이 없습니다.</div>
            ):(
              <div style={{overflowX:"auto"}}>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1.5fr 1fr 1fr 1.5fr":"2fr 0.8fr 1.2fr 0.9fr 1fr 1.5fr",minWidth:isMobile?480:640}}>
                  {(isMobile?["이름","기수","상태","관리"]:["이름 / 이메일","기수","상태","가입일","로그","관리"]).map(h=>(
                    <div key={h} style={{padding:"8px 12px",fontSize:11,color:T.muted,fontWeight:700,borderBottom:`2px solid ${T.border}`,letterSpacing:"0.04em"}}>{h}</div>
                  ))}
                  {filteredStudents.map(s=>[
                    <div key={`${s.id}-n`} style={{padding:"12px",borderBottom:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:3}}>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontSize:13,color:T.navy,fontWeight:700}}>{s.name||"(이름 미설정)"}</span>
                        {s.role==="parent"&&<span style={{fontSize:10,fontWeight:700,background:"#E0F2FE",color:"#0369A1",borderRadius:4,padding:"1px 5px"}}>학부모</span>}
                      </div>
                      {!isMobile&&<span style={{fontSize:11,color:T.muted}}>{s.email||"이메일 없음"}</span>}
                      {s.role==="parent"&&(parentLinks[s.id]?.length>0
                        ? <div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:2}}>
                            {parentLinks[s.id].map(c=>(
                              <span key={c.id} style={{fontSize:10,background:T.navy+"12",color:T.navy,borderRadius:4,padding:"1px 6px",fontWeight:600}}>
                                {c.name} ({c.grade})
                              </span>
                            ))}
                          </div>
                        : <span style={{fontSize:10,color:T.muted,marginTop:2}}>연결된 자녀 없음</span>
                      )}
                    </div>,
                    <div key={`${s.id}-g`} style={{padding:"8px 12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",flexWrap:"wrap",gap:4}}>
                      {getCohorts(s).map(cohort=>{const is2=cohort.includes("2기");return(
                        <span key={cohort} style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:20,background:is2?"#EEF2FF":"#F0FDF4",color:is2?"#4F46E5":"#16A34A",border:`1px solid ${is2?"#C7D2FE":"#BBF7D0"}`,whiteSpace:"nowrap"}}>{cohort}</span>
                      );})}
                    </div>,
                    <div key={`${s.id}-st`} style={{padding:"12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center"}}>{statusBadge(s.approval_status)}</div>,
                    ...(!isMobile?[<div key={`${s.id}-ca`} style={{padding:"12px",fontSize:12,borderBottom:`1px solid ${T.border}`,color:T.muted,display:"flex",alignItems:"center"}}>{s.created_at?.slice(0,10)||"—"}</div>]:[]),
                    ...(!isMobile?[<div key={`${s.id}-lc`} style={{padding:"12px",fontSize:13,borderBottom:`1px solid ${T.border}`,color:T.muted,display:"flex",alignItems:"center"}}>{normLogs.filter(l=>l.uid===s.id).length}건</div>]:[]),
                    <div key={`${s.id}-m`} style={{padding:"10px 12px",borderBottom:`1px solid ${T.border}`,display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
                      {s.approval_status==="pending"&&<>
                        <button onClick={()=>setApproval(s.id,"approved")} style={{fontSize:11,padding:"5px 10px",borderRadius:6,border:"none",background:T.success+"18",color:T.success,cursor:"pointer",fontWeight:700}}>✓ 승인</button>
                        <button onClick={()=>setApproval(s.id,"rejected")} style={{fontSize:11,padding:"5px 10px",borderRadius:6,border:"none",background:T.danger+"18",color:T.danger,cursor:"pointer",fontWeight:700}}>✕ 거절</button>
                      </>}
                      {s.approval_status==="approved"&&<button onClick={()=>setApproval(s.id,"rejected")} style={{fontSize:11,padding:"5px 10px",borderRadius:6,border:`1px solid ${T.border}`,background:T.white,color:T.muted,cursor:"pointer"}}>비활성</button>}
                      {s.approval_status==="rejected"&&<button onClick={()=>setApproval(s.id,"approved")} style={{fontSize:11,padding:"5px 10px",borderRadius:6,border:`1px solid ${T.border}`,background:T.white,color:T.success,cursor:"pointer",fontWeight:700}}>재승인</button>}
                      {s.role==="student"&&s.approval_status==="approved"&&<button onClick={()=>setViewAsStudent(s)} style={{fontSize:11,padding:"5px 10px",borderRadius:6,border:`1px solid #4F46E5`,background:T.white,color:"#4F46E5",cursor:"pointer",fontWeight:700}}>👁 학생화면</button>}
                      <button onClick={()=>setEditStudent(s)} style={{fontSize:11,padding:"5px 10px",borderRadius:6,border:`1px solid ${T.border}`,background:T.white,color:T.navy,cursor:"pointer"}}>수정</button>
                    </div>,
                  ])}
                </div>
              </div>
            )}
            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:4,padding:"12px 0",borderTop:`1px solid ${T.border}`,flexWrap:"wrap"}}>
                <button onClick={()=>setMemberPage(1)} disabled={safePage===1}
                  style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${T.border}`,cursor:safePage===1?"default":"pointer",fontSize:12,color:safePage===1?T.muted:T.navy,background:"transparent"}}>«</button>
                <button onClick={()=>setMemberPage(p=>Math.max(1,p-1))} disabled={safePage===1}
                  style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${T.border}`,cursor:safePage===1?"default":"pointer",fontSize:12,color:safePage===1?T.muted:T.navy,background:"transparent"}}>‹</button>
                {Array.from({length:totalPages},(_,i)=>i+1).filter(p=>Math.abs(p-safePage)<=2||p===1||p===totalPages).reduce((acc,p,i,arr)=>{
                  if(i>0&&p-arr[i-1]>1) acc.push("...");
                  acc.push(p);
                  return acc;
                },[]).map((p,i)=>p==="..."
                  ?<span key={`e${i}`} style={{padding:"4px 6px",fontSize:12,color:T.muted}}>…</span>
                  :<button key={p} onClick={()=>setMemberPage(p)}
                    style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${p===safePage?T.navy:T.border}`,cursor:"pointer",fontSize:12,fontWeight:p===safePage?700:400,background:p===safePage?T.navy:"transparent",color:p===safePage?T.white:T.navy}}>{p}</button>
                )}
                <button onClick={()=>setMemberPage(p=>Math.min(totalPages,p+1))} disabled={safePage===totalPages}
                  style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${T.border}`,cursor:safePage===totalPages?"default":"pointer",fontSize:12,color:safePage===totalPages?T.muted:T.navy,background:"transparent"}}>›</button>
                <button onClick={()=>setMemberPage(totalPages)} disabled={safePage===totalPages}
                  style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${T.border}`,cursor:safePage===totalPages?"default":"pointer",fontSize:12,color:safePage===totalPages?T.muted:T.navy,background:"transparent"}}>»</button>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ── 20HA 2기 현황 하위 탭 바 (overview 외 모든 서브뷰 공통) ── */}
      {adminTab==="roster2" && (
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          {[
            {k:"overview",   l:"📊 전체 현황"},
            {k:"scoresheet", l:"📋 인증글 점수표"},
            {k:"grading",    l:"📝 인증글 채점"},
            {k:"session",    l:"🔢 회차별 확인"},
            {k:"makeup",     l:"📚 Make-up"},
            {k:"attendance", l:"📋 출석체크"},
          ].map(({k,l})=>(
            <button key={k} onClick={()=>setRoster2Tab(k)}
              style={{...roster2Tab===k?css.btnOrange:css.btnOutline,padding:"7px 16px",fontSize:13,fontWeight:700}}>{l}</button>
          ))}
        </div>
      )}

      {/* ── 출석체크 (CSV 업로드) ── */}
      {adminTab==="roster2" && roster2Tab==="attendance" && (
        <AttendanceUploadView onRefresh={onRefresh}/>
      )}

      {/* ── Make-up 대상자 ── */}
      {adminTab==="roster2" && roster2Tab==="makeup" && (
        <MakeupView/>
      )}

      {/* ── 인증 현황 (점수표 / 채점) — 2기 현황의 하위 탭 ── */}
      {adminTab==="roster2" && (roster2Tab==="scoresheet" || roster2Tab==="grading") && (()=>{
        // ── 헬퍼 ──
        const kstDateStr = ts => {
          const k = new Date(new Date(ts).getTime() + 9*3600*1000);
          return `${k.getUTCFullYear()}-${String(k.getUTCMonth()+1).padStart(2,'0')}-${String(k.getUTCDate()).padStart(2,'0')}`;
        };
        const getSessionInfo = (posted_at) => {
          const postStr = kstDateStr(posted_at);
          for (let i = ROSTER2_NAVER_DATES.length - 1; i >= 0; i--) {
            const certStr = roster2FmtKey(ROSTER2_NAVER_DATES[i]);
            if (postStr >= certStr) return { sessionNum: i+1, isLate: isLateByDeadline(posted_at, ROSTER2_NAVER_DATES[i]) };
          }
          return { sessionNum: null, isLate: false };
        };
        // 회차 배열 결정: session_override가 있으면 그것, 없으면 작성일 자동 매칭
        const getCertSessions = (cert) => {
          if (cert.session_override && cert.session_override.length > 0) return cert.session_override.map(Number);
          const auto = getSessionInfo(cert.posted_at).sessionNum;
          return auto ? [auto] : [];
        };
        // 회차당 지각 여부 (회차일 다음날 12:00 KST 까지는 정시)
        const isLateForSession = (cert, sessionNum) => {
          return isLateByDeadline(cert.posted_at, ROSTER2_NAVER_DATES[sessionNum-1]);
        };
        // 회차 단위 매칭: 해당 회차의 정시·지각 글을 모두 포함, 다중 회차 글 포함
        // 같은 회차에 글이 여러 개면 가장 먼저 올린 글 1건만 채택
        const getStudentCertOnDate = (student, date) => {
          const sessionIdx = ROSTER2_NAVER_DATES.findIndex(d => roster2FmtKey(d) === roster2FmtKey(date));
          if (sessionIdx < 0) return null;
          const targetSessionNum = sessionIdx + 1;
          const candidates = attendanceCerts
            .filter(c => c.assigned_student_id === student.id)
            .filter(c => getCertSessions(c).includes(targetSessionNum))
            .sort((a,b) => new Date(a.posted_at) - new Date(b.posted_at));
          return candidates[0] || null;
        };
        const studentHasCert = (student, date) => !!getStudentCertOnDate(student, date);
        const fmtCrawledAt = ts => {
          if (!ts) return null;
          const k = new Date(new Date(ts).getTime() + 9*3600*1000);
          return `${k.getUTCMonth()+1}/${k.getUTCDate()} ${String(k.getUTCHours()).padStart(2,'0')}:${String(k.getUTCMinutes()).padStart(2,'0')}`;
        };

        const CELL_W = 70;
        const stickyBase = {position:"sticky",left:0,zIndex:2,borderRight:`1px solid ${T.border}`};
        const today = new Date();

        return (
          <div>
            {/* 헤더: 크롤링 버튼 (서브탭은 상위 SubTabBar에서 처리) */}
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,flexWrap:"wrap",justifyContent:"flex-end"}}>
              {lastCrawledAt&&(
                <span style={{fontSize:11,color:T.muted}}>최근: {fmtCrawledAt(lastCrawledAt)}</span>
              )}
              <button onClick={triggerCrawl} disabled={crawlRunning}
                style={{...css.btnOrange,padding:"7px 16px",fontSize:13,background:"#059669",opacity:crawlRunning?0.6:1}}>
                {crawlRunning?"⏳ 실행 중...":"🔄 지금 크롤링"}
              </button>
            </div>

            {/* ── 인증글 점수표 (회차×명단 그리드) ── */}
            {roster2Tab==="scoresheet"&&(
              <div>
                <div style={{fontSize:12,color:T.muted,marginBottom:10}}>
                  총 {certStudents.length}명 · 닉네임은 크롤링 시 제목 파싱으로 자동 등록됩니다
                </div>
                <Card style={{padding:0,overflow:"hidden"}}>
                  {/* 상단 스크롤바 */}
                  <div id="cert-top"
                    onScroll={e=>{const b=document.getElementById('cert-body');if(b)b.scrollLeft=e.target.scrollLeft;}}
                    style={{overflowX:"auto",overflowY:"hidden",height:14,borderBottom:`1px solid ${T.border}`,cursor:"ew-resize"}}>
                    <div style={{width:32+100+140+42+44+ROSTER2_NAVER_DATES.length*CELL_W+20,height:1}}/>
                  </div>
                  <div id="cert-body"
                    onScroll={e=>{const t=document.getElementById('cert-top');if(t)t.scrollLeft=e.target.scrollLeft;}}
                    style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                    <table style={{borderCollapse:"collapse",tableLayout:"fixed"}}>
                      <thead>
                        <tr>
                          <th rowSpan={2} style={{...stickyBase,width:32,minWidth:32,fontSize:10,color:T.muted,fontWeight:700,textAlign:"center",padding:"4px 2px",background:T.surfaceAlt,borderBottom:`2px solid ${T.borderStrong}`,verticalAlign:"middle"}}>#</th>
                          <th rowSpan={2} style={{...stickyBase,left:32,width:100,minWidth:100,fontSize:11,color:T.muted,fontWeight:700,padding:"4px 6px",background:T.surfaceAlt,borderBottom:`2px solid ${T.borderStrong}`,borderLeft:`1px solid ${T.border}`,verticalAlign:"middle"}}>이름 / 연락처</th>
                          <th rowSpan={2} style={{width:140,minWidth:140,fontSize:11,color:T.muted,fontWeight:700,padding:"4px 8px",background:T.surfaceAlt,borderBottom:`2px solid ${T.borderStrong}`,borderLeft:`1px solid ${T.border}`,verticalAlign:"middle"}}>네이버 닉네임</th>
                          <th rowSpan={2} style={{width:42,minWidth:42,fontSize:9,fontWeight:700,color:"#4F46E5",background:"#EEF2FF",textAlign:"center",padding:"2px 0",borderBottom:`2px solid ${T.borderStrong}`,borderLeft:`2px solid #4F46E5`,verticalAlign:"middle"}}>인증<br/>합계</th>
                          <th rowSpan={2} style={{width:44,minWidth:44,fontSize:9,fontWeight:700,color:"#16A34A",background:"#F0FDF4",textAlign:"center",padding:"2px 0",borderBottom:`2px solid ${T.borderStrong}`,borderLeft:`1px solid ${T.border}`,verticalAlign:"middle"}}>점수<br/>평균</th>
                          <th colSpan={ROSTER2_NAVER_DATES.length} style={{background:"#EEF2FF",color:"#4F46E5",fontSize:11,fontWeight:800,textAlign:"center",padding:"4px 0",borderBottom:`1px solid ${T.border}`,borderLeft:`2px solid #4F46E5`}}>
                            카페 인증 ({ROSTER2_NAVER_DATES.length}일) — 제출/미션/충실도 · 종합
                          </th>
                        </tr>
                        <tr>
                          {ROSTER2_NAVER_DATES.map((dt,di)=>(
                            <th key={di} style={{width:CELL_W,minWidth:CELL_W,fontSize:8,fontWeight:600,color:"#4F46E5",textAlign:"center",padding:"2px 0",verticalAlign:"middle",borderBottom:`2px solid ${T.borderStrong}`,borderLeft:di===0?`2px solid #4F46E5`:`1px solid ${T.border}`,background:"#EEF2FF",whiteSpace:"nowrap",lineHeight:"1.2"}}>
                              <div>{roster2Fmt(dt)}</div>
                              <div>{ROSTER2_DAY_KO[dt.getDay()]}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {certStudents.length===0?(
                          <tr><td colSpan={4+ROSTER2_NAVER_DATES.length} style={{textAlign:"center",padding:40,color:T.muted,fontSize:13}}>데이터 로딩 중...</td></tr>
                        ):certStudents.map((s,ri)=>{
                          const nicks = s.naver_nicknames || [];
                          const studentCerts = ROSTER2_NAVER_DATES
                            .map(dt => getStudentCertOnDate(s,dt))
                            .filter(Boolean);
                          const certCount = studentCerts.length;
                          const scoreVals = studentCerts
                            .map(c => c.completeness_score)
                            .filter(v => v !== null && v !== undefined);
                          const avgScore = scoreVals.length
                            ? Math.round(scoreVals.reduce((a,b)=>a+Number(b),0)/scoreVals.length)
                            : null;
                          const isEd = rosterEditRow?.id===s.id;
                          const rowBg = ri%2===0?T.surface:T.surfaceAlt;
                          return (
                            <tr key={s.id} style={{background:rowBg}}>
                              {/* # */}
                              <td style={{...stickyBase,width:32,minWidth:32,textAlign:"center",fontSize:10,color:T.muted,fontWeight:700,padding:0,height:36,background:rowBg,borderBottom:`1px solid ${T.border}`}}>{ri+1}</td>
                              {/* 이름+연락처 */}
                              <td style={{...stickyBase,left:32,width:100,minWidth:100,padding:"4px 6px",background:rowBg,borderBottom:`1px solid ${T.border}`,borderLeft:`1px solid ${T.border}`,verticalAlign:"middle"}}>
                                <div style={{fontSize:12,fontWeight:800,color:T.navy,textAlign:"center",lineHeight:1.3}}>{s.name}</div>
                                <div style={{fontSize:9,color:T.muted,textAlign:"center",fontFamily:"monospace",marginTop:1}}>{s.phone||"-"}</div>
                              </td>
                              {/* 닉네임 (읽기 전용 — 크롤링 시 자동 등록) */}
                              <td style={{width:140,minWidth:140,padding:"4px 8px",borderBottom:`1px solid ${T.border}`,borderLeft:`1px solid ${T.border}`,background:rowBg,verticalAlign:"middle"}}>
                                <div style={{display:"flex",alignItems:"center",gap:3,flexWrap:"wrap",minHeight:28}}>
                                  {nicks.length===0
                                    ? <span style={{fontSize:10,color:T.muted,fontStyle:"italic"}}>자동 등록 예정</span>
                                    : nicks.map((nick,ni)=>(
                                        <span key={ni} style={{fontSize:10,padding:"2px 5px",borderRadius:8,background:"#EEF2FF",color:"#4F46E5",border:"1px solid #C7D2FE"}}>{nick}</span>
                                      ))
                                  }
                                </div>
                              </td>
                              {/* 인증 합계 (한 줄) */}
                              <td style={{width:42,minWidth:42,textAlign:"center",verticalAlign:"middle",background:"#EEF2FF",borderBottom:`1px solid ${T.border}`,borderLeft:`2px solid #4F46E5`,height:38}}>
                                <span style={{fontSize:11,fontWeight:800,color:"#4F46E5"}}>{certCount}</span>
                                <span style={{fontSize:9,fontWeight:600,color:"#6D28D9"}}>/{ROSTER2_NAVER_DATES.length}</span>
                              </td>
                              {/* 점수 평균 */}
                              <td style={{width:44,minWidth:44,textAlign:"center",verticalAlign:"middle",background:avgScore!==null&&avgScore<80?"#FEE2E2":"#F0FDF4",borderBottom:`1px solid ${T.border}`,borderLeft:`1px solid ${T.border}`,height:38}}>
                                <div style={{fontSize:13,fontWeight:800,color:avgScore===null?T.muted:avgScore<80?"#DC2626":"#16A34A"}}>
                                  {avgScore!==null ? avgScore : "—"}
                                </div>
                              </td>
                              {/* 날짜별 셀 — 제출/미션/충실도/종합 */}
                              {ROSTER2_NAVER_DATES.map((dt,di)=>{
                                const cert = getStudentCertOnDate(s,dt);
                                const has = !!cert;
                                const ev = has && cert.completeness_score !== null && cert.completeness_score !== undefined ? cert.completeness_score : null;
                                const isLow = ev !== null && Number(ev) < 80;
                                const fmtPart = (v) => (v===null || v===undefined ? "—" : v);
                                return (
                                  <td key={di} style={{
                                    width:CELL_W,minWidth:CELL_W,height:60,
                                    textAlign:"center",verticalAlign:"middle",
                                    borderBottom:`1px solid ${T.border}`,
                                    borderLeft:di===0?`2px solid #4F46E5`:`1px solid ${T.border}`,
                                    background:has ? (isLow ? "#FEE2E2" : "#D1FAE5") : rowBg,
                                  }}>
                                    {has ? (
                                      ev !== null ? (
                                        <div style={{lineHeight:1.25}}>
                                          <div style={{fontSize:11,fontWeight:600,color:isLow?"#7F1D1D":"#475569"}}>
                                            {fmtPart(cert.submit_score)}/{fmtPart(cert.mission_score)}/{fmtPart(cert.fidelity_score)}
                                          </div>
                                          <div style={{fontSize:17,fontWeight:900,color:isLow?"#991B1B":"#065F46",marginTop:2}}>{ev}</div>
                                        </div>
                                      ) : <span style={{fontSize:11,color:"#94A3B8",fontStyle:"italic"}}>—</span>
                                    ) : <span style={{fontSize:11,color:"#D1D5DB"}}>·</span>}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            )}

            {/* ── 크롤링된 인증글 탭 ── */}
            {roster2Tab==="grading"&&(()=>{
              // 필터 적용
              const q = certSearch.trim().toLowerCase();
              const filteredRecords = certRecords.filter(rec => {
                // 검색: 학생명·제목·닉네임
                if (q) {
                  const hay = `${rec.matched_student_name||""} ${rec.post_title||""} ${rec.naver_nickname||""}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                // 회차 필터
                if (certSessionFilter !== "all") {
                  const sessions = getCertSessions(rec);
                  if (!sessions.includes(Number(certSessionFilter))) return false;
                }
                // 점수 상태
                const hasScore = rec.completeness_score !== null && rec.completeness_score !== undefined;
                if (certScoreFilter === "scored" && !hasScore) return false;
                if (certScoreFilter === "unscored" && hasScore) return false;
                if (certScoreFilter === "low" && (!hasScore || Number(rec.completeness_score) >= 80)) return false;
                // 정시/지각 (회차 매칭된 경우에만)
                if (certTimeFilter !== "all") {
                  const sessions = getCertSessions(rec);
                  if (sessions.length === 0) return false;
                  // 회차 중 하나라도 조건 맞으면 통과
                  const anyMatch = sessions.some(sn => {
                    const late = isLateForSession(rec, sn);
                    return certTimeFilter === "late" ? late : !late;
                  });
                  if (!anyMatch) return false;
                }
                return true;
              });
              const totalPages = Math.max(1, Math.ceil(filteredRecords.length / CERT_RECORDS_PAGE_SIZE));
              const safePage = Math.min(certRecordsPage, totalPages);
              const pageStart = (safePage-1)*CERT_RECORDS_PAGE_SIZE;
              const pageRecords = filteredRecords.slice(pageStart, pageStart+CERT_RECORDS_PAGE_SIZE);
              const filterActive = q || certSessionFilter!=="all" || certScoreFilter!=="all" || certTimeFilter!=="all";
              const resetFilters = () => {
                setCertSearch(""); setCertSessionFilter("all"); setCertScoreFilter("all"); setCertTimeFilter("all"); setCertRecordsPage(1);
              };
              const onChangeFilter = (setter) => (v) => { setter(v); setCertRecordsPage(1); };

              const inputStyle = {padding:"6px 10px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:12,background:T.white,color:T.navy};

              return (
              <div>
                {/* 검색 + 필터 바 */}
                <Card style={{padding:"10px 12px",marginBottom:12}}>
                  <div style={{display:"flex",flexWrap:"wrap",gap:8,alignItems:"center"}}>
                    <input type="text" placeholder="🔍 학생명 · 제목 · 닉네임 검색"
                      value={certSearch}
                      onChange={e=>{ setCertSearch(e.target.value); setCertRecordsPage(1); }}
                      style={{...inputStyle,flex:"1 1 220px",minWidth:180}}/>
                    <select value={certSessionFilter} onChange={e=>onChangeFilter(setCertSessionFilter)(e.target.value)}
                      style={{...inputStyle,cursor:"pointer"}}>
                      <option value="all">회차 전체</option>
                      {ROSTER2_NAVER_DATES.map((dt,idx)=>(
                        <option key={idx} value={idx+1}>{idx+1}회 ({dt.getMonth()+1}/{dt.getDate()})</option>
                      ))}
                    </select>
                    <select value={certScoreFilter} onChange={e=>onChangeFilter(setCertScoreFilter)(e.target.value)}
                      style={{...inputStyle,cursor:"pointer"}}>
                      <option value="all">점수 전체</option>
                      <option value="scored">채점 완료</option>
                      <option value="unscored">미채점</option>
                      <option value="low">80점 미만</option>
                    </select>
                    <select value={certTimeFilter} onChange={e=>onChangeFilter(setCertTimeFilter)(e.target.value)}
                      style={{...inputStyle,cursor:"pointer"}}>
                      <option value="all">정시·지각 전체</option>
                      <option value="ontime">정시</option>
                      <option value="late">지각</option>
                    </select>
                    {filterActive && (
                      <button onClick={resetFilters}
                        style={{padding:"6px 12px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:12,fontWeight:700,background:T.white,color:T.navy,cursor:"pointer"}}>
                        ✕ 필터 초기화
                      </button>
                    )}
                  </div>
                </Card>
                {/* 일괄 채점 바: 체크한 글 한 번에 만점 처리 */}
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:12,fontWeight:700,color:certChecked.size>0?T.navy:T.muted}}>
                    선택 {certChecked.size}건
                  </span>
                  <button onClick={bulkFullScore} disabled={certChecked.size===0||bulkScoring}
                    style={{padding:"6px 14px",borderRadius:6,border:"none",fontSize:12,fontWeight:700,
                      cursor:(certChecked.size===0||bulkScoring)?"default":"pointer",
                      background:(certChecked.size===0||bulkScoring)?"#E5E7EB":T.orange,
                      color:(certChecked.size===0||bulkScoring)?T.muted:T.white}}>
                    {bulkScoring?"처리 중...":"✓ 체크한 글 만점처리 (100점)"}
                  </button>
                  {certChecked.size>0 && !bulkScoring && (
                    <button onClick={()=>setCertChecked(new Set())}
                      style={{padding:"6px 10px",borderRadius:6,border:`1px solid ${T.border}`,fontSize:12,fontWeight:600,background:T.white,color:T.muted,cursor:"pointer"}}>
                      선택 해제
                    </button>
                  )}
                </div>
                <div style={{fontSize:12,color:T.muted,marginBottom:10}}>
                  {filterActive
                    ? <>필터 결과 <strong style={{color:T.navy}}>{filteredRecords.length}</strong>건 / 전체 {certRecords.length}건 · {safePage}/{totalPages}페이지</>
                    : <>총 {certRecords.length}건 · {safePage}/{totalPages}페이지 · 제목 클릭 → 원문 / 점수 클릭 → 입력</>
                  }
                </div>
                {certRecordsLoading?(
                  <div style={{textAlign:"center",padding:40,color:T.muted,fontSize:13}}>불러오는 중...</div>
                ):(
                  <Card style={{padding:0,overflow:"hidden"}}>
                    {/* 헤더 */}
                    <div style={{display:"grid",gridTemplateColumns:"34px 50px 80px 80px 60px 1fr 90px 65px 50px 50px 50px 56px",
                      background:T.navy,padding:"9px 12px",gap:6,alignItems:"center"}}>
                      {(()=>{
                        const pageIds = pageRecords.map(r=>r.id);
                        const allChecked = pageIds.length>0 && pageIds.every(id=>certChecked.has(id));
                        return (
                          <div style={{display:"flex",justifyContent:"center"}}>
                            <input type="checkbox" checked={allChecked} title="이 페이지 전체 선택"
                              onChange={()=>setCertChecked(prev=>{
                                const n=new Set(prev);
                                if(allChecked) pageIds.forEach(id=>n.delete(id));
                                else pageIds.forEach(id=>n.add(id));
                                return n;
                              })}
                              style={{width:14,height:14,accentColor:T.orange,cursor:"pointer"}}/>
                          </div>
                        );
                      })()}
                      {["글번호","학생","회차","정시여부","제목","닉네임","작성일","제출","미션","충실도","종합"].map(h=>(
                        <div key={h} style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.85)",textAlign:h==="제목"?"left":"center"}}>{h}</div>
                      ))}
                    </div>
                    {certRecords.length===0&&(
                      <div style={{padding:24,textAlign:"center",color:T.muted,fontSize:13}}>
                        크롤링된 글이 없습니다. 우측 상단 "지금 크롤링" 버튼을 눌러주세요.
                      </div>
                    )}
                    {pageRecords.map((rec,i)=>{
                      const recSessions = getCertSessions(rec);
                      const isOverride = !!(rec.session_override && rec.session_override.length > 0);
                      const rowBg = i%2===0?T.white:"#F9FAFB";
                      const articleId = rec.post_url ? rec.post_url.split("/").pop() : rec.id;
                      const hasScore = rec.completeness_score !== null && rec.completeness_score !== undefined;
                      const hasSubmit   = rec.submit_score   !== null && rec.submit_score   !== undefined;
                      const hasMission  = rec.mission_score  !== null && rec.mission_score  !== undefined;
                      const hasFidelity = rec.fidelity_score !== null && rec.fidelity_score !== undefined;
                      const openScoreModal = () => setCertScoreModal({
                        id: rec.id, postTitle: rec.post_title,
                        // 미채점 글은 만점 기준값(제출 50 / 미션 30 / 충실도 20)을 기본 입력해 둠
                        submit:   hasSubmit   ? String(rec.submit_score)   : "50",
                        mission:  hasMission  ? String(rec.mission_score)  : "30",
                        fidelity: hasFidelity ? String(rec.fidelity_score) : "20",
                      });
                      const openSessionModal = () => setCertSessionModal({
                        id: rec.id, postTitle: rec.post_title, posted_at: rec.posted_at,
                        sessions: recSessions.slice(),
                      });
                      const scoreCellStyle = (has) => ({
                        cursor:"pointer", fontSize:13, fontWeight:800, padding:"3px 4px",
                        borderRadius:4, border:"1px dashed transparent",
                        color: has ? T.navy : T.muted, textAlign:"center",
                      });
                      const isChecked = certChecked.has(rec.id);
                      return (
                        <div key={rec.id} style={{display:"grid",gridTemplateColumns:"34px 50px 80px 80px 60px 1fr 90px 65px 50px 50px 50px 56px",
                          padding:"8px 12px",gap:6,borderTop:`1px solid ${T.border}`,
                          background:isChecked?"#FFF7ED":rowBg,alignItems:"center"}}>
                          {/* 체크박스 */}
                          <div style={{display:"flex",justifyContent:"center"}}>
                            <input type="checkbox" checked={isChecked}
                              onChange={()=>setCertChecked(prev=>{const n=new Set(prev); n.has(rec.id)?n.delete(rec.id):n.add(rec.id); return n;})}
                              style={{width:14,height:14,accentColor:T.orange,cursor:"pointer"}}/>
                          </div>
                          {/* 글번호 */}
                          <div style={{fontSize:11,color:T.muted,textAlign:"center",fontFamily:"monospace"}}>{articleId}</div>
                          {/* 학생 (가운데) */}
                          <div style={{fontSize:12,fontWeight:700,color:rec.matched_student_name?T.navy:T.muted,textAlign:"center"}}>
                            {rec.matched_student_name||"—"}
                          </div>
                          {/* 회차 (클릭 → 편집 모달, 다중 회차 칩) */}
                          <div onClick={openSessionModal}
                            style={{textAlign:"center",fontSize:11,cursor:"pointer",padding:"2px 0",
                              borderRadius:4,border:`1px dashed ${isOverride?"#4F46E5":"transparent"}`}}
                            title={isOverride?"수동 지정됨":"작성일 기반 자동"}
                            onMouseOver={e=>{if(!isOverride)e.currentTarget.style.borderColor="#D1D5DB";}}
                            onMouseOut={e=>{if(!isOverride)e.currentTarget.style.borderColor="transparent";}}>
                            {recSessions.length === 0
                              ? <span style={{color:T.muted}}>—</span>
                              : <div style={{display:"flex",flexWrap:"wrap",gap:2,justifyContent:"center"}}>
                                  {recSessions.map(sn => (
                                    <span key={sn} style={{
                                      fontSize:10,fontWeight:700,
                                      padding:"1px 5px",borderRadius:8,
                                      background:isOverride?"#EEF2FF":"#F3F4F6",
                                      color:isOverride?"#4F46E5":T.navy,
                                      border:`1px solid ${isOverride?"#C7D2FE":T.border}`,
                                    }}>{sn}회</span>
                                  ))}
                                </div>}
                          </div>
                          {/* 정시여부 (회차별, 다중이면 모두 표기) */}
                          <div style={{textAlign:"center",fontSize:10,lineHeight:1.3}}>
                            {recSessions.length === 0
                              ? <span style={{color:T.muted}}>—</span>
                              : recSessions.map(sn => {
                                  const late = isLateForSession(rec, sn);
                                  return (
                                    <div key={sn}>
                                      {recSessions.length>1 && <span style={{color:T.muted,marginRight:2}}>{sn}회</span>}
                                      {late
                                        ? <span style={{color:"#B45309",fontWeight:700}}>⚠️ 지각</span>
                                        : <span style={{color:"#065F46",fontWeight:700}}>✅ 정시</span>}
                                    </div>
                                  );
                                })}
                          </div>
                          {/* 제목 (링크, 좌측 정렬) */}
                          <div style={{fontSize:11,overflow:"hidden"}}>
                            <a href={cafeArticleUrl(rec.post_url)} target="_blank" rel="noreferrer"
                              style={{color:T.navy,textDecoration:"none",fontWeight:600,
                                display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                              title={rec.post_title}
                              onMouseOver={e=>e.currentTarget.style.color=T.orange}
                              onMouseOut={e=>e.currentTarget.style.color=T.navy}>
                              {rec.post_title}
                            </a>
                          </div>
                          {/* 닉네임 (가운데) */}
                          <div style={{fontSize:11,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"center"}}>{rec.naver_nickname}</div>
                          {/* 작성일 (가운데) */}
                          <div style={{fontSize:11,color:T.muted,textAlign:"center"}}>
                            {(()=>{const k=new Date(new Date(rec.posted_at).getTime()+9*3600*1000);return`${k.getUTCMonth()+1}/${k.getUTCDate()}`;})()}
                          </div>
                          {/* 점수 4열 (제출/미션/충실도/종합) — 어디든 클릭 → 모달 */}
                          <div onClick={openScoreModal} style={scoreCellStyle(hasSubmit)}
                            onMouseOver={e=>e.currentTarget.style.borderColor="#D1D5DB"}
                            onMouseOut={e=>e.currentTarget.style.borderColor="transparent"}>
                            {hasSubmit ? rec.submit_score : "—"}
                          </div>
                          <div onClick={openScoreModal} style={scoreCellStyle(hasMission)}
                            onMouseOver={e=>e.currentTarget.style.borderColor="#D1D5DB"}
                            onMouseOut={e=>e.currentTarget.style.borderColor="transparent"}>
                            {hasMission ? rec.mission_score : "—"}
                          </div>
                          <div onClick={openScoreModal} style={scoreCellStyle(hasFidelity)}
                            onMouseOver={e=>e.currentTarget.style.borderColor="#D1D5DB"}
                            onMouseOut={e=>e.currentTarget.style.borderColor="transparent"}>
                            {hasFidelity ? rec.fidelity_score : "—"}
                          </div>
                          <div onClick={openScoreModal} style={{...scoreCellStyle(hasScore), color: hasScore ? "#16A34A" : T.muted}}
                            onMouseOver={e=>e.currentTarget.style.borderColor="#D1D5DB"}
                            onMouseOut={e=>e.currentTarget.style.borderColor="transparent"}>
                            {hasScore ? rec.completeness_score : "—"}
                          </div>
                        </div>
                      );
                    })}
                  </Card>
                )}
                {/* 페이지네이션 */}
                {totalPages > 1 && (
                  <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:6,marginTop:14,flexWrap:"wrap"}}>
                    <button onClick={()=>setCertRecordsPage(1)} disabled={safePage===1}
                      style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${T.border}`,cursor:safePage===1?"default":"pointer",fontSize:12,color:safePage===1?T.muted:T.navy,background:"transparent"}}>«</button>
                    <button onClick={()=>setCertRecordsPage(p=>Math.max(1,p-1))} disabled={safePage===1}
                      style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${T.border}`,cursor:safePage===1?"default":"pointer",fontSize:12,color:safePage===1?T.muted:T.navy,background:"transparent"}}>‹</button>
                    {Array.from({length: totalPages}, (_,i)=>i+1).reduce((acc,p)=>{
                      if(p===1||p===totalPages||(p>=safePage-2&&p<=safePage+2)){
                        if(acc.length&&acc[acc.length-1]!=="..."&&p-acc[acc.length-1]>1) acc.push("...");
                        acc.push(p);
                      }
                      return acc;
                    },[]).map((p,i)=>p==="..."
                      ? <span key={`e${i}`} style={{padding:"4px 6px",fontSize:12,color:T.muted}}>…</span>
                      : <button key={p} onClick={()=>setCertRecordsPage(p)}
                          style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${p===safePage?T.navy:T.border}`,cursor:"pointer",fontSize:12,fontWeight:p===safePage?700:400,background:p===safePage?T.navy:"transparent",color:p===safePage?T.white:T.navy}}>{p}</button>
                    )}
                    <button onClick={()=>setCertRecordsPage(p=>Math.min(totalPages,p+1))} disabled={safePage===totalPages}
                      style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${T.border}`,cursor:safePage===totalPages?"default":"pointer",fontSize:12,color:safePage===totalPages?T.muted:T.navy,background:"transparent"}}>›</button>
                    <button onClick={()=>setCertRecordsPage(totalPages)} disabled={safePage===totalPages}
                      style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${T.border}`,cursor:safePage===totalPages?"default":"pointer",fontSize:12,color:safePage===totalPages?T.muted:T.navy,background:"transparent"}}>»</button>
                  </div>
                )}
              </div>
              );
            })()}

            {/* ── 회차 편집 모달 (다중 선택) ── */}
            {certSessionModal && (() => {
              const cur = certSessionModal.sessions || [];
              const autoNum = getSessionInfo(certSessionModal.posted_at).sessionNum;
              const postedKstDate = (()=>{const k=new Date(new Date(certSessionModal.posted_at).getTime()+9*3600*1000);return`${k.getUTCMonth()+1}/${k.getUTCDate()}`;})();
              const toggle = (n) => {
                setCertSessionModal(p => {
                  const set = new Set(p.sessions);
                  if (set.has(n)) set.delete(n); else set.add(n);
                  return {...p, sessions: [...set].sort((a,b)=>a-b)};
                });
              };
              const save = async () => {
                await updateCertSessions(certSessionModal.id, certSessionModal.sessions);
                setCertSessionModal(null);
              };
              const resetAuto = () => {
                setCertSessionModal(p => ({...p, sessions: autoNum ? [autoNum] : []}));
              };
              return (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}
                onClick={()=>setCertSessionModal(null)}>
                <div style={{background:T.white,borderRadius:16,padding:22,maxWidth:520,width:"92%",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}
                  onClick={e=>e.stopPropagation()}>
                  <div style={{fontWeight:800,fontSize:15,color:T.navy,marginBottom:4}}>회차 지정</div>
                  <div style={{fontSize:12,color:T.muted,marginBottom:6,wordBreak:"break-word",lineHeight:1.4}}>
                    {certSessionModal.postTitle}
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginBottom:14}}>
                    작성일 {postedKstDate} · 자동 추천: {autoNum?`${autoNum}회`:"없음"}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4, 1fr)",gap:6,marginBottom:14}}>
                    {ROSTER2_NAVER_DATES.map((dt,idx)=>{
                      const sn = idx+1;
                      const checked = cur.includes(sn);
                      const isAuto = sn===autoNum;
                      return (
                        <button key={sn} onClick={()=>toggle(sn)}
                          style={{
                            padding:"7px 6px",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer",
                            textAlign:"center",lineHeight:1.3,
                            border:`1px solid ${checked?T.navy:isAuto?"#C7D2FE":T.border}`,
                            background:checked?T.navy:isAuto?"#EEF2FF":T.white,
                            color:checked?T.white:isAuto?"#4F46E5":T.navy,
                          }}>
                          <div>{sn}회{isAuto?" ⭐":""}</div>
                          <div style={{fontSize:9,fontWeight:500,opacity:0.85}}>{dt.getMonth()+1}/{dt.getDate()}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{fontSize:10,color:T.muted,marginBottom:14}}>
                    ⭐ = 작성일 기준 자동 추천 회차 · 모두 해제 시 자동 매칭으로 복귀
                  </div>
                  <div style={{display:"flex",gap:8,justifyContent:"space-between",alignItems:"center"}}>
                    <button onClick={resetAuto}
                      style={{...css.btnOutline,padding:"6px 12px",fontSize:12}}>자동 복귀</button>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>setCertSessionModal(null)} disabled={certSessionSaving}
                        style={{...css.btnOutline,padding:"7px 18px",fontSize:13,opacity:certSessionSaving?0.5:1}}>취소</button>
                      <button onClick={save} disabled={certSessionSaving}
                        style={{...css.btnOrange,padding:"7px 18px",fontSize:13,opacity:certSessionSaving?0.6:1,display:"flex",alignItems:"center",gap:6}}>
                        {certSessionSaving ? <><Spinner size={12} color="#fff"/>저장 중</> : "확인"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* ── 점수 입력 팝업 모달 (제출 50 + 필수미션 30 + 충실도 20 = 100) ── */}
            {certScoreModal && (() => {
              const clamp = (v,max) => {
                if(v==="" || v==null) return "";
                const n = Math.max(0, Math.min(max, Number(v)));
                return Number.isFinite(n) ? String(n) : "";
              };
              const toNum = v => v==="" || v==null ? 0 : Number(v);
              const total = toNum(certScoreModal.submit) + toNum(certScoreModal.mission) + toNum(certScoreModal.fidelity);
              const submit = async () => {
                await updateCertScore(certScoreModal.id, {
                  submit: certScoreModal.submit, mission: certScoreModal.mission, fidelity: certScoreModal.fidelity,
                });
                setCertScoreModal(null);
              };
              const rowStyle = {display:"grid",gridTemplateColumns:"1fr 110px",alignItems:"center",gap:10,marginBottom:10};
              const inputStyle = {...css.input,width:"100%",padding:"8px 12px",fontSize:14,boxSizing:"border-box"};
              const labelStyle = {fontSize:12,fontWeight:700,color:T.navy};
              const hintStyle  = {fontSize:10,color:T.muted,fontWeight:400,marginLeft:4};
              return (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}
                onClick={()=>setCertScoreModal(null)}>
                <div style={{background:T.white,borderRadius:16,padding:24,maxWidth:460,width:"90%",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}
                  onClick={e=>e.stopPropagation()}
                  onKeyDown={e=>{
                    if(e.key==="Enter"){ e.preventDefault(); submit(); }
                    if(e.key==="Escape") setCertScoreModal(null);
                  }}>
                  <div style={{fontWeight:800,fontSize:15,color:T.navy,marginBottom:6}}>점수 입력</div>
                  <div style={{fontSize:12,color:T.muted,marginBottom:18,wordBreak:"break-word",lineHeight:1.4}}>
                    {certScoreModal.postTitle}
                  </div>
                  <div style={rowStyle}>
                    <div style={labelStyle}>① 제출<span style={hintStyle}>인증 주기 내 제출 (0~50)</span></div>
                    <input type="number" min="0" max="50" autoFocus
                      value={certScoreModal.submit}
                      onChange={e=>setCertScoreModal(p=>({...p,submit:clamp(e.target.value,50)}))}
                      style={inputStyle} placeholder="0~50" />
                  </div>
                  <div style={rowStyle}>
                    <div style={labelStyle}>② 필수 미션<span style={hintStyle}>모두 30 / 일부 15 (0~30)</span></div>
                    <input type="number" min="0" max="30"
                      value={certScoreModal.mission}
                      onChange={e=>setCertScoreModal(p=>({...p,mission:clamp(e.target.value,30)}))}
                      style={inputStyle} placeholder="0~30" />
                  </div>
                  <div style={rowStyle}>
                    <div style={labelStyle}>③ 내용 충실도<span style={hintStyle}>매우우수 20 / 우수 15 / 보통 10 / 미흡 5 (0~20)</span></div>
                    <input type="number" min="0" max="20"
                      value={certScoreModal.fidelity}
                      onChange={e=>setCertScoreModal(p=>({...p,fidelity:clamp(e.target.value,20)}))}
                      style={inputStyle} placeholder="0~20" />
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14,marginBottom:14,padding:"10px 14px",background:"#F0FDF4",borderRadius:8,border:"1px solid #BBF7D0"}}>
                    <div style={{fontSize:13,fontWeight:700,color:T.navy}}>종합</div>
                    <div style={{fontSize:18,fontWeight:800,color:"#16A34A"}}>{total}<span style={{fontSize:12,fontWeight:600,marginLeft:2}}>/ 100점</span></div>
                  </div>
                  <div style={{fontSize:10,color:T.muted,marginBottom:14}}>※ 세 칸 모두 비우고 확인 시 점수 삭제</div>
                  <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <button onClick={()=>setCertScoreModal(null)}
                      style={{...css.btnOutline,padding:"7px 18px",fontSize:13}}>취소</button>
                    <button onClick={submit} disabled={certScoreSaving}
                      style={{...css.btnOrange,padding:"7px 18px",fontSize:13,opacity:certScoreSaving?0.6:1}}>
                      {certScoreSaving?"저장 중...":"확인"}
                    </button>
                  </div>
                </div>
              </div>
              );
            })()}
          </div>
        );
      })()}

      {/* ── 회차별 확인 ── */}
      {adminTab==="roster2" && roster2Tab==="session" && (()=>{
        const kstDateStr = ts => {
          const k = new Date(new Date(ts).getTime() + 9*3600*1000);
          return `${k.getUTCFullYear()}-${String(k.getUTCMonth()+1).padStart(2,'0')}-${String(k.getUTCDate()).padStart(2,'0')}`;
        };
        const getSessionInfo = (posted_at) => {
          const postStr = kstDateStr(posted_at);
          for (let i = ROSTER2_NAVER_DATES.length - 1; i >= 0; i--) {
            const certStr = roster2FmtKey(ROSTER2_NAVER_DATES[i]);
            if (postStr >= certStr) return { sessionNum: i+1, isLate: isLateByDeadline(posted_at, ROSTER2_NAVER_DATES[i]) };
          }
          return { sessionNum: null, isLate: false };
        };
        const getCertSessions = (cert) => {
          if (cert.session_override && cert.session_override.length > 0) return cert.session_override.map(Number);
          const auto = getSessionInfo(cert.posted_at).sessionNum;
          return auto ? [auto] : [];
        };

        // 테스트학생 제외한 실제 학생
        const realStudents = certStudents.filter(s => s.name !== "테스트학생");
        const totalStudents = realStudents.length;

        const targetSession = sessionViewIdx + 1;
        const sessionDate = ROSTER2_NAVER_DATES[sessionViewIdx];

        // 해당 회차에 글 올린 학생별 best 점수글 (가장 먼저 올린 글 1건, 다중 회차 글 포함)
        const studentCertMap = new Map();
        attendanceCerts.forEach(c => {
          if (!c.assigned_student_id) return;
          if (!getCertSessions(c).includes(targetSession)) return;
          const prev = studentCertMap.get(c.assigned_student_id);
          if (!prev || new Date(c.posted_at) < new Date(prev.posted_at)) {
            studentCertMap.set(c.assigned_student_id, c);
          }
        });

        const submitted = realStudents.filter(s => studentCertMap.has(s.id));
        const notSubmitted = realStudents.filter(s => !studentCertMap.has(s.id));
        const submitRate = totalStudents > 0 ? Math.round(submitted.length/totalStudents*100) : 0;

        const scored = submitted
          .map(s => ({ name:s.name, cert: studentCertMap.get(s.id) }))
          .filter(x => x.cert.completeness_score !== null && x.cert.completeness_score !== undefined);
        const avgScore = scored.length > 0
          ? Math.round(scored.reduce((acc,x)=>acc+Number(x.cert.completeness_score),0)/scored.length)
          : null;
        const lowScored = scored
          .filter(x => Number(x.cert.completeness_score) < 80)
          .sort((a,b) => Number(a.cert.completeness_score) - Number(b.cert.completeness_score));

        const Stat = ({label, value, color}) => (
          <Card style={{padding:"14px 16px",textAlign:"center"}}>
            <div style={{fontSize:11,color:T.muted,fontWeight:600,marginBottom:4}}>{label}</div>
            <div style={{fontSize:22,fontWeight:900,color:color||T.navy,lineHeight:1.1}}>{value}</div>
          </Card>
        );
        const NameChip = ({label, color, bg, border}) => (
          <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"4px 10px",borderRadius:14,
            background:bg,color,border:`1px solid ${border}`,fontSize:12,fontWeight:700,margin:"3px 4px 3px 0"}}>{label}</span>
        );

        return (
          <div>
            {/* 회차 선택 버튼 */}
            <Card style={{padding:"12px 14px",marginBottom:16}}>
              <div style={{fontSize:11,color:T.muted,fontWeight:700,marginBottom:8}}>회차 선택</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {ROSTER2_NAVER_DATES.map((dt,idx)=>{
                  const active = idx===sessionViewIdx;
                  return (
                    <button key={idx} onClick={()=>setSessionViewIdx(idx)}
                      style={{padding:"6px 10px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",
                        border:`1px solid ${active?T.navy:T.border}`,
                        background:active?T.navy:T.white,
                        color:active?T.white:T.navy}}>
                      {idx+1}회 <span style={{fontSize:10,fontWeight:500,opacity:0.8,marginLeft:2}}>
                        ({dt.getMonth()+1}/{dt.getDate()} {ROSTER2_DAY_KO[dt.getDay()]})
                      </span>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* 통계 카드 5개 */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(5, 1fr)",gap:10,marginBottom:16}}>
              <Stat label="전체 인원" value={`${totalStudents}명`}/>
              <Stat label="인증 제출" value={`${submitted.length}명`} color="#16A34A"/>
              <Stat label="인증 미제출" value={`${notSubmitted.length}명`} color="#DC2626"/>
              <Stat label="제출률" value={`${submitRate}%`} color={submitRate>=80?"#16A34A":submitRate>=50?"#B45309":"#DC2626"}/>
              <Stat label="제출자 평균" value={avgScore!==null ? `${avgScore}점` : "—"} color="#4F46E5"/>
            </div>

            {/* 미제출자 명단 */}
            <Card style={{padding:"14px 16px",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                ⚠️ 미제출자 명단
                <span style={{fontSize:11,fontWeight:600,color:"#DC2626",background:"#FEE2E2",padding:"2px 8px",borderRadius:10}}>
                  {notSubmitted.length}명
                </span>
              </div>
              {notSubmitted.length===0 ? (
                <div style={{fontSize:12,color:T.muted,padding:"8px 4px"}}>전원 제출했습니다 🎉</div>
              ) : (
                <div>
                  {notSubmitted.map(s => (
                    <NameChip key={s.id} label={s.name} color="#991B1B" bg="#FEE2E2" border="#FECACA"/>
                  ))}
                </div>
              )}
            </Card>

            {/* 80점 이하 명단 */}
            <Card style={{padding:"14px 16px"}}>
              <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                📉 80점 미만 제출자
                <span style={{fontSize:11,fontWeight:600,color:"#B45309",background:"#FEF3C7",padding:"2px 8px",borderRadius:10}}>
                  {lowScored.length}명
                </span>
                <span style={{fontSize:10,fontWeight:500,color:T.muted,marginLeft:2}}>점수 낮은 순</span>
              </div>
              {lowScored.length===0 ? (
                <div style={{fontSize:12,color:T.muted,padding:"8px 4px"}}>해당 학생 없음</div>
              ) : (
                <div>
                  {lowScored.map(x => (
                    <NameChip key={x.name}
                      label={`${x.name} · ${x.cert.completeness_score}점`}
                      color="#92400E" bg="#FEF3C7" border="#FDE68A"/>
                  ))}
                </div>
              )}
            </Card>

            <div style={{fontSize:10,color:T.muted,marginTop:10,textAlign:"right"}}>
              ※ 회차 매칭: 정시·지각 글 모두 포함 (같은 회차 다중 글은 가장 먼저 올린 1건 기준) · 테스트학생 제외
              {sessionDate && ` · 기준일 ${sessionDate.getMonth()+1}/${sessionDate.getDate()}(${ROSTER2_DAY_KO[sessionDate.getDay()]})`}
            </div>
          </div>
        );
      })()}


      {adminTab==="dashboard"&&(
        <div>
          {/* ── 학생 상세 페이지 ── */}
          {detailStudent ? (
            <div>
              <button onClick={()=>setDetailStudent(null)}
                style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:8,border:`1px solid ${T.border}`,
                  background:T.white,color:T.navy,fontSize:13,fontWeight:700,cursor:"pointer",marginBottom:16}}>
                ← 전체 학생 목록으로
              </button>
              <div style={{background:T.navy,borderRadius:14,padding:"14px 18px",marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:40,height:40,borderRadius:"50%",background:T.orange,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:900,color:T.white,flexShrink:0}}>
                  {(detailStudent.name||"?")[0]}
                </div>
                <div>
                  <div style={{fontSize:16,fontWeight:800,color:T.white}}>{detailStudent.name}</div>
                  <div style={{fontSize:12,color:T.white+"99"}}>{detailStudent.grade} · 목표 EI {detailStudent.target_ei}점</div>
                </div>
              </div>
              <StudentDashboard
                logs={normLogs.filter(l=>l.uid===detailStudent.id)}
                profile={detailStudent}
                isAdminView={true}
              />
            </div>
          ) : (
            <div>
              {/* ── 전체 요약 KPI ── */}
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:10,marginBottom:16}}>
                {[
                  {label:"전체 평균 EI",value:avgEI,color:EI_COLOR(avgEI)},
                  {label:"총 학습 로그",value:filtered.length,unit:"건",color:T.navyMid},
                  {label:"Red Flag 학생",value:byStudent.filter(s=>s.redFlag).length,unit:"명",color:T.danger},
                  {label:"C-I 과신 비율",value:(ciRate*100).toFixed(1),unit:"%",color:ciRate>0.3?T.danger:T.success},
                ].map(({label,value,unit="",color})=>(
                  <Card key={label} style={{padding:"16px 18px"}}>
                    <div style={{fontSize:10,color:T.muted,marginBottom:8,letterSpacing:"0.06em",textTransform:"uppercase",fontWeight:700}}>{label}</div>
                    <NavyNum value={value} unit={unit} size={isMobile?22:26} color={color}/>
                  </Card>
                ))}
              </div>

              {/* ── 학생별 주요지표 목록 ── */}
              <Card style={{marginBottom:12}} onClick={()=>setDashFilterOpen(null)}>
                <SectionTitle sub="클릭하면 상세 대시보드로 이동합니다">👥 전체 학생 현황</SectionTitle>
                {byStudent.length===0
                  ? <div style={{color:T.muted,fontSize:13,textAlign:"center",padding:"24px 0"}}>학생 데이터 없음</div>
                  : <div style={{overflowX:"auto"}}>
                      {Object.values(dashColFilter).some(v=>v)&&(
                        <div style={{display:"flex",gap:6,marginBottom:8,alignItems:"center",flexWrap:"wrap"}}>
                          <span style={{fontSize:11,color:T.muted}}>필터:</span>
                          {Object.entries(dashColFilter).filter(([,v])=>v).map(([col,grp])=>{
                            const colLabel={week7:"7일EI",month30:"30일EI",allTime:"전체기간EI",ci:"과신비율"}[col];
                            const color=GRADE_C[grp];
                            const grpLabel={S:"녹색",A:"파랑",B:"검정",C:"주황",D:"빨강"}[grp];
                            return(
                              <span key={col} onClick={e=>{e.stopPropagation();setDashColFilter(f=>({...f,[col]:null}));}}
                                style={{fontSize:11,padding:"3px 8px",borderRadius:20,border:`1px solid ${color}40`,color,background:color+"15",cursor:"pointer",fontWeight:700}}>
                                {colLabel}: {grpLabel} ✕
                              </span>
                            );
                          })}
                          <span onClick={e=>{e.stopPropagation();setDashColFilter({});}} style={{fontSize:11,color:T.muted,cursor:"pointer",textDecoration:"underline"}}>전체 초기화</span>
                        </div>
                      )}
                      <div style={{display:"grid",gridTemplateColumns:isMobile?"2fr 0.7fr 1fr 0.9fr":"2fr 0.7fr 1fr 1fr 1fr 1fr 1fr 0.9fr",minWidth:isMobile?360:960,marginBottom:6}}>
                        {(isMobile
                          ?[{key:null,label:"이름/학년"},{key:null,label:"로그"},{key:"week7",label:"7일EI"},{key:null,label:"상태"}]
                          :[{key:null,label:"이름 / 학년"},{key:null,label:"로그(건)"},{key:null,label:"전체 학생 평균 EI"},{key:"week7",label:"최근 일주일 EI"},{key:"month30",label:"최근 한 달 EI"},{key:"allTime",label:"전체 기간 EI"},{key:"ci",label:"과신비율"},{key:null,label:"상태"}]
                        ).map(h=>(
                          <div key={h.label} onClick={e=>{if(h.key){e.stopPropagation();setDashFilterOpen(dashFilterOpen===h.key?null:h.key);}}}
                            style={{position:"relative",padding:"8px 12px",fontSize:11,color:h.key?T.navy:T.muted,fontWeight:700,
                              borderBottom:`2px solid ${T.border}`,cursor:h.key?"pointer":"default",userSelect:"none",
                              background:dashColFilter[h.key]?T.surfaceAlt:"transparent"}}>
                            <div style={{display:"flex",alignItems:"center",gap:3}}>
                              {h.key&&dashColFilter[h.key]&&<span style={{width:7,height:7,borderRadius:"50%",background:GRADE_C[dashColFilter[h.key]],display:"inline-block",flexShrink:0}}/>}
                              {h.label}
                              {h.key&&<span style={{fontSize:9,opacity:0.6,marginLeft:1}}>{dashFilterOpen===h.key?"▲":"▾"}</span>}
                            </div>
                            {h.key&&dashFilterOpen===h.key&&(
                              <div onClick={e=>e.stopPropagation()} style={{position:"absolute",top:"100%",left:0,zIndex:200,background:T.white,
                                border:`1px solid ${T.border}`,borderRadius:8,padding:6,boxShadow:"0 4px 12px rgba(0,0,0,0.12)",minWidth:90,marginTop:2}}>
                                <div onClick={()=>{setDashColFilter(f=>({...f,[h.key]:null}));setDashFilterOpen(null);}}
                                  style={{padding:"5px 8px",cursor:"pointer",fontSize:12,color:T.muted,borderRadius:4,background:!dashColFilter[h.key]?T.surfaceAlt:"transparent"}}>
                                  전체
                                </div>
                                {[{g:"S",label:"녹색",color:"#16A34A"},{g:"A",label:"파랑",color:"#2563EB"},{g:"B",label:"검정",color:"#111827"},{g:"C",label:"주황",color:"#F97316"},{g:"D",label:"빨강",color:"#DC2626"}].map(({g,label,color})=>(
                                  <div key={g} onClick={()=>{setDashColFilter(f=>({...f,[h.key]:g}));setDashFilterOpen(null);}}
                                    style={{padding:"5px 8px",cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",gap:6,borderRadius:4,background:dashColFilter[h.key]===g?T.surfaceAlt:"transparent"}}>
                                    <span style={{width:9,height:9,borderRadius:"50%",background:color,display:"inline-block",flexShrink:0}}/>{label}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {filteredByDash.map(s=>{
                        const fmtEI=v=>v!=null?v.toFixed(1):"—";
                        const ciDisp=s.ciRatePct!=null?s.ciRatePct.toFixed(0)+"%":"—";
                        const ciColor=s.ciRatePct!=null?(s.ciRatePct>=30?T.danger:s.ciRatePct>=20?T.orange:T.success):T.muted;
                        const COLS=isMobile?"2fr 0.7fr 1fr 0.9fr":"2fr 0.7fr 1fr 1fr 1fr 1fr 1fr 0.9fr";
                        return(
                          <div key={s.id} onClick={()=>setDetailStudent(allProfiles.find(p=>p.id===s.id))}
                            style={{display:"grid",gridTemplateColumns:COLS,minWidth:isMobile?360:960,cursor:"pointer",borderRadius:8,transition:"background 0.15s"}}
                            onMouseEnter={e=>e.currentTarget.style.background=T.surfaceAlt}
                            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <div style={{padding:"12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:8}}>
                              <div style={{width:30,height:30,borderRadius:"50%",background:T.navy,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:T.white,flexShrink:0}}>
                                {(s.name||"?")[0]}
                              </div>
                              <div>
                                <div style={{fontSize:13,fontWeight:700,color:T.navy,display:"flex",alignItems:"center",gap:5}}>
                                  {s.redFlag&&<span style={{fontSize:11}}>🔴</span>}{s.name}
                                </div>
                                <div style={{fontSize:11,color:T.muted}}>{s.grade||"—"}</div>
                              </div>
                            </div>
                            <div style={{padding:"12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center"}}>
                              <span style={{fontSize:13,color:T.muted}}>{s.logCount}</span>
                            </div>
                            {!isMobile&&<div style={{padding:"12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center"}}>
                              <span style={{fontSize:13,fontWeight:700,color:EI_COLOR(classAvgEI)}}>{classAvgEI}</span>
                            </div>}
                            <div style={{padding:"12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center"}}>
                              <span style={{fontSize:13,fontWeight:700,color:s.avg7!=null?EI_COLOR(s.avg7):T.muted}}>{fmtEI(s.avg7)}</span>
                            </div>
                            {!isMobile&&<div style={{padding:"12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center"}}>
                              <span style={{fontSize:13,fontWeight:700,color:s.avg30!=null?EI_COLOR(s.avg30):T.muted}}>{fmtEI(s.avg30)}</span>
                            </div>}
                            {!isMobile&&<div style={{padding:"12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center"}}>
                              <span style={{fontSize:13,fontWeight:700,color:s.avgAll!=null?EI_COLOR(s.avgAll):T.muted}}>{fmtEI(s.avgAll)}</span>
                            </div>}
                            {!isMobile&&<div style={{padding:"12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center"}}>
                              <span style={{fontSize:13,fontWeight:700,color:ciColor}}>{ciDisp}</span>
                            </div>}
                            <div style={{padding:"12px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center"}}>
                              {statusBadge(allProfiles.find(p=>p.id===s.id)?.approval_status)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                }
              </Card>

              {/* ── 자동 피드백 진단 ── */}
              <Card style={{marginBottom:12}}>
                <SectionTitle><span style={{display:"flex",alignItems:"center",gap:5}}>{HI.search(14,T.navy)} 자동 피드백 진단</span></SectionTitle>
                {feedbacks.map((f,i)=>(
                  <div key={i} style={{padding:"12px 16px",borderRadius:10,marginBottom:i<feedbacks.length-1?8:0,fontSize:13,color:T.navy,
                    background:f.type==="warn"?T.orangePale:f.type==="alert"?"#FEE2E2":"#F0FDF4",
                    border:`1px solid ${f.type==="warn"?T.orange+"50":f.type==="alert"?T.danger+"40":T.success+"40"}`}}>
                    <span style={{display:"flex",alignItems:"center",gap:7}}>
                      {HI[f.icon]?.(15, f.type==="ok"?"#16A34A":f.type==="warn"?"#EA580C":"#DC2626")}
                      {f.msg}
                    </span>
                  </div>
                ))}
              </Card>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:12,marginBottom:12}}>
                <Card>
                  <SectionTitle sub="과목별 평균 EI">📚 과목별 EI</SectionTitle>
                  {Object.entries(bySubject).map(([subj,ei])=>(
                    <div key={subj} style={{marginBottom:10}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                        <span style={{fontSize:13,color:T.textMid,fontWeight:600}}>{subj}</span>
                        <span style={{fontSize:13,fontWeight:800,color:EI_COLOR(ei)}}>{ei}</span>
                      </div>
                      <div style={{height:6,background:T.surfaceAlt,borderRadius:3}}>
                        <div style={{height:"100%",width:`${ei}%`,background:T.grad,borderRadius:3,transition:"width 0.6s"}}/>
                      </div>
                    </div>
                  ))}
                  {Object.keys(bySubject).length===0&&<div style={{color:T.muted,fontSize:13}}>데이터 없음</div>}
                </Card>
                <Card>
                  <SectionTitle sub="자기 객관화 분포">🧩 Co-In 분포</SectionTitle>
                  <ResponsiveContainer width="100%" height={175}>
                    <BarChart data={[{name:"C-C",v:coinT.cc},{name:"C-I",v:coinT.ci},{name:"I-C",v:coinT.ic},{name:"I-I",v:coinT.ii}]} margin={{top:4,right:8,bottom:4,left:0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
                      <XAxis dataKey="name" tick={{fill:T.muted,fontSize:12}}/>
                      <YAxis tick={{fill:T.muted,fontSize:10}} width={28}/>
                      <Tooltip content={<ChartTip/>}/>
                      <Bar dataKey="v" name="건수" radius={[4,4,0,0]}>{[GRAPH.ccColor,GRAPH.ciColor,GRAPH.icColor,GRAPH.iiColor].map((c,i)=><Cell key={i} fill={c}/>)}</Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
              {maData.length>0&&(
                <Card>
                  <SectionTitle>📈 EI 추이</SectionTitle>
                  <ResponsiveContainer width="100%" height={180}>
                    <ComposedChart data={maData} margin={{top:4,right:8,bottom:4,left:0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
                      <XAxis dataKey="date" tick={{fill:T.muted,fontSize:10}} tickFormatter={d=>d.slice(5)}/>
                      <YAxis domain={[0,100]} tick={{fill:T.muted,fontSize:10}} width={28}/>
                      <Tooltip content={<ChartTip/>}/>
                      <Bar dataKey="engramIndex" name="일일 EI" fill={T.navy+"22"} stroke={T.navy+"50"} radius={[3,3,0,0]}/>
                      <Line type="monotone" dataKey="movingAvg" name="7일 평균" stroke={T.orange} strokeWidth={2.5} dot={false}/>
                    </ComposedChart>
                  </ResponsiveContainer>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── 전체 현황 (회차×명단 종합 그리드) ── */}
      {adminTab==="roster2" && roster2Tab==="overview" && (()=>{
        // ROSTER2, 날짜 배열은 컴포넌트 외부 상수 사용 (렌더마다 재생성 방지)
        const naverDates   = ROSTER2_NAVER_DATES;
        const morningDates = ROSTER2_MORNING_DATES;
        const nightDates   = ROSTER2_NIGHT_DATES;
        const fmt    = roster2Fmt;
        const fmtKey = roster2FmtKey;
        const DAY_KO = ROSTER2_DAY_KO;

        // 카페 인증(N) → cert_students(name)→ attendanceCerts(assigned_student_id) 매칭 헬퍼
        const kstDateStr = ts => {
          const k = new Date(new Date(ts).getTime() + 9*3600*1000);
          return `${k.getUTCFullYear()}-${String(k.getUTCMonth()+1).padStart(2,'0')}-${String(k.getUTCDate()).padStart(2,'0')}`;
        };
        const certStudentByName = {};
        (certStudents||[]).forEach(cs => { certStudentByName[cs.name] = cs.id; });

        // 회차 매칭 (지각 포함). naverDates의 sessionIdx 단위로 cert를 분류.
        const getSessionForPostedAt = (posted_at) => {
          const postStr = kstDateStr(posted_at);
          for (let i = naverDates.length - 1; i >= 0; i--) {
            const certStr = fmtKey(naverDates[i]);
            if (postStr >= certStr) return { sessionIdx: i, isLate: isLateByDeadline(posted_at, naverDates[i]) };
          }
          return { sessionIdx: -1, isLate: false };
        };
        // cert의 회차 배열 (session_override 우선)
        const getCertSessionsLocal = (cert) => {
          if (cert.session_override && cert.session_override.length > 0) return cert.session_override.map(Number);
          const auto = getSessionForPostedAt(cert.posted_at).sessionIdx;
          return auto >= 0 ? [auto+1] : [];
        };
        // 학생의 sessionIdx별 cert(있으면 정시/지각 구분, 다중 회차 글 포함)
        const getNaverCertForSession = (studentName, sessionIdx) => {
          const csId = certStudentByName[studentName];
          if (csId === undefined) return null;
          const targetSession = sessionIdx + 1;
          for (const cert of attendanceCerts) {
            if (cert.assigned_student_id !== csId) continue;
            const sessions = getCertSessionsLocal(cert);
            if (sessions.includes(targetSession)) {
              const sessionDate = naverDates[sessionIdx];
              const late = isLateByDeadline(cert.posted_at, sessionDate);
              return { cert, isLate: late };
            }
          }
          return null;
        };

        // 출석 토글/조회
        const toggleAtt = (studentIdx, type, dateKey) => {
          const k = `${studentIdx}-${type}-${dateKey}`;
          setAttendance2(prev => ({...prev, [k]: !prev[k]}));
        };
        // 반환: null(미참석) / { valid: boolean, minutes: number|null }
        const getAtt = (studentIdx, type, dateKey) => {
          return attendance2[`${studentIdx}-${type}-${dateKey}`] || null;
        };

        // Group A ↔ Group B 매칭 (이름 또는 전화 마지막4자리)
        const phone4 = (ph) => ph.replace(/-/g,'').slice(-4);
        const matchedProfile = (s) =>
          (allProfiles||[]).find(p =>
            (p.role==="student"||p.role==="parent") &&
            (p.name===s.name || phone4(p.phone||"")===phone4(s.phone))
          );

        // 검색 필터 + 정렬
        const toggleSort = (col) => {
          setRosterSort(prev => prev.by===col ? {by:col,dir:prev.dir==="asc"?"desc":"asc"} : {by:col,dir:"asc"});
        };
        const SortBtn = ({col}) => {
          const active = rosterSort.by===col;
          const asc = rosterSort.dir==="asc";
          return (
            <span style={{display:"inline-flex",flexDirection:"column",lineHeight:1,marginLeft:3,cursor:"pointer",verticalAlign:"middle"}}
              onClick={()=>toggleSort(col)}>
              <span style={{fontSize:7,color:active&&asc?"#4F46E5":T.muted,lineHeight:1}}>▲</span>
              <span style={{fontSize:7,color:active&&!asc?"#4F46E5":T.muted,lineHeight:1}}>▼</span>
            </span>
          );
        };
        const filtered = ROSTER2
          .map((s, i) => ({...s, idx: i, profile: matchedProfile(s)}))
          .filter(s => s.name.includes(rosterSearch) || s.phone.includes(rosterSearch) || (s.profile?.email||"").includes(rosterSearch))
          .sort((a, b) => {
            const dir = rosterSort.dir==="asc" ? 1 : -1;
            if (rosterSort.by==="idx") return (a.idx - b.idx) * dir;
            return a.name.localeCompare(b.name, 'ko') * dir;
          });

        // 섹션 스타일 정의
        const SEC = {
          naver:   { bg:"#EEF2FF", color:"#4F46E5", label:"카페 인증",  dates: naverDates,   type:"N", total: naverDates.length },
          morning: { bg:"#FFF7ED", color:"#EA580C", label:"미라클모닝",   dates: morningDates, type:"M", total: morningDates.length },
          night:   { bg:"#F0FDF4", color:"#16A34A", label:"미라클나이트", dates: nightDates,   type:"나", total: nightDates.length },
        };
        const SECS = [SEC.naver, SEC.morning, SEC.night];

        // 고정 컬럼 공통 스타일
        const stickyBase = {
          position:"sticky", left:0, zIndex:2,
          background:T.surface, borderRight:`1px solid ${T.border}`,
        };
        const stickyHead = {...stickyBase, background:T.surfaceAlt};

        const CELL_W = 26;
        const CELL_H = 26;

        return (
          <div>
            {/* 상단: 제목 + 검색 */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
              <div style={{fontSize:15,fontWeight:800,color:T.navy,whiteSpace:"nowrap"}}>
                20HA 2기 학생 명단 {ROSTER2.length}명
              </div>
              <div style={{flex:1,minWidth:140,maxWidth:240,marginLeft:"auto"}}>
                <input
                  type="text"
                  placeholder="이름·전화번호 검색"
                  value={rosterSearch}
                  onChange={e=>setRosterSearch(e.target.value)}
                  style={{...css.input,padding:"7px 11px",fontSize:13}}
                />
              </div>
            </div>

            {/* 스프레드시트 테이블 */}
            <Card style={{padding:0,overflow:"hidden"}}>
              {/* 상단 스크롤바 */}
              <div id="r2-top"
                onScroll={e=>{ const b=document.getElementById('r2-body'); if(b) b.scrollLeft=e.target.scrollLeft; }}
                style={{overflowX:"auto",overflowY:"hidden",height:14,borderBottom:`1px solid ${T.border}`,cursor:"ew-resize"}}>
                <div style={{width:2820,height:1}}/>
              </div>
              <div id="r2-body"
                onScroll={e=>{ const t=document.getElementById('r2-top'); if(t) t.scrollLeft=e.target.scrollLeft; }}
                style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                <table style={{borderCollapse:"collapse",tableLayout:"fixed"}}>
                  <thead>
                    {/* 섹션 헤더 행 */}
                    <tr>
                      {/* sticky 고정 헤더 — rowSpan=2로 두 행 병합 */}
                      <th rowSpan={2} style={{...stickyHead, width:30, minWidth:30, fontSize:10, color:T.muted, fontWeight:700, textAlign:"center", padding:"4px 2px", borderBottom:`2px solid ${T.borderStrong}`, verticalAlign:"middle"}}>
                        #<SortBtn col="idx"/>
                      </th>
                      <th rowSpan={2} style={{...stickyHead, left:30, width:110, minWidth:110, fontSize:11, color:T.muted, fontWeight:700, padding:"4px 6px", borderBottom:`2px solid ${T.borderStrong}`, borderLeft:`1px solid ${T.border}`, verticalAlign:"middle"}}>
                        이름<SortBtn col="name"/>
                      </th>
                      {/* 이메일(ID연동) — rowSpan=2 */}
                      <th rowSpan={2} style={{fontSize:11, color:T.muted, fontWeight:700, padding:"4px 8px", borderBottom:`2px solid ${T.borderStrong}`, borderLeft:`1px solid ${T.border}`, minWidth:160, whiteSpace:"nowrap", verticalAlign:"middle", background:T.surfaceAlt}}>이메일 (ID연동)</th>
                      {/* 인증현황 요약 3열 헤더 */}
                      <th colSpan={3} style={{background:"#F5F3FF", color:"#6D28D9", fontSize:11, fontWeight:800, textAlign:"center", padding:"4px 0", borderBottom:`1px solid ${T.border}`, borderLeft:`2px solid #6D28D9`}}>인증 현황</th>
                      {/* 섹션 헤더 */}
                      {SECS.map((sec, si) => (
                        <th key={si} colSpan={sec.dates.length}
                          style={{
                            background: sec.bg, color: sec.color,
                            fontSize: 11, fontWeight: 800,
                            textAlign:"center", padding:"4px 0",
                            borderBottom:`1px solid ${T.border}`,
                            borderLeft:`2px solid ${sec.color}`,
                          }}>
                          {sec.label}
                        </th>
                      ))}
                    </tr>
                    {/* 날짜 헤더 행 — #/이름/이메일 칸 없음 (rowSpan으로 병합됨) */}
                    <tr>
                      {/* 요약 3열 서브헤더 */}
                      {[["네이버",SEC.naver.color,SEC.naver.bg],["모닝",SEC.morning.color,SEC.morning.bg],["나잇",SEC.night.color,SEC.night.bg]].map(([lbl,clr,bg],i)=>(
                        <th key={`sum-${i}`} style={{width:36,minWidth:36,fontSize:9,fontWeight:700,color:clr,background:bg,textAlign:"center",padding:"2px 0",borderBottom:`2px solid ${T.borderStrong}`,borderLeft:i===0?`2px solid #6D28D9`:`1px solid ${T.border}`}}>{lbl}</th>
                      ))}
                      {SECS.map((sec, si) =>
                        sec.dates.map((dt, di) => (
                          <th key={`${si}-${di}`}
                            style={{
                              width: CELL_W, minWidth: CELL_W,
                              fontSize: 8, fontWeight: 600,
                              color: sec.color, textAlign:"center",
                              padding:"2px 0", verticalAlign:"middle",
                              borderBottom:`2px solid ${T.borderStrong}`,
                              borderLeft: di===0 ? `2px solid ${sec.color}` : `1px solid ${T.border}`,
                              background: sec.bg,
                              whiteSpace:"nowrap", lineHeight:"1.2",
                            }}>
                            <div>{fmt(dt)}</div>
                            <div>{DAY_KO[dt.getDay()]}</div>
                          </th>
                        ))
                      )}
                      <th style={{background:T.surfaceAlt,borderBottom:`2px solid ${T.borderStrong}`,borderLeft:`1px solid ${T.border}`,minWidth:90}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={3 + 3 + naverDates.length + morningDates.length + nightDates.length}
                          style={{textAlign:"center",color:T.muted,padding:"40px 20px",fontSize:13}}>
                          검색 결과가 없습니다.
                        </td>
                      </tr>
                    ) : filtered.map((s, rowI) => {
                      const nCount = naverDates.filter((_, idx) => !!getNaverCertForSession(s.name, idx)).length;
                      // 분 임계값 미달은 카운트 제외 (att.valid만 카운트)
                      const mCount = morningDates.filter(dt => { const a = getAtt(s.idx,"M",fmtKey(dt));  return a && a.valid; }).length;
                      const naCount = nightDates.filter(dt => { const a = getAtt(s.idx,"나",fmtKey(dt)); return a && a.valid; }).length;
                      return (
                        <tr key={s.idx} style={{background: rowI%2===0 ? T.surface : T.surfaceAlt}}>
                          {/* 고정: 번호 */}
                          <td style={{
                            ...stickyBase,
                            width:30, minWidth:30,
                            textAlign:"center", fontSize:10, color:T.muted, fontWeight:700,
                            padding:"0", height:CELL_H,
                            background: rowI%2===0 ? T.surface : T.surfaceAlt,
                            borderBottom:`1px solid ${T.border}`,
                          }}>{s.idx+1}</td>
                          {/* 고정: 이름+연락처 */}
                          <td style={{
                            ...stickyBase, left:30,
                            width:110, minWidth:110,
                            padding:"2px 6px",
                            background: rowI%2===0 ? T.surface : T.surfaceAlt,
                            borderBottom:`1px solid ${T.border}`,
                            borderLeft:`1px solid ${T.border}`,
                            verticalAlign:"middle",
                          }}>
                            <div style={{textAlign:"center",fontSize:12,fontWeight:800,color:T.navy,lineHeight:1.3}}>{s.name}</div>
                            <div style={{textAlign:"center",fontSize:9,color:T.muted,fontFamily:"'DM Mono',monospace",marginTop:1}}>{s.phone}</div>
                          </td>
                          {/* 이메일(ID연동) */}
                          <td style={{
                            minWidth:160, height:CELL_H,
                            padding:"0 8px", fontSize:11,
                            borderBottom:`1px solid ${T.border}`,
                            borderLeft:`1px solid ${T.border}`,
                            background: rowI%2===0 ? T.surface : T.surfaceAlt,
                            verticalAlign:"middle",
                          }}>
                            {s.profile
                              ? <span style={{color:T.navy, fontWeight:600}}>{s.profile.email}</span>
                              : <span style={{color:T.muted, fontSize:10}}>미가입</span>
                            }
                          </td>
                          {/* 요약 3열 */}
                          {[[nCount,SEC.naver.total,SEC.naver.color,SEC.naver.bg,true],[mCount,SEC.morning.total,SEC.morning.color,SEC.morning.bg,false],[naCount,SEC.night.total,SEC.night.color,SEC.night.bg,false]].map(([cnt,tot,clr,bg,first],i)=>(
                            <td key={`sum-${i}`} style={{
                              width:36,minWidth:36,height:CELL_H,
                              textAlign:"center",verticalAlign:"middle",
                              fontSize:10,fontWeight:700,
                              color:cnt>0?clr:T.muted,
                              background:cnt===tot&&tot>0?bg:"transparent",
                              borderBottom:`1px solid ${T.border}`,
                              borderLeft:first?`2px solid #6D28D9`:`1px solid ${T.border}`,
                              padding:0,whiteSpace:"nowrap",
                            }}>{cnt}/{tot}</td>
                          ))}
                          {/* 출석 셀들 */}
                          {SECS.map((sec, si) =>
                            sec.dates.map((dt, di) => {
                              const key = fmtKey(dt);
                              // 카페 인증(N): O(정시) / △(지각), 회차 단위로 매칭
                              const naverMatch = sec.type === "N" ? getNaverCertForSession(s.name, di) : null;
                              const hasNaverCert = !!naverMatch;
                              const isLate = hasNaverCert && naverMatch.isLate;
                              // M/나 출석: {valid, minutes}. 미달은 valid=false, minutes는 표시용
                              const att = sec.type !== "N" ? getAtt(s.idx, sec.type, key) : null;
                              const preAbs = sec.type !== "N" && !!(att && att.preAbs);
                              const preAbsExcess = preAbs && !!att.preAbsExcess; // 한도 초과 → 미인정
                              const checked = sec.type === "N" ? hasNaverCert : !!(att && att.valid);
                              const underMin = sec.type !== "N" && att && !att.valid && !preAbs;
                              const minThr = sec.type === "나" ? MIN_ATTENDANCE_MINUTES.N : sec.type === "M" ? MIN_ATTENDANCE_MINUTES.M : null;
                              const status = sec.type === "N"
                                ? (hasNaverCert ? (isLate ? "지각 인정" : "정시 인증") : "미인증")
                                : (preAbsExcess ? "사전결석(한도초과·미인정)" : preAbs ? "사전결석(출석 인정)" : checked ? "완료" : underMin ? "참여시간미달" : "미참여");
                              const tipLines = [
                                { label: `${s.name} · ${dt.getMonth()+1}/${dt.getDate()} · ${sec.label}`, color: sec.color },
                                { label: `상태: ${status}`, color: preAbsExcess ? "#64748B" : preAbs ? "#2563EB" : checked ? "#16A34A" : underMin ? "#9A3412" : (sec.type==="N" && isLate) ? "#B45309" : T.muted },
                                ...(att && att.minutes != null ? [{ label: `참여 시간: ${att.minutes}분`, color: T.navy }] : []),
                                ...(underMin && minThr ? [{ label: `(인정 기준 ${minThr}분 미만)`, color: "#9A3412" }] : []),
                                { label: "클릭해서 수정", color: T.muted },
                              ];
                              const showTip = (e) => setGridTip({ x: e.clientX, y: e.clientY, lines: tipLines });
                              const moveTip = (e) => setGridTip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
                              const hideTip = () => setGridTip(null);
                              // 셀 클릭 → 편집 모달
                              const onCellClick = () => {
                                if (sec.type === "N") {
                                  const cert = naverMatch?.cert || null;
                                  const pd = naverDates[di];
                                  setGridCertModal({
                                    certId: cert?.id || null,
                                    studentId: certStudentByName[s.name] ?? null,
                                    studentName: s.name,
                                    sessionNum: di + 1,
                                    sessionLabel: `${di+1}회 · ${fmt(dt)}(${DAY_KO[dt.getDay()]}) 카페 인증`,
                                    postedAt: new Date(pd.getFullYear(), pd.getMonth(), pd.getDate(), 12, 0, 0).toISOString(),
                                    submit:   cert && cert.submit_score   != null ? String(cert.submit_score)   : "50",
                                    mission:  cert && cert.mission_score  != null ? String(cert.mission_score)  : "30",
                                    fidelity: cert && cert.fidelity_score != null ? String(cert.fidelity_score) : "20",
                                  });
                                } else {
                                  setAttEditModal({
                                    studentId: certStudentByName[s.name] ?? null,
                                    studentName: s.name,
                                    sessionDate: key,
                                    sessionType: sec.type === "나" ? "N" : "M",
                                    sessionLabel: `${fmt(dt)}(${DAY_KO[dt.getDay()]}) ${sec.label}`,
                                    present: !!(att && att.valid && !att.preAbs),
                                    preAbsence: preAbs,
                                  });
                                }
                                setGridTip(null);
                              };
                              return (
                                <td key={`${si}-${di}`}
                                  onMouseEnter={showTip} onMouseMove={moveTip} onMouseLeave={hideTip}
                                  onClick={onCellClick}
                                  style={{
                                    width: CELL_W, minWidth: CELL_W,
                                    height: CELL_H,
                                    textAlign:"center", verticalAlign:"middle",
                                    cursor:"pointer",
                                    fontSize: 12,
                                    fontWeight: (checked||underMin||preAbs) ? 800 : 400,
                                    color: preAbsExcess ? "#94A3B8"
                                                   : preAbs ? "#2563EB"
                                                   : checked ? (sec.type==="N" && isLate ? "#B45309" : sec.color)
                                                   : underMin ? "#9A3412" : "transparent",
                                    background: preAbsExcess ? "#F1F5F9"
                                                       : preAbs ? "#DBEAFE"
                                                       : checked ? (sec.type==="N" && isLate ? "#FEF3C7" : sec.bg)
                                                       : underMin ? "#FED7AA" : "transparent",
                                    borderBottom:`1px solid ${T.border}`,
                                    borderLeft: di===0 ? `2px solid ${sec.color}` : `1px solid ${T.border}`,
                                    userSelect:"none",
                                    padding:0,
                                  }}>
                                  {sec.type === "N"
                                    ? (hasNaverCert ? (isLate ? "△" : "O") : "")
                                    : (preAbs ? <span style={{fontSize:9,fontWeight:800}}>사전</span> : checked ? "✓" : underMin ? "△" : "")}
                                </td>
                              );
                            })
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        );
      })()}

      {/* 관리자 "학생 화면 보기" 풀스크린 오버레이 — viewerMode="parent"로 학생 본인 화면 그대로 렌더
          학부모 사이드바와 동일하게 cert/dashboard/history 3개 탭 지원 */}
      {viewAsStudent && (() => {
        const tabs = [
          {k:"cert", label:"20HA 2기 인증현황"},
          {k:"dashboard", label:"메타인지 분석"},
          {k:"history", label:"학습 기록"},
        ];
        return (
        <div style={{position:"fixed",inset:0,background:T.bg,zIndex:9999,overflow:"auto"}}>
          <div style={{position:"sticky",top:0,zIndex:1,background:"#FEF3C7",borderBottom:"1px solid #FDE68A",padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#92400E"}}>
              👁 관리자 view 모드 — <strong>{viewAsStudent.name}</strong> 학생이 보는 화면 그대로 (읽기 전용)
            </div>
            <button onClick={()=>setViewAsStudent(null)} style={{fontSize:12,fontWeight:700,padding:"6px 14px",borderRadius:8,border:"none",background:T.navy,color:T.white,cursor:"pointer"}}>← 돌아가기</button>
          </div>
          <div style={{background:T.surface,borderBottom:`1px solid ${T.border}`,padding:"8px 16px",display:"flex",gap:6,overflowX:"auto"}}>
            {tabs.map(t => (
              <button key={t.k} onClick={()=>setViewAsTab(t.k)}
                style={{padding:"7px 14px",borderRadius:8,border:"none",cursor:"pointer",fontSize:12,fontWeight:700,whiteSpace:"nowrap",
                  background: viewAsTab===t.k ? T.navy : "transparent",
                  color: viewAsTab===t.k ? T.white : T.navy}}>
                {t.label}
              </button>
            ))}
          </div>
          <div style={{padding:"16px",maxWidth:1280,margin:"0 auto"}}>
            {viewAsLoading && viewAsTab !== "cert" ? (
              <Card style={{padding:40, textAlign:"center", color:T.muted}}>학생 데이터 불러오는 중…</Card>
            ) : viewAsTab === "cert" ? (
              <StudentCertView profile={viewAsStudent} viewerMode="parent" />
            ) : viewAsTab === "dashboard" ? (
              <StudentDashboard logs={viewAsLogs} profile={viewAsStudent} isAdminView={true} />
            ) : (
              <LogHistory logs={viewAsLogs} onDelete={null} isAdmin={false} allProfiles={[viewAsStudent]} />
            )}
          </div>
        </div>
        );
      })()}

      {/* 전체현황 그리드 — 카페 인증 셀 편집 모달 (점수 수정 or 수동 인증 생성) */}
      {gridCertModal && (() => {
        const clamp = (v,max) => { if(v===""||v==null) return ""; const n=Math.max(0,Math.min(max,Number(v))); return Number.isFinite(n)?String(n):""; };
        const toNum = v => v==="" || v==null ? 0 : Number(v);
        const total = toNum(gridCertModal.submit) + toNum(gridCertModal.mission) + toNum(gridCertModal.fidelity);
        const rowStyle = {display:"grid",gridTemplateColumns:"1fr 110px",alignItems:"center",gap:10,marginBottom:10};
        const inputStyle = {...css.input,width:"100%",padding:"8px 12px",fontSize:14,boxSizing:"border-box"};
        const labelStyle = {fontSize:12,fontWeight:700,color:T.navy};
        const hintStyle  = {fontSize:10,color:T.muted,fontWeight:400,marginLeft:4};
        return (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setGridCertModal(null)}>
          <div style={{background:T.white,borderRadius:16,padding:24,maxWidth:460,width:"90%",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}
            onClick={e=>e.stopPropagation()}
            onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); saveGridCert(); } if(e.key==="Escape") setGridCertModal(null); }}>
            <div style={{fontWeight:800,fontSize:15,color:T.navy,marginBottom:6}}>카페 인증 점수</div>
            <div style={{fontSize:12,color:T.muted,marginBottom:6,lineHeight:1.4}}>
              <b style={{color:T.navy}}>{gridCertModal.studentName}</b> · {gridCertModal.sessionLabel}
            </div>
            <div style={{fontSize:11,marginBottom:16,fontWeight:700,color:gridCertModal.certId?"#16A34A":"#2563EB"}}>
              {gridCertModal.certId ? "기존 인증 점수 수정" : "이 회차 수동 인증 생성"}
            </div>
            <div style={rowStyle}>
              <div style={labelStyle}>① 제출<span style={hintStyle}>0~50</span></div>
              <input type="number" min="0" max="50" autoFocus value={gridCertModal.submit}
                onChange={e=>setGridCertModal(p=>({...p,submit:clamp(e.target.value,50)}))} style={inputStyle} placeholder="0~50" />
            </div>
            <div style={rowStyle}>
              <div style={labelStyle}>② 필수 미션<span style={hintStyle}>0~30</span></div>
              <input type="number" min="0" max="30" value={gridCertModal.mission}
                onChange={e=>setGridCertModal(p=>({...p,mission:clamp(e.target.value,30)}))} style={inputStyle} placeholder="0~30" />
            </div>
            <div style={rowStyle}>
              <div style={labelStyle}>③ 내용 충실도<span style={hintStyle}>0~20</span></div>
              <input type="number" min="0" max="20" value={gridCertModal.fidelity}
                onChange={e=>setGridCertModal(p=>({...p,fidelity:clamp(e.target.value,20)}))} style={inputStyle} placeholder="0~20" />
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14,marginBottom:14,padding:"10px 14px",background:"#F0FDF4",borderRadius:8,border:"1px solid #BBF7D0"}}>
              <div style={{fontSize:13,fontWeight:700,color:T.navy}}>종합</div>
              <div style={{fontSize:18,fontWeight:800,color:"#16A34A"}}>{total}<span style={{fontSize:12,fontWeight:600,marginLeft:2}}>/ 100점</span></div>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setGridCertModal(null)} style={{...css.btnOutline,padding:"7px 18px",fontSize:13}}>취소</button>
              <button onClick={saveGridCert} disabled={gridCertSaving || gridCertModal.studentId==null}
                style={{...css.btnOrange,padding:"7px 18px",fontSize:13,opacity:(gridCertSaving||gridCertModal.studentId==null)?0.6:1}}>
                {gridCertSaving?"저장 중...":"저장"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* 전체현황 그리드 — 출석 셀 편집 모달 (출석/결석 + 사전결석) */}
      {attEditModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setAttEditModal(null)}>
          <div style={{background:T.white,borderRadius:16,padding:24,maxWidth:420,width:"90%",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}
            onClick={e=>e.stopPropagation()}
            onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); saveGridAttendance(); } if(e.key==="Escape") setAttEditModal(null); }}>
            <div style={{fontWeight:800,fontSize:15,color:T.navy,marginBottom:6}}>출석 처리</div>
            <div style={{fontSize:12,color:T.muted,marginBottom:16,lineHeight:1.4}}>
              <b style={{color:T.navy}}>{attEditModal.studentName}</b> · {attEditModal.sessionLabel}
            </div>
            {/* 출석 / 결석 선택 */}
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              {[{v:true,label:"출석",bg:"#16A34A"},{v:false,label:"결석",bg:"#DC2626"}].map(o=>(
                <button key={String(o.v)}
                  onClick={()=>setAttEditModal(p=>({...p,present:o.v, ...(o.v?{preAbsence:false}:{})}))}
                  style={{flex:1,padding:"10px 0",borderRadius:10,fontSize:14,fontWeight:800,cursor:"pointer",
                    border:`2px solid ${attEditModal.present===o.v?o.bg:T.border}`,
                    background:attEditModal.present===o.v?o.bg:T.white,
                    color:attEditModal.present===o.v?T.white:T.muted}}>
                  {o.label}
                </button>
              ))}
            </div>
            {/* 사전결석 신청 (결석일 때만 의미) */}
            <label style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",borderRadius:10,cursor:attEditModal.present?"not-allowed":"pointer",
              background:attEditModal.present?"#F8FAFC":"#EFF6FF",border:`1px solid ${attEditModal.present?T.border:"#BFDBFE"}`,opacity:attEditModal.present?0.5:1}}>
              <input type="checkbox" disabled={attEditModal.present} checked={!!attEditModal.preAbsence}
                onChange={e=>setAttEditModal(p=>({...p,preAbsence:e.target.checked}))}
                style={{width:16,height:16,accentColor:"#2563EB"}} />
              <span style={{fontSize:13,fontWeight:700,color:attEditModal.present?T.muted:"#1D4ED8"}}>사전결석 신청</span>
            </label>
            <div style={{fontSize:10,color:T.muted,margin:"10px 2px 16px"}}>
              ※ 참여시간과 무관하게 처리됩니다. 사전결석은 출석으로 인정되며, 한도(모닝 3·나잇 5) 초과분은 카운트에서 제외됩니다.
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setAttEditModal(null)} style={{...css.btnOutline,padding:"7px 18px",fontSize:13}}>취소</button>
              <button onClick={saveGridAttendance} disabled={attEditSaving || attEditModal.studentId==null}
                style={{...css.btnOrange,padding:"7px 18px",fontSize:13,opacity:(attEditSaving||attEditModal.studentId==null)?0.6:1}}>
                {attEditSaving?"저장 중...":"저장"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 관리자 전체현황 그리드 출석 셀 hover tooltip */}
      {gridTip && (
        <div style={{
          position:"fixed",
          left: Math.min(window.innerWidth - 280, gridTip.x + 14),
          top:  Math.max(8, gridTip.y - 12),
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          padding: "8px 12px",
          fontSize: 11,
          boxShadow: "0 4px 14px rgba(25,29,84,0.18)",
          zIndex: 10000,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}>
          {gridTip.lines.map((l,i) => (
            <div key={i} style={{color: l.color, fontWeight: i===0?700:600, fontSize: i===0?11:10.5, marginBottom: i===gridTip.lines.length-1?0:3}}>
              {l.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// ADMIN: Make-up — 경고 2회 이상 누적자
// 경고 = 미제출(어제 이전 회차 한정) OR 80점 미만
// ══════════════════════════════════════════════════════
const MakeupView = () => {
  const [students, setStudents] = useState([]);
  const [certs, setCerts] = useState([]);
  const [exemptions, setExemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  // 사유 입력 모달 — {studentId, studentName, sn} | null
  const [exemptTarget, setExemptTarget] = useState(null);
  const [exemptReason, setExemptReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    const [{data:s}, {data:c}, {data:e}] = await Promise.all([
      supabase.rpc("get_cert_students"),
      supabase.rpc("get_attendance_certs", {
        p_from: new Date(ROSTER2_NAVER_DATES[0]).toISOString(),
        p_to:   new Date(ROSTER2_NAVER_DATES[ROSTER2_NAVER_DATES.length-1].getTime()+86400000).toISOString(),
      }),
      supabase.rpc("get_makeup_exemptions"),
    ]);
    setStudents(s||[]);
    setCerts(c||[]);
    setExemptions(e||[]);
    setLoading(false);
  };
  useEffect(()=>{ loadAll(); },[]);

  // (학생id, 회차) → 면제 row
  const exemptionMap = useMemo(() => {
    const m = new Map();
    exemptions.forEach(ex => m.set(`${ex.student_id}:${ex.session_idx}`, ex));
    return m;
  }, [exemptions]);
  const isExempt = (studentId, sn) => exemptionMap.has(`${studentId}:${sn}`);

  const submitExemption = async () => {
    if (!exemptTarget) return;
    const reason = exemptReason.trim();
    if (!reason) { alert("사유를 입력해주세요."); return; }
    setSubmitting(true);
    const { error } = await supabase.rpc("set_makeup_exemption", {
      p_student_id: exemptTarget.studentId, p_session_idx: exemptTarget.sn, p_reason: reason
    });
    setSubmitting(false);
    if (error) { alert("저장 실패: " + error.message); return; }
    setExemptTarget(null); setExemptReason("");
    loadAll();
  };
  const revokeExemption = async (ex) => {
    if (!window.confirm(`${ex.student_name} · ${ex.session_idx}회차 면제를 해제할까요?`)) return;
    const { error } = await supabase.rpc("revoke_makeup_exemption", { p_id: ex.id, p_revoke_reason: null });
    if (error) { alert("해제 실패: " + error.message); return; }
    loadAll();
  };

  const kstDateStr = ts => {
    const k = new Date(new Date(ts).getTime() + 9*3600*1000);
    return `${k.getUTCFullYear()}-${String(k.getUTCMonth()+1).padStart(2,'0')}-${String(k.getUTCDate()).padStart(2,'0')}`;
  };
  const getSessionAuto = (posted_at) => {
    const postStr = kstDateStr(posted_at);
    for (let i = ROSTER2_NAVER_DATES.length - 1; i >= 0; i--) {
      if (postStr >= roster2FmtKey(ROSTER2_NAVER_DATES[i])) return i+1;
    }
    return null;
  };
  const getCertSessions = (cert) => {
    if (cert.session_override && cert.session_override.length > 0) return cert.session_override.map(Number);
    const auto = getSessionAuto(cert.posted_at);
    return auto ? [auto] : [];
  };

  // 어제 KST 일자
  const now = new Date();
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate()-1);
  const yesterdayKey = roster2FmtKey(yesterday);

  // 회차 중 어제까지 끝난 회차만 검사 (회차 일자 <= 어제)
  // 1주차(1회·2회 = 5/20·5/24)는 적응 기간으로 Make-up 검사에서 제외
  const elapsedSessions = ROSTER2_NAVER_DATES
    .map((d,i)=>({idx:i, sn:i+1, date:d, key:roster2FmtKey(d)}))
    .filter(s => s.key <= yesterdayKey)
    .filter(s => s.sn > 2);

  // 학생별 분석 — 회차 순서대로 경고 검사 후 '연속 2회 이상' 발생한 학생만 대상자
  const realStudents = students.filter(s => s.name !== "테스트학생");
  const studentReports = realStudents.map(stu => {
    const warnings = [];
    // 회차 순서 보장 (sn 오름차순)
    const ordered = [...elapsedSessions].sort((a,b)=>a.sn-b.sn);
    let currentStreak = 0;
    let maxStreak = 0;
    let currentChain = [];
    let bestChain = [];
    ordered.forEach(sess => {
      // 면제된 회차는 정상 처리(streak 끊김). 별도 카운트 안 함.
      if (isExempt(stu.id, sess.sn)) { currentStreak = 0; currentChain = []; return; }
      const candidate = certs
        .filter(c => c.assigned_student_id === stu.id)
        .filter(c => getCertSessions(c).includes(sess.sn))
        .sort((a,b) => new Date(a.posted_at) - new Date(b.posted_at))[0];
      let warn = null;
      if (!candidate) {
        warn = {sn: sess.sn, date: sess.date, reason: "미제출"};
      } else if (candidate.completeness_score !== null && candidate.completeness_score !== undefined && Number(candidate.completeness_score) < 80) {
        warn = {sn: sess.sn, date: sess.date, reason: `80점 미만 (${candidate.completeness_score}점)`, score: Number(candidate.completeness_score)};
      }
      if (warn) {
        warnings.push(warn);
        currentStreak++;
        currentChain.push(warn);
        if (currentStreak > maxStreak) {
          maxStreak = currentStreak;
          bestChain = [...currentChain];
        }
      } else {
        currentStreak = 0;
        currentChain = [];
      }
    });
    return {student: stu, warnings, maxStreak, bestChain};
  }).filter(r => r.maxStreak >= 2)
    .sort((a,b) => b.maxStreak - a.maxStreak || b.warnings.length - a.warnings.length || a.student.name.localeCompare(b.student.name));

  if (loading) {
    return <Card style={{padding:40, textAlign:"center", color:T.muted}}>불러오는 중...</Card>;
  }

  return (
    <div>
      <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <div style={{fontSize:18,fontWeight:800,color:T.navy}}>📚 Make-up 대상자</div>
        <div style={{fontSize:12,color:T.muted}}>
          ※ 1주차(1·2회) 제외 · 3회차부터 어제({yesterday.getMonth()+1}/{yesterday.getDate()})까지 검사 · <strong>2회 연속 경고</strong> 시 대상자
        </div>
      </div>

      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:700,color:T.navy}}>
          현재 검사 회차: <span style={{color:"#4F46E5"}}>{elapsedSessions.length}개</span>
          {elapsedSessions.length>0 && <span style={{fontSize:11,color:T.muted,marginLeft:6}}>
            (1회 ~ {elapsedSessions[elapsedSessions.length-1].sn}회)
          </span>}
        </div>
        <div style={{marginLeft:"auto",fontSize:13,fontWeight:700,color:T.navy}}>
          대상자 <span style={{color:"#DC2626"}}>{studentReports.length}명</span>
        </div>
      </div>

      {studentReports.length === 0 ? (
        <Card style={{padding:40, textAlign:"center", color:T.muted, fontSize:13}}>
          현재 Make-up 대상자가 없습니다 🎉
        </Card>
      ) : (
        <div style={{display:"grid",gap:10}}>
          {studentReports.map(({student, warnings, maxStreak}) => {
            return (
            <Card key={student.id} style={{padding:"14px 16px"}}>
              <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:10,borderBottom:`1px solid ${T.border}`,paddingBottom:8,flexWrap:"wrap"}}>
                <div style={{fontSize:15,fontWeight:800,color:T.navy}}>{student.name}</div>
                <div style={{fontSize:11,color:T.muted}}>{student.phone||""}</div>
                <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{fontSize:12,fontWeight:700,
                    color:maxStreak>=4?"#DC2626":maxStreak>=3?"#B45309":"#92400E",
                    background:maxStreak>=4?"#FEE2E2":"#FEF3C7",
                    padding:"3px 10px",borderRadius:10,border:`1px solid ${maxStreak>=4?"#FECACA":"#FDE68A"}`}}>
                    연속 {maxStreak}회 경고
                  </span>
                  <span style={{fontSize:11,color:T.muted}}>(총 {warnings.length}회)</span>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(220px,1fr))",gap:6}}>
                {warnings.map(w => {
                  const isLow = w.reason.startsWith("80점");
                  return (
                    <div key={w.sn} style={{
                      padding:"6px 10px",borderRadius:8,fontSize:12,display:"flex",alignItems:"center",gap:6,
                      background:isLow?"#FEF3C7":"#FEE2E2",
                      border:`1px solid ${isLow?"#FDE68A":"#FECACA"}`,
                      color:isLow?"#92400E":"#991B1B",
                    }}>
                      <span style={{fontWeight:800}}>{w.sn}회</span>
                      <span style={{fontSize:10,opacity:0.8}}>({w.date.getMonth()+1}/{w.date.getDate()})</span>
                      <span style={{fontWeight:600,flex:1}}>· {w.reason}</span>
                      <button
                        onClick={()=>{ setExemptTarget({studentId: student.id, studentName: student.name, sn: w.sn, date: w.date}); setExemptReason(""); }}
                        style={{padding:"2px 8px",fontSize:11,fontWeight:700,borderRadius:6,
                          border:"1px solid #6366F1",background:"#fff",color:"#4F46E5",cursor:"pointer"}}>
                        제외
                      </button>
                    </div>
                  );
                })}
              </div>
            </Card>
            );
          })}
        </div>
      )}

      {/* 활성 면제 목록 (감사 추적) */}
      {exemptions.length > 0 && (
        <Card style={{padding:"14px 16px",marginTop:16}}>
          <div style={{fontSize:14,fontWeight:800,color:T.navy,marginBottom:10,display:"flex",alignItems:"baseline",gap:8}}>
            🛡️ 면제 적용 목록
            <span style={{fontSize:11,color:T.muted,fontWeight:500}}>총 {exemptions.length}건 — 자동 계산은 그대로 두되 이 학생·회차는 경고 대상에서 빠집니다</span>
          </div>
          <div style={{display:"grid",gap:6}}>
            {exemptions.map(ex => (
              <div key={ex.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",
                background:"#F0F9FF",border:"1px solid #BAE6FD",borderRadius:8,fontSize:12}}>
                <span style={{fontWeight:800,color:T.navy,minWidth:80}}>{ex.student_name}</span>
                <span style={{fontWeight:700,color:"#0369A1"}}>{ex.session_idx}회차</span>
                <span style={{flex:1,color:"#0C4A6E"}}>· {ex.reason}</span>
                <span style={{fontSize:10,color:T.muted}}>{new Date(ex.created_at).toLocaleDateString("ko-KR")}</span>
                <button onClick={()=>revokeExemption(ex)}
                  style={{padding:"2px 8px",fontSize:11,fontWeight:700,borderRadius:6,
                    border:"1px solid #DC2626",background:"#fff",color:"#DC2626",cursor:"pointer"}}>
                  해제
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 사유 입력 모달 */}
      {exemptTarget && (
        <div onClick={()=>!submitting && setExemptTarget(null)}
          style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:"#fff",borderRadius:12,padding:20,maxWidth:480,width:"100%",boxShadow:"0 10px 40px rgba(0,0,0,0.2)"}}>
            <div style={{fontSize:16,fontWeight:800,color:T.navy,marginBottom:6}}>Make-up 면제 처리</div>
            <div style={{fontSize:13,color:T.muted,marginBottom:14}}>
              <strong style={{color:T.navy}}>{exemptTarget.studentName}</strong> · <strong style={{color:T.navy}}>{exemptTarget.sn}회차</strong>
              {exemptTarget.date && <span> ({exemptTarget.date.getMonth()+1}/{exemptTarget.date.getDate()})</span>}
              를 경고 대상에서 제외합니다.
            </div>
            <textarea
              value={exemptReason}
              onChange={e=>setExemptReason(e.target.value)}
              placeholder="예: 가족 경조사로 결석 / 학교 행사 / 질병 (입원) 등"
              rows={3}
              style={{width:"100%",padding:10,fontSize:13,border:`1px solid ${T.border}`,borderRadius:8,resize:"vertical",boxSizing:"border-box",fontFamily:"inherit"}}
            />
            <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:14}}>
              <button onClick={()=>setExemptTarget(null)} disabled={submitting}
                style={{padding:"8px 16px",fontSize:13,fontWeight:600,borderRadius:8,border:`1px solid ${T.border}`,background:"#fff",color:T.muted,cursor:"pointer"}}>
                취소
              </button>
              <button onClick={submitExemption} disabled={submitting||!exemptReason.trim()}
                style={{padding:"8px 16px",fontSize:13,fontWeight:700,borderRadius:8,border:"none",
                  background:exemptReason.trim()?"#4F46E5":"#C7D2FE",color:"#fff",cursor:exemptReason.trim()?"pointer":"not-allowed"}}>
                {submitting?"저장 중…":"제외 적용"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// ADMIN: 20HA 2기 출석체크 (CSV 업로드)
// ══════════════════════════════════════════════════════
const AttendanceUploadView = ({onRefresh}) => {
  const [parsed, setParsed] = useState(null);   // {sessionDate, sessionType, sessionIdx, sessionLabel, matched: [{name,csvName,id}], errors: [{csvName,reason}], errorActions: {csvName: 'ignore'|'name'|...}}
  const [students, setStudents] = useState([]); // cert_students
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const fileRef = useRef(null);

  useEffect(()=>{
    supabase.rpc("get_cert_students").then(({data})=>setStudents(data||[]));
  },[]);

  // 헬퍼: 폰 뒷4 / 중간4 인덱스
  const studentIndex = useMemo(()=>{
    const byLast4 = {}, byMid4 = {}, byName = {};
    students.forEach(s=>{
      const ph = (s.phone||"").replace(/-/g,"");
      if(ph.length>=4) byLast4[ph.slice(-4)] = s;
      if(ph.length>=7) byMid4[ph.slice(3,7)] = s;
      byName[s.name] = s;
    });
    return {byLast4, byMid4, byName};
  },[students]);

  // CSV 한 줄 파싱 (큰따옴표 처리 포함)
  const parseCSVLine = (line) => {
    const out = [];
    let cur = "", inQ = false;
    for(let i=0;i<line.length;i++){
      const c = line[i];
      if(c === '"'){ inQ = !inQ; continue; }
      if(c === ',' && !inQ){ out.push(cur); cur = ""; continue; }
      cur += c;
    }
    out.push(cur);
    return out;
  };

  // 이름 파싱 ("강예나/초5/7565", "초6/김가인/0142", "심수윤", "한설아/초5/1931 (한설아)" 등)
  const parseParticipantName = (raw) => {
    if(!raw) return {name:null, code:null};
    // 괄호 안 내용 제거
    let s = raw.replace(/\([^)]*\)/g, "").trim();
    // 슬래시/공백/언더스코어 → 공백
    s = s.replace(/[\/_]/g, " ").replace(/\s+/g," ").trim();
    // 4자리 숫자 (전화 뒷4 또는 중간4)
    const codeM = s.match(/\d{4}/);
    const code = codeM ? codeM[0] : null;
    // 한글 이름 (2~4자, "주차" 등 제외)
    const names = (s.match(/[가-힣]{2,4}/g) || []).filter(n => !["주차","초등","중등","고등"].includes(n) && !/^[초중고]\d?$/.test(n));
    const name = names[0] || null;
    return {name, code};
  };


  // CSV 처리
  // Zoom 참가자 보고서: 같은 학생이 입퇴장 반복 시 여러 row로 분리됨 (예: 20분, 10분).
  // 학생별로 모든 row의 분(참가 시간)을 합산한 뒤 임계값과 비교.
  const handleFile = async (file) => {
    setSaveMsg(null);
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(l=>l.trim());
    if(lines.length < 2){ alert("CSV가 비어있어요"); return; }
    const header = parseCSVLine(lines[0]);
    const nameIdx = header.findIndex(h => h.includes("이름") || h.includes("원래"));
    const joinIdx = header.findIndex(h => h.includes("참가"));
    // 분 컬럼 — "기간(분)" / "참가 시간(분)" / "Duration" 등
    const durIdx  = header.findIndex(h => /기간|Duration|참여|지속/i.test(h) && /분|Minute|\(분\)/i.test(h));
    if(nameIdx < 0 || joinIdx < 0){ alert("CSV 헤더 형식이 다릅니다 (이름/참가 시간 필요)"); return; }
    if(durIdx < 0){ alert('CSV에 분 컬럼이 없어요. 헤더에 "기간(분)" 또는 "Duration (Minutes)" 필요'); return; }

    // 첫 데이터 행에서 날짜/시간 파악 → 모닝/나잇 구분
    let sessionDate = null, sessionType = null;
    for(let i=1;i<lines.length;i++){
      const cols = parseCSVLine(lines[i]);
      const t = cols[joinIdx];
      // "2026/05/22 06:57:23 AM" 형태
      const m = t && t.match(/(\d{4})\/(\d{1,2})\/(\d{1,2})\s+\d{1,2}:\d{2}(?::\d{2})?\s*(AM|PM)/i);
      if(m){
        sessionDate = `${m[1]}-${String(m[2]).padStart(2,'0')}-${String(m[3]).padStart(2,'0')}`;
        sessionType = m[4].toUpperCase()==="AM" ? "M" : "N"; // M=모닝, N=나잇
        break;
      }
    }
    if(!sessionDate){ alert("CSV에서 날짜를 인식하지 못했어요"); return; }
    // 회차일 기준 실효 임계값 (6/2까지 유예 → 0, 이후 모닝15·나잇50)
    const minMin = attendanceThreshold(sessionType, sessionDate);

    // 회차 매칭 (모닝/나잇)
    const dates = sessionType==="M" ? ROSTER2_MORNING_DATES : ROSTER2_NIGHT_DATES;
    const sessionIdx = dates.findIndex(d => roster2FmtKey(d) === sessionDate);
    const typeLabel = sessionType==="M" ? "미라클모닝" : "미라클나이트";
    const sessionLabel = sessionIdx >= 0
      ? `${sessionDate.slice(5)} ${sessionIdx+1}회 ${typeLabel}`
      : `${sessionDate.slice(5)} ${typeLabel} (일정 외)`;

    // 학생별 분 합산. 같은 학생이 csv에 여러 줄(입퇴장 반복) 있으면 모두 합산.
    // 미매칭 학생도 누적해서 한 번만 errors에 기록
    const studentTotals = {}; // student_id → {id,name,csvNames:Set,totalMinutes}
    const errorTotals = {};   // csvName → {csvName, reason, minutes}
    for(let i=1;i<lines.length;i++){
      const cols = parseCSVLine(lines[i]);
      const rawName = cols[nameIdx];
      if(!rawName) continue;
      const mins = Math.max(0, parseInt((cols[durIdx]||"").replace(/[^\d]/g,""),10) || 0);
      // 외부인 ("아이작", "Kevin" 등 알려진 외부인은 제외)
      if(/iforyou76@/i.test(cols[1]||"")) continue;
      if(rawName === "아이작" || rawName === "Kevin") continue;
      const {name, code} = parseParticipantName(rawName);
      if(!name && !code){
        if(!errorTotals[rawName]) errorTotals[rawName] = {csvName: rawName, reason: "이름/코드 파싱 실패", minutes: 0};
        errorTotals[rawName].minutes += mins;
        continue;
      }
      // 매칭 시도: 이름+뒷4 → 이름+중간4 → 이름만 → 코드만
      let stu = null;
      if(name && code){
        if(studentIndex.byLast4[code] && studentIndex.byLast4[code].name===name) stu = studentIndex.byLast4[code];
        else if(studentIndex.byMid4[code] && studentIndex.byMid4[code].name===name) stu = studentIndex.byMid4[code];
      }
      if(!stu && name && studentIndex.byName[name]) stu = studentIndex.byName[name];
      if(!stu && code){
        if(studentIndex.byLast4[code]) stu = studentIndex.byLast4[code];
        else if(studentIndex.byMid4[code]) stu = studentIndex.byMid4[code];
      }
      if(stu){
        if(!studentTotals[stu.id]) studentTotals[stu.id] = {id: stu.id, name: stu.name, csvNames: new Set(), totalMinutes: 0};
        studentTotals[stu.id].csvNames.add(rawName);
        studentTotals[stu.id].totalMinutes += mins;
      } else {
        const key = rawName;
        if(!errorTotals[key]) errorTotals[key] = {csvName: rawName, reason: name?`'${name}' 명단에 없음`:`코드 ${code||'?'} 매칭 실패`, minutes: 0};
        errorTotals[key].minutes += mins;
      }
    }
    // 임계값 분리: 매칭된 학생을 출석 인정/미달로 분리
    const allMatched = Object.values(studentTotals)
      .map(s => ({...s, csvNames: [...s.csvNames]}))
      .sort((a,b)=>a.name.localeCompare(b.name,'ko'));
    const matched   = allMatched.filter(s => s.totalMinutes >= minMin);
    const underMin  = allMatched.filter(s => s.totalMinutes <  minMin);
    const errors = Object.values(errorTotals);
    const errorActions = {};
    errors.forEach(e => { errorActions[e.csvName] = "ignore"; });

    setParsed({
      sessionDate, sessionType, sessionIdx,
      sessionLabel, typeLabel, minMin,
      matched, underMin, errors, errorActions,
      fileName: file.name,
    });
  };

  const handleSave = async () => {
    if(!parsed) return;
    setSaving(true);
    setSaveMsg(null);
    // 에러 학생 중 수동 할당된 것 추가 (분 정보 없음 → 임계값 최소값으로 처리)
    const minMin = parsed.minMin || 0;
    const extraEntries = [];
    Object.entries(parsed.errorActions).forEach(([csvName, action])=>{
      if(action && action !== "ignore"){
        const sid = parseInt(action);
        if(!isNaN(sid)){
          // 에러 학생의 합산 분 (있으면 사용, 없으면 임계값)
          const errInfo = parsed.errors.find(e => e.csvName === csvName);
          const minutes = errInfo && errInfo.minutes >= minMin ? errInfo.minutes : minMin;
          extraEntries.push({ student_id: sid, minutes });
        }
      }
    });
    // 인정 + 미달 모두 DB 저장 (분은 그대로). 카운트 로직은 분 >= 임계값일 때만 출석으로 처리
    const matchedEntries = parsed.matched.map(m => ({ student_id: m.id, minutes: m.totalMinutes }));
    const underEntries   = (parsed.underMin || []).map(m => ({ student_id: m.id, minutes: m.totalMinutes }));
    const entries = [...matchedEntries, ...underEntries, ...extraEntries];
    const {error} = await supabase.rpc("save_attendance_batch", {
      p_session_date: parsed.sessionDate,
      p_session_type: parsed.sessionType,
      p_entries: entries,
    });
    setSaving(false);
    if(error){
      setSaveMsg({type:"error", text:`저장 실패: ${error.message}`});
    } else {
      setSaveMsg({type:"success", text:`✅ ${parsed.sessionLabel} 출석 ${entries.length}명 저장 완료!`});
      setTimeout(()=>{ setParsed(null); setSaveMsg(null); if(fileRef.current) fileRef.current.value=""; },2500);
      onRefresh && onRefresh();
    }
  };

  return (
    <div style={{display:"grid",gap:14}}>
      <div style={{fontSize:18,fontWeight:800,color:T.navy}}>📋 20HA 2기 출석체크</div>
      <Card style={{padding:"18px 20px"}}>
        <div style={{fontSize:13,color:T.muted,marginBottom:12}}>
          Zoom 참가자 CSV를 업로드하면 자동으로 날짜/회차/세션을 판별해 출석을 정리합니다.
          미리보기 후 <b>저장</b>을 눌러야 DB에 반영됩니다.
        </div>
        {/* 드래그 드롭 + 클릭 영역 — 직접 클릭으로 input.click() 호출 */}
        <div
          onClick={()=>fileRef.current?.click()}
          onDragOver={e=>{e.preventDefault();e.currentTarget.style.background="#EEF2FF";e.currentTarget.style.borderColor="#4F46E5";}}
          onDragLeave={e=>{e.currentTarget.style.background="#F8FAFC";e.currentTarget.style.borderColor="#CBD5E1";}}
          onDrop={e=>{
            e.preventDefault();
            e.currentTarget.style.background="#F8FAFC";
            e.currentTarget.style.borderColor="#CBD5E1";
            const f = e.dataTransfer.files?.[0];
            if(f) handleFile(f);
          }}
          style={{
            display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
            gap:10,padding:"32px 20px",borderRadius:12,
            border:"2px dashed #CBD5E1",background:"#F8FAFC",
            cursor:"pointer",transition:"all .15s",userSelect:"none",
          }}>
          <div style={{fontSize:36,opacity:0.6}}>📄</div>
          <div style={{fontSize:14,fontWeight:700,color:T.navy}}>CSV 파일을 여기로 드래그하거나 클릭해서 선택</div>
          <div style={{fontSize:11,color:T.muted}}>참가자 명단 CSV (Zoom 내보내기)</div>
          {/* hidden input — 화면에서만 보이지 않게, 접근성 유지 */}
          <input ref={fileRef} type="file" accept=".csv,text/csv"
            onChange={e=>{const f=e.target.files?.[0]; if(f) handleFile(f);}}
            style={{position:"absolute",left:-9999,width:1,height:1,opacity:0}}/>
        </div>
      </Card>

      {parsed && (
        <>
          {/* 세션 정보 */}
          <Card style={{padding:"18px 20px",background:parsed.sessionIdx>=0?"#F0FDF4":"#FEF3C7",border:`2px solid ${parsed.sessionIdx>=0?"#86EFAC":"#FCD34D"}`}}>
            <div style={{fontSize:12,color:T.muted,marginBottom:6}}>파일: {parsed.fileName}</div>
            <div style={{fontSize:20,fontWeight:900,color:T.navy}}>{parsed.sessionLabel}</div>
            <div style={{fontSize:12,color:T.navy,marginTop:6,fontWeight:600}}>
              출석 인정 기준: {parsed.minMin > 0 ? <><strong>{parsed.minMin}분 이상</strong> 참여</> : <strong>참여 시 인정 (유예 기간)</strong>}
            </div>
            {parsed.sessionIdx < 0 && (
              <div style={{fontSize:11,color:"#B45309",marginTop:6}}>⚠️ ROSTER2 회차 일정에 없는 날짜입니다. 저장은 가능하지만 회차 표시는 안 됩니다.</div>
            )}
          </Card>

          {/* 인정 명단 */}
          <Card style={{padding:"16px 20px"}}>
            <div style={{fontSize:14,fontWeight:800,color:T.navy,marginBottom:10}}>
              ✅ 출석 인정 ({parsed.matched.length}명) <span style={{fontSize:11,color:T.muted,fontWeight:500}}>· {parsed.minMin}분 이상</span>
            </div>
            {parsed.matched.length === 0 ? (
              <div style={{fontSize:12,color:T.muted,textAlign:"center",padding:"16px 0"}}>매칭된 학생 없음</div>
            ) : (
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:6}}>
                {parsed.matched.map(m => (
                  <div key={m.id} style={{padding:"6px 10px",borderRadius:8,background:"#F0FDF4",border:"1px solid #86EFAC",fontSize:12,color:T.navy,display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
                    <div style={{flex:1,minWidth:0}}>
                      <span style={{fontWeight:700}}>{m.name}</span>
                      {m.csvNames[0] !== m.name && (
                        <div style={{fontSize:9,color:T.muted,marginTop:2}}>CSV: {m.csvNames[0]}</div>
                      )}
                    </div>
                    <span style={{fontSize:11,fontWeight:700,color:"#16A34A",padding:"2px 6px",borderRadius:6,background:"#fff",border:"1px solid #BBF7D0",whiteSpace:"nowrap"}}>{m.totalMinutes}분</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* 미달 명단 — 임계값 미만 참여자 (출석 인정 X) */}
          {parsed.underMin && parsed.underMin.length > 0 && (
            <Card style={{padding:"16px 20px",background:"#FFF7ED",border:"1px solid #FDBA74"}}>
              <div style={{fontSize:14,fontWeight:800,color:"#9A3412",marginBottom:10}}>
                ⏱️ 출석 미달 ({parsed.underMin.length}명) <span style={{fontSize:11,color:"#B45309",fontWeight:500}}>· {parsed.minMin}분 미만 — 출석 처리 안 됨</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:6}}>
                {parsed.underMin.map(m => (
                  <div key={m.id} style={{padding:"6px 10px",borderRadius:8,background:"#fff",border:"1px solid #FED7AA",fontSize:12,color:"#9A3412",display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
                    <div style={{flex:1,minWidth:0}}>
                      <span style={{fontWeight:700}}>{m.name}</span>
                      {m.csvNames[0] !== m.name && (
                        <div style={{fontSize:9,color:T.muted,marginTop:2}}>CSV: {m.csvNames[0]}</div>
                      )}
                    </div>
                    <span style={{fontSize:11,fontWeight:700,color:"#DC2626",padding:"2px 6px",borderRadius:6,background:"#fff",border:"1px solid #FECACA",whiteSpace:"nowrap"}}>{m.totalMinutes}분</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* 에러 처리 */}
          {parsed.errors.length > 0 && (
            <Card style={{padding:"16px 20px",background:"#FFFBEB",border:"1px solid #FCD34D"}}>
              <div style={{fontSize:14,fontWeight:800,color:"#B45309",marginBottom:10}}>
                ⚠️ 매칭 실패 ({parsed.errors.length}명) — 어떻게 처리할지 선택
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {parsed.errors.map((e,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:T.white,borderRadius:8,border:`1px solid ${T.border}`}}>
                    <span style={{flex:1,fontSize:12,color:T.navy}}>
                      <b>{e.csvName}</b>
                      <span style={{fontSize:10,color:T.muted,marginLeft:6}}>({e.reason})</span>
                    </span>
                    <select value={parsed.errorActions[e.csvName] || "ignore"}
                      onChange={ev=>setParsed(p=>({...p,errorActions:{...p.errorActions,[e.csvName]:ev.target.value}}))}
                      style={{...css.input,fontSize:11,padding:"4px 8px",width:160}}>
                      <option value="ignore">무시</option>
                      <optgroup label="학생 수동 할당">
                        {students.filter(s=>s.sort_order<43).map(s=>(
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* 저장 영역 */}
          <div style={{display:"flex",justifyContent:"flex-end",gap:10,alignItems:"center"}}>
            {saveMsg && (
              <span style={{fontSize:13,fontWeight:700,color:saveMsg.type==="success"?"#065F46":T.danger}}>
                {saveMsg.text}
              </span>
            )}
            <button onClick={()=>{setParsed(null); if(fileRef.current) fileRef.current.value="";}}
              style={{...css.btnOutline,padding:"10px 20px",fontSize:14}}>취소</button>
            <button onClick={handleSave} disabled={saving}
              style={{...css.btnOrange,padding:"10px 24px",fontSize:14,opacity:saving?0.6:1}}>
              {saving?"저장 중...":"💾 저장"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// ADMIN: 만점 테스트
// ══════════════════════════════════════════════════════

// ── 문항 관리 (Question Bank) ─────────────────────────
const ManjeomQuestionsTab = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // 선택된 문항 또는 새 문항 객체
  const [filter, setFilter] = useState({type:"", kw:""});
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    const {data, error} = await supabase.from("manjeom_questions")
      .select("*").order("created_at",{ascending:false});
    if(error){ alert("문항 조회 실패: "+error.message); }
    else setList(data||[]);
    setLoading(false);
  };
  useEffect(()=>{ load(); }, []);

  const filtered = list.filter(q=>{
    if(filter.type && q.q_type!==filter.type) return false;
    const kw = (filter.kw||"").trim();
    if(kw){
      const kwLower = kw.toLowerCase();
      const inPrompt = (q.prompt||"").toLowerCase().includes(kwLower);
      const inTags   = (q.tags||[]).some(t => String(t||"").toLowerCase().includes(kwLower));
      const inSeq    = String(q.seq||"").includes(kw) || `q-${q.seq||""}`.toLowerCase().includes(kwLower);
      if(!inPrompt && !inTags && !inSeq) return false;
    }
    return true;
  });

  const MCQ_DEFAULT_CHOICES = ["①","②","③","④","⑤"];
  const MCQ_LABELS = ["①","②","③","④","⑤","⑥","⑦","⑧","⑨","⑩"];
  const blank = () => ({
    id:null, q_type:"short", prompt:"", image_url:"",
    choices:["",""], answers:[""], tags:[],
    _correctIdx: [], // mcq 정답 보기 인덱스 (편집 UX용)
    _tagsRaw: "",     // 태그 원본 입력 문자열 (쉼표 입력 시 사라지지 않도록)
  });

  const startNew = () => setEditing(blank());
  const startEdit = (q) => {
    // mcq 정답은 정렬된 콤마 결합 문자열 1개로 저장됨 (예: "①,③")
    let correctIdx = [];
    if(q.q_type === "mcq" && Array.isArray(q.answers) && q.answers.length > 0){
      const canonical = String(q.answers[0] || "");
      const tokens = canonical.split(",").map(s=>s.trim()).filter(Boolean);
      correctIdx = tokens.map(t => (q.choices||[]).indexOf(t)).filter(i => i >= 0);
    }
    setEditing({
      ...q,
      choices: q.choices || ["",""],
      answers: q.answers || [""],
      tags: q.tags || [],
      _correctIdx: correctIdx,
      _tagsRaw: (q.tags || []).join(", "),
    });
  };

  const upload = async (file) => {
    if(!file) return;
    if(file.size > 2*1024*1024){ alert("파일 크기는 2MB 이하만 가능합니다."); return; }
    if(!/^image\/(png|jpeg|jpg|webp)$/.test(file.type)){ alert("PNG/JPG/WebP만 가능합니다."); return; }
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `q_${Date.now()}_${Math.random().toString(36).slice(2,8)}.${ext}`;
    const {error} = await supabase.storage.from("manjeom-images").upload(path, file, {contentType:file.type});
    if(error){ alert("업로드 실패: "+error.message); setUploading(false); return; }
    const {data:{publicUrl}} = supabase.storage.from("manjeom-images").getPublicUrl(path);
    setEditing(e => ({...e, image_url: publicUrl}));
    setUploading(false);
  };

  const save = async () => {
    if(!editing.prompt.trim()){ alert("문제 본문을 입력해주세요."); return; }

    let answers;
    if(editing.q_type === "short"){
      answers = (editing.answers||[]).map(a=>String(a||"").trim()).filter(Boolean);
      if(answers.length === 0){ alert("정답을 한 개 이상 입력해주세요."); return; }
    } else {
      const cleanChoices = (editing.choices||[]).map(c=>String(c||"").trim()).filter(Boolean);
      if(cleanChoices.length < 2){ alert("보기를 2개 이상 입력해주세요."); return; }
      const corr = (editing._correctIdx||[]).filter(i => i < cleanChoices.length);
      if(corr.length === 0){ alert("정답 보기를 1개 이상 선택해주세요."); return; }
      // 다중 정답 가능 — 정렬된 콤마 결합 한 문자열로 저장 (예: "①,③")
      const canonical = [...corr].sort((a,b)=>a-b).map(i => cleanChoices[i]).join(",");
      answers = [canonical];
      editing.choices = cleanChoices;
    }

    const parsedTags = String(editing._tagsRaw || "")
      .split(",").map(s=>s.trim()).filter(Boolean);
    const payload = {
      q_type: editing.q_type,
      prompt: editing.prompt.trim(),
      image_url: editing.image_url || null,
      choices: editing.q_type==="mcq" ? editing.choices : null,
      answers,
      tags: parsedTags.length>0 ? parsedTags : null,
    };

    let res;
    if(editing.id){
      res = await supabase.from("manjeom_questions").update({...payload, updated_at:new Date().toISOString()}).eq("id", editing.id);
    } else {
      res = await supabase.from("manjeom_questions").insert(payload);
    }
    if(res.error){ alert("저장 실패: "+res.error.message); return; }
    setEditing(null);
    await load();
  };

  const remove = async (q) => {
    if(!window.confirm(`"${q.prompt.slice(0,30)}..." 문항을 삭제할까요?\n(시험지에 사용 중이면 삭제 불가)`)) return;
    const {error} = await supabase.from("manjeom_questions").delete().eq("id", q.id);
    if(error){
      if(error.code === "23503") alert("이 문항은 시험지에 포함되어 있어 삭제할 수 없습니다. 먼저 시험지에서 제거하세요.");
      else alert("삭제 실패: "+error.message);
      return;
    }
    setEditing(null);
    await load();
  };

  return (
    <div style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:14,alignItems:"start"}}>
      {/* 좌측 목록 */}
      <Card style={{padding:"14px 16px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:800,color:T.navy}}>문항 ({list.length})</div>
          <button style={{...css.btnOrange,padding:"6px 12px",fontSize:11}} onClick={startNew}>+ 새 문항</button>
        </div>
        <div style={{display:"flex",gap:6,marginBottom:10}}>
          <select value={filter.type} onChange={e=>setFilter(f=>({...f,type:e.target.value}))}
            style={{...css.select,padding:"6px 8px",fontSize:11,flex:"0 0 100px"}}>
            <option value="">전체</option>
            <option value="short">단답형</option>
            <option value="mcq">객관식</option>
          </select>
          <input value={filter.kw} onChange={e=>setFilter(f=>({...f,kw:e.target.value}))}
            placeholder="검색 (본문·태그·번호)" style={{...css.input,padding:"6px 10px",fontSize:11}}/>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:520,overflowY:"auto"}}>
          {loading ? <Spinner/> :
            filtered.length===0 ? <div style={{fontSize:11,color:T.muted,textAlign:"center",padding:"20px 0"}}>문항이 없습니다.</div> :
            filtered.map(q=>(
              <div key={q.id} onClick={()=>startEdit(q)}
                style={{padding:"8px 10px",borderRadius:8,border:`1px solid ${editing?.id===q.id?T.navy:T.border}`,
                  background:editing?.id===q.id?"#EEF2FF":T.surfaceAlt,cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
                  <span style={{fontSize:10,fontWeight:800,color:T.muted,letterSpacing:"0.04em"}}>Q-{q.seq||"?"}</span>
                  <Pill color={q.q_type==="short"?"#0EA5E9":"#A855F7"}>{q.q_type==="short"?"단답":"객관식"}</Pill>
                  {q.image_url && <span style={{fontSize:10}}>🖼️</span>}
                </div>
                <div style={{fontSize:12,color:T.navy,lineHeight:1.4,marginBottom:4,
                  display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{q.prompt}</div>
                {(q.tags||[]).length > 0 && (
                  <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                    {q.tags.map((t,i)=>(
                      <span key={i} onClick={(e)=>{e.stopPropagation(); setFilter(f=>({...f,kw:t}));}}
                        style={{fontSize:10,color:T.navy,background:"#E0E7FF",
                          padding:"1px 7px",borderRadius:10,fontWeight:600,cursor:"pointer"}}
                        title={`태그 "${t}"로 검색`}>
                        #{t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          }
        </div>
      </Card>

      {/* 우측 편집 */}
      <Card style={{padding:"18px 20px"}}>
        {!editing ? (
          <div style={{textAlign:"center",padding:"60px 0",color:T.muted}}>
            <div style={{fontSize:36,opacity:0.4,marginBottom:10}}>📝</div>
            <div style={{fontSize:13}}>좌측에서 문항을 선택하거나 "+ 새 문항"을 눌러주세요.</div>
          </div>
        ) : (
          <div style={{display:"grid",gap:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{fontSize:14,fontWeight:800,color:T.navy}}>{editing.id ? "문항 편집":"새 문항"}</div>
                {editing.id && editing.seq && (
                  <span style={{fontSize:11,fontWeight:700,color:T.muted,
                    padding:"2px 8px",background:T.surfaceAlt,borderRadius:6,letterSpacing:"0.04em"}}>
                    Q-{editing.seq}
                  </span>
                )}
              </div>
              <div style={{display:"flex",gap:6}}>
                {editing.id && <button style={{...css.btnGhost,padding:"7px 14px",fontSize:11,color:"#DC2626"}} onClick={()=>remove(editing)}>삭제</button>}
                <button style={{...css.btnGhost,padding:"7px 14px",fontSize:11}} onClick={()=>setEditing(null)}>취소</button>
                <button style={{...css.btnOrange,padding:"7px 14px",fontSize:11}} onClick={save}>저장</button>
              </div>
            </div>

            <div style={{display:"flex",gap:14,alignItems:"center"}}>
              <label style={{...css.label,marginBottom:0}}>유형</label>
              {[{v:"short",l:"단답형"},{v:"mcq",l:"객관식"}].map(({v,l})=>(
                <label key={v} style={{display:"flex",alignItems:"center",gap:4,fontSize:12,cursor:"pointer"}}>
                  <input type="radio" name="qtype" checked={editing.q_type===v}
                    onChange={()=>setEditing(e=>{
                      // 객관식 전환 시 보기를 ①~⑤ 5개로 기본 채움 (보기 내용은 이미지에 포함)
                      if(v==="mcq" && (e.choices.length<2 || e.choices.every(c=>!c?.trim()))){
                        return {...e,q_type:v,choices:[...MCQ_DEFAULT_CHOICES],_correctIdx:[]};
                      }
                      return {...e,q_type:v};
                    })}/>{l}
                </label>
              ))}
            </div>

            <div>
              <label style={css.label}>문제 본문</label>
              <textarea value={editing.prompt} onChange={e=>setEditing({...editing,prompt:e.target.value})}
                rows={3} style={{...css.input,fontFamily:"inherit",resize:"vertical"}}/>
            </div>

            <div>
              <label style={css.label}>문제 이미지 (선택, 권장 가로 800~1200px, 2MB 이하)</label>
              {editing.image_url ? (
                <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                  <img src={editing.image_url} alt="문제" style={{maxWidth:300,borderRadius:8,border:`1px solid ${T.border}`}}/>
                  <button style={{...css.btnGhost,padding:"6px 12px",fontSize:11}}
                    onClick={()=>setEditing(e=>({...e,image_url:""}))}>이미지 제거</button>
                </div>
              ) : (
                <>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                    style={{display:"none"}}
                    onChange={e=>{const f=e.target.files?.[0]; if(f) upload(f); e.target.value="";}}/>
                  <button style={{...css.btnGhost,padding:"8px 16px",fontSize:12}} onClick={()=>fileRef.current?.click()}>
                    {uploading ? <Spinner size={14}/> : "📁 이미지 업로드"}
                  </button>
                </>
              )}
            </div>

            {editing.q_type === "short" ? (
              <div>
                <label style={css.label}>정답 후보 (여러 개 등록 가능 — 학생이 어느 것 하나만 맞으면 정답. 공백/대소문자 무시)</label>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {editing.answers.map((a,i)=>(
                    <div key={i} style={{display:"flex",gap:6}}>
                      <input value={a} onChange={e=>{
                        const next=[...editing.answers]; next[i]=e.target.value;
                        setEditing({...editing,answers:next});
                      }} placeholder={`정답 ${i+1}`}
                      style={{...css.input,padding:"8px 12px",fontSize:13}}/>
                      <button style={{...css.btnGhost,padding:"6px 12px",fontSize:11}}
                        onClick={()=>setEditing(e=>({...e,answers:e.answers.filter((_,idx)=>idx!==i)}))}>✕</button>
                    </div>
                  ))}
                  <button style={{...css.btnOutline,alignSelf:"flex-start",padding:"6px 14px",fontSize:11}}
                    onClick={()=>setEditing(e=>({...e,answers:[...e.answers,""]}))}>+ 정답 추가</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                  <label style={{...css.label,marginBottom:0}}>
                    객관식 정답 ({editing.choices.length}개 보기 · 정답 번호만 체크 — 복수 가능)
                  </label>
                  <div style={{display:"flex",gap:6}}>
                    <button style={{...css.btnOutline,padding:"4px 10px",fontSize:11}}
                      disabled={editing.choices.length<=2}
                      onClick={()=>setEditing(ed=>{
                        const last = ed.choices.length - 1;
                        return {
                          ...ed,
                          choices: ed.choices.slice(0,last),
                          _correctIdx: (ed._correctIdx||[]).filter(i => i !== last),
                        };
                      })}>− 보기 줄이기</button>
                    <button style={{...css.btnOutline,padding:"4px 10px",fontSize:11}}
                      disabled={editing.choices.length>=10}
                      onClick={()=>setEditing(ed=>{
                        const i = ed.choices.length;
                        const lbl = MCQ_LABELS[i] || String(i+1);
                        return {...ed, choices:[...ed.choices, lbl]};
                      })}>+ 보기 늘리기</button>
                  </div>
                </div>
                <div style={{fontSize:11,color:T.muted,marginBottom:8}}>
                  보기 내용은 보통 문제 이미지에 포함됩니다. 텍스트도 넣고 싶다면 아래 "보기 텍스트 입력" 펼치세요.
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {editing.choices.map((c,i)=>(
                    <label key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",
                      border:`2px solid ${(editing._correctIdx||[]).includes(i)?"#16A34A":T.border}`,
                      borderRadius:10,
                      background:(editing._correctIdx||[]).includes(i)?"#F0FDF4":T.surfaceAlt,
                      cursor:"pointer",minWidth:64,justifyContent:"center"}}>
                      <input type="checkbox" checked={(editing._correctIdx||[]).includes(i)}
                        onChange={e=>setEditing(ed=>{
                          const set = new Set(ed._correctIdx||[]);
                          if(e.target.checked) set.add(i); else set.delete(i);
                          return {...ed,_correctIdx:[...set]};
                        })}/>
                      <span style={{fontSize:16,fontWeight:800,color:T.navy}}>{c || (MCQ_LABELS[i]||(i+1))}</span>
                    </label>
                  ))}
                </div>
                <details style={{marginTop:10}}>
                  <summary style={{fontSize:11,color:T.muted,cursor:"pointer"}}>보기 텍스트 입력 (선택)</summary>
                  <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:8}}>
                    {editing.choices.map((c,i)=>(
                      <div key={i} style={{display:"flex",gap:6,alignItems:"center"}}>
                        <span style={{fontSize:12,color:T.muted,width:20}}>{MCQ_LABELS[i]||(i+1)}</span>
                        <input value={c} onChange={e=>{
                          const next=[...editing.choices]; next[i]=e.target.value;
                          setEditing({...editing,choices:next});
                        }} placeholder={`보기 ${i+1} (선택)`}
                        style={{...css.input,padding:"6px 10px",fontSize:12}}/>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}

            <div>
              <label style={css.label}>태그 (선택, 쉼표 구분 — 예: "수학, 중1, 비례식")</label>
              <input value={editing._tagsRaw ?? (editing.tags||[]).join(", ")}
                onChange={e=>setEditing({...editing,_tagsRaw:e.target.value})}
                placeholder='예: 수학, 중1, 비례식'
                style={{...css.input,padding:"8px 12px",fontSize:13}}/>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

// ── 시험지 관리 (Test Composer) ────────────────────────
const ManjeomTestsTab = () => {
  const [tests, setTests] = useState([]);
  const [questions, setQuestions] = useState([]); // 문항 은행 전체
  const [editing, setEditing] = useState(null);   // 시험지 편집 객체
  const [editingQids, setEditingQids] = useState([]); // 시험지에 포함된 question id 배열 (순서)
  const [showPicker, setShowPicker] = useState(false);
  const [pickerKw, setPickerKw] = useState("");

  const load = async () => {
    const {data:t} = await supabase.from("manjeom_tests").select("*").order("created_at",{ascending:false});
    const {data:q} = await supabase.from("manjeom_questions").select("id,seq,q_type,prompt,image_url,tags").order("seq",{ascending:true});
    setTests(t||[]);
    setQuestions(q||[]);
  };
  useEffect(()=>{ load(); }, []);

  const startNew = () => { setEditing({id:null,title:"",description:"",is_published:false}); setEditingQids([]); };
  const startEdit = async (t) => {
    setEditing({...t});
    const {data} = await supabase.from("manjeom_test_questions")
      .select("question_id,q_order").eq("test_id",t.id).order("q_order");
    setEditingQids((data||[]).map(r=>r.question_id));
  };

  const save = async () => {
    if(!editing.title.trim()){ alert("제목을 입력해주세요."); return; }
    let testId = editing.id;
    if(testId){
      const {error} = await supabase.from("manjeom_tests")
        .update({title:editing.title,description:editing.description,is_published:editing.is_published,updated_at:new Date().toISOString()})
        .eq("id",testId);
      if(error){ alert("저장 실패: "+error.message); return; }
    } else {
      const {data,error} = await supabase.from("manjeom_tests")
        .insert({title:editing.title,description:editing.description,is_published:editing.is_published})
        .select().single();
      if(error){ alert("저장 실패: "+error.message); return; }
      testId = data.id;
    }
    // 문항 매핑 일괄 교체
    const {error:e2} = await supabase.rpc("set_manjeom_test_questions",{p_test_id:testId,p_question_ids:editingQids});
    if(e2){ alert("문항 매핑 저장 실패: "+e2.message); return; }
    setEditing(null);
    await load();
  };

  const remove = async (t) => {
    if(!window.confirm(`"${t.title}" 시험지를 삭제할까요?\n(배정 및 시도 기록도 함께 삭제됩니다)`)) return;
    const {error} = await supabase.from("manjeom_tests").delete().eq("id",t.id);
    if(error){ alert("삭제 실패: "+error.message); return; }
    setEditing(null);
    await load();
  };

  const move = (idx, dir) => {
    const next=[...editingQids]; const j=idx+dir;
    if(j<0||j>=next.length) return;
    [next[idx],next[j]]=[next[j],next[idx]];
    setEditingQids(next);
  };

  const addQuestions = (ids) => {
    setEditingQids(prev => [...prev, ...ids.filter(id=>!prev.includes(id))]);
    setShowPicker(false);
  };

  const qMap = Object.fromEntries(questions.map(q=>[q.id,q]));
  const pickerList = questions.filter(q =>
    !editingQids.includes(q.id) &&
    (!pickerKw || (q.prompt||"").includes(pickerKw))
  );

  return (
    <div style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:14,alignItems:"start"}}>
      <Card style={{padding:"14px 16px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:800,color:T.navy}}>시험지 ({tests.length})</div>
          <button style={{...css.btnOrange,padding:"6px 12px",fontSize:11}} onClick={startNew}>+ 새 시험지</button>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:520,overflowY:"auto"}}>
          {tests.length===0 ? <div style={{fontSize:11,color:T.muted,textAlign:"center",padding:"20px 0"}}>시험지가 없습니다.</div> :
            tests.map(t=>(
              <div key={t.id} onClick={()=>startEdit(t)}
                style={{padding:"10px 12px",borderRadius:8,border:`1px solid ${editing?.id===t.id?T.navy:T.border}`,
                  background:editing?.id===t.id?"#EEF2FF":T.surfaceAlt,cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                  <Pill color={t.is_published?"#16A34A":"#94A3B8"}>{t.is_published?"🟢 공개":"📝 초안"}</Pill>
                </div>
                <div style={{fontSize:13,fontWeight:700,color:T.navy}}>{t.title}</div>
                {t.description && <div style={{fontSize:11,color:T.muted,marginTop:2}}>{t.description}</div>}
              </div>
            ))
          }
        </div>
      </Card>

      <Card style={{padding:"18px 20px"}}>
        {!editing ? (
          <div style={{textAlign:"center",padding:"60px 0",color:T.muted}}>
            <div style={{fontSize:36,opacity:0.4,marginBottom:10}}>🗂️</div>
            <div style={{fontSize:13}}>좌측에서 시험지를 선택하거나 "+ 새 시험지"를 눌러주세요.</div>
          </div>
        ) : (
          <div style={{display:"grid",gap:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:14,fontWeight:800,color:T.navy}}>{editing.id?"시험지 편집":"새 시험지"}</div>
              <div style={{display:"flex",gap:6}}>
                {editing.id && <button style={{...css.btnGhost,padding:"7px 14px",fontSize:11,color:"#DC2626"}} onClick={()=>remove(editing)}>삭제</button>}
                <button style={{...css.btnGhost,padding:"7px 14px",fontSize:11}} onClick={()=>setEditing(null)}>취소</button>
                <button style={{...css.btnOrange,padding:"7px 14px",fontSize:11}} onClick={save}>저장</button>
              </div>
            </div>

            <div>
              <label style={css.label}>제목</label>
              <input value={editing.title} onChange={e=>setEditing({...editing,title:e.target.value})}
                style={{...css.input,padding:"8px 12px",fontSize:13}}/>
            </div>
            <div>
              <label style={css.label}>설명 (선택)</label>
              <input value={editing.description||""} onChange={e=>setEditing({...editing,description:e.target.value})}
                style={{...css.input,padding:"8px 12px",fontSize:13}}/>
            </div>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
              <input type="checkbox" checked={editing.is_published}
                onChange={e=>setEditing({...editing,is_published:e.target.checked})}/>
              <span style={{fontSize:13,fontWeight:700,color:T.navy}}>공개 — 학생 목록에 노출</span>
            </label>

            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <label style={{...css.label,marginBottom:0}}>포함된 문항 ({editingQids.length})</label>
                <button style={{...css.btnOutline,padding:"6px 12px",fontSize:11}} onClick={()=>setShowPicker(true)}>+ 문항 추가</button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {editingQids.length===0 ? <div style={{fontSize:11,color:T.muted,textAlign:"center",padding:"14px 0"}}>문항을 추가해주세요.</div> :
                  editingQids.map((qid,i)=>{
                    const q=qMap[qid];
                    return (
                      <div key={qid} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",border:`1px solid ${T.border}`,borderRadius:8,background:T.surfaceAlt}}>
                        <div style={{fontSize:11,fontWeight:700,color:T.muted,width:24}}>{i+1}.</div>
                        <Pill color={q?.q_type==="short"?"#0EA5E9":"#A855F7"}>{q?.q_type==="short"?"단답":"객관식"}</Pill>
                        <div style={{flex:1,fontSize:12,color:T.navy,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {q?.prompt || "(삭제된 문항)"}
                        </div>
                        <button style={{...css.btnGhost,padding:"4px 8px",fontSize:11}} onClick={()=>move(i,-1)}>↑</button>
                        <button style={{...css.btnGhost,padding:"4px 8px",fontSize:11}} onClick={()=>move(i,1)}>↓</button>
                        <button style={{...css.btnGhost,padding:"4px 8px",fontSize:11,color:"#DC2626"}}
                          onClick={()=>setEditingQids(prev=>prev.filter(x=>x!==qid))}>✕</button>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          </div>
        )}

        {/* 문항 선택 모달 */}
        {showPicker && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
            onClick={()=>setShowPicker(false)}>
            <div onClick={e=>e.stopPropagation()} style={{background:T.surface,borderRadius:14,padding:20,maxWidth:600,width:"100%",maxHeight:"80vh",display:"flex",flexDirection:"column",gap:10}}>
              <div style={{fontSize:14,fontWeight:800,color:T.navy}}>문항 추가</div>
              <input value={pickerKw} onChange={e=>setPickerKw(e.target.value)}
                placeholder="본문 검색" style={{...css.input,padding:"8px 12px",fontSize:13}}/>
              <PickerList list={pickerList} onAdd={addQuestions}/>
              <button style={{...css.btnGhost,padding:"7px 14px",fontSize:12,alignSelf:"flex-end"}} onClick={()=>setShowPicker(false)}>닫기</button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const PickerList = ({list, onAdd}) => {
  const [sel, setSel] = useState(new Set());
  const toggle = (id) => setSel(prev=>{ const n=new Set(prev); if(n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <>
      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:6,minHeight:200}}>
        {list.length===0 ? <div style={{fontSize:12,color:T.muted,textAlign:"center",padding:20}}>일치하는 문항이 없습니다.</div> :
          list.map(q=>(
            <label key={q.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",border:`1px solid ${sel.has(q.id)?T.navy:T.border}`,borderRadius:8,background:sel.has(q.id)?"#EEF2FF":T.surfaceAlt,cursor:"pointer"}}>
              <input type="checkbox" checked={sel.has(q.id)} onChange={()=>toggle(q.id)}/>
              <Pill color={q.q_type==="short"?"#0EA5E9":"#A855F7"}>{q.q_type==="short"?"단답":"객관식"}</Pill>
              <div style={{flex:1,fontSize:12,color:T.navy,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{q.prompt}</div>
            </label>
          ))
        }
      </div>
      <button style={{...css.btnOrange,padding:"8px 16px",fontSize:12,alignSelf:"flex-end"}}
        disabled={sel.size===0} onClick={()=>onAdd([...sel])}>선택한 {sel.size}개 추가</button>
    </>
  );
};

// ── 배정 관리 ────────────────────────────────────────
const ManjeomAssignTab = ({allProfiles}) => {
  const [tests, setTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const [assignedIds, setAssignedIds] = useState(new Set());
  const [originalIds, setOriginalIds] = useState(new Set());
  const [kw, setKw] = useState("");
  const [saving, setSaving] = useState(false);

  const students = (allProfiles||[]).filter(p=>p.role==="student" && p.approval_status==="approved");

  const loadTests = async () => {
    const {data} = await supabase.from("manjeom_tests").select("id,title,is_published").order("created_at",{ascending:false});
    setTests(data||[]);
  };
  useEffect(()=>{ loadTests(); }, []);

  const loadAssignments = async (testId) => {
    const {data} = await supabase.from("manjeom_assignments").select("student_id").eq("test_id",testId);
    const ids = new Set((data||[]).map(r=>r.student_id));
    setAssignedIds(ids); setOriginalIds(new Set(ids));
  };
  useEffect(()=>{ if(selectedTestId) loadAssignments(selectedTestId); }, [selectedTestId]);

  const toggle = (id) => setAssignedIds(prev=>{ const n=new Set(prev); if(n.has(id)) n.delete(id); else n.add(id); return n; });

  const save = async () => {
    if(!selectedTestId) return;
    setSaving(true);
    const {error} = await supabase.rpc("set_manjeom_assignments",{
      p_test_id: selectedTestId,
      p_student_ids: [...assignedIds],
    });
    if(error){ alert("저장 실패: "+error.message); setSaving(false); return; }
    setOriginalIds(new Set(assignedIds));
    setSaving(false);
    alert("배정이 저장되었습니다.");
  };

  const filtered = students.filter(s=>!kw || (s.name||"").includes(kw) || (s.email||"").includes(kw));
  const dirty = assignedIds.size !== originalIds.size ||
    [...assignedIds].some(id=>!originalIds.has(id));

  return (
    <div style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:14,alignItems:"start"}}>
      <Card style={{padding:"14px 16px"}}>
        <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:10}}>시험지 선택</div>
        <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:520,overflowY:"auto"}}>
          {tests.length===0 ? <div style={{fontSize:11,color:T.muted,textAlign:"center",padding:"20px 0"}}>시험지가 없습니다.</div> :
            tests.map(t=>(
              <div key={t.id} onClick={()=>setSelectedTestId(t.id)}
                style={{padding:"10px 12px",borderRadius:8,border:`1px solid ${selectedTestId===t.id?T.navy:T.border}`,
                  background:selectedTestId===t.id?"#EEF2FF":T.surfaceAlt,cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                  <Pill color={t.is_published?"#16A34A":"#94A3B8"}>{t.is_published?"🟢 공개":"📝 초안"}</Pill>
                </div>
                <div style={{fontSize:13,fontWeight:700,color:T.navy}}>{t.title}</div>
              </div>
            ))
          }
        </div>
      </Card>

      <Card style={{padding:"18px 20px"}}>
        {!selectedTestId ? (
          <div style={{textAlign:"center",padding:"60px 0",color:T.muted}}>
            <div style={{fontSize:36,opacity:0.4,marginBottom:10}}>👥</div>
            <div style={{fontSize:13}}>좌측에서 시험지를 선택해주세요.</div>
          </div>
        ) : (
          <div style={{display:"grid",gap:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:14,fontWeight:800,color:T.navy}}>
                배정 학생 ({assignedIds.size}/{students.length})
              </div>
              <button style={{...css.btnOrange,padding:"7px 14px",fontSize:12}}
                disabled={!dirty||saving} onClick={save}>
                {saving ? <Spinner size={12}/> : (dirty ? "저장" : "변경 없음")}
              </button>
            </div>
            <input value={kw} onChange={e=>setKw(e.target.value)} placeholder="학생 이름·이메일 검색"
              style={{...css.input,padding:"8px 12px",fontSize:13}}/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:6,maxHeight:480,overflowY:"auto"}}>
              {filtered.map(s=>(
                <label key={s.id} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 10px",
                  border:`1px solid ${assignedIds.has(s.id)?"#16A34A":T.border}`,borderRadius:8,
                  background:assignedIds.has(s.id)?"#F0FDF4":T.surfaceAlt,cursor:"pointer"}}>
                  <input type="checkbox" checked={assignedIds.has(s.id)} onChange={()=>toggle(s.id)}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:T.navy}}>{s.name||"(이름 없음)"}</div>
                    <div style={{fontSize:9,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.email}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

// ── 결과 (시험지별 / 학생별) ──────────────────────────
const ManjeomResultsTab = ({allProfiles}) => {
  const [mode, setMode] = useState("by_test"); // by_test | by_student
  const [tests, setTests] = useState([]);
  const [selTestId, setSelTestId] = useState(null);
  const [selStudentId, setSelStudentId] = useState(null);
  const [byTestRows, setByTestRows] = useState([]);
  const [byStudentRows, setByStudentRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null); // {testId, studentId}
  const [details, setDetails] = useState([]);
  const [questionsMap, setQuestionsMap] = useState({});

  useEffect(()=>{ (async()=>{
    const {data} = await supabase.from("manjeom_tests").select("id,title,is_published").order("created_at",{ascending:false});
    setTests(data||[]);
  })(); }, []);

  useEffect(()=>{ if(mode==="by_test" && selTestId){
    (async()=>{
      const [{data:rows},{data:stat}] = await Promise.all([
        supabase.rpc("get_manjeom_results_by_test",{p_test_id:selTestId}),
        supabase.rpc("get_manjeom_test_stats",{p_test_id:selTestId}),
      ]);
      setByTestRows(rows||[]); setStats((stat&&stat[0])||null);
      // 문항 prompt 미리 캐싱
      const {data:qs} = await supabase.from("manjeom_test_questions").select("question_id,q_order,manjeom_questions(id,prompt,q_type)").eq("test_id",selTestId).order("q_order");
      const map={}; (qs||[]).forEach(r=>{
        const q=r.manjeom_questions; if(q) map[q.id]={...q,q_order:r.q_order};
      });
      setQuestionsMap(map);
      setExpanded(null);
    })();
  } }, [mode, selTestId]);

  useEffect(()=>{ if(mode==="by_student" && selStudentId){
    (async()=>{
      const {data} = await supabase.rpc("get_manjeom_results_by_student",{p_student_id:selStudentId});
      setByStudentRows(data||[]);
      setExpanded(null);
    })();
  } }, [mode, selStudentId]);

  const loadDetails = async (testId, studentId) => {
    const {data} = await supabase.rpc("get_manjeom_attempts_detail",{p_test_id:testId,p_student_id:studentId});
    setDetails(data||[]);
  };

  const students = (allProfiles||[]).filter(p=>p.role==="student"&&p.approval_status==="approved");

  return (
    <div style={{display:"grid",gap:14}}>
      <div style={{display:"flex",gap:8}}>
        {[{k:"by_test",l:"📋 시험지별 보기"},{k:"by_student",l:"👤 학생별 보기"}].map(({k,l})=>(
          <button key={k} onClick={()=>setMode(k)}
            style={{...mode===k?css.btnOrange:css.btnOutline,padding:"7px 14px",fontSize:12,fontWeight:700}}>{l}</button>
        ))}
      </div>

      {mode==="by_test" && (
        <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:14,alignItems:"start"}}>
          <Card style={{padding:"14px 16px"}}>
            <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:10}}>시험지</div>
            <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:520,overflowY:"auto"}}>
              {tests.map(t=>(
                <div key={t.id} onClick={()=>setSelTestId(t.id)}
                  style={{padding:"10px 12px",borderRadius:8,border:`1px solid ${selTestId===t.id?T.navy:T.border}`,
                    background:selTestId===t.id?"#EEF2FF":T.surfaceAlt,cursor:"pointer",fontSize:12,fontWeight:700,color:T.navy}}>
                  {t.title}
                </div>
              ))}
            </div>
          </Card>
          <div style={{display:"grid",gap:10}}>
            {!selTestId ? <Card style={{padding:30,textAlign:"center",color:T.muted}}>좌측에서 시험지를 선택해주세요.</Card> : (
              <>
                {stats && (
                  <Card style={{padding:"14px 18px",display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                    {[
                      {l:"배정 학생",v:stats.assigned_count},
                      {l:"통과",v:stats.pass_count},
                      {l:"진행 중",v:stats.in_progress_count},
                      {l:"평균 시도(통과자)",v:stats.avg_pass_attempts??"-"},
                    ].map(({l,v})=>(
                      <div key={l}>
                        <div style={{fontSize:10,color:T.muted,marginBottom:2}}>{l}</div>
                        <div style={{fontSize:18,fontWeight:800,color:T.navy}}>{v}</div>
                      </div>
                    ))}
                  </Card>
                )}
                <Card style={{padding:"14px 18px"}}>
                  <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:10}}>학생별 결과</div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    {byTestRows.length===0 ? <div style={{fontSize:11,color:T.muted,textAlign:"center",padding:20}}>배정된 학생이 없습니다.</div> :
                      byTestRows.map(r=>{
                        const expandKey = `${selTestId}-${r.student_id}`;
                        const isExp = expanded===expandKey;
                        return (
                          <div key={r.student_id}>
                            <div onClick={()=>{ if(isExp){setExpanded(null);} else {setExpanded(expandKey); loadDetails(selTestId,r.student_id);} }}
                              style={{display:"grid",gridTemplateColumns:"1fr 80px 90px 140px 80px",gap:8,alignItems:"center",
                                padding:"8px 10px",borderRadius:8,background:isExp?"#EEF2FF":"transparent",cursor:"pointer",
                                borderBottom:`1px solid ${T.border}`}}>
                              <div style={{fontSize:13,fontWeight:700,color:T.navy}}>{r.student_name}</div>
                              <div style={{fontSize:12,color:T.muted}}>{r.attempt_count}회</div>
                              <Pill color={r.is_passed?"#16A34A":"#94A3B8"}>{r.is_passed?`✅ ${r.pass_attempt_no}회`:"🔄 진행"}</Pill>
                              <div style={{fontSize:11,color:T.muted}}>{r.last_attempt_at?new Date(r.last_attempt_at).toLocaleString("ko-KR"):"-"}</div>
                              <div style={{fontSize:11,color:T.navy,textAlign:"right"}}>{isExp?"▲ 접기":"▼ 상세"}</div>
                            </div>
                            {isExp && (
                              <div style={{padding:"10px 14px",background:T.surfaceAlt,borderRadius:8,marginTop:4,marginBottom:8}}>
                                {details.length===0 ? <div style={{fontSize:11,color:T.muted}}>시도 기록이 없습니다.</div> :
                                  details.map(d=>(
                                    <div key={d.attempt_no} style={{fontSize:11,marginBottom:6,padding:"6px 8px",background:T.surface,borderRadius:6}}>
                                      <div style={{fontWeight:700,color:T.navy,marginBottom:3}}>
                                        시도 {d.attempt_no} · {new Date(d.submitted_at).toLocaleString("ko-KR")} ·
                                        <span style={{color:d.is_pass?"#16A34A":"#DC2626",marginLeft:6}}>{d.is_pass?"통과":"미통과"}</span>
                                      </div>
                                      <div style={{color:T.muted}}>
                                        틀린 문항: {(d.wrong_q_ids||[]).length===0 ? "없음" :
                                          (d.wrong_q_ids||[]).map(qid=>{
                                            const q=questionsMap[qid]; return q?`Q${q.q_order}`:"?";
                                          }).join(", ")}
                                      </div>
                                    </div>
                                  ))
                                }
                              </div>
                            )}
                          </div>
                        );
                      })
                    }
                  </div>
                </Card>
              </>
            )}
          </div>
        </div>
      )}

      {mode==="by_student" && (
        <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:14,alignItems:"start"}}>
          <Card style={{padding:"14px 16px"}}>
            <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:10}}>학생</div>
            <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:520,overflowY:"auto"}}>
              {students.map(s=>(
                <div key={s.id} onClick={()=>setSelStudentId(s.id)}
                  style={{padding:"8px 12px",borderRadius:8,border:`1px solid ${selStudentId===s.id?T.navy:T.border}`,
                    background:selStudentId===s.id?"#EEF2FF":T.surfaceAlt,cursor:"pointer",fontSize:12,fontWeight:700,color:T.navy}}>
                  {s.name}
                </div>
              ))}
            </div>
          </Card>
          <Card style={{padding:"14px 18px"}}>
            {!selStudentId ? <div style={{padding:30,textAlign:"center",color:T.muted}}>좌측에서 학생을 선택해주세요.</div> : (
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                <div style={{fontSize:13,fontWeight:800,color:T.navy,marginBottom:10}}>배정된 시험지</div>
                {byStudentRows.length===0 ? <div style={{fontSize:11,color:T.muted,textAlign:"center",padding:20}}>배정된 시험지가 없습니다.</div> :
                  byStudentRows.map(r=>(
                    <div key={r.test_id} style={{display:"grid",gridTemplateColumns:"1fr 80px 100px 140px",gap:8,alignItems:"center",
                      padding:"8px 10px",borderBottom:`1px solid ${T.border}`}}>
                      <div style={{fontSize:13,fontWeight:700,color:T.navy}}>{r.title}</div>
                      <div style={{fontSize:12,color:T.muted}}>{r.attempt_count}회</div>
                      <Pill color={r.is_passed?"#16A34A":"#94A3B8"}>{r.is_passed?`✅ ${r.pass_attempt_no}회`:"🔄 진행"}</Pill>
                      <div style={{fontSize:11,color:T.muted}}>{r.last_attempt_at?new Date(r.last_attempt_at).toLocaleString("ko-KR"):"-"}</div>
                    </div>
                  ))
                }
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

// ── 메인 ────────────────────────────────────────────
const ManjeomView = ({onRefresh, allProfiles}) => {
  const [tab, setTab] = useState("manage"); // manage | results
  const [subTab, setSubTab] = useState("questions"); // questions | tests | assign

  return (
    <div style={{display:"grid",gap:14}}>
      <div style={{fontSize:18,fontWeight:800,color:T.navy}}>💯 만점 테스트</div>

      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        {[
          {k:"manage", l:"📝 만점 테스트 관리"},
          {k:"results", l:"📊 만점 테스트 결과"},
        ].map(({k,l})=>(
          <button key={k} onClick={()=>setTab(k)}
            style={{...tab===k?css.btnOrange:css.btnOutline,padding:"7px 16px",fontSize:13,fontWeight:700}}>{l}</button>
        ))}
      </div>

      {tab==="manage" && (
        <>
          <div style={{display:"flex",gap:6,borderBottom:`1px solid ${T.border}`,paddingBottom:0}}>
            {[
              {k:"questions",l:"📚 문항 관리"},
              {k:"tests",l:"🗂️ 시험지 관리"},
              {k:"assign",l:"👥 배정 관리"},
            ].map(({k,l})=>(
              <button key={k} onClick={()=>setSubTab(k)}
                style={{background:"transparent",border:"none",borderBottom:`3px solid ${subTab===k?T.orange:"transparent"}`,
                  padding:"8px 14px",fontSize:13,fontWeight:700,color:subTab===k?T.navy:T.muted,cursor:"pointer"}}>{l}</button>
            ))}
          </div>
          {subTab==="questions" && <ManjeomQuestionsTab/>}
          {subTab==="tests"     && <ManjeomTestsTab/>}
          {subTab==="assign"    && <ManjeomAssignTab allProfiles={allProfiles}/>}
        </>
      )}

      {tab==="results" && <ManjeomResultsTab allProfiles={allProfiles}/>}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// STUDENT/PARENT: 만점 테스트 (시험지 풀이)
// ══════════════════════════════════════════════════════
const ManjeomStudentView = ({profile}) => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // { test, questions, last_answers, is_passed }
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type:"ok"|"err", text}

  const isParentView = profile.id !== undefined; // dummy — profile prop always present
  const studentId = profile.id;

  const loadList = async () => {
    setLoading(true);
    // 본인이면 get_my_manjeom_tests, 학부모/관리자가 자녀 보는 경우 get_child_manjeom_tests
    const {data, error} = await supabase.rpc("get_child_manjeom_tests",{p_child_id:studentId});
    if(error){ console.error(error); setTests([]); }
    else setTests(data||[]);
    setLoading(false);
  };
  useEffect(()=>{ loadList(); }, [studentId]);

  const open = async (testId) => {
    const {data, error} = await supabase.rpc("get_my_manjeom_test_detail",{p_test_id:testId,p_student_id:studentId});
    if(error || !data){ alert("시험지를 불러오지 못했습니다."); return; }
    setActive(data);
    setAnswers(data.last_answers || {});
    setFeedback(null);
  };

  const encourageByAttempt = (n) => {
    if(n <= 1) return { title:"아직 만점이 아니에요", body:"의심되는 답부터 차분히 다시 살펴봐 주세요." };
    if(n === 2) return { title:"한 번 더 도전!", body:"이번엔 잠깐 호흡 고르고 한 문제씩 천천히 다시 봐 볼까요?" };
    if(n === 3) return { title:"좋아요, 잘하고 있어요", body:"답을 너무 빨리 바꾸지 말고, 왜 그 답인지 한 번 더 생각해 봐요." };
    if(n <= 5) return { title:"거의 다 왔어요", body:"틀린 문제는 적어도 한 문제 이상이에요. 처음부터 다시 한 번 천천히 짚어보면 보일 거예요." };
    if(n <= 8) return { title:"잠깐 쉬어가도 돼요", body:"눈을 잠깐 쉬게 한 다음, 새 마음으로 한 번 더 봐 볼까요? 만점은 가까이 있어요." };
    return { title:"포기하지 말아요", body:"여기까지 풀어낸 것만으로도 충분히 멋져요. 천천히, 한 문제씩, 한 번만 더 함께 봐 봐요." };
  };

  const submit = async () => {
    setSubmitting(true);
    const {data, error} = await supabase.rpc("submit_manjeom_attempt",{p_test_id:active.test.id,p_answers:answers});
    setSubmitting(false);
    if(error){ alert("제출 실패: "+error.message); return; }
    if(data.is_pass){
      setFeedback({
        type:"ok",
        attempt_no: data.attempt_no,
        title: data.attempt_no === 1 ? "한 번에 통과!" : `${data.attempt_no}번 만에 통과!`,
        body: data.attempt_no === 1
          ? "완벽해요. 한 번에 만점, 정말 잘했어요!"
          : "포기하지 않고 끝까지 풀어낸 게 진짜 멋져요. 만점 축하해요!",
      });
      await loadList();
    } else {
      const m = encourageByAttempt(data.attempt_no);
      setFeedback({type:"err", attempt_no:data.attempt_no, title:m.title, body:m.body});
    }
  };

  if(active){
    const isPassed = active.is_passed;
    return (
      <div style={{display:"grid",gap:14}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button style={{...css.btnGhost,padding:"7px 14px",fontSize:12}} onClick={()=>{setActive(null); setFeedback(null);}}>← 목록</button>
          <div style={{fontSize:18,fontWeight:800,color:T.navy}}>{active.test.title}</div>
          {isPassed && <Pill color="#16A34A">✅ 통과</Pill>}
        </div>
        {active.test.description && <div style={{fontSize:13,color:T.muted}}>{active.test.description}</div>}

        {feedback && (
          <div onClick={()=>setFeedback(null)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1000,
              display:"flex",alignItems:"center",justifyContent:"center",padding:20,
              animation:"fadeIn 0.2s ease"}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:T.surface,borderRadius:16,padding:"32px 32px 28px",maxWidth:440,width:"100%",
                textAlign:"center",boxShadow:"0 12px 40px rgba(0,0,0,0.3)",
                border:`2px solid ${feedback.type==="ok"?"#86EFAC":"#FCA5A5"}`}}>
              <div style={{fontSize:54,marginBottom:8}}>{feedback.type==="ok"?"🎉":"💭"}</div>
              <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:6,letterSpacing:"0.04em"}}>
                시도 {feedback.attempt_no}회
              </div>
              <div style={{fontSize:18,fontWeight:800,color:feedback.type==="ok"?"#16A34A":T.navy,marginBottom:10}}>
                {feedback.title}
              </div>
              <div style={{fontSize:13,color:T.muted,lineHeight:1.6,marginBottom:22,whiteSpace:"pre-wrap"}}>
                {feedback.body}
              </div>
              {feedback.type==="err" ? (
                <button style={{...css.btnOrange,padding:"11px 30px",fontSize:13}} onClick={()=>setFeedback(null)}>
                  다시 풀기
                </button>
              ) : (
                <button style={{...css.btnOrange,padding:"11px 30px",fontSize:13}}
                  onClick={()=>{ setFeedback(null); setActive(null); }}>
                  목록으로
                </button>
              )}
            </div>
          </div>
        )}

        <Card style={{padding:"18px 20px"}}>
          <div style={{display:"flex",flexDirection:"column",gap:18}}>
            {(active.questions||[]).map((q,i)=>(
              <div key={q.id}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                  <div style={{fontSize:13,fontWeight:800,color:T.navy}}>Q{i+1}.</div>
                  <Pill color={q.q_type==="short"?"#0EA5E9":"#A855F7"}>{q.q_type==="short"?"단답":"객관식"}</Pill>
                </div>
                <div style={{fontSize:14,color:T.navy,whiteSpace:"pre-wrap",marginBottom:10}}>{q.prompt}</div>
                {q.image_url && (
                  <div style={{marginBottom:10}}>
                    <img src={q.image_url} alt={`Q${i+1}`} style={{maxWidth:"100%",height:"auto",borderRadius:8,border:`1px solid ${T.border}`}}/>
                  </div>
                )}
                {q.q_type==="short" ? (
                  <input value={answers[q.id]||""} onChange={e=>setAnswers(a=>({...a,[q.id]:e.target.value}))}
                    disabled={isPassed}
                    placeholder="정답 입력"
                    style={{...css.input,padding:"10px 14px",maxWidth:400}}/>
                ) : (() => {
                  const choices = q.choices || [];
                  const labelOnly = choices.every((c,ci)=> !c || c === ("①②③④⑤"[ci]||String(ci+1)));
                  // 학생 답은 "①,③" 형식 정렬된 콤마 결합 문자열. 다중 선택 가능 (체크박스).
                  const selectedSet = new Set(
                    String(answers[q.id]||"").split(",").map(s=>s.trim()).filter(Boolean)
                  );
                  const toggle = (val) => {
                    if(isPassed) return;
                    const next = new Set(selectedSet);
                    if(next.has(val)) next.delete(val); else next.add(val);
                    // 보기 순서 기준으로 정렬해 콤마 결합
                    const sortedVal = choices.filter(c => next.has(c)).join(",");
                    setAnswers(a => ({...a, [q.id]: sortedVal}));
                  };
                  return labelOnly ? (
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {choices.map((c,ci)=>{
                        const display = c || ("①②③④⑤"[ci]||String(ci+1));
                        const checked = selectedSet.has(display);
                        return (
                          <label key={ci} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                            padding:"10px 18px",minWidth:64,
                            border:`2px solid ${checked?T.navy:T.border}`,borderRadius:10,
                            background:checked?"#EEF2FF":T.surfaceAlt,cursor:isPassed?"default":"pointer"}}>
                            <input type="checkbox" checked={checked}
                              disabled={isPassed}
                              onChange={()=>toggle(display)}/>
                            <span style={{fontSize:16,fontWeight:800,color:T.navy}}>{display}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {choices.map((c,ci)=>{
                        const checked = selectedSet.has(c);
                        return (
                          <label key={ci} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",
                            border:`1px solid ${checked?T.navy:T.border}`,borderRadius:8,
                            background:checked?"#EEF2FF":T.surfaceAlt,cursor:isPassed?"default":"pointer"}}>
                            <input type="checkbox" checked={checked}
                              disabled={isPassed}
                              onChange={()=>toggle(c)}/>
                            <span style={{fontSize:11,color:T.muted,width:18}}>{"①②③④⑤"[ci]||(ci+1)}</span>
                            <span style={{fontSize:13,color:T.navy}}>{c}</span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>

          {!isPassed && (
            <div style={{marginTop:24,display:"flex",justifyContent:"flex-end"}}>
              <button style={{...css.btnOrange,padding:"12px 32px",fontSize:14,fontWeight:800}}
                disabled={submitting} onClick={submit}>
                {submitting ? <Spinner size={14} color="#fff"/> : "제출"}
              </button>
            </div>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div style={{display:"grid",gap:14}}>
      <div style={{fontSize:18,fontWeight:800,color:T.navy}}>💯 만점 테스트</div>
      <div style={{fontSize:13,color:T.muted}}>
        시험지의 <b style={{color:T.navy}}>모든 문항을 정답</b>으로 맞춰야 통과합니다. 한 개라도 틀리면 다시 풀어주세요 (어느 문제가 틀렸는지는 알려주지 않습니다).
      </div>

      {loading ? <Card style={{padding:30,textAlign:"center"}}><Spinner/></Card> :
        tests.length===0 ? (
          <Card style={{padding:30,textAlign:"center",color:T.muted}}>
            <div style={{fontSize:36,opacity:0.4,marginBottom:10}}>💯</div>
            <div style={{fontSize:13}}>배정된 시험지가 없습니다.</div>
          </Card>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12}}>
            {tests.map(t=>(
              <div key={t.test_id} onClick={()=>open(t.test_id)}
                style={{padding:"16px 18px",borderRadius:12,border:`1px solid ${T.border}`,background:T.surface,
                  cursor:"pointer",boxShadow:"0 1px 6px rgba(25,29,84,0.06)",transition:"transform .15s"}}
                onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"}
                onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                  {t.is_passed ? <Pill color="#16A34A">✅ 통과</Pill> :
                    t.attempt_count>0 ? <Pill color="#F59E0B">🔄 {t.attempt_count}회 시도</Pill> :
                    <Pill color="#94A3B8">시작 전</Pill>}
                </div>
                <div style={{fontSize:14,fontWeight:800,color:T.navy,marginBottom:4}}>{t.title}</div>
                {t.description && <div style={{fontSize:11,color:T.muted,marginBottom:6}}>{t.description}</div>}
                <div style={{fontSize:11,color:T.muted}}>문항 {t.question_count}개{t.last_attempt_at?` · 최근 ${new Date(t.last_attempt_at).toLocaleDateString("ko-KR")}`:""}</div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
};

// ══════════════════════════════════════════════════════
// LOG HISTORY
// ══════════════════════════════════════════════════════
const LogHistory = ({logs, onDelete, isAdmin, allProfiles}) => {
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const sorted=[...logs].sort((a,b)=>b.date.localeCompare(a.date));
  const nameMap=Object.fromEntries((allProfiles||[]).map(p=>[p.id,p.name]));
  if(sorted.length===0) return <div style={{textAlign:"center",padding:"80px 20px",color:T.muted,fontSize:14}}>학습 기록이 없습니다.</div>;
  const grouped={};sorted.forEach(log=>{if(!grouped[log.date])grouped[log.date]=[];grouped[log.date].push(log);});
  const dates=Object.keys(grouped).sort((a,b)=>b.localeCompare(a));
  const totalPages=Math.ceil(dates.length/PAGE_SIZE);
  const pagedDates=dates.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);
  const LogCard=({log})=>{
    const ei=log.engram_index||0;
    const{g,c}=gradeInfo(ei);
    return(
      <Card style={{marginBottom:8,display:"flex",alignItems:"center",gap:12}}>
        <div style={{minWidth:44,textAlign:"center"}}>
          <Pill color={c}>{g}</Pill>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",gap:6,marginBottom:5,alignItems:"center",flexWrap:"wrap"}}>
            {isAdmin&&<span style={{fontSize:12,color:T.orange,fontWeight:700}}>{nameMap[log.uid]||"—"}</span>}
            <span style={{fontSize:14,fontWeight:700,color:T.navy}}>{log.subject}</span>
            {log.subject==="수학"?(
  <>{log.q_basic>0&&<Pill color={T.navy}>기본 {log.q_basic}</Pill>}{log.q_mid>0&&<Pill color={T.orange}>응용 {log.q_mid}</Pill>}{log.q_adv>0&&<Pill color="#7C3AED">심화 {log.q_adv}</Pill>}</>
):(
  (log.q_basic||log.question_count)>0&&<Pill color={T.navy}>{log.q_basic||log.question_count}문항</Pill>
)}
          </div>
          <div style={{display:"flex",gap:10,fontSize:11,color:T.muted,flexWrap:"wrap"}}>
            <span>전략 {log.strategy_score}</span>
            <span>효율 {log.efficiency_index}</span>
            <span>메타인지 {(log.metacognition_accuracy||0).toFixed(1)}%</span>
            <span>{log.question_count}문항 · {log.net_time}분</span>
          </div>
        </div>
        <NavyNum value={ei.toFixed(1)} unit="EI" size={20} color={EI_COLOR(ei)}/>
        {isAdmin&&<button onClick={()=>{if(window.confirm(`${log.date} · ${log.subject} 기록을 삭제하시겠습니까?\n삭제 후 복구가 불가능합니다.`)) onDelete(log.id);}} style={{background:"none",border:"none",color:T.muted,cursor:"pointer",fontSize:18,padding:4,flexShrink:0}}>✕</button>}
      </Card>
    );
  };
  return(
    <div>
      {/* 상단 요약 + 페이지 정보 */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,padding:"10px 14px",background:T.surface,border:`1px solid ${T.border}`,borderRadius:12}}>
        <span style={{fontSize:13,color:T.muted}}>총 <strong style={{color:T.navy}}>{sorted.length}건</strong> · <strong style={{color:T.navy}}>{dates.length}일</strong></span>
        {totalPages>1&&<span style={{fontSize:12,color:T.muted}}>{page} / {totalPages} 페이지</span>}
      </div>

      {pagedDates.map(date=>(
        <div key={date} style={{marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:T.muted,marginBottom:8,paddingBottom:6,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:6}}>
            {HI.calendar(12,T.muted)} {date}
            <span style={{fontSize:11,color:T.border,fontWeight:400}}>({grouped[date].length}건)</span>
          </div>
          {grouped[date].map(log=><LogCard key={log.id} log={log}/>)}
        </div>
      ))}

      {/* 페이지네이션 */}
      {totalPages>1&&(
        <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:6,marginTop:8,paddingTop:16,borderTop:`1px solid ${T.border}`}}>
          <button onClick={()=>{setPage(p=>Math.max(1,p-1));window.scrollTo(0,0);}}
            disabled={page===1}
            style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${T.border}`,background:T.surface,color:page===1?T.border:T.navy,cursor:page===1?"default":"pointer",fontSize:13,fontWeight:700}}>◀</button>
          {Array.from({length:totalPages},(_,i)=>i+1).map(p=>(
            <button key={p} onClick={()=>{setPage(p);window.scrollTo(0,0);}}
              style={{width:34,height:34,borderRadius:8,border:`1px solid ${page===p?T.navy:T.border}`,
                background:page===p?T.navy:T.surface,color:page===p?"#fff":T.muted,
                cursor:"pointer",fontSize:13,fontWeight:page===p?800:400}}>{p}</button>
          ))}
          <button onClick={()=>{setPage(p=>Math.min(totalPages,p+1));window.scrollTo(0,0);}}
            disabled={page===totalPages}
            style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${T.border}`,background:T.surface,color:page===totalPages?T.border:T.navy,cursor:page===totalPages?"default":"pointer",fontSize:13,fontWeight:700}}>▶</button>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// EI SETUP MODAL — 첫 로그인 목표 EI 설정
// ══════════════════════════════════════════════════════
const GRADE_MIN = {S:93,A:81,B:66,C:51,D:50};
const GRADE_NEXT = {D:"C",C:"B",B:"A",A:"S",S:null};
const EI_GRADES_DEF = [
  {grade:"S",min:93,max:100,color:"#16A34A",desc:"매 회차 집중이 흐트러지지 않고, 내가 아는 것과 모르는 것을 정확히 구분하는 상태.",sub:"현실적으로 달성하기 매우 어렵지만, 이 수준을 목표로 삼는 것 자체가 성장의 출발점입니다."},
  {grade:"A",min:81,max:92,color:"#2563EB",desc:"배운 내용을 정해진 방식대로 꾸준히 실천하고, 자기 실력을 객관적으로 파악하는 단계.",sub:"상위 10% 수준의 학습 밀도. 하루하루 루틴을 성실히 이어가는 학생이 도달하는 목표입니다."},
  {grade:"B",min:66,max:80,color:"#111827",desc:"학습 루틴이 잡혀가고 있지만, 가끔 집중이 흔들리거나 자신의 실력을 과대·과소평가하는 경향이 있는 상태.",sub:"성실하게 공부하는 대부분의 학생이 처음 도달하는 구간입니다. 여기서 A로 올리는 것이 핵심 과제입니다."},
  {grade:"C",min:51,max:65,color:"#F97316",desc:"공부 시간은 채우고 있지만, 방법이 습관화되지 않았거나 틀린 문제를 반복하는 패턴이 있는 상태.",sub:"지금 당장 높은 목표보다 루틴을 먼저 잡는 것이 중요한 시점입니다."},
  {grade:"D",min:0,max:50,color:"#DC2626",desc:"공부 방법·집중도·자기 파악 모두 아직 자리를 잡지 못한 상태.",sub:"지금 수준을 솔직하게 인정하고 시작하는 것이 가장 빠른 방법입니다."},
];

const GradeCards = ({selected, onSelect}) => (
  <div style={{display:"flex",flexDirection:"column",gap:6}}>
    {EI_GRADES_DEF.map(g=>(
      <div key={g.grade} onClick={()=>onSelect(g.grade)}
        style={{display:"flex",gap:10,alignItems:"flex-start",padding:"10px 12px",borderRadius:10,cursor:"pointer",
          border:`2px solid ${selected===g.grade?g.color:"transparent"}`,
          background:selected===g.grade?g.color+"12":T.surfaceAlt,transition:"all 0.15s"}}>
        <div style={{minWidth:28,height:28,borderRadius:8,background:g.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:900,color:"#fff",flexShrink:0}}>{g.grade}</div>
        <div>
          <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:3}}>
            <span style={{fontSize:12,fontWeight:800,color:g.color,whiteSpace:"nowrap"}}>{g.min}~{g.max}점</span>
            <span style={{fontSize:11,color:T.muted}}>|</span>
            <span style={{fontSize:11,fontWeight:700,color:T.textMid}}>{g.desc}</span>
          </div>
          <div style={{fontSize:11,color:T.muted,lineHeight:1.6}}>{g.sub}</div>
        </div>
      </div>
    ))}
  </div>
);

const EISetupModal = ({profile, logs=[], onSave}) => {
  const [selected, setSelected] = useState(gradeInfo(profile?.target_ei||85).g);
  const [saving, setSaving] = useState(false);

  // 자동 추천
  const recent7 = logs.filter(l=>new Date(l.date)>=new Date(Date.now()-7*86400000));
  const avg7 = recent7.length>0?(recent7.reduce((s,l)=>s+l.engramIndex,0)/recent7.length):null;
  const curGrade = avg7?gradeInfo(avg7).g:null;
  const sugGrade = curGrade?GRADE_NEXT[curGrade]:null;
  const showRec = logs.length>=3 && sugGrade;
  const sugColor = sugGrade?EI_GRADES_DEF.find(g=>g.grade===sugGrade)?.color:null;

  const save = async()=>{
    setSaving(true);
    const targetEI = GRADE_MIN[selected];
    await supabase.from("profiles").update({target_ei:targetEI,updated_at:new Date().toISOString()}).eq("id",profile.id);
    setSaving(false);
    onSave(targetEI);
  };
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(25,29,84,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20,overflowY:"auto"}}>
      <Card style={{width:"100%",maxWidth:480}}>
        <div style={{textAlign:"center",marginBottom:16}}>
          <div style={{fontSize:22,fontWeight:900,color:T.navy,marginBottom:4}}>목표 등급을 선택해주세요</div>
          <div style={{fontSize:13,color:T.muted}}>목표 등급을 선택하면 EI 기준점이 자동으로 설정됩니다.</div>
        </div>

        {/* 자동 추천 배너 */}
        {showRec&&(
          <div style={{background:sugColor+"12",border:`1px solid ${sugColor}40`,borderRadius:10,padding:"10px 14px",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
            <div>
              <div style={{fontSize:12,fontWeight:800,color:sugColor,marginBottom:2}}>✨ 추천 목표</div>
              <div style={{fontSize:12,color:T.textMid}}>
                최근 7일 평균 <strong>{avg7.toFixed(1)}점</strong> ({curGrade}등급) → <strong style={{color:sugColor}}>{sugGrade}등급</strong> ({GRADE_MIN[sugGrade]}점) 추천
              </div>
            </div>
            <button onClick={()=>setSelected(sugGrade)}
              style={{padding:"6px 12px",borderRadius:8,border:"none",background:sugColor,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
              적용
            </button>
          </div>
        )}

        <div style={{marginBottom:16}}>
          <GradeCards selected={selected} onSelect={setSelected}/>
        </div>

        {/* 선택된 등급 요약 */}
        {selected&&(()=>{
          return(
            <div style={{background:T.grad,borderRadius:10,padding:"12px 16px",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span style={{fontSize:13,color:"rgba(255,255,255,0.7)"}}>선택한 목표 등급</span>
              <div style={{display:"flex",alignItems:"baseline",gap:6}}>
                <span style={{fontSize:28,fontWeight:900,color:"#fff"}}>{selected}</span>
                <span style={{fontSize:13,color:"rgba(255,255,255,0.7)"}}>({GRADE_MIN[selected]}점 이상)</span>
              </div>
            </div>
          );
        })()}

        <div style={{background:T.orangePale,border:`1px solid ${T.orange}30`,borderRadius:10,padding:"10px 14px",fontSize:12,color:T.textMid,marginBottom:16}}>
          💡 목표는 나중에 프로필에서 언제든지 변경할 수 있습니다.
        </div>

        <button onClick={save} disabled={saving||!selected}
          style={{...css.btnPrimary,width:"100%",padding:"13px 0",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          {saving?<><Spinner size={18} color="#fff"/>저장 중...</>:"목표 설정 완료 →"}
        </button>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// PROFILE MODAL
// ══════════════════════════════════════════════════════
const ProfileModal = ({profile, onClose, onSave, onDelete}) => {
  const PROFILE_DRAFT_KEY = draftKey("profile", profile.id);
  const _restoredProfile = useMemo(() => restoreDraft(PROFILE_DRAFT_KEY) || {}, []);
  const [name, setName]             = useState(_restoredProfile.name ?? (profile.name||""));
  const [birthYear, setBirthYear]   = useState(_restoredProfile.birthYear ?? (profile.birth_year ? String(profile.birth_year) : ""));
  const [birthMonth, setBirthMonth] = useState(_restoredProfile.birthMonth ?? (profile.birth_month ? String(profile.birth_month) : ""));
  const [birthDay, setBirthDay]     = useState(_restoredProfile.birthDay ?? (profile.birth_day ? String(profile.birth_day) : ""));
  const [target, setTarget]       = useState(_restoredProfile.target ?? gradeInfo(profile.target_ei||85).g);
  useDraftBackup(PROFILE_DRAFT_KEY, { name, birthYear, birthMonth, birthDay, target });
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url||"");
  const [uploading, setUploading] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw]         = useState("");
  const [newPwC, setNewPwC]       = useState("");
  const [saving, setSaving]       = useState(false);
  const [done, setDone]           = useState(false);
  const [err, setErr]             = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [cropSrc, setCropSrc]     = useState(null);
  const [crop, setCrop]           = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if(!file) return;
    setErr("");
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const onImageLoad = (e) => {
    const {width,height} = e.currentTarget;
    const c = centerCrop(makeAspectCrop({unit:"%",width:80},1,width,height),width,height);
    setCrop(c); setCompletedCrop(c);
  };

  const compressAndUpload = async () => {
    if(!completedCrop || !imgRef.current) return;
    setUploading(true); setErr("");
    const img = imgRef.current;
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    const canvas = document.createElement("canvas");
    const size = Math.min(800, completedCrop.width * scaleX);
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img,
      completedCrop.x*scaleX, completedCrop.y*scaleY,
      completedCrop.width*scaleX, completedCrop.height*scaleY,
      0, 0, size, size
    );
    const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.82));
    const path = `${profile.id}.jpg`;
    const {error:upErr} = await supabase.storage.from("avatars").upload(path, blob, {upsert:true, contentType:"image/jpeg"});
    if(upErr){ setErr("업로드 실패: "+upErr.message); setUploading(false); return; }
    const {data:{publicUrl}} = supabase.storage.from("avatars").getPublicUrl(path);
    setAvatarUrl(publicUrl+"?t="+Date.now());
    setCropSrc(null); setUploading(false);
  };

  const save = async () => {
    setErr("");
    if(newPw){
      const pwRegex=/^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/;
      if(!currentPw){ setErr("비밀번호 변경 시에는 현재 비밀번호를 입력해주세요."); return; }
      if(newPw!==newPwC){ setErr("비밀번호가 일치하지 않습니다."); return; }
      if(!pwRegex.test(newPw)){ setErr("대소문자+특수문자 포함 8자 이상이어야 합니다."); return; }
      if(currentPw === newPw){ setErr("현재 비밀번호와 동일합니다. 다른 비밀번호를 입력해주세요."); return; }
    }
    setSaving(true);
    // 비밀번호 변경 시도 시 현재 비밀번호로 재인증 (로그인된 디바이스를 잠시 빌린 사람의 비번 변경 차단)
    if(newPw){
      const { data: { session: curSess } } = await supabase.auth.getSession();
      const email = curSess?.user?.email;
      if(!email){ setSaving(false); setErr("세션이 만료되었습니다. 다시 로그인 후 시도해주세요."); return; }
      const { error: reauthErr } = await supabase.auth.signInWithPassword({ email, password: currentPw });
      if(reauthErr){
        setSaving(false);
        const m = reauthErr.message?.toLowerCase()||"";
        if(m.includes("invalid")) setErr("현재 비밀번호가 올바르지 않습니다.");
        else setErr("재인증 실패: "+reauthErr.message);
        return;
      }
    }
    const targetEI = GRADE_MIN[target]||85;
    const by = birthYear?Number(birthYear):null, bm = birthMonth?Number(birthMonth):null, bd = birthDay?Number(birthDay):null;
    const newGrade = calcGrade(by, bm) || profile.grade;
    await supabase.from("profiles").update({name, grade: newGrade, birth_year:by, birth_month:bm, birth_day:bd, target_ei:targetEI, avatar_url:avatarUrl, updated_at:new Date().toISOString()}).eq("id", profile.id);
    if(newPw){
      const {error:pwErr} = await supabase.auth.updateUser({password:newPw});
      if(pwErr){
        setSaving(false);
        const m = pwErr.message?.toLowerCase()||"";
        if(m.includes("same password")||m.includes("different from")) setErr("현재 비밀번호와 동일합니다. 다른 비밀번호를 입력해주세요.");
        else setErr("비밀번호 변경 실패: "+pwErr.message);
        return;
      }
    }
    setSaving(false);
    setDone(true);
    clearDraft(PROFILE_DRAFT_KEY);
    setTimeout(()=>onSave({...profile,name,grade:newGrade,birth_year:by,birth_month:bm,birth_day:bd,target_ei:GRADE_MIN[target]||85,avatar_url:avatarUrl}), 800);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(25,29,84,0.45)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Card style={{width:"100%",maxWidth:420,maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <div style={{fontSize:17,fontWeight:800,color:T.navy}}>프로필 수정</div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,color:T.muted,cursor:"pointer",lineHeight:1}}>×</button>
        </div>

        {/* 크롭 모달 */}
        {cropSrc&&(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:10000,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:20}}>
            <div style={{background:T.surface,borderRadius:16,padding:20,maxWidth:480,width:"100%"}}>
              <div style={{fontSize:14,fontWeight:700,color:T.navy,marginBottom:12}}>사진 영역 선택</div>
              <div style={{maxHeight:"60vh",overflowY:"auto",marginBottom:14}}>
                <ReactCrop crop={crop} onChange={c=>setCrop(c)} onComplete={c=>setCompletedCrop(c)} aspect={1} circularCrop={false}>
                  <img ref={imgRef} src={cropSrc} onLoad={onImageLoad} style={{maxWidth:"100%"}} alt="crop"/>
                </ReactCrop>
              </div>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>setCropSrc(null)} style={{...css.btnGhost,flex:1}}>취소</button>
                <button onClick={compressAndUpload} disabled={uploading} style={{...css.btnPrimary,flex:2,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {uploading?<><Spinner size={16} color="#fff"/>업로드 중...</>:"적용"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 프로필 사진 */}
        <div style={{textAlign:"center",marginBottom:20}}>
          <label style={{cursor:"pointer",display:"inline-block",position:"relative"}}>
            <input type="file" accept="image/*" onChange={onFileSelect} style={{display:"none"}}/>
            <div style={{width:80,height:80,borderRadius:24,background:avatarUrl?"transparent":T.grad,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:32,boxShadow:"0 4px 12px rgba(25,29,84,0.2)",overflow:"hidden",border:`2px solid ${T.border}`}}>
              {uploading
                ? <Spinner size={28} color={T.white}/>
                : avatarUrl
                  ? <img src={avatarUrl} alt="avatar" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  : (profile.role==="admin" ? HI.user(28,"#fff") : HI.cap(28,"#fff"))
              }
            </div>
            <div style={{position:"absolute",bottom:0,right:0,width:24,height:24,background:T.orange,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}>
              {HI.camera(13,"#fff")}
            </div>
          </label>
          <div style={{fontSize:11,color:T.muted,marginTop:8}}>사진 클릭하여 변경</div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>
            {profile.role==="admin"?"관리자 계정":profile.role==="parent"?"학부모 계정":"학생 계정"} · 가입 {profile.created_at?.slice(0,10)||""}
          </div>
        </div>

        <div style={{display:"grid",gap:14,marginBottom:16}}>
          <div>
            <label style={css.label}>이름</label>
            <input value={name} onChange={e=>setName(e.target.value)} style={css.input}/>
          </div>
          {profile.role==="student"&&<>
            <div>
              <label style={css.label}>생년월일</label>
              <BirthInput year={birthYear} month={birthMonth} day={birthDay}
                onYear={setBirthYear} onMonth={setBirthMonth} onDay={setBirthDay}
                showGrade={true}/>
            </div>
            <div>
              <label style={css.label}>목표 등급</label>
              <GradeCards selected={target} onSelect={setTarget}/>
            </div>
          </>}

          {/* 비밀번호 변경 — 보안: 현재 비밀번호 재확인 후에만 변경 가능 (잠시 빌린 디바이스로 비번 변경 차단) */}
          <div style={{borderTop:`1px solid ${T.border}`,paddingTop:14}}>
            <label style={{...css.label,marginBottom:10}}>비밀번호 변경 <span style={{fontWeight:400,color:T.muted}}>(변경 시에만 입력)</span></label>
            <div style={{display:"grid",gap:10}}>
              <input name="current-password" type="password" value={currentPw} onChange={e=>setCurrentPw(e.target.value)} placeholder="현재 비밀번호 (보안 확인)" autoComplete="current-password" style={css.input}/>
              <input name="new-password" type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="새 비밀번호 (대소문자+특수문자 8자↑)" autoComplete="new-password" style={css.input}/>
              <input name="new-password-confirm" type="password" value={newPwC} onChange={e=>setNewPwC(e.target.value)} placeholder="새 비밀번호 확인" autoComplete="new-password" style={css.input}/>
            </div>
          </div>
        </div>

        {err&&<div style={{background:"#FEE2E2",border:"1px solid #FECACA",borderRadius:8,padding:"10px 14px",fontSize:13,color:T.danger,marginBottom:12}}>{err}</div>}
        {done
          ? <div style={{background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:10,padding:"12px 16px",textAlign:"center",color:T.success,fontWeight:700,fontSize:14}}>✅ 저장 완료!</div>
          : <div style={{display:"flex",gap:10}}>
              <button onClick={onClose} style={{...css.btnGhost,flex:1}}>취소</button>
              <button onClick={save} disabled={saving||uploading} style={{...css.btnPrimary,flex:2,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                {saving?<><Spinner size={16} color="#fff"/>저장 중...</>:"저장"}
              </button>
            </div>}

        {/* 계정 탈퇴 */}
        {profile.role !== "admin" && (
          <div style={{marginTop:24,paddingTop:16,borderTop:`1px solid ${T.border}`}}>
            {!deleteConfirm ? (
              <button onClick={()=>setDeleteConfirm(true)}
                style={{background:"none",border:"none",color:T.muted,fontSize:12,cursor:"pointer",textDecoration:"underline",padding:0}}>
                계정 탈퇴 신청
              </button>
            ) : (
              <div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:10,padding:"14px"}}>
                <div style={{fontSize:13,fontWeight:700,color:T.danger,marginBottom:6}}>정말 탈퇴하시겠어요?</div>
                <div style={{fontSize:12,color:"#666",lineHeight:1.6,marginBottom:12}}>
                  탈퇴 신청 후 관리자가 7일 이내 처리합니다.<br/>
                  탈퇴 시 모든 학습 데이터가 삭제되며 복구할 수 없습니다.
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setDeleteConfirm(false)} style={{...css.btnGhost,flex:1,fontSize:12,padding:"8px 0"}}>취소</button>
                  <button onClick={async()=>{
                    setDeleting(true);
                    await supabase.from("profiles").update({approval_status:"deletion_requested"}).eq("id",profile.id);
                    setDeleting(false);
                    if(onDelete) onDelete();
                  }} disabled={deleting}
                    style={{flex:2,background:T.danger,color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",padding:"8px 0",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                    {deleting?<><Spinner size={14} color="#fff"/>처리 중...</>:"탈퇴 신청"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// SESSION EXPIRED MODAL
// 토큰 자동 refresh 실패 시 표시. session/profile은 그대로 두고
// 화면 위에 overlay → 재로그인 성공 시 onAuthStateChange가 처리
// ══════════════════════════════════════════════════════
const SessionExpiredModal = ({prefillEmail = ""}) => {
  const [email, setEmail] = useState(prefillEmail);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const tryLogin = async () => {
    if (loading) return;
    setLoading(true); setErr("");
    const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
    setLoading(false);
    if (error) {
      const msg = (error.message||"").toLowerCase();
      if (msg.includes("invalid")) setErr("이메일 또는 비밀번호가 올바르지 않습니다.");
      else setErr("로그인 실패: " + error.message);
    }
    // 성공 시 onAuthStateChange가 잡아서 모달은 자동 사라짐
  };
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(25,29,84,0.55)",backdropFilter:"blur(2px)",zIndex:10000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:"'Noto Sans KR',sans-serif"}}>
      <Card style={{width:"100%",maxWidth:380}}>
        <div style={{fontSize:36,marginBottom:14,textAlign:"center"}}>🔒</div>
        <div style={{fontSize:17,fontWeight:800,color:T.navy,textAlign:"center",marginBottom:6}}>세션이 만료됐어요</div>
        <div style={{fontSize:12,color:T.muted,textAlign:"center",lineHeight:1.7,marginBottom:18}}>
          작업 중이던 내용은 그대로 보존돼요.<br/>
          다시 로그인하면 계속 진행할 수 있어요.
        </div>
        <div style={{display:"grid",gap:10,marginBottom:12}}>
          <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="이메일" autoFocus={!prefillEmail}
            disabled={loading} style={css.input}/>
          <input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="비밀번호"
            autoFocus={!!prefillEmail} disabled={loading}
            onKeyDown={e=>{if(e.key==="Enter") tryLogin();}} style={css.input}/>
        </div>
        {err && <div style={{background:"#FEE2E2",border:"1px solid #FECACA",borderRadius:8,padding:"8px 12px",fontSize:12,color:T.danger,marginBottom:12}}>{err}</div>}
        <button onClick={tryLogin} disabled={loading||!email||!pw}
          style={{...css.btnPrimary,width:"100%",padding:"12px 0",display:"flex",alignItems:"center",justifyContent:"center",gap:8,opacity:(loading||!email||!pw)?0.5:1}}>
          {loading ? <><Spinner size={16} color="#fff"/>로그인 중...</> : "다시 로그인"}
        </button>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// MOBILE BOTTOM NAV
// ══════════════════════════════════════════════════════
const BottomNav = ({nav,view,showInput,onNavigate,isAdmin}) => (
  <div style={{position:"fixed",bottom:0,left:0,right:0,background:T.surface,borderTop:`1px solid ${T.border}`,display:"flex",zIndex:50,paddingBottom:"env(safe-area-inset-bottom)"}}>
    {nav.filter(n=>!n.header).map(n=>(
      <button key={n.key} onClick={()=>onNavigate(n.key,false)}
        style={{flex:1,padding:"12px 0 10px",border:"none",background:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,color:view===n.key&&!showInput?T.navy:T.muted}}>
        <span style={{fontSize:20}}>{n.icon}</span>
        <span style={{fontSize:10,fontWeight:700}}>{n.label}</span>
        {view===n.key&&!showInput&&<div style={{width:20,height:2,background:T.orange,borderRadius:2}}/>}
      </button>
    ))}
    {!isAdmin&&(
      <button onClick={()=>onNavigate(view,true)}
        style={{flex:1,padding:"10px 0",border:"none",background:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
        <div style={{width:44,height:44,borderRadius:14,background:showInput?T.orange:T.navy,display:"flex",alignItems:"center",justifyContent:"center",marginTop:-20,boxShadow:"0 4px 16px rgba(25,29,84,0.3)"}}>
          <span style={{fontSize:24,color:T.white,lineHeight:1,fontWeight:300}}>+</span>
        </div>
        <span style={{fontSize:10,fontWeight:700,marginTop:2,color:showInput?T.orange:T.navy}}>입력</span>
      </button>
    )}
  </div>
);

// ══════════════════════════════════════════════════════
// SIDE NAVIGATION
// ══════════════════════════════════════════════════════
const SideNav = ({nav, view, showInput, onNavigate, profile, isAdmin, isParent, onShowInput, onLogout, onShowProfile, pendingCount, isOpen}) => {
  const w = isOpen ? 240 : 56;
  return (
    <div style={{
      width:w, background:"#F2F3F7", position:"fixed", top:0, left:0, bottom:0,
      display:"flex", flexDirection:"column", zIndex:200, overflowY:"auto", overflowX:"hidden",
      borderRight:"1px solid #E2E6F3", transition:"width 0.22s ease"
    }}>
      {/* 프로필 아바타 */}
      <div style={{padding: isOpen ? "20px 16px 16px" : "16px 0", borderBottom:"1px solid #E2E6F3",
          display:"flex", alignItems:"center", gap:10, justifyContent: isOpen ? "flex-start" : "center",
          cursor:"pointer", transition:"padding 0.22s"}}
        onClick={onShowProfile}>
        {isOpen && <Logo size="md" headerMode onClick={e=>{e.stopPropagation(); onNavigate(nav[0]?.key||"cert", false);}}/>}
        {!isOpen && (
          <div style={{width:34, height:34, borderRadius:10, background:"linear-gradient(135deg,#191D54,#3D4499)",
              display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, color:"#fff", overflow:"hidden", flexShrink:0}}>
            {profile.avatar_url
              ? <img src={profile.avatar_url} alt="avatar" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              : isAdmin ? HI.user(16,"#fff") : profile.name?.charAt(0)||HI.cap(16,"#fff")}
          </div>
        )}
        {isOpen && (
          <>
            <div style={{width:34, height:34, borderRadius:10, background:"linear-gradient(135deg,#191D54,#3D4499)",
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, color:"#fff", flexShrink:0, overflow:"hidden"}}>
              {profile.avatar_url
                ? <img src={profile.avatar_url} alt="avatar" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                : isAdmin ? HI.user(16,"#fff") : profile.name?.charAt(0)||HI.cap(16,"#fff")}
            </div>
            <div style={{minWidth:0}}>
              <div style={{fontSize:13, fontWeight:700, color:"#191D54", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{profile.name}</div>
              <div style={{fontSize:11, color:"#8B91C0", marginTop:1}}>
                {isAdmin ? "관리자" : calcGrade(profile.birth_year, profile.birth_month) || profile.grade || "학생"}
              </div>
            </div>
            <span style={{fontSize:10, color:"#C8CEED", marginLeft:"auto"}}>✎</span>
          </>
        )}
      </div>

      {/* 네비게이션 */}
      <div style={{flex:1, padding: isOpen ? "12px 8px" : "12px 4px"}}>
        {isAdmin && pendingCount > 0 && (
          <div style={{
              background:"#FEE2E2", border:"1px solid #FECACA", borderRadius:8,
              padding: isOpen ? "8px 12px" : "8px 4px", marginBottom:8,
              fontSize: isOpen ? 12 : 16, color:"#E8394A", fontWeight:700, cursor:"pointer",
              textAlign: isOpen ? "left" : "center", overflow:"hidden", whiteSpace:"nowrap"}}
            onClick={()=>onNavigate("dashboard",false)}>
            {isOpen
              ? <span style={{display:"flex",alignItems:"center",gap:5}}>{HI.bell(13,"#E8394A")}{pendingCount}명 승인 대기</span>
              : <span style={{display:"flex",justifyContent:"center"}}>{HI.bell(16,"#E8394A")}</span>}
          </div>
        )}
        {nav.map((n, i) => {
          // 헤더 항목 (자녀 이름 등) — 클릭 불가, 구분 라벨
          if(n.header){
            if(!isOpen) return null;
            return (
              <div key={`hdr-${i}`} style={{
                padding:"14px 14px 4px 14px", fontSize:11, fontWeight:800,
                color:"#9CA3AF", letterSpacing:"0.02em", marginTop:4,
                borderTop: i>0 ? "1px solid rgba(25,29,84,0.08)" : "none",
              }}>
                {n.label}
              </div>
            );
          }
          const isActive = view===n.key && !showInput;
          return (
            <div key={n.key} onClick={()=>onNavigate(n.key, false)}
              style={{
                display:"flex", alignItems:"center", gap:10,
                padding: isOpen ? "11px 14px" : "11px 0",
                justifyContent: isOpen ? "flex-start" : "center",
                cursor:"pointer", borderRadius:10, marginBottom:2,
                background: isActive ? "#fff" : "transparent",
                borderLeft: isActive && isOpen ? "3px solid #F68B1E" : "3px solid transparent",
                boxShadow: isActive ? "0 1px 4px rgba(25,29,84,0.08)" : "none",
                transition:"all 0.15s", overflow:"hidden"
              }}
              onMouseEnter={e=>{ if(!isActive) e.currentTarget.style.background="rgba(25,29,84,0.05)"; }}
              onMouseLeave={e=>{ if(!isActive) e.currentTarget.style.background="transparent"; }}>
              <span style={{display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {navIcon(n.icon, 19, isActive?"#191D54":"#6B7299")}
              </span>
              {isOpen && <span style={{fontSize:13, fontWeight:isActive?700:500, color:isActive?"#191D54":"#4A5080", whiteSpace:"nowrap"}}>{n.label}</span>}
            </div>
          );
        })}
      </div>

      {/* 하단 버튼들 */}
      <div style={{padding: isOpen ? "0 12px 16px" : "0 6px 16px", display:"flex", flexDirection:"column", gap:6}}>
        {!isAdmin && !isParent && (
          <button onClick={onShowInput}
            style={{width:"100%", background:"#F68B1E", border:"none", borderRadius:10,
              padding:"12px", color:"#fff", fontSize: isOpen ? 13 : 18, fontWeight:800,
              cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6}}>
            {isOpen ? <><span style={{fontSize:16, fontWeight:300}}>+</span> 학습 입력</> : "+"}
          </button>
        )}
        {isOpen && <>
          {/* 도움말 버튼 — 비활성화
          <button onClick={()=>window.open("/manual.html","_blank")}
            style={{width:"100%", background:"transparent", border:"1px solid #E2E6F3", borderRadius:8, padding:"9px", color:"#8B91C0", fontSize:12, cursor:"pointer"}}>
            ❓ 도움말
          </button>
          */}
          <button onClick={onLogout}
            style={{width:"100%", background:"transparent", border:"1px solid #E2E6F3", borderRadius:8, padding:"9px", color:"#C8CEED", fontSize:12, cursor:"pointer"}}>
            로그아웃
          </button>
        </>}
        {!isOpen && <>
          {/* 도움말 아이콘 — 비활성화 */}
          <button onClick={onLogout} title="로그아웃"
            style={{width:"100%", background:"transparent", border:"1px solid #E2E6F3", borderRadius:8, padding:"8px 0", color:"#C8CEED", fontSize:14, cursor:"pointer"}}>↩</button>
        </>}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════
// ADMIN: 챌린지 관리 (학생 아이디 무관 — 크롤링 + 확인 체크만)
// 챌린지: 사고력 영작 / 독서챌린지 / 수학독해 챌린지
// 참여 명단·회차별 마감은 추후 입력 (현재는 틀)
// ══════════════════════════════════════════════════════
const CHALLENGE_FALLBACK = [
  { key:"sagoyeong", name:"사고력 영작" },
  { key:"reading",   name:"독서챌린지" },
  { key:"mathread",  name:"수학독해 챌린지" },
];

const ChallengeAdmin = () => {
  const [challenges, setChallenges] = useState(CHALLENGE_FALLBACK);
  const [activeKey, setActiveKey]   = useState("sagoyeong");
  const [subTab, setSubTab]         = useState("overview"); // overview(전체현황) | posts(인증글 확인) | rounds(회차별 확인)
  const [roundFilter, setRoundFilter] = useState("all");
  const [assignModal, setAssignModal] = useState(null); // 참여자 수동 연결 모달 대상 글
  const [assignQuery, setAssignQuery] = useState("");    // 검색어(이름/번호)
  const [assignPage, setAssignPage]   = useState(1);     // 모달 목록 페이지
  const [editRoundPost, setEditRoundPost] = useState(null); // 회차 인라인 편집 중인 글 id
  const [posts, setPosts]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [crawlRunning, setCrawlRunning] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [rounds, setRounds]         = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(null); // {key|null, name, board_url, participantsText, roundsText, active}
  const [saving, setSaving]         = useState(false);

  const reloadChallenges = async (selectKey) => {
    const { data } = await supabase.rpc("get_challenges");
    const list = (data && data.length) ? data : CHALLENGE_FALLBACK;
    setChallenges(list);
    if (selectKey) setActiveKey(selectKey);
    else if (!list.find(c => c.key === activeKey)) setActiveKey(list[0]?.key);
  };

  useEffect(() => {
    supabase.rpc("get_challenges").then(
      ({ data }) => { if (data && data.length) setChallenges(data); },
      () => {}
    );
  }, []);

  const loadParticipants = async (key) => {
    const { data } = await supabase.rpc("get_challenge_participants", { p_key: key });
    setParticipants(data || []);
  };
  const loadRounds = async (key) => {
    const { data } = await supabase.rpc("get_challenge_rounds", { p_key: key });
    setRounds(data || []);
  };
  useEffect(() => { loadParticipants(activeKey); loadRounds(activeKey); }, [activeKey]);

  const fmtDeadline = (d) => { if (!d) return "—"; const [y, m, dd] = d.split("-"); return `${+m}/${+dd}`; };
  // 회차 입력 텍스트 → [{round, deadline}] (예: "1주차 2026-06-07" / "1, 6/7")
  const parseRoundsText = (text) => (text || "").split("\n").map(line => {
    const t = line.trim(); if (!t) return null;
    const rm = t.match(/(\d+)/); if (!rm) return null;
    const round = parseInt(rm[1], 10);
    let deadline = null;
    const iso = t.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (iso) deadline = `${iso[1]}-${String(+iso[2]).padStart(2,"0")}-${String(+iso[3]).padStart(2,"0")}`;
    else { const md = t.replace(/^\s*\d+\s*(주차|회|차)?/, "").match(/(\d{1,2})\s*[\/월.\-]\s*(\d{1,2})/);
      if (md) deadline = `2026-${String(+md[1]).padStart(2,"0")}-${String(+md[2]).padStart(2,"0")}`; }
    return { round, deadline };
  }).filter(Boolean);

  // 게시판 URL에서 카페 club id / 게시판 menu id 추출 (20HA와 동일 형식)
  const parseBoard = (url) => {
    const u = url || "";
    let m = u.match(/cafes\/(\d+)\/menus\/(\d+)/);
    if (m) return { cafeId: m[1], menuId: m[2] };
    m = u.match(/clubid=(\d+)[\s\S]*?menuid=(\d+)/i);
    if (m) return { cafeId: m[1], menuId: m[2] };
    m = u.match(/menus\/(\d+)/);
    if (m) return { cafeId: null, menuId: m[1] };
    return { cafeId: null, menuId: null };
  };

  const openNew  = () => { setForm({ key: null, name: "", board_url: "", participantsText: "", roundsText: "", active: true }); setShowForm(true); };
  const openEdit = async () => {
    const [{ data: pData }, { data: rData }] = await Promise.all([
      supabase.rpc("get_challenge_participants", { p_key: activeKey }),
      supabase.rpc("get_challenge_rounds", { p_key: activeKey }),
    ]);
    const pText = (pData || []).map(p => p.name || p.parent_name || "").filter(Boolean).join("\n");
    setForm({
      key: active.key, name: active.name || "", board_url: active.board_url || "",
      participantsText: pText, _participantsOriginal: pText, _participantsCount: (pData || []).length,
      roundsText: (rData || []).map(r => `${r.round_no}주차  ${r.deadline || ""}`.trim()).join("\n"),
      active: active.active !== false,
    });
    setShowForm(true);
  };

  const saveForm = async () => {
    if (!form.name.trim()) { alert("챌린지명을 입력해주세요."); return; }
    setSaving(true);
    try {
      const key = form.key || ("c" + Date.now().toString(36));
      const { cafeId, menuId } = parseBoard(form.board_url);
      await supabase.rpc("upsert_challenge", {
        p_key: key, p_name: form.name.trim(), p_board_url: form.board_url.trim() || null,
        p_cafe_id: cafeId, p_board_menu_id: menuId, p_active: form.active,
      });
      // 명단 보호: 텍스트가 실제로 바뀐 경우에만 재작성 (게시판·회차만 수정 시 풍부한 명단 데이터 유지)
      const changed = (form.participantsText || "").trim() !== (form._participantsOriginal || "").trim();
      if (changed) {
        if (form.key && form._participantsCount > 0 &&
            !window.confirm(`명단을 새로 입력한 내용으로 덮어씁니다.\n(기존 ${form._participantsCount}명의 연락처·부가정보가 사라지고 입력한 이름만 남습니다.)\n계속할까요?`)) {
          // 명단 덮어쓰기 취소 — 나머지(게시판·회차)는 저장 진행
        } else {
          const names = form.participantsText.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
          await supabase.rpc("set_challenge_participants", { p_key: key, p_names: names });
        }
      }
      const roundsArr = parseRoundsText(form.roundsText).map(r => ({ round: r.round, deadline: r.deadline }));
      await supabase.rpc("set_challenge_rounds", { p_key: key, p_rounds: roundsArr });
      await reloadChallenges(key);
      await loadParticipants(key);
      await loadRounds(key);
      setShowForm(false);
    } catch (e) { alert("저장 실패: " + (e.message || e)); }
    setSaving(false);
  };

  const removeChallenge = async () => {
    if (!window.confirm(`'${active.name}' 챌린지를 삭제할까요?\n수집된 글·명단도 함께 삭제됩니다.`)) return;
    await supabase.rpc("delete_challenge", { p_key: activeKey });
    setShowForm(false);
    await reloadChallenges();
  };

  const loadPosts = async (key) => {
    setLoading(true);
    const { data } = await supabase.rpc("get_challenge_posts", { p_challenge_key: key, p_limit: 500 });
    setPosts(data || []);
    setLoading(false);
  };
  useEffect(() => { loadPosts(activeKey); }, [activeKey]);

  const active = challenges.find(c => c.key === activeKey) || {};
  const checkedCount = posts.filter(p => p.checked).length;

  const toggleCheck = async (post) => {
    const next = !post.checked;
    setPosts(prev => prev.map(p => p.id === post.id ? { ...p, checked: next, checked_at: next ? new Date().toISOString() : null } : p));
    await supabase.rpc("toggle_challenge_check", { p_post_id: post.id, p_checked: next });
  };

  // ── 크롤 글 ↔ 명단 매칭 (뒤4자리 → 이름 → 수동) ──
  const postLast4 = (t) => { const ms = (t || "").match(/\d{4}/g); return ms ? ms[ms.length - 1] : null; };
  const partsByL4 = {}; participants.forEach(p => { if (p.phone) { const k = p.phone.slice(-4); (partsByL4[k] = partsByL4[k] || []).push(p); } });
  const partsByName = {}; participants.forEach(p => { if (p.name) (partsByName[p.name] = partsByName[p.name] || []).push(p); });
  const matchOf = (post) => {
    if (post.participant_id) { const p = participants.find(x => x.id === post.participant_id); return { p: p || null, how: "수동" }; }
    const nm = (post.parsed_name || "").trim();
    const l4 = postLast4(post.post_title);
    const cands = l4 ? (partsByL4[l4] || []) : [];
    if (cands.length === 1) return { p: cands[0], how: "뒤4" };
    if (cands.length > 1) { const byn = cands.filter(p => p.name === nm || p.parent_name === nm); if (byn.length === 1) return { p: byn[0], how: "뒤4+이름" }; return { p: null, how: "중복" }; }
    const byn = partsByName[nm] || []; if (byn.length === 1) return { p: byn[0], how: "이름" };
    return { p: null, how: "미매칭" };
  };
  const matchByPost = {}; posts.forEach(p => { const m = matchOf(p); matchByPost[p.id] = m.p ? m.p.id : null; });

  const setPostRound = async (post, r) => {
    const v = r === "" ? null : Number(r);
    setPosts(prev => prev.map(p => p.id === post.id ? { ...p, parsed_round: v } : p));
    await supabase.rpc("set_challenge_post_round", { p_post_id: post.id, p_round: v });
  };
  const assignPost = async (post, pid) => {
    const v = pid === "" ? null : Number(pid);
    setPosts(prev => prev.map(p => p.id === post.id ? { ...p, participant_id: v } : p));
    await supabase.rpc("set_challenge_post_participant", { p_post_id: post.id, p_participant_id: v });
    // 연결되면 글 제목의 학생 이름을 참여자(학부모만 있던 행)에 채워줌
    if (v) {
      const pt = participants.find(x => x.id === v);
      const sn = (post.parsed_name || "").trim();
      if (pt && !pt.name && /^[가-힣]{2,4}$/.test(sn)) {
        await supabase.rpc("set_challenge_participant_name", { p_id: v, p_name: sn });
        setParticipants(prev => prev.map(x => x.id === v ? { ...x, name: sn } : x));
      }
    }
  };

  const triggerCrawl = async () => {
    if (!active.naver_board_id) {
      alert(`'${active.name}' 챌린지의 네이버 게시판이 아직 설정되지 않았습니다.\n참여 명단·회차 정보와 함께 게시판을 등록하면 크롤링할 수 있어요.`);
      return;
    }
    const token = (process.env.REACT_APP_GITHUB_TOKEN || "").trim();
    if (!token) { alert("REACT_APP_GITHUB_TOKEN 환경변수가 설정되지 않았습니다."); return; }
    setCrawlRunning(true);
    try {
      const ref = ["meta-x.ai.kr", "20ha-meta-x.vercel.app"].includes(window.location.hostname) ? "main" : "dev";
      const r = await fetch(
        "https://api.github.com/repos/MinDaehyeon/20ha-meta-x/actions/workflows/challenge_crawler.yml/dispatches",
        { method:"POST",
          headers:{ "Authorization":`Bearer ${token}`, "Accept":"application/vnd.github+json", "Content-Type":"application/json" },
          body: JSON.stringify({ ref, inputs: { challenge_key: activeKey } }) }
      );
      if (r.status === 204) alert("크롤링 시작됐습니다!\n약 1분 후 새로고침 하면 글이 업데이트됩니다.");
      else alert("실행 실패: " + r.status);
    } catch (e) { alert("오류: " + e.message); }
    setCrawlRunning(false);
  };

  const fmtDate = (ts) => { if (!ts) return "—"; const k = new Date(new Date(ts).getTime() + 9*3600*1000); return `${k.getUTCMonth()+1}/${k.getUTCDate()}`; };

  return (
    <div>
      <div style={{ fontSize:18, fontWeight:800, color:T.navy, marginBottom:14 }}>📋 챌린지 관리</div>

      {/* 챌린지 선택 탭 + 관리 버튼 */}
      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
        {challenges.map(c => {
          const on = c.key === activeKey;
          return (
            <button key={c.key} onClick={() => setActiveKey(c.key)}
              style={{ padding:"9px 18px", borderRadius:10, border:`1px solid ${on?T.navy:T.border}`,
                background:on?T.navy:T.white, color:on?T.white:T.navy, fontSize:13, fontWeight:700, cursor:"pointer" }}>
              {c.name}{c.active === false ? " (비활성)" : ""}
            </button>
          );
        })}
        <div style={{ flex:1 }}/>
        {active.key && (
          <button onClick={openEdit}
            style={{ padding:"9px 14px", borderRadius:10, border:`1px solid ${T.border}`, background:T.white, color:T.navy, fontSize:13, fontWeight:700, cursor:"pointer" }}>
            ⚙️ 설정
          </button>
        )}
        <button onClick={openNew}
          style={{ padding:"9px 16px", borderRadius:10, border:"none", background:T.orange, color:T.white, fontSize:13, fontWeight:800, cursor:"pointer" }}>
          + 새 챌린지
        </button>
      </div>

      {/* 전체 현황 / 인증글 확인 / 회차별 확인 서브탭 */}
      <div style={{ display:"flex", gap:6, marginBottom:16, borderBottom:`1px solid ${T.border}` }}>
        {[{ k:"overview", l:"📊 전체 현황" }, { k:"posts", l:"📝 인증글 확인" }, { k:"rounds", l:"🔢 회차별 확인" }].map(t => {
          const on = t.k === subTab;
          return (
            <button key={t.k} onClick={() => setSubTab(t.k)}
              style={{ padding:"9px 16px", border:"none", background:"none", cursor:"pointer",
                fontSize:13, fontWeight:on?800:600, color:on?T.navy:T.muted,
                borderBottom:on?`3px solid ${T.orange}`:"3px solid transparent", marginBottom:-1 }}>
              {t.l}
            </button>
          );
        })}
      </div>

      {subTab === "overview" ? (
        // ── 전체 현황 (참여자 × 회차 매트릭스, 20HA 전체현황 스타일) ──
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, flexWrap:"wrap" }}>
            <button onClick={() => { loadPosts(activeKey); loadParticipants(activeKey); loadRounds(activeKey); }}
              style={{ padding:"8px 14px", borderRadius:8, border:`1px solid ${T.border}`, background:T.white, color:T.navy, fontSize:13, fontWeight:700, cursor:"pointer" }}>
              ↻ 새로고침
            </button>
            <span style={{ fontSize:12, color:T.muted }}>참여 <strong style={{ color:T.navy }}>{participants.length}</strong>명 · 회차 <strong style={{ color:T.navy }}>{rounds.length}</strong>개 · 수집글 {posts.length}건 · 확인 <strong style={{ color:"#16A34A" }}>{checkedCount}</strong>건</span>
            {/* 범례 */}
            <div style={{ display:"flex", gap:12, marginLeft:"auto", fontSize:11, color:T.muted, alignItems:"center" }}>
              <span><span style={{ color:"#16A34A", fontWeight:800 }}>●</span> 확인완료</span>
              <span><span style={{ color:T.orange, fontWeight:800 }}>●</span> 제출(미확인)</span>
              <span><span style={{ color:"#D1D5DB", fontWeight:800 }}>●</span> 미제출</span>
            </div>
          </div>

          {participants.length === 0 ? (
            <Card style={{ padding:24 }}>
              <div style={{ fontSize:13, color:T.muted, lineHeight:1.9 }}>
                아직 <strong>참여 명단</strong>이 없습니다. 우측 상단 <strong>⚙️ 설정</strong>에서 명단을 등록하면<br/>
                <strong>참여자 × 회차</strong> 전체 현황표가 여기에 표시됩니다.
                {rounds.length > 0 && <><br/><span style={{ color:T.navy }}>회차: {rounds.map(r => `${r.round_no}주차(~${fmtDeadline(r.deadline)})`).join(" · ")}</span></>}
              </div>
            </Card>
          ) : (
            <Card style={{ padding:0, overflow:"auto" }}>
              {(() => { const COLS = `100px 100px 120px repeat(${rounds.length}, 64px) 60px`; return (
              <div style={{ minWidth: 320 + rounds.length*64 + 60 }}>
                {/* 헤더 */}
                <div style={{ display:"grid", gridTemplateColumns:COLS, background:T.navy, position:"sticky", top:0 }}>
                  <div style={{ padding:"10px 12px", fontSize:12, fontWeight:700, color:"#fff" }}>학생 이름</div>
                  <div style={{ padding:"10px 8px", fontSize:12, fontWeight:700, color:"#fff", borderLeft:"1px solid rgba(255,255,255,0.12)" }}>학부모 이름</div>
                  <div style={{ padding:"10px 8px", fontSize:12, fontWeight:700, color:"#fff", borderLeft:"1px solid rgba(255,255,255,0.12)" }}>연락처</div>
                  {rounds.map(r => (
                    <div key={r.round_no} style={{ padding:"6px 4px", textAlign:"center", color:"#fff", borderLeft:"1px solid rgba(255,255,255,0.12)" }}>
                      <div style={{ fontSize:12, fontWeight:700 }}>{r.round_no}주차</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.7)" }}>~{fmtDeadline(r.deadline)}</div>
                    </div>
                  ))}
                  <div style={{ padding:"10px 4px", textAlign:"center", fontSize:12, fontWeight:700, color:"#fff", borderLeft:"1px solid rgba(255,255,255,0.12)" }}>제출/{rounds.length}</div>
                </div>
                {/* 행 */}
                {participants.map((pt, i) => {
                  let done = 0;
                  return (
                    <div key={pt.id} style={{ display:"grid", gridTemplateColumns:COLS,
                      borderTop:`1px solid ${T.border}`, background:i%2===0?T.white:"#F9FAFB", alignItems:"stretch" }}>
                      <div style={{ padding:"8px 12px", display:"flex", alignItems:"center", fontSize:13, fontWeight:600, color: pt.name?T.navy:"#D1D5DB" }}>{pt.name || "—"}</div>
                      <div style={{ padding:"8px 8px", display:"flex", alignItems:"center", fontSize:12, color: pt.parent_name?T.text:"#D1D5DB", borderLeft:`1px solid ${T.border}` }}>{pt.parent_name || "—"}</div>
                      <div style={{ padding:"8px 8px", display:"flex", alignItems:"center", fontSize:11, color: pt.phone?T.muted:"#D1D5DB", borderLeft:`1px solid ${T.border}` }}>{pt.phone ? pt.phone.replace(/(\d{2,3})(\d{3,4})(\d{4})/, "$1-$2-$3") : "—"}</div>
                      {rounds.map(r => {
                        const ps = posts.filter(p => matchByPost[p.id] === pt.id && p.parsed_round === r.round_no);
                        const checked = ps.some(p => p.checked);
                        const submitted = ps.length > 0;
                        if (submitted) done++;
                        const sym = checked ? "●" : submitted ? "●" : "–";
                        const color = checked ? "#16A34A" : submitted ? T.orange : "#D1D5DB";
                        const bg = checked ? "#F0FDF4" : submitted ? (T.orangePale || "#FFF7ED") : "transparent";
                        return (
                          <div key={r.round_no} title={ps.length ? ps.map(p=>p.post_title).join("\n") : "미제출"}
                            style={{ borderLeft:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", background:bg, fontSize:16, fontWeight:800, color }}>
                            {sym}
                          </div>
                        );
                      })}
                      <div style={{ borderLeft:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color: done>0?T.navy:T.muted }}>
                        {done}/{rounds.length}
                      </div>
                    </div>
                  );
                })}
              </div>
              ); })()}
            </Card>
          )}
          {rounds.length === 0 && participants.length > 0 && (
            <div style={{ fontSize:12, color:T.orange, marginTop:10 }}>⚠️ 회차 정보가 없습니다. ⚙️ 설정에서 회차별 마감일을 등록하면 열이 생깁니다.</div>
          )}
          <div style={{ fontSize:11, color:T.muted, marginTop:10, lineHeight:1.7 }}>
            ※ 글 제목에서 이름·회차를 자동 인식해 매칭합니다. 글 확인(체크)은 <strong>인증글 확인</strong> 탭에서 합니다.
          </div>
        </div>
      ) : subTab === "posts" ? (
        // ── 인증글 확인 (크롤링 + 회차 필터 + 확인 체크) ──
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, flexWrap:"wrap" }}>
            <button onClick={triggerCrawl} disabled={crawlRunning}
              style={{ ...css.btnOrange, padding:"8px 16px", fontSize:13, background:"#059669", opacity:crawlRunning?0.6:1 }}>
              {crawlRunning ? "⏳ 실행 중..." : "🔄 지금 크롤링"}
            </button>
            <button onClick={() => loadPosts(activeKey)}
              style={{ padding:"8px 14px", borderRadius:8, border:`1px solid ${T.border}`, background:T.white, color:T.navy, fontSize:13, fontWeight:700, cursor:"pointer" }}>
              ↻ 새로고침
            </button>
            <select value={roundFilter} onChange={e => setRoundFilter(e.target.value)}
              style={{ padding:"7px 10px", borderRadius:8, border:`1px solid ${T.border}`, fontSize:13, background:T.white, color:T.navy, cursor:"pointer" }}>
              <option value="all">회차 전체</option>
              {rounds.map(r => <option key={r.round_no} value={String(r.round_no)}>{r.round_no}주차 (~{fmtDeadline(r.deadline)})</option>)}
              <option value="none">회차 미분류</option>
            </select>
            {(() => {
              const fp = posts.filter(p => roundFilter==="all" ? true : roundFilter==="none" ? !p.parsed_round : p.parsed_round===Number(roundFilter));
              return <span style={{ fontSize:12, color:T.muted }}>확인 <strong style={{ color:"#16A34A" }}>{fp.filter(p=>p.checked).length}</strong> / {fp.length}건</span>;
            })()}
            {!active.naver_board_id && (
              <span style={{ fontSize:12, color:T.orange, fontWeight:600 }}>⚠️ 게시판 미설정 (⚙️ 설정에서 게시판 URL 등록)</span>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign:"center", padding:40, color:T.muted, fontSize:13 }}>불러오는 중...</div>
          ) : (
            <Card style={{ padding:0, overflow:"hidden" }}>
              {/* 헤더 */}
              <div style={{ display:"grid", gridTemplateColumns:"170px 54px 74px 92px 1fr 56px 64px", background:T.navy, padding:"9px 12px", gap:8, alignItems:"center" }}>
                {["참여자 연결","회차","글번호","닉네임","제목","작성일","확인"].map(h => (
                  <div key={h} style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.85)", textAlign:(h==="제목"||h==="참여자 연결")?"left":"center" }}>{h}</div>
                ))}
              </div>
              {(() => {
                const fp = posts.filter(p => roundFilter==="all" ? true : roundFilter==="none" ? !p.parsed_round : p.parsed_round===Number(roundFilter));
                if (fp.length === 0) return (
                  <div style={{ padding:28, textAlign:"center", color:T.muted, fontSize:13 }}>
                    {posts.length === 0 ? '아직 크롤링된 글이 없습니다. "지금 크롤링"을 눌러주세요.' : "해당 회차의 글이 없습니다."}
                  </div>
                );
                return fp.map((p, i) => {
                  const articleId = p.post_url ? p.post_url.split("/").pop() : p.id;
                  const m = matchOf(p);
                  return (
                    <div key={p.id} style={{ display:"grid", gridTemplateColumns:"170px 54px 74px 92px 1fr 56px 64px", padding:"9px 12px", gap:8, alignItems:"center",
                      borderTop:`1px solid ${T.border}`, background:p.checked ? "#F0FDF4" : (i%2===0?T.white:"#F9FAFB") }}>
                      {/* 참여자 연결 (클릭 → 검색 모달) */}
                      <div style={{ fontSize:11, overflow:"hidden" }}>
                        <div onClick={() => { setAssignModal(p); setAssignQuery(""); setAssignPage(1); }} title="클릭해서 참여자 검색·연결"
                          style={{ cursor:"pointer", display:"flex", alignItems:"center", gap:4, padding:"3px 4px", borderRadius:6, border:`1px dashed ${m.p?"#E5E7EB":"#FCA5A5"}` }}>
                          {m.p ? (
                            <>
                              <span style={{ fontWeight:700, color:T.navy, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.p.name || m.p.parent_name}</span>
                              <span style={{ fontSize:9, color:"#fff", background:p.participant_id?"#4F46E5":(m.how==="중복"?T.orange:"#16A34A"), borderRadius:4, padding:"1px 4px", flexShrink:0 }}>{p.participant_id?"수동":m.how}</span>
                            </>
                          ) : (
                            <span style={{ color: m.how==="중복"?T.orange:"#DC2626", fontWeight:600 }}>{m.how==="중복"?"중복-수동지정":"미매칭-지정"}</span>
                          )}
                        </div>
                      </div>
                      {/* 회차 (클릭 인라인 수정) */}
                      <div style={{ textAlign:"center" }}>
                        {editRoundPost === p.id ? (
                          <select autoFocus defaultValue={p.parsed_round || ""}
                            onChange={e => { setPostRound(p, e.target.value); setEditRoundPost(null); }}
                            onBlur={() => setEditRoundPost(null)}
                            style={{ width:"100%", padding:"2px", borderRadius:6, border:`1px solid ${T.border}`, fontSize:11 }}>
                            <option value="">미분류</option>
                            {rounds.map(r => <option key={r.round_no} value={r.round_no}>{r.round_no}주차</option>)}
                          </select>
                        ) : (
                          <span onClick={() => setEditRoundPost(p.id)} title="클릭해서 회차 변경"
                            style={{ cursor:"pointer", fontSize:11, fontWeight:700, color: p.parsed_round?T.navy:T.muted, borderBottom:"1px dashed #CBD5E1" }}>
                            {p.parsed_round ? `${p.parsed_round}주차` : "—"}
                          </span>
                        )}
                      </div>
                      {/* 글번호 */}
                      <div style={{ fontSize:11, color:T.muted, textAlign:"center", fontFamily:"monospace" }}>{articleId}</div>
                      {/* 닉네임 */}
                      <div style={{ fontSize:12, color:T.navy, fontWeight:600, textAlign:"center", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.naver_nickname || "—"}</div>
                      {/* 제목 */}
                      <div style={{ fontSize:12, overflow:"hidden" }}>
                        <a href={cafeArticleUrl(p.post_url)} target="_blank" rel="noreferrer"
                          style={{ color:T.navy, textDecoration:"none", fontWeight:600, display:"block", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                          title={p.post_title}>{p.post_title || "(제목 없음)"}</a>
                      </div>
                      {/* 작성일 */}
                      <div style={{ fontSize:11, color:T.muted, textAlign:"center" }}>{fmtDate(p.posted_at)}</div>
                      {/* 확인 */}
                      <div style={{ display:"flex", justifyContent:"center" }}>
                        <input type="checkbox" checked={p.checked} onChange={() => toggleCheck(p)}
                          style={{ width:18, height:18, accentColor:"#16A34A", cursor:"pointer" }}/>
                      </div>
                    </div>
                  );
                });
              })()}
            </Card>
          )}
        </div>
      ) : (
        // ── 회차별 확인 (회차별 통계) ──
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, flexWrap:"wrap" }}>
            <button onClick={() => { loadPosts(activeKey); loadParticipants(activeKey); loadRounds(activeKey); }}
              style={{ padding:"8px 14px", borderRadius:8, border:`1px solid ${T.border}`, background:T.white, color:T.navy, fontSize:13, fontWeight:700, cursor:"pointer" }}>
              ↻ 새로고침
            </button>
            <span style={{ fontSize:12, color:T.muted }}>회차별 제출·확인 통계 {participants.length>0 && <>(명단 {participants.length}명 기준)</>}</span>
          </div>
          {rounds.length === 0 ? (
            <Card style={{ padding:24 }}>
              <div style={{ fontSize:13, color:T.muted, lineHeight:1.8 }}>
                회차 정보가 없습니다. 우측 상단 <strong>⚙️ 설정</strong>에서 회차별 마감일을 등록하세요.
              </div>
            </Card>
          ) : (
            <Card style={{ padding:0, overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"90px 100px 1fr 1fr 1fr", background:T.navy, padding:"10px 12px", gap:8 }}>
                {["회차","마감일","제출(명)","확인완료(명)","미제출(명)"].map(h => (
                  <div key={h} style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.85)", textAlign:h==="회차"||h==="마감일"?"left":"center" }}>{h}</div>
                ))}
              </div>
              {rounds.map((r, i) => {
                const ps = posts.filter(p => p.parsed_round === r.round_no);
                // 명단에 연결된 글 기준으로 제출/확인 인원 집계 (연결 안 된 글은 제외)
                const submitPids = new Set(ps.map(p => matchByPost[p.id]).filter(Boolean));
                const checkPids  = new Set(ps.filter(p => p.checked).map(p => matchByPost[p.id]).filter(Boolean));
                const total = participants.length;
                const submitCnt = submitPids.size;
                const checkCnt  = checkPids.size;
                const missCnt   = total > 0 ? total - submitCnt : null;
                return (
                  <div key={r.round_no} style={{ display:"grid", gridTemplateColumns:"90px 100px 1fr 1fr 1fr", padding:"10px 12px", gap:8, alignItems:"center",
                    borderTop:`1px solid ${T.border}`, background:i%2===0?T.white:"#F9FAFB" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:T.navy }}>{r.round_no}주차</div>
                    <div style={{ fontSize:12, color:T.muted }}>~ {fmtDeadline(r.deadline)}</div>
                    <div style={{ fontSize:14, fontWeight:800, color:T.navy, textAlign:"center" }}>{submitCnt}{total>0 && <span style={{ fontSize:11, color:T.muted, fontWeight:600 }}> /{total}</span>}</div>
                    <div style={{ fontSize:14, fontWeight:800, color:"#16A34A", textAlign:"center" }}>{checkCnt}{total>0 && <span style={{ fontSize:11, color:T.muted, fontWeight:600 }}> /{total}</span>}</div>
                    <div style={{ fontSize:14, fontWeight:800, color: missCnt===null?T.muted:(missCnt>0?T.orange:"#16A34A"), textAlign:"center" }}>{missCnt===null ? "—" : missCnt}</div>
                  </div>
                );
              })}
            </Card>
          )}
          {participants.length === 0 && rounds.length > 0 && (
            <div style={{ fontSize:11, color:T.muted, marginTop:10 }}>※ 참여 명단을 등록하면 미제출 인원과 명단 대비 비율(/N)이 함께 표시됩니다. (현재는 수집된 글의 고유 이름 수 기준)</div>
          )}
        </div>
      )}

      {/* 챌린지 추가/수정 모달 */}
      {showForm && form && (
        <div onClick={() => !saving && setShowForm(false)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:10000, padding:16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:T.white, borderRadius:14, padding:24, width:"100%", maxWidth:480, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 10px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize:17, fontWeight:800, color:T.navy, marginBottom:16 }}>
              {form.key ? "챌린지 설정" : "새 챌린지 추가"}
            </div>

            <label style={{ fontSize:12, fontWeight:700, color:T.navy, display:"block", marginBottom:6 }}>챌린지명 *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="예: 사고력 영작"
              style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${T.border}`, fontSize:14, marginBottom:16, boxSizing:"border-box" }}/>

            <label style={{ fontSize:12, fontWeight:700, color:T.navy, display:"block", marginBottom:6 }}>게시판 URL <span style={{ fontWeight:400, color:T.muted }}>(네이버 카페 게시판 링크, 나중에 입력 가능)</span></label>
            <input value={form.board_url} onChange={e => setForm(f => ({ ...f, board_url: e.target.value }))}
              placeholder="https://cafe.naver.com/f-e/cafes/31045190/menus/165"
              style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${T.border}`, fontSize:13, marginBottom:4, boxSizing:"border-box" }}/>
            {form.board_url && (() => { const { cafeId, menuId } = parseBoard(form.board_url); return (
              <div style={{ fontSize:11, color: menuId ? "#16A34A" : T.orange, marginBottom:16 }}>
                {menuId ? `✓ 인식됨 — 카페 ${cafeId || "31045190"} / 게시판 ${menuId}` : "⚠️ 게시판 번호를 인식하지 못했어요. /menus/번호 형식 링크인지 확인해주세요."}
              </div>
            ); })()}
            {!form.board_url && <div style={{ marginBottom:16 }}/>}

            <label style={{ fontSize:12, fontWeight:700, color:T.navy, display:"block", marginBottom:6 }}>참여 명단 <span style={{ fontWeight:400, color:T.muted }}>(한 줄에 한 명, 또는 쉼표로 구분)</span></label>
            <textarea value={form.participantsText} onChange={e => setForm(f => ({ ...f, participantsText: e.target.value }))}
              placeholder={"홍길동\n김철수\n이영희"}
              rows={5}
              style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${T.border}`, fontSize:13, marginBottom:16, boxSizing:"border-box", resize:"vertical", fontFamily:"inherit" }}/>

            <label style={{ fontSize:12, fontWeight:700, color:T.navy, display:"block", marginBottom:6 }}>회차별 마감일 <span style={{ fontWeight:400, color:T.muted }}>(한 줄에 "회차 날짜" — 예: 1주차 2026-06-14)</span></label>
            <textarea value={form.roundsText} onChange={e => setForm(f => ({ ...f, roundsText: e.target.value }))}
              placeholder={"1주차 2026-06-14\n2주차 2026-06-21\n3주차 2026-06-27"}
              rows={5}
              style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${T.border}`, fontSize:13, marginBottom:16, boxSizing:"border-box", resize:"vertical", fontFamily:"inherit" }}/>

            <label style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20, cursor:"pointer" }}>
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                style={{ width:16, height:16, accentColor:T.navy, cursor:"pointer" }}/>
              <span style={{ fontSize:13, color:T.text }}>활성 (목록·크롤링 사용)</span>
            </label>

            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
              {form.key
                ? <button onClick={removeChallenge} disabled={saving}
                    style={{ padding:"8px 14px", borderRadius:8, border:`1px solid ${T.danger||"#DC2626"}`, background:T.white, color:T.danger||"#DC2626", fontSize:13, fontWeight:700, cursor:"pointer" }}>삭제</button>
                : <span/>}
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={() => setShowForm(false)} disabled={saving}
                  style={{ padding:"8px 18px", borderRadius:8, border:`1px solid ${T.border}`, background:T.white, color:T.muted, fontSize:13, fontWeight:600, cursor:"pointer" }}>취소</button>
                <button onClick={saveForm} disabled={saving}
                  style={{ ...css.btnOrange, padding:"8px 20px", fontSize:13, opacity:saving?0.6:1 }}>{saving ? "저장 중..." : "저장"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 참여자 연결 검색 모달 */}
      {assignModal && (() => {
        const cur = matchOf(assignModal);
        const q = assignQuery.trim();
        const qDigits = q.replace(/[^0-9]/g, "");
        const filtered = !q ? participants : participants.filter(pt => {
          const nameHit = (pt.name && pt.name.includes(q)) || (pt.parent_name && pt.parent_name.includes(q));
          const phoneHit = qDigits && pt.phone && pt.phone.includes(qDigits);
          return nameHit || phoneHit;
        });
        const PAGE = 20;
        const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
        const safePage = Math.min(assignPage, totalPages);
        const results = filtered.slice((safePage - 1) * PAGE, safePage * PAGE);
        const pick = (pid) => { assignPost(assignModal, pid === null ? "" : pid); setAssignModal(null); };
        const fmtPhone = (ph) => ph ? ph.replace(/(\d{2,3})(\d{3,4})(\d{4})/, "$1-$2-$3") : "";
        return (
          <div onClick={() => setAssignModal(null)}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:10001, padding:16 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background:T.white, borderRadius:14, padding:20, width:"100%", maxWidth:460, maxHeight:"80vh", display:"flex", flexDirection:"column", boxShadow:"0 10px 40px rgba(0,0,0,0.25)" }}>
              <div style={{ fontSize:15, fontWeight:800, color:T.navy, marginBottom:4 }}>참여자 연결</div>
              <div style={{ fontSize:12, color:T.muted, marginBottom:10, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>글: {assignModal.post_title}</div>
              <input autoFocus value={assignQuery} onChange={e => { setAssignQuery(e.target.value); setAssignPage(1); }}
                placeholder="이름 또는 전화번호로 검색"
                style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1px solid ${T.border}`, fontSize:14, marginBottom:10, boxSizing:"border-box" }}/>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <span style={{ fontSize:11, color:T.muted }}>{q ? `검색 결과 ${filtered.length}명` : `전체 ${participants.length}명`} · {safePage}/{totalPages}페이지</span>
                {(assignModal.participant_id || cur.p) && (
                  <button onClick={() => pick(null)} style={{ fontSize:11, color:"#DC2626", background:"none", border:"none", cursor:"pointer", fontWeight:700 }}>연결 해제</button>
                )}
              </div>
              <div style={{ overflowY:"auto", border:`1px solid ${T.border}`, borderRadius:8 }}>
                {results.length === 0 && <div style={{ padding:20, textAlign:"center", color:T.muted, fontSize:13 }}>검색 결과가 없습니다.</div>}
                {results.map(pt => {
                  const isCur = cur.p && cur.p.id === pt.id;
                  return (
                    <div key={pt.id} onClick={() => pick(pt.id)}
                      style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 12px", cursor:"pointer", borderBottom:`1px solid ${T.border}`, background:isCur?"#EEF2FF":T.white }}
                      onMouseEnter={e => { if(!isCur) e.currentTarget.style.background="#F9FAFB"; }}
                      onMouseLeave={e => { if(!isCur) e.currentTarget.style.background=T.white; }}>
                      <span style={{ fontSize:13, fontWeight:700, color:T.navy, minWidth:60 }}>{pt.name || "—"}</span>
                      <span style={{ fontSize:12, color:T.muted, flex:1 }}>{pt.parent_name ? `학부모 ${pt.parent_name}` : ""}</span>
                      <span style={{ fontSize:12, color:T.text, fontFamily:"monospace" }}>{fmtPhone(pt.phone)}</span>
                      {isCur && <span style={{ fontSize:10, color:"#4F46E5", fontWeight:700 }}>현재</span>}
                    </div>
                  );
                })}
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <button onClick={() => setAssignPage(1)} disabled={safePage===1}
                    style={{ padding:"5px 9px", borderRadius:6, border:`1px solid ${T.border}`, background:T.white, color:safePage===1?"#CBD5E1":T.navy, fontSize:12, cursor:safePage===1?"default":"pointer" }}>«</button>
                  <button onClick={() => setAssignPage(p => Math.max(1, p-1))} disabled={safePage===1}
                    style={{ padding:"5px 9px", borderRadius:6, border:`1px solid ${T.border}`, background:T.white, color:safePage===1?"#CBD5E1":T.navy, fontSize:12, cursor:safePage===1?"default":"pointer" }}>‹ 이전</button>
                  <span style={{ fontSize:12, color:T.muted, minWidth:60, textAlign:"center" }}>{safePage} / {totalPages}</span>
                  <button onClick={() => setAssignPage(p => Math.min(totalPages, p+1))} disabled={safePage===totalPages}
                    style={{ padding:"5px 9px", borderRadius:6, border:`1px solid ${T.border}`, background:T.white, color:safePage===totalPages?"#CBD5E1":T.navy, fontSize:12, cursor:safePage===totalPages?"default":"pointer" }}>다음 ›</button>
                  <button onClick={() => setAssignPage(totalPages)} disabled={safePage===totalPages}
                    style={{ padding:"5px 9px", borderRadius:6, border:`1px solid ${T.border}`, background:T.white, color:safePage===totalPages?"#CBD5E1":T.navy, fontSize:12, cursor:safePage===totalPages?"default":"pointer" }}>»</button>
                </div>
                <button onClick={() => setAssignModal(null)}
                  style={{ padding:"8px 18px", borderRadius:8, border:`1px solid ${T.border}`, background:T.white, color:T.muted, fontSize:13, fontWeight:600, cursor:"pointer" }}>닫기</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// ══════════════════════════════════════════════════════
// ROOT APP
// ══════════════════════════════════════════════════════
export default function App() {
  const [authState, setAuthState] = useState("loading"); // loading | unauthenticated | ready | pending | passwordRecovery | sessionExpired
  const intentionalLogoutRef = useRef(false);
  // 가입 진행 중에는 OTP verify로 생긴 임시 세션의 SIGNED_IN을 무시해야 한다.
  // 그렇지 않으면 loadUserData가 "프로필 없음"으로 보고 강제 signOut → 가입 버튼 실패.
  const signupInProgressRef = useRef(false);
  const [showEISetup, setShowEISetup] = useState(false);
  // "loading" | "unauthenticated" | "needsProfile" | "pending" | "ready" | "passwordRecovery"
  const [session, setSession]     = useState(null);
  const [profile, setProfile]     = useState(null);
  const [logs, setLogs]           = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [children, setChildren]   = useState([]); // [{profile, logs}]
  const [selChildId, setSelChildId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const getInitialView = () => {
    const path = window.location.pathname;
    if(path === "/history") return "history";
    if(path === "/cert")    return "cert";
    if(path === "/input")   return "input";
    if(path === "/users")   return "users";
    if(path === "/roster2") return "roster2";
    if(path === "/challenge") return "challenge";
    if(path === "/attendance") return "attendance";
    if(path === "/manjeom")    return "manjeom";
    if(path === "/makeup")     return "roster2"; // backward-compat → roster2 makeup sub-tab
    return "dashboard";
  };
  const [view, setView]           = useState(getInitialView);
  const [showInput, setShowInput] = useState(window.location.pathname === "/input");
  const [showProfileModal, setShowProfileModal] = useState(false);
  const isMobile = useMobile();

  // URL ↔ 상태 동기화
  const navigate = (v, input=false) => {
    // 학부모 자녀별 view (child-*)는 URL을 / 로 두고 view 상태만 변경
    const path = input ? "/input"
      : v === "history" ? "/history"
      : v === "cert" ? "/cert"
      : v === "users" ? "/users"
      : v === "roster2" ? "/roster2"
      : v === "challenge" ? "/challenge"
      : v === "attendance" ? "/attendance"
      : v === "manjeom" ? "/manjeom"
      : "/";
    window.history.pushState({ view:v, input }, "", path);
    setView(v);
    setShowInput(input);
  };

  useEffect(() => {
    const onPop = (e) => {
      const s = e.state;
      const path = window.location.pathname;
      if(s) { setView(s.view||"dashboard"); setShowInput(s.input||false); }
      else if(path==="/cert")       { setView("cert");       setShowInput(false); }
      else if(path==="/users")      { setView("users");      setShowInput(false); }
      else if(path==="/roster2")    { setView("roster2");    setShowInput(false); }
      else if(path==="/challenge")  { setView("challenge");  setShowInput(false); }
      else if(path==="/attendance") { setView("attendance"); setShowInput(false); }
      else if(path==="/manjeom")    { setView("manjeom");    setShowInput(false); }
      else if(path==="/makeup")     { setView("roster2");    setShowInput(false); }
      else if(path==="/history")    { setView("history");    setShowInput(false); }
      else                          { setView("dashboard");  setShowInput(false); }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // 김도현 학생: 매주 월요일 미라클 나이트(아이인사이드 영작 수업) 자동 출석
  // 어제까지의 월요일들만 INSERT (미래는 안 함). 화요일에 자동 적용 효과.
  // 모든 인증 사용자가 트리거 가능 (멱등 SECURITY DEFINER RPC라 부담 없음)
  // supabase-js의 rpc()는 thenable이지만 Promise가 아니어서 .catch 없음 → .then(onOK, onErr)
  useEffect(() => {
    if (!session) return;
    supabase.rpc("apply_kimdohyun_monday_attendance").then(()=>{}, ()=>{});
  }, [session]);

  const loadUserData = async (sess) => {
    if(!sess?.user) { setAuthState("unauthenticated"); return; }
    const uid = sess.user.id;
    const { data: prof, error } = await supabase
      .from("profiles").select("*").eq("id", uid).single();

    // 프로필 없음 or 미완성 프로필 → 로그아웃 후 처음부터 재가입
    // cleanup_incomplete_signup이 같은 이메일 재시도 시 자동 처리
    if(!prof || error || !prof.name || prof.name === "미입력") {
      await supabase.auth.signOut();
      setSession(null);
      setAuthState("unauthenticated");
      return;
    }

    // 관리자 approval 자동 보정
    if(prof.role === "admin" && prof.approval_status !== "approved") {
      await supabase.from("profiles").update({ approval_status: "approved" }).eq("id", uid);
      prof.approval_status = "approved";
    }

    setProfile(prof);
    setSession(sess);

    // 미승인 학생
    if(prof.role !== "admin" && prof.approval_status !== "approved") {
      setAuthState("pending");
      return;
    }

    // 데이터 로드
    if(prof.role === "admin") {
      const [{ data: allLogs }, { data: allProfs }] = await Promise.all([
        supabase.rpc("get_all_logs"),
        supabase.rpc("get_all_profiles"),
      ]);
      setLogs(allLogs || []);
      setAllProfiles(allProfs || []);
    } else if(prof.role === "parent") {
      const { data: childProfiles } = await supabase.rpc("get_child_profiles");
      if(childProfiles && childProfiles.length > 0) {
        const childData = await Promise.all(childProfiles.map(async (cp) => {
          const { data: cl } = await supabase.rpc("get_child_logs", { child_id: cp.id });
          return { profile: cp, logs: cl || [] };
        }));
        setChildren(childData);
        setSelChildId(childData[0].profile.id);
      }
    } else {
      const { data: myLogs } = await supabase
        .from("learning_logs").select("*").eq("uid", uid).order("date", { ascending: false });
      setLogs(myLogs || []);
      // 목표 EI 미설정이면 팝업
      const isFirstLogin = (prof.target_ei===85||!prof.target_ei) && !(myLogs?.length>0);
      if(isFirstLogin) setShowEISetup(true);
    }
    setAuthState("ready");
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: sess }, error }) => {
      if(error) { supabase.auth.signOut(); setAuthState("unauthenticated"); return; }
      if(sess) loadUserData(sess);
      else setAuthState("unauthenticated");
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      if(event === "PASSWORD_RECOVERY") {
        setAuthState("passwordRecovery");
        setSession(sess);
        return;
      }
      // 첫 로드 시 발화 — getSession()이 이미 처리하므로 여기선 무시
      if(event === "INITIAL_SESSION") return;
      // 로그인 성공 → 정상 복귀 (sessionExpired에서 재로그인 포함)
      if(event === "SIGNED_IN" && sess) {
        intentionalLogoutRef.current = false;
        // 가입 중(OTP verify 직후) SIGNED_IN: 임시 세션. handleSignup이 끝까지 책임지므로 loadUserData 건너뜀
        if (signupInProgressRef.current) { setSession(sess); return; }
        loadUserData(sess);
        return;
      }
      if(event === "TOKEN_REFRESHED" && sess) {
        // 자동 refresh 성공 — 조용히 세션만 갱신
        setSession(sess);
        return;
      }
      // 명시 SIGNED_OUT 또는 토큰 만료
      if(event === "SIGNED_OUT") {
        if (intentionalLogoutRef.current) {
          intentionalLogoutRef.current = false;
          setAuthState("unauthenticated");
          setSession(null); setProfile(null);
          setLogs([]); setAllProfiles([]);
        } else {
          // 자동 refresh 실패 또는 토큰 만료 — 단, 이전에 인증된 적 있을 때만
          // (첫 방문에서는 이미 unauthenticated로 가 있음)
          setAuthState(prev => prev === "ready" || prev === "pending" ? "sessionExpired" : prev);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const refreshData = () => { if(session) loadUserData(session); };

  // AuthScreen이 가입/로그인 모드 토글 시 호출 — SIGNED_IN 핸들러가 이 ref를 보고 loadUserData 우회
  const handleSignupModeChange = useCallback((isSignup) => {
    signupInProgressRef.current = !!isSignup;
  }, []);

  // 역할별 초기 화면 보정
  useEffect(() => {
    if(authState !== "ready" || !profile) return;
    const in2ki = ROSTER2.some(s => s.name === profile.name);
    // 관리자: 항상 users(회원 관리) 시작
    if(profile.role === "admin" && (view === "cert" || view === "dashboard")) {
      navigate("users", false); return;
    }
    // 2기 학생: cert 시작
    if(profile.role === "student" && in2ki && view === "dashboard") {
      navigate("cert", false); return;
    }
    // 2기 아닌 학생이 cert 접근 시 dashboard로
    if(profile.role === "student" && !in2ki && view === "cert") {
      navigate("dashboard", false);
    }
  }, [authState, profile?.id]);

  const handleLogout = async () => {
    intentionalLogoutRef.current = true;
    await supabase.auth.signOut();
    setAuthState("unauthenticated");
    setSession(null); setProfile(null);
    setLogs([]); setAllProfiles([]);
  };

  // ── 로딩
  if(authState === "loading") return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center",
      justifyContent:"center", flexDirection:"column", gap:20, fontFamily:"'Noto Sans KR',sans-serif" }}>
      <Logo size="md"/>
      <Spinner size={36}/>
      <div style={{ fontSize:13, color:T.muted }}>로딩 중...</div>
    </div>
  );

  // ── 비밀번호 재설정
  if(authState === "passwordRecovery") return (
    <PasswordResetScreen onDone={()=>{ supabase.auth.signOut(); setAuthState("unauthenticated"); }}/>
  );

  // ── 비로그인
  if(authState === "unauthenticated") return (
    <AuthScreen
      onLogin={(sess) => { setAuthState("loading"); loadUserData(sess); }}
      onSignupModeChange={handleSignupModeChange}
    />
  );

  // ── 가입 미완료 (이름 미입력) → 이름/역할/비밀번호 입력 완료 화면
  if(authState === "needsProfile") return <CompleteProfileScreen session={session} onDone={async()=>{ await supabase.auth.signOut(); setSession(null); setAuthState("unauthenticated"); }}/>;

  // ── 승인 대기
  if(authState === "pending") return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'Noto Sans KR',sans-serif",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24 }}>
      <Card style={{ maxWidth:400, width:"100%", textAlign:"center", padding:"40px 32px" }}>
        <div style={{ fontSize:60, marginBottom:20 }}>⏳</div>
        <Logo size="md"/>
        <div style={{ height:1, background:T.border, margin:"20px 0" }}/>
        <div style={{ fontSize:20, fontWeight:800, color:T.navy, marginBottom:10 }}>승인 대기 중</div>
        <div style={{ fontSize:14, color:T.textMid, lineHeight:1.8, marginBottom:24 }}>
          안녕하세요, <strong style={{ color:T.navy }}>{profile?.name}</strong>님!<br/>
          관리자 승인 후 이용하실 수 있습니다.
        </div>
        <div style={{ background:T.orangePale, border:`1px solid ${T.orange}30`, borderRadius:12,
          padding:"14px 18px", fontSize:13, color:T.textMid, marginBottom:24 }}>
          📞 승인 관련 문의는 담당 선생님께 연락해 주세요.
        </div>
        <button onClick={handleLogout} style={{ ...css.btnGhost, padding:"10px 28px" }}>로그아웃</button>
      </Card>
    </div>
  );

  // ── 소셜 최초 프로필 설정 (authState === "needsProfile" 은 현재 미사용, signOut 처리)

  if(authState !== "ready" || !profile) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center",
      justifyContent:"center", fontFamily:"'Noto Sans KR',sans-serif" }}>
      <Spinner size={36}/>
    </div>
  );

  const isAdmin  = profile.role === "admin";
  const isParent = profile.role === "parent";
  // 20HA 2기 소속 여부 (학생 전용)
  const isIn2ki  = !isAdmin && !isParent && ROSTER2.some(s => s.name === profile.name);
  const NAV = isAdmin
    ? [
        { key:"users",       label:"회원 관리",          icon:"users"     },
        { key:"roster2",     label:"20HA 2기 현황",      icon:"trophy"    },
        { key:"challenge",   label:"챌린지 관리",         icon:"clipboard" },
        { key:"manjeom",     label:"만점 테스트",         icon:"cap"       },
        { key:"history",     label:"전체 기록",           icon:"calendar"  },
      ]
    : isParent
      ? [
          { key:"dashboard", label:"자녀 아이디 관리", icon:"users" },
          // 등록된 자녀별로 그룹 메뉴 동적 추가
          ...children.flatMap(c => {
            const cid = c.profile.id;
            const cname = c.profile.name;
            const childIs2ki = ROSTER2.some(s => s.name === cname);
            return [
              { header: true, label: `👤 ${cname}` },
              ...(childIs2ki ? [{ key:`child-cert-${cid}`,      label:"20HA 2기 인증현황", icon:"trophy"   }] : []),
              // 만점 테스트는 관리자 검토 중 — 학부모 메뉴에서도 임시 숨김 (admin 전용)
              { key:`child-dashboard-${cid}`, label:"메타인지 분석", icon:"cap"      },
              { key:`child-history-${cid}`,   label:"학습 기록",     icon:"calendar" },
            ];
          }),
        ]
      : [
          ...(isIn2ki ? [{ key:"cert", label:"20HA 2기 인증현황", icon:"trophy" }] : []),
          // 만점 테스트는 관리자 검토 중 — 학생 메뉴에서 임시 숨김 (admin 전용)
          { key:"dashboard", label:"메타인지 분석", icon:"cap" },
          { key:"history",   label:"학습 기록",     icon:"calendar" },
        ];
  const pendingCount = allProfiles.filter(p => (p.role==="student"||p.role==="parent") && p.approval_status==="pending").length;

  return (
    <div style={{ display:"flex", minHeight:"100vh", background:T.bg, fontFamily:"'Noto Sans KR',sans-serif", color:T.text }}>
      {/* 좌측 사이드바 — 데스크톱 전용 */}
      {!isMobile && (
        <SideNav
          nav={NAV}
          view={view}
          showInput={showInput}
          onNavigate={(v, input=false) => navigate(v, input)}
          profile={profile}
          isAdmin={isAdmin}
          isParent={isParent}
          onShowInput={() => navigate(view, true)}
          onLogout={handleLogout}
          onShowProfile={() => setShowProfileModal(true)}
          pendingCount={pendingCount}
          isOpen={sidebarOpen}
        />
      )}

      {/* 메인 영역 */}
      <div style={{ flex:1, minWidth:0, marginLeft: isMobile ? 0 : (sidebarOpen ? 240 : 56), minHeight:"100vh", display:"flex", flexDirection:"column", transition:"margin-left 0.22s ease", overflow:"hidden" }}>
        {/* 상단바 */}
        <div style={{ position:"sticky", top:0, zIndex:100, borderBottom:`1px solid ${T.border}`, background:T.surface, boxShadow:"0 1px 4px rgba(25,29,84,0.05)" }}>
          <div style={{ display:"flex", alignItems:"center", height:isMobile?56:58, gap:12, padding:`0 ${isMobile?16:24}px` }}>
            {/* 햄버거 버튼 — 데스크톱 */}
            {!isMobile && (
              <button onClick={() => setSidebarOpen(o => !o)}
                style={{ background:"none", border:"none", cursor:"pointer", padding:"6px 8px",
                  borderRadius:8, display:"flex", flexDirection:"column", gap:4, flexShrink:0 }}
                title={sidebarOpen ? "메뉴 닫기" : "메뉴 열기"}>
                <span style={{display:"block", width:18, height:2, background:T.navy, borderRadius:1}}/>
                <span style={{display:"block", width:18, height:2, background:T.navy, borderRadius:1}}/>
                <span style={{display:"block", width:18, height:2, background:T.navy, borderRadius:1}}/>
              </button>
            )}
            {isMobile && <Logo size="sm" headerMode={true} onClick={()=>navigate(NAV[0]?.key||"cert",false)}/>}
            {!isMobile && (
              <div style={{ fontSize:16, fontWeight:800, color:T.navy }}>
                {showInput ? "학습 입력" : NAV.find(n=>n.key===view)?.label || ""}
              </div>
            )}
            <div style={{ flex:1 }}/>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              {isMobile && !isAdmin && !isParent && (
                <button onClick={() => navigate(view, true)} style={{ padding:"7px 14px", borderRadius:8, border:"none", cursor:"pointer", fontSize:12, fontWeight:700, background:T.orange, color:T.white, display:"flex", alignItems:"center", gap:4 }}>
                  <span style={{fontSize:15, fontWeight:300}}>+</span> 입력
                </button>
              )}
              {isMobile && (
                <div onClick={()=>setShowProfileModal(true)}
                  style={{ width:32, height:32, borderRadius:10, background:T.grad, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:T.white, cursor:"pointer", overflow:"hidden", flexShrink:0 }}>
                  {profile.avatar_url
                    ? <img src={profile.avatar_url} alt="avatar" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    : isAdmin ? HI.user(16,"#fff") : profile.name?.charAt(0)||HI.cap(16,"#fff")}
                </div>
              )}
              {isMobile && (
                <button onClick={handleLogout} style={{ padding:"5px 10px", borderRadius:8, border:`1px solid ${T.border}`, background:T.white, color:T.muted, cursor:"pointer", fontSize:11, whiteSpace:"nowrap" }}>로그아웃</button>
              )}
            </div>
          </div>
        </div>

        {/* 페이지 콘텐츠 */}
        <div style={{ padding: isMobile ? "16px 12px 100px" : "28px 32px 60px", overflowX:"auto", flex:1 }}>
          {showInput ? (
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
                <button onClick={() => navigate(view, false)}
                  style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:22 }}>←</button>
                <div>
                  <div style={{ fontSize:isMobile?16:18, fontWeight:800, color:T.navy }}>학습 데이터 입력</div>
                  <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>정직하게 입력할수록 EI의 정확도가 높아집니다.</div>
                </div>
              </div>
              <DataInputForm uid={session.user.id}
                onSave={() => { navigate(view, false); refreshData(); }}
                onCancel={() => navigate(view, false)}/>
            </div>
          ) : view === "cert" && !isAdmin && !isParent && isIn2ki ? (
            <StudentCertView profile={profile}/>
          ) : view === "manjeom" && isAdmin ? (
            <ManjeomView onRefresh={refreshData} allProfiles={allProfiles}/>
          ) : view === "challenge" && isAdmin ? (
            <ChallengeAdmin/>
          ) : (view === "users" || view === "cert" || view === "roster2" || view === "attendance") && isAdmin ? (
            <AdminDashboard
              allLogs={logs} allProfiles={allProfiles} onRefresh={refreshData}
              defaultTab={view === "users" ? "users" : "roster2"}
              defaultRoster2Tab={
                view === "cert" ? "grading" :
                view === "attendance" ? "attendance" :
                (window.location.pathname === "/makeup") ? "makeup" : "overview"
              }/>
          ) : isParent && view.startsWith("child-") ? (
            (() => {
              // 만점 테스트는 관리자 검토 중 — child-manjeom-* 라우트는 admin 전환 전까지 비활성
              const m = view.match(/^child-(cert|dashboard|history)-(.+)$/);
              if(!m) return null;
              const [, sub, cid] = m;
              const child = children.find(c => c.profile.id === cid);
              if(!child) return <Card style={{padding:24,textAlign:"center",color:T.muted}}>자녀 정보를 찾을 수 없어요.</Card>;
              if(sub === "cert")      return <StudentCertView profile={child.profile} viewerMode="parent"/>;
              if(sub === "history")   return <LogHistory logs={child.logs} onDelete={null} isAdmin={false} allProfiles={[child.profile]}/>;
              return <StudentDashboard logs={child.logs} profile={child.profile} isAdminView={true}/>;
            })()
          ) : view === "dashboard" ? (
            isAdmin
              ? <AdminDashboard allLogs={logs} allProfiles={allProfiles} onRefresh={refreshData} defaultTab="users"/>
              : isParent
                ? <ParentHomeView
                    children={children}
                    parentId={session.user.id}
                    onChildrenUpdate={(newChildren, newSelId) => { setChildren(newChildren); if(newSelId) setSelChildId(newSelId); }}
                  />
                : <StudentDashboard logs={logs} profile={profile}/>
          ) : (
            <LogHistory
              logs={logs}
              onDelete={async (id) => {
                await supabase.from("learning_logs").delete().eq("id", id);
                setLogs(l => l.filter(x => x.id !== id));
              }}
              isAdmin={isAdmin}
              allProfiles={allProfiles}/>
          )}
        </div>
      </div>

      {isMobile && <BottomNav nav={NAV} view={view} showInput={showInput} onNavigate={navigate} isAdmin={isAdmin||isParent}/>}
      {showEISetup && <EISetupModal profile={profile} logs={logs} onSave={(t)=>{setProfile(p=>({...p,target_ei:t}));setShowEISetup(false);}}/> }
      {showProfileModal && <ProfileModal profile={profile} onClose={()=>setShowProfileModal(false)} onSave={(updated)=>{setProfile(updated);setShowProfileModal(false);}} onDelete={()=>{setShowProfileModal(false);handleLogout();}}/>}
      {authState === "sessionExpired" && (
        <SessionExpiredModal prefillEmail={session?.user?.email || ""}/>
      )}
    </div>
  );
}
